import { useState, useEffect, useRef, useMemo, startTransition } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useAuth } from '../../contexts/AuthContext';
import { useDgiiAdmin } from '../../contexts/DgiiAdminContext';
import AlertasAPI from './AlertasAPI';
import {
	TIPO_BIEN_SERVICIO,
	FORMA_PAGO,
	TIPO_RETENCION_ISR,
	TIPO_BIEN_SERVICIO_OPTIONS,
	FORMA_PAGO_OPTIONS,
	TIPO_RETENCION_ISR_OPTIONS,
	FORMATO_606_COLUMNAS,
	getTipoBienServicioLabel,
	getFormaPagoLabel,
	getTipoRetencionISRLabel,
	normalizarCodigoFormaPago,
	normalizarCodigoTipoBienServicio
} from '../../utils/dgiiConstants';
import { 
	DocumentTextIcon,
	MagnifyingGlassIcon,
	PencilIcon,
	PencilSquareIcon,
	CheckIcon,
	XMarkIcon,
	PhotoIcon,
	FunnelIcon,
	TrashIcon,
	ArrowDownTrayIcon,
	DocumentIcon,
	ArrowLeftIcon,
	ArrowRightIcon,
	ArrowPathIcon,
	EyeIcon,
	DocumentDuplicateIcon,
	PaperClipIcon,
	ArrowUpIcon,
	ArrowDownIcon,
	ExclamationTriangleIcon,
	Bars3Icon,
	XMarkIcon as XMarkIconSolid,
	SparklesIcon,
	CalendarIcon,
	ClockIcon,
	StarIcon,
	AdjustmentsHorizontalIcon,
	ChevronDownIcon,
	ChevronUpIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	InformationCircleIcon,
	LockClosedIcon,
	LockOpenIcon,
	ChartBarIcon
} from '@heroicons/react/24/outline';

export default function AdminDGIIView() {
	const { isAdmin, isGestor, isContador } = useAuth();
	const { stats: dgiiAdminStats, formularioId: dgiiAdminFormularioId, periodoLabel: dgiiAdminPeriodoLabel, setDgiiAdminState } = useDgiiAdmin();
	const [formularios, setFormularios] = useState([]);
	const [allFormularios, setAllFormularios] = useState([]); // Todos los formularios sin filtrar
	const [loading, setLoading] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [formularioToDelete, setFormularioToDelete] = useState(null);
	const [adminKey, setAdminKey] = useState('');
	const [deleteError, setDeleteError] = useState('');
	const [filtros, setFiltros] = useState({
		empresa: '',
		usuario: '',
		periodo: '',
		tipo: '',
		estado: ''
	});
	// Filtros horizontales para la tabla de registros
	const [tablaFiltros, setTablaFiltros] = useState({
		busqueda: '',
		forma_pago: '',
		tipo_bien_servicio: '',
		estado: '',       // 'auditado' | 'pendiente' | 'sin_revisar'
		monto_min: '',
		monto_max: '',
	});
	const [showTablaFiltros, setShowTablaFiltros] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [searchSuggestions, setSearchSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [sortBy, setSortBy] = useState('fecha'); // 'fecha', 'tipo', 'periodo', 'estado'
	const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
	const [formulariosPage, setFormulariosPage] = useState(1);
	const [formulariosPerPage, setFormulariosPerPage] = useState(5); // Formularios por página (default: 5)
	const [topFormularios] = useState(10); // Top X formularios a mostrar por defecto (sin filtros)
	const [selectedFormulario, setSelectedFormulario] = useState(null);
	const [editingData, setEditingData] = useState(null);
	const [facturasEditadas, setFacturasEditadas] = useState([]); // Nueva estructura: versiones editadas
	const [showImageModal, setShowImageModal] = useState(false);
	const [imageUrl, setImageUrl] = useState(null);
	const [imageZoom, setImageZoom] = useState(1);
	const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [usuarios, setUsuarios] = useState([]);
	const [empresas, setEmpresas] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);
	const [tableSortKey, setTableSortKey] = useState(null); // 'fecha_comprobante' | 'monto_servicios' | 'monto_bienes' | 'monto_facturado' | 'descuento' | 'itbis_facturado' | null
	const [tableSortDir, setTableSortDir] = useState('asc'); // 'asc' | 'desc'
	const [editingRowIndex, setEditingRowIndex] = useState(null);
	const [editingCell, setEditingCell] = useState(null); // { rowId, field } para edición de una sola celda
	const escapePressedRef = useRef(false); // Para cancelar edición con Escape sin guardar
	const [cellEditValue, setCellEditValue] = useState(''); // valor temporal al editar una celda
	const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
	const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
	const [showFilters, setShowFilters] = useState(true);
	const [showFiltersModal, setShowFiltersModal] = useState(false);
	const [formulariosGridCollapsed, setFormulariosGridCollapsed] = useState(false);
	const [showFormulariosSection, setShowFormulariosSection] = useState(true); // Botón para mostrar/ocultar listado de formularios
	const [showStatsPanel, setShowStatsPanel] = useState(true); // Botón para mostrar/ocultar panel de estadísticas
	const [quickFilters, setQuickFilters] = useState({
		ultimoMes: false,
		mesAnterior: false,
		esteAno: false,
		abiertos: false,
		cerrados: false
	});
	const [advancedFilters, setAdvancedFilters] = useState({
		rangoFechas: { inicio: '', fin: '' },
		minFacturas: '',
		maxFacturas: '',
		minArchivos: '',
		maxArchivos: '',
		soloConProblemas: false,
		soloSinProblemas: false
	});
	const [periodoPreset, setPeriodoPreset] = useState('');
	// Detalle de facturas físicas (FacturaExtraida) para auditoría
	const [showFacturasFisicas, setShowFacturasFisicas] = useState(false);
	const [facturasFisicas, setFacturasFisicas] = useState([]);
	const [loadingFacturasFisicas, setLoadingFacturasFisicas] = useState(false);
	// Tab para mostrar registros duplicados y faltantes
	const [tabRegistros, setTabRegistros] = useState('todos'); // 'todos', 'duplicados', 'pendientes', 'errores'
	const [registrosFaltantes, setRegistrosFaltantes] = useState([]); // Estado para registros pendientes
	// Persistencia de selección y pestaña al cambiar de ruta/menú (t3)
	const STORAGE_KEY_FORMULARIO = 'dgii_admin_selected_formulario_id';
	const STORAGE_KEY_TAB = 'dgii_admin_tab_registros';
	const STORAGE_KEY_LAST_STATS = 'dgii_admin_last_stats';
	const STORAGE_KEY_EMPRESA = 'dgii_admin_filter_empresa';
	const restoredSelectionRef = useRef(false);
	const restoringFromStorageRef = useRef(false);
	const loadFormulariosDebounceRef = useRef(null);
	const loadFormulariosFirstRunRef = useRef(true);
	const identificadorFaltantesCancelRef = useRef(false);
	// t3: caché de datos de restauración desde sessionStorage; se actualiza en useEffect, no en cada render
	const [cachedRestoreData, setCachedRestoreData] = useState(() => {
		try {
			if (typeof sessionStorage === 'undefined') return { formularioId: null, stats: null };
			const id = sessionStorage.getItem(STORAGE_KEY_FORMULARIO);
			const raw = sessionStorage.getItem(STORAGE_KEY_LAST_STATS);
			return { formularioId: id || null, stats: raw ? JSON.parse(raw) : null };
		} catch (e) {
			return { formularioId: null, stats: null };
		}
	});
	const [sidebarWidth, setSidebarWidth] = useState(256); // Ancho del sidebar (64 = 256px, 20 = 80px)
	const [isDesktop, setIsDesktop] = useState(false); // Si es desktop (>= 1024px)
	
	// Columnas contraíbles para más espacio en el grid (Acciones e Imagen)
	const [accionesColumnExpanded, setAccionesColumnExpanded] = useState(() => {
		try {
			const v = localStorage.getItem('dgii_acciones_column_expanded');
			return v !== 'false';
		} catch { return true; }
	});
	const [imagenColumnExpanded, setImagenColumnExpanded] = useState(() => {
		try {
			const v = localStorage.getItem('dgii_imagen_column_expanded');
			return v !== 'false';
		} catch { return true; }
	});
	const toggleAccionesColumn = () => {
		setAccionesColumnExpanded(prev => {
			const next = !prev;
			try { localStorage.setItem('dgii_acciones_column_expanded', String(next)); } catch {}
			return next;
		});
	};
	const toggleImagenColumn = () => {
		setImagenColumnExpanded(prev => {
			const next = !prev;
			try { localStorage.setItem('dgii_imagen_column_expanded', String(next)); } catch {}
			return next;
		});
	};

	// Modal de tasa de cambio para conversión de moneda (modos: 'editing' y 'normal')
	const [showCurrencyModal, setShowCurrencyModal] = useState(false);
	const [pendingCurrencyChange, setPendingCurrencyChange] = useState({ rowId: null, fromCurrency: 'DOP', toCurrency: 'DOP', mode: 'editing' });
	const currencyConversionRef = useRef(null); // Ref para evitar stale state: { rowId, foundRow, mode } al abrir modal
	const [exchangeRateInput, setExchangeRateInput] = useState('');

	// Estados para funcionalidades de duplicados
	const [duplicadosSeleccionados, setDuplicadosSeleccionados] = useState(new Set()); // IDs seleccionados para lote
	const [mostrarModalComparacion, setMostrarModalComparacion] = useState(false);
	const [datosComparacion, setDatosComparacion] = useState(null);
	const [mostrarModalMerge, setMostrarModalMerge] = useState(false);
	const [datosMerge, setDatosMerge] = useState(null);
	const [mostrarModalHistorial, setMostrarModalHistorial] = useState(false);
	const [historialFactura, setHistorialFactura] = useState(null);
	
	// Filtros avanzados para duplicados
	const [filtroArchivo, setFiltroArchivo] = useState('');
	const [filtroMotivo, setFiltroMotivo] = useState('');
	const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
	const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

	// Detectar el ancho del sidebar basado en el padding del contenedor principal
	useEffect(() => {
		const checkIsDesktop = () => {
			const desktop = window.innerWidth >= 1024;
			setIsDesktop(desktop);
			if (!desktop) {
				setSidebarWidth(0);
			}
		};

		checkIsDesktop();
		window.addEventListener('resize', checkIsDesktop);

		const detectSidebarWidth = () => {
			// Solo en desktop (lg y superior)
			if (window.innerWidth < 1024) {
				setSidebarWidth(0);
				return;
			}

			// Buscar el contenedor principal que tiene el padding del AdminLayout
			const mainContent = document.querySelector('div[class*="pl-20"], div[class*="pl-64"]');
			if (mainContent) {
				const computedStyle = window.getComputedStyle(mainContent);
				const paddingLeft = parseFloat(computedStyle.paddingLeft);
				if (paddingLeft > 0) {
					setSidebarWidth(paddingLeft);
					return;
				}
			}

			// Fallback: buscar el sidebar directamente
			const sidebar = document.querySelector('aside.fixed.left-0');
			if (sidebar) {
				const width = sidebar.offsetWidth || sidebar.clientWidth;
				if (width > 0) {
					setSidebarWidth(width);
				} else {
					setSidebarWidth(256); // Default expandido
				}
			} else {
				setSidebarWidth(256); // Default expandido
			}
		};

		// Detectar al cargar
		const timeouts = [
			setTimeout(detectSidebarWidth, 50),
			setTimeout(detectSidebarWidth, 200),
			setTimeout(detectSidebarWidth, 500)
		];

		// Observar cambios
		let resizeObserver = null;
		let mutationObserver = null;

		const setupObservers = () => {
			const mainContent = document.querySelector('div[class*="pl-20"], div[class*="pl-64"]');
			const sidebar = document.querySelector('aside.fixed.left-0');

			if (mainContent && window.ResizeObserver) {
				if (!resizeObserver) {
					resizeObserver = new ResizeObserver(() => {
						detectSidebarWidth();
					});
					resizeObserver.observe(mainContent);
				}
			}

			if (window.MutationObserver && !mutationObserver) {
				mutationObserver = new MutationObserver(() => {
					setTimeout(detectSidebarWidth, 10);
				});

				if (mainContent) {
					mutationObserver.observe(mainContent, {
						attributes: true,
						attributeFilter: ['class']
					});
				}
				if (sidebar) {
					mutationObserver.observe(sidebar, {
						attributes: true,
						attributeFilter: ['class']
					});
				}
			}
		};

		setTimeout(setupObservers, 100);
		window.addEventListener('resize', detectSidebarWidth);

		return () => {
			timeouts.forEach(clearTimeout);
			if (resizeObserver) resizeObserver.disconnect();
			if (mutationObserver) mutationObserver.disconnect();
			window.removeEventListener('resize', detectSidebarWidth);
			window.removeEventListener('resize', checkIsDesktop);
		};
	}, []);

	// Restaurar filtro empresa desde sessionStorage al montar para que las cards sigan visibles al volver (t3)
	useEffect(() => {
		try {
			if (typeof sessionStorage !== 'undefined') {
				const storedEmpresa = sessionStorage.getItem(STORAGE_KEY_EMPRESA);
				if (storedEmpresa) {
					setFiltros(prev => ({ ...prev, empresa: storedEmpresa }));
				}
			}
		} catch (e) { /* ignore */ }
	}, []);

	// t3: actualizar caché de restauración solo cuando no hay formulario seleccionado (evita leer sessionStorage en cada render)
	useEffect(() => {
		if (!selectedFormulario && typeof sessionStorage !== 'undefined') {
			try {
				const id = sessionStorage.getItem(STORAGE_KEY_FORMULARIO);
				const raw = sessionStorage.getItem(STORAGE_KEY_LAST_STATS);
				setCachedRestoreData({ formularioId: id || null, stats: raw ? JSON.parse(raw) : null });
			} catch (e) {
				setCachedRestoreData({ formularioId: null, stats: null });
			}
		}
	}, [selectedFormulario]);

	useEffect(() => {
		loadUsuarios();
	}, []);
	// loadFormularios se dispara en el efecto con debounce (filtros, searchTerm, ...)

	// Persistir formulario seleccionado, pestaña y empresa; limpiar context al deseleccionar (t3)
	useEffect(() => {
		if (selectedFormulario?.id != null) {
			try {
				sessionStorage.setItem(STORAGE_KEY_FORMULARIO, String(selectedFormulario.id));
				const empresaId = selectedFormulario.empresa ?? selectedFormulario.empresa_id ?? filtros.empresa;
				if (empresaId) sessionStorage.setItem(STORAGE_KEY_EMPRESA, String(empresaId));
			} catch (e) { /* ignore */ }
		} else {
			try {
				sessionStorage.removeItem(STORAGE_KEY_FORMULARIO);
			} catch (e) { /* ignore */ }
			setDgiiAdminState({ formularioId: null, stats: null, periodoLabel: null });
		}
	}, [selectedFormulario?.id, selectedFormulario?.empresa, selectedFormulario?.empresa_id, filtros.empresa]);
	useEffect(() => {
		try {
			sessionStorage.setItem(STORAGE_KEY_TAB, tabRegistros);
		} catch (e) { /* ignore */ }
	}, [tabRegistros]);

	// Resetear tab y filtros cuando cambia el formulario seleccionado (no si venimos de restaurar sesión)
	useEffect(() => {
		if (selectedFormulario) {
			if (!restoringFromStorageRef.current) {
				setTabRegistros('todos');
			} else {
				restoringFromStorageRef.current = false;
			}
			setTablaFiltros({ busqueda: '', rnc: '', ncf: '', forma_pago: '', tipo_bien_servicio: '' });
			setFiltros({ empresa: '', usuario: '', periodo: '', tipo: '', estado: '' });
			setSearchTerm('');
			setRegistrosFaltantes([]); // Limpiar registros faltantes al cambiar de formulario
			// Cargar facturas físicas al seleccionar un formulario
			const cargarFacturasFisicas = async () => {
				try {
					setLoadingFacturasFisicas(true);
					const data = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
					const facturasArray = Array.isArray(data) ? data : [];
					setFacturasFisicas(facturasArray);
				} catch (err) {
					setFacturasFisicas([]);
				} finally {
					setLoadingFacturasFisicas(false);
				}
			};
			cargarFacturasFisicas();
		}
	}, [selectedFormulario?.id]);

	// Cargar facturas físicas cuando se cambia al tab de duplicados
	useEffect(() => {
		if (tabRegistros === 'duplicados' && selectedFormulario && !loadingFacturasFisicas) {
			const cargarFacturasFisicas = async () => {
				try {
					setLoadingFacturasFisicas(true);
					const data = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
					const facturasArray = Array.isArray(data) ? data : [];
					setFacturasFisicas(facturasArray);
				} catch (err) {
					setFacturasFisicas([]);
				} finally {
					setLoadingFacturasFisicas(false);
				}
			};
			cargarFacturasFisicas();
		}
	}, [tabRegistros, selectedFormulario?.id]);

	// Cerrar edición de celda: clic fuera = cancelar, Escape = cancelar (sin guardar)
	useEffect(() => {
		if (!editingCell) return;
		const cancelEdit = () => {
			escapePressedRef.current = true;
			setEditingCell(null);
		};
		const onMouseDown = (e) => {
			const active = document.activeElement;
			if (!active) return;
			const editingTd = active.closest && active.closest('td');
			// Si el clic no es dentro de la celda en edición → cancelar (no guardar)
			if (editingTd && e.target && !editingTd.contains(e.target)) {
				e.preventDefault();
				e.stopPropagation();
				cancelEdit();
			}
		};
		const onKeyDown = (e) => {
			if (e.key === 'Escape' && editingCell) {
				const active = document.activeElement;
				const isInputOrSelect = active && (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.tagName === 'TEXTAREA');
				const isInTable = active && active.closest && active.closest('table');
				if (isInputOrSelect && isInTable) {
					e.preventDefault();
					e.stopPropagation();
					cancelEdit();
				}
			}
		};
		document.addEventListener('mousedown', onMouseDown, true);
		document.addEventListener('keydown', onKeyDown, true);
		return () => {
			document.removeEventListener('mousedown', onMouseDown, true);
			document.removeEventListener('keydown', onKeyDown, true);
		};
	}, [editingCell]);

	// Escape para cerrar modales (imagen, moneda, etc.) cuando están abiertos
	useEffect(() => {
		if (!showImageModal && !showCurrencyModal) return;
		const onKeyDown = (e) => {
			if (e.key === 'Escape') {
				e.preventDefault();
				if (showImageModal) {
					setShowImageModal(false);
					setImageZoom(1);
					setImagePosition({ x: 0, y: 0 });
				}
				if (showCurrencyModal) {
					setShowCurrencyModal(false);
					setExchangeRateInput('');
					setPendingCurrencyChange({ rowId: null, fromCurrency: 'DOP', toCurrency: 'DOP', mode: 'editing' });
				}
			}
		};
		document.addEventListener('keydown', onKeyDown, true);
		return () => document.removeEventListener('keydown', onKeyDown, true);
	}, [showImageModal, showCurrencyModal]);

	// Cargar facturas físicas si entramos a tabs que las necesitan y no hay datos cargados
	useEffect(() => {
		if (!selectedFormulario) return;
		if (!loadingFacturasFisicas && facturasFisicas.length === 0 && ['duplicados', 'pendientes', 'errores'].includes(tabRegistros)) {
			const cargar = async () => {
				try {
					setLoadingFacturasFisicas(true);
					const data = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
					setFacturasFisicas(Array.isArray(data) ? data : []);
				} catch (err) {
					setFacturasFisicas([]);
				} finally {
					setLoadingFacturasFisicas(false);
				}
			};
			cargar();
		}
	}, [tabRegistros, selectedFormulario, facturasFisicas.length, loadingFacturasFisicas]);

	// Calcular registros faltantes cuando cambia el formulario seleccionado
	useEffect(() => {
		if (!selectedFormulario) {
			setRegistrosFaltantes([]);
			return;
		}

		// Función para identificar registros faltantes (logs solo en dev para no bloquear UI)
		const logFaltantes = () => {};
		const identificarRegistrosFaltantes = () => {
			const registrosFaltantesLocal = [];
			
			// Función para normalizar y crear clave única
			const normalizar = (val) => String(val || '').trim().toUpperCase().replace(/[-\s]/g, '');
			const normalizarFecha = (fecha) => {
				if (!fecha) return '';
				if (typeof fecha === 'string') {
					if (fecha.includes('T')) return fecha.split('T')[0];
					if (fecha.includes(' ')) return fecha.split(' ')[0];
					if (fecha.match(/^\d{4}-\d{2}-\d{2}$/)) return fecha;
				}
				return String(fecha);
			};
			
			const crearClaveUnica = (datos) => {
				const ncf = normalizar(datos.ncf || '');
				const rnc = normalizar(datos.rnc_emisor || datos.rnc_cedula || datos.rnc || '');
				const total = datos.total_factura || datos.monto_total || datos.monto_facturado || 0;
				const fecha = normalizarFecha(datos.fecha_comprobante || datos.fecha || '');
				
				if (ncf && ncf !== 'NA' && ncf.length > 0) {
					return `ncf_${ncf}`;
				}
				if (rnc && rnc.length > 0) {
					return `rnc_${rnc}_total_${total}_fecha_${fecha}`;
				}
				return `total_${total}_fecha_${fecha}`;
			};
			
			// Obtener datos de la tabla (606 → compras, 607 → ventas)
			const tipoForm = selectedFormulario.tipo || '606';
			const claveLista = tipoForm === '607' ? 'ventas' : 'compras';
			const datosExtraidos = selectedFormulario.datos_extraidos || {};
			const datosCompletados = selectedFormulario.datos_completados || {};
			const listaExtraidos = datosExtraidos[claveLista] || [];
			const listaCompletados = datosCompletados[claveLista] || [];
			const todosLosRegistros = [...listaExtraidos, ...listaCompletados];
			
			// Crear un mapa de registros de la tabla por clave única
			const registrosTablaPorClave = new Map();
			todosLosRegistros.forEach((reg, idx) => {
				const clave = crearClaveUnica(reg);
				if (!registrosTablaPorClave.has(clave)) {
					registrosTablaPorClave.set(clave, []);
				}
				registrosTablaPorClave.get(clave).push({ reg, idx });
			});
			
			logFaltantes(`📊 Registros únicos en tabla: ${registrosTablaPorClave.size}`);
			logFaltantes(`📊 Total registros en tabla (con posibles duplicados): ${todosLosRegistros.length}`);
			
			// Contador de facturas físicas por clave única
			const facturasFisicasPorClave = new Map();
			let numFacturasExtraidas = 0;
			
			if (selectedFormulario.archivos_adjuntos) {
				selectedFormulario.archivos_adjuntos.forEach((archivo) => {
					const facturasDelArchivo = archivo.facturas_extraidas?.filter(factura => {
						const facturaFormId = typeof factura.formulario === 'object' 
							? factura.formulario?.id 
							: factura.formulario;
						return facturaFormId === selectedFormulario.id;
					}) || [];
					
					numFacturasExtraidas += facturasDelArchivo.length;
					
					facturasDelArchivo.forEach((factura) => {
						const datosFactura = factura.datos_estructurados || {};
						const clave = crearClaveUnica(datosFactura);
						
						if (!facturasFisicasPorClave.has(clave)) {
							facturasFisicasPorClave.set(clave, []);
						}
						facturasFisicasPorClave.get(clave).push({
							factura,
							archivo: archivo.nombre_original || 'Sin nombre',
							datos: datosFactura
						});
					});
				});
			}
			
			logFaltantes(`📊 Facturas físicas únicas: ${facturasFisicasPorClave.size}`);
			logFaltantes(`📊 Total facturas físicas: ${numFacturasExtraidas}`);
			
			// ANÁLISIS DETALLADO: Listar TODAS las facturas físicas con sus detalles
			logFaltantes(`\n📋 ========== ANÁLISIS DETALLADO DE TODAS LAS FACTURAS FÍSICAS ==========`);
			let todasLasFacturasFisicas = [];
			if (selectedFormulario.archivos_adjuntos) {
				selectedFormulario.archivos_adjuntos.forEach((archivo) => {
					const facturasDelArchivo = archivo.facturas_extraidas?.filter(factura => {
						const facturaFormId = typeof factura.formulario === 'object' 
							? factura.formulario?.id 
							: factura.formulario;
						return facturaFormId === selectedFormulario.id;
					}) || [];
					
					facturasDelArchivo.forEach((factura) => {
						const datosFactura = factura.datos_estructurados || {};
						const clave = crearClaveUnica(datosFactura);
						const registrosEnTabla = registrosTablaPorClave.get(clave) || [];
						
						// LÓGICA CORRECTA: Una factura está "en la tabla" si:
						// 1. Hay registro(s) en la tabla con su clave Y
						// 2. Es la primera factura con esa clave (por ID) O está marcada como "incluida" (no "duplicada")
						const estado = factura.estado_analisis || 'pendiente';
						const facturasConMismaClave = facturasFisicasPorClave.get(clave) || [];
						const facturasOrdenadas = [...facturasConMismaClave].sort((a, b) => a.factura.id - b.factura.id);
						const esPrimeraFactura = facturasOrdenadas.length > 0 && facturasOrdenadas[0].factura.id === factura.id;
						
						// Está realmente en la tabla si:
						// - Hay registro en tabla Y (es la primera factura con esa clave O está marcada como "incluida" y no "duplicada")
						const estaEnTabla = registrosEnTabla.length > 0 && (esPrimeraFactura || (estado === 'incluida' && estado !== 'duplicada'));
						
						todasLasFacturasFisicas.push({
							id: factura.id,
							archivo: archivo.nombre_original || 'Sin nombre',
							posicion: factura.posicion_en_archivo || 0,
							ncf: datosFactura.ncf || 'Sin NCF',
							rnc: datosFactura.rnc_emisor || datosFactura.rnc_cedula || datosFactura.rnc || 'Sin RNC',
							total: datosFactura.total_factura || datosFactura.monto_total || datosFactura.monto_facturado || 0,
							fecha: datosFactura.fecha_comprobante || datosFactura.fecha || 'Sin fecha',
							estado_analisis: estado,
							motivo_analisis: factura.motivo_analisis || '',
							clave: clave,
							estaEnTabla: estaEnTabla,
							registrosEnTabla: registrosEnTabla.length,
							esPrimeraFactura: esPrimeraFactura,
							totalFacturasMismaClave: facturasConMismaClave.length
						});
					});
				});
			}
			
			// Ordenar por ID para consistencia
			todasLasFacturasFisicas.sort((a, b) => a.id - b.id);
			
			logFaltantes(`\n📊 Lista completa de ${todasLasFacturasFisicas.length} facturas físicas:`);
			todasLasFacturasFisicas.forEach((f, idx) => {
				const icono = f.estaEnTabla ? '✅' : '❌';
				const estado = f.estado_analisis || 'pendiente';
				logFaltantes(`${icono} Factura #${idx + 1} (ID: ${f.id}):`);
				logFaltantes(`   Archivo: ${f.archivo}`);
				logFaltantes(`   Posición: ${f.posicion}`);
				logFaltantes(`   NCF: ${f.ncf}`);
				logFaltantes(`   RNC: ${f.rnc}`);
				logFaltantes(`   Total: ${f.total}`);
				logFaltantes(`   Fecha: ${f.fecha}`);
				logFaltantes(`   Estado Análisis: ${estado}`);
				logFaltantes(`   Está en Tabla: ${f.estaEnTabla ? 'SÍ' : 'NO'} (${f.registrosEnTabla} registro(s) en tabla, ${f.totalFacturasMismaClave} factura(s) con misma clave)`);
				logFaltantes(`   Es Primera Factura: ${f.esPrimeraFactura ? 'SÍ' : 'NO'}`);
				logFaltantes(`   Clave: ${f.clave}`);
				if (f.motivo_analisis) {
					logFaltantes(`   Motivo: ${f.motivo_analisis.substring(0, 100)}${f.motivo_analisis.length > 100 ? '...' : ''}`);
				}
				logFaltantes('');
			});
			
			// Identificar las facturas que NO están en la tabla
			const facturasNoEnTabla = todasLasFacturasFisicas.filter(f => !f.estaEnTabla);
			logFaltantes(`\n❌ Facturas que NO están en la tabla (${facturasNoEnTabla.length}):`);
			if (facturasNoEnTabla.length === 0) {
				logFaltantes(`   ⚠️ Todas las facturas dicen estar en la tabla, pero hay ${numFacturasExtraidas} facturas y ${todosLosRegistros.length} registros.`);
				logFaltantes(`   Esto indica que hay ${numFacturasExtraidas - todosLosRegistros.length} facturas que están marcadas como "incluida" pero no están realmente consolidadas.`);
				logFaltantes(`   Probablemente son duplicados físicos que deberían estar marcados como "duplicada".`);
			} else {
				facturasNoEnTabla.forEach((f, idx) => {
					logFaltantes(`   ${idx + 1}. FacturaExtraida ID: ${f.id}`);
					logFaltantes(`      - Archivo: ${f.archivo}`);
					logFaltantes(`      - Posición: ${f.posicion}`);
					logFaltantes(`      - NCF: ${f.ncf}`);
					logFaltantes(`      - RNC: ${f.rnc}`);
					logFaltantes(`      - Total: ${f.total}`);
					logFaltantes(`      - Fecha: ${f.fecha}`);
					logFaltantes(`      - Estado: ${f.estado_analisis}`);
					logFaltantes(`      - Clave: ${f.clave}`);
					logFaltantes(`      - Motivo: ${f.motivo_analisis || 'Sin motivo'}`);
				});
			}
			
			// Identificar registros faltantes
			// LÓGICA CORRECTA: Respetar el estado_analisis del backend
			// - "incluida": Ya está en la tabla (NO es faltante)
			// - "duplicada": Es un duplicado físico, ya está representada por otra (NO es faltante)
			// - "pendiente" o "excluida": Puede ser faltante si no hay registro en la tabla
			
			// Contar facturas por estado de análisis
			let facturasIncluidas = 0;
			let facturasDuplicadas = 0;
			let facturasPendientes = 0;
			let facturasExcluidas = 0;
			
			// LÓGICA CORRECTA: Identificar facturas faltantes comparando factura por factura
			// Si hay 25 facturas con el mismo NCF y 1 registro en la tabla:
			// - Solo 1 factura está realmente representada (la primera por ID)
			// - Las otras 24 son duplicados físicos (deberían estar marcadas como "duplicada")
			// - Si están marcadas como "incluida" pero no son la primera, son "faltantes" (no consolidadas)
			
			// Mapa para rastrear qué facturas están realmente representadas en la tabla
			const facturasRepresentadas = new Set();
			
			// Para cada clave única, marcar solo la primera factura como representada
			facturasFisicasPorClave.forEach((facturasConMismaClave, clave) => {
				const registrosEnTabla = registrosTablaPorClave.get(clave) || [];
				const numFacturasFisicas = facturasConMismaClave.length;
				const numRegistrosEnTabla = registrosEnTabla.length;
				
				// Contar estados de análisis
				facturasConMismaClave.forEach(({ factura }) => {
					const estado = factura.estado_analisis || 'pendiente';
					if (estado === 'incluida') facturasIncluidas++;
					else if (estado === 'duplicada') facturasDuplicadas++;
					else if (estado === 'excluida') facturasExcluidas++;
					else facturasPendientes++;
				});
				
				if (numRegistrosEnTabla === 0) {
					// No hay registro en la tabla: todas las facturas de esta clave son faltantes
					logFaltantes(`❌ Clave "${clave}": ${numFacturasFisicas} factura(s) física(s) pero 0 registro(s) en tabla`);
					facturasConMismaClave.forEach(({ factura, archivo, datos }) => {
						registrosFaltantesLocal.push({
							facturaId: factura.id,
							archivo: archivo,
							ncf: datos.ncf || 'Sin NCF',
							rnc: datos.rnc_emisor || datos.rnc_cedula || datos.rnc || 'Sin RNC',
							total: datos.total_factura || datos.monto_total || datos.monto_facturado || 0,
							fecha: datos.fecha_comprobante || datos.fecha || 'Sin fecha',
							datos: datos,
							posicion: factura.posicion_en_archivo || 0,
							estado_analisis: factura.estado_analisis || 'pendiente',
							motivo_analisis: `Clave única "${clave}" no encontrada en tabla. Estado: ${factura.estado_analisis || 'pendiente'}`
						});
					});
				} else if (numFacturasFisicas > numRegistrosEnTabla) {
					// Hay más facturas físicas que registros en la tabla
					// Ordenar facturas por ID para identificar la primera (la que está representada)
					const facturasOrdenadas = [...facturasConMismaClave].sort((a, b) => a.factura.id - b.factura.id);
					
					// Las primeras N facturas (donde N = numRegistrosEnTabla) están representadas
					facturasOrdenadas.slice(0, numRegistrosEnTabla).forEach(({ factura }) => {
						facturasRepresentadas.add(factura.id);
					});
					
					// Las restantes son faltantes SOLO si NO están marcadas como "duplicada"
					// Las duplicadas NO son faltantes porque ya están representadas por la factura principal
					const facturasFaltantes = facturasOrdenadas.slice(numRegistrosEnTabla).filter(({ factura }) => {
						const estado = factura.estado_analisis || 'pendiente';
						// NO son faltantes si están marcadas como "duplicada" o "incluida"
						return estado !== 'duplicada' && estado !== 'incluida';
					});
					
					if (facturasFaltantes.length > 0) {
						logFaltantes(`⚠️ Clave "${clave}": ${numFacturasFisicas} factura(s) física(s) pero solo ${numRegistrosEnTabla} registro(s) en tabla. Faltan ${facturasFaltantes.length} (excluyendo duplicadas)`);
						facturasFaltantes.forEach(({ factura, archivo, datos }) => {
							registrosFaltantesLocal.push({
								facturaId: factura.id,
								archivo: archivo,
								ncf: datos.ncf || 'Sin NCF',
								rnc: datos.rnc_emisor || datos.rnc_cedula || datos.rnc || 'Sin RNC',
								total: datos.total_factura || datos.monto_total || datos.monto_facturado || 0,
								fecha: datos.fecha_comprobante || datos.fecha || 'Sin fecha',
								datos: datos,
								posicion: factura.posicion_en_archivo || 0,
								estado_analisis: factura.estado_analisis || 'pendiente',
								motivo_analisis: `Factura física adicional: ${numFacturasFisicas} facturas con misma clave pero solo ${numRegistrosEnTabla} registro(s) en tabla. Estado: ${factura.estado_analisis || 'pendiente'} (NO es duplicada)`
							});
						});
					} else {
						const facturasDuplicadasEnGrupo = facturasOrdenadas.slice(numRegistrosEnTabla).filter(({ factura }) => {
							const estado = factura.estado_analisis || 'pendiente';
							return estado === 'duplicada';
						});
						if (facturasDuplicadasEnGrupo.length > 0) {
							logFaltantes(`✅ Clave "${clave}": ${facturasDuplicadasEnGrupo.length} factura(s) adicional(es) están correctamente marcadas como "duplicada" (NO son faltantes)`);
						}
					}
				} else {
					// Hay igual o menos facturas físicas que registros: todas están representadas
					logFaltantes(`✅ Clave "${clave}": ${numFacturasFisicas} factura(s) física(s) y ${numRegistrosEnTabla} registro(s) en tabla - OK`);
					facturasConMismaClave.forEach(({ factura }) => {
						facturasRepresentadas.add(factura.id);
					});
				}
			});
			
			logFaltantes(`\n📊 Facturas realmente representadas en tabla: ${facturasRepresentadas.size} de ${numFacturasExtraidas}`);
			
			logFaltantes(`\n📊 Estados de análisis de facturas físicas:`);
			logFaltantes(`   - Incluidas: ${facturasIncluidas}`);
			logFaltantes(`   - Duplicadas: ${facturasDuplicadas}`);
			logFaltantes(`   - Pendientes: ${facturasPendientes}`);
			logFaltantes(`   - Excluidas: ${facturasExcluidas}`);
			
			logFaltantes(`\n📊 ========== RESUMEN DEL ANÁLISIS ==========`);
			logFaltantes(`📊 Total facturas extraídas: ${numFacturasExtraidas}`);
			logFaltantes(`📊 Total registros faltantes identificados: ${registrosFaltantesLocal.length}`);
			logFaltantes(`📊 Registros faltantes:`, registrosFaltantesLocal);
			logFaltantes(`📊 ===========================================\n`);
			
			startTransition(() => setRegistrosFaltantes(registrosFaltantesLocal));
		};

		// Diferir para no bloquear el hilo principal (evita lentitud al seleccionar formulario)
		identificadorFaltantesCancelRef.current = false;
		const timeoutId = setTimeout(() => {
			if (identificadorFaltantesCancelRef.current) return;
			identificarRegistrosFaltantes();
		}, 0);
		return () => {
			identificadorFaltantesCancelRef.current = true;
			clearTimeout(timeoutId);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFormulario?.id]);

	// Debounce 400ms al cambiar filtros/búsqueda; primera ejecución inmediata para no retrasar carga inicial
	useEffect(() => {
		if (loadFormulariosDebounceRef.current) clearTimeout(loadFormulariosDebounceRef.current);
		if (loadFormulariosFirstRunRef.current) {
			loadFormulariosFirstRunRef.current = false;
			loadFormularios();
			return;
		}
		loadFormulariosDebounceRef.current = setTimeout(() => {
			loadFormulariosDebounceRef.current = null;
			loadFormularios();
		}, 400);
		return () => {
			if (loadFormulariosDebounceRef.current) {
				clearTimeout(loadFormulariosDebounceRef.current);
				loadFormulariosDebounceRef.current = null;
			}
		};
	}, [filtros, searchTerm, sortBy, sortOrder, quickFilters]);

	const loadFormularios = async () => {
		setLoading(true);
		try {
			// Para gestores y administradores, no filtrar por empresa (ver todos)
			// Para clientes, el backend ya filtra automáticamente
			const params = {};
			if (filtros.empresa) {
				params.empresa = filtros.empresa;
			}
			if (filtros.usuario) {
				params.usuario = filtros.usuario;
			}
			const data = await dgiiApi.getFormularios(params);
			// Asegurar que data sea un array
			let dataArray = [];
			if (Array.isArray(data)) {
				dataArray = data;
			} else if (data && Array.isArray(data.results)) {
				dataArray = data.results;
			} else {
				dataArray = [];
			}
			
			// Guardar todos los formularios sin filtrar
			setAllFormularios(dataArray);
			
			// Aplicar filtros y búsqueda
			let filtered = [...dataArray];

			// Búsqueda inteligente por texto (mejorada)
			if (searchTerm) {
				const term = searchTerm.toLowerCase();
				filtered = filtered.filter(f => {
					// Búsqueda en múltiples campos
					const matchTipo = f.tipo?.toLowerCase().includes(term);
					const matchPeriodo = f.periodo?.toLowerCase().includes(term);
					const matchEstado = f.estado?.toLowerCase().includes(term);
					const matchUsuario = f.usuario_nombre?.toLowerCase().includes(term);
					const matchEmpresa = f.empresa_nombre?.toLowerCase().includes(term);
					
					// Búsqueda inteligente: si el término parece un período (YYYY-MM), buscar específicamente
					if (/^\d{4}-\d{2}$/.test(term)) {
						return matchPeriodo;
					}
					
					// Búsqueda inteligente: si el término es un número, buscar en tipo
					if (/^\d+$/.test(term)) {
						return matchTipo || matchPeriodo;
					}
					
					return matchTipo || matchPeriodo || matchEstado || matchUsuario || matchEmpresa;
				});
			}

			// Aplicar filtros rápidos
			if (quickFilters.ultimoMes) {
				const ahora = new Date();
				const ultimoMes = new Date(ahora.getFullYear(), ahora.getMonth() - 1, ahora.getDate());
				filtered = filtered.filter(f => {
					if (!f.fecha_creacion) return false;
					const fechaForm = new Date(f.fecha_creacion);
					return fechaForm >= ultimoMes;
				});
			}
			
			if (quickFilters.mesAnterior) {
				const ahora = new Date();
				const mesAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
				const mesAnteriorFin = new Date(ahora.getFullYear(), ahora.getMonth(), 0, 23, 59, 59, 999);
				const mesAnteriorStr = String(mesAnterior.getFullYear()) + '-' + String(mesAnterior.getMonth() + 1).padStart(2, '0');
				filtered = filtered.filter(f => {
					if (!f.periodo) return false;
					return f.periodo === mesAnteriorStr;
				});
			}
			
			if (quickFilters.esteAno) {
				const esteAno = new Date().getFullYear();
				filtered = filtered.filter(f => {
					if (!f.periodo) return false;
					return f.periodo.startsWith(String(esteAno));
				});
			}
			
			if (quickFilters.abiertos) {
				filtered = filtered.filter(f => f.estado === 'abierto');
			}
			
			if (quickFilters.cerrados) {
				filtered = filtered.filter(f => f.estado === 'cerrado');
			}

			// Aplicar filtros avanzados
			if (advancedFilters.rangoFechas.inicio || advancedFilters.rangoFechas.fin) {
				filtered = filtered.filter(f => {
					if (!f.fecha_creacion) return false;
					const fechaForm = new Date(f.fecha_creacion);
					if (advancedFilters.rangoFechas.inicio) {
						const inicio = new Date(advancedFilters.rangoFechas.inicio);
						if (fechaForm < inicio) return false;
					}
					if (advancedFilters.rangoFechas.fin) {
						const fin = new Date(advancedFilters.rangoFechas.fin);
						fin.setHours(23, 59, 59, 999);
						if (fechaForm > fin) return false;
					}
					return true;
				});
			}

			// Filtro por cantidad de facturas
			if (advancedFilters.minFacturas || advancedFilters.maxFacturas) {
				filtered = filtered.filter(f => {
					let numFacturas = 0;
					if (f.archivos_adjuntos) {
						for (const archivo of f.archivos_adjuntos) {
							if (archivo.facturas_extraidas && Array.isArray(archivo.facturas_extraidas)) {
								const facturasDelFormulario = archivo.facturas_extraidas.filter(factura => {
									const facturaFormId = typeof factura.formulario === 'object' 
										? factura.formulario?.id 
										: factura.formulario;
									return facturaFormId === f.id;
								});
								numFacturas += facturasDelFormulario.length;
							}
						}
					}
					if (advancedFilters.minFacturas && numFacturas < parseInt(advancedFilters.minFacturas)) return false;
					if (advancedFilters.maxFacturas && numFacturas > parseInt(advancedFilters.maxFacturas)) return false;
					return true;
				});
			}

			// Filtro por cantidad de archivos
			if (advancedFilters.minArchivos || advancedFilters.maxArchivos) {
				filtered = filtered.filter(f => {
					const numArchivos = f.archivos_adjuntos?.length || 0;
					if (advancedFilters.minArchivos && numArchivos < parseInt(advancedFilters.minArchivos)) return false;
					if (advancedFilters.maxArchivos && numArchivos > parseInt(advancedFilters.maxArchivos)) return false;
					return true;
				});
			}

			// Filtro por problemas
			if (advancedFilters.soloConProblemas || advancedFilters.soloSinProblemas) {
				filtered = filtered.filter(f => {
					let tieneProblemas = false;
					if (f.datos_extraidos?.compras || f.datos_completados?.compras) {
						const compras = f.datos_completados?.compras || f.datos_extraidos?.compras || [];
						tieneProblemas = compras.some(compra => {
							const ncf = compra.ncf || '';
							const rnc = compra.rnc_emisor || compra.rnc_cedula || '';
							return !ncf || ncf === 'N/A' || !rnc || rnc === 'N/A';
						});
					}
					if (advancedFilters.soloConProblemas) return tieneProblemas;
					if (advancedFilters.soloSinProblemas) return !tieneProblemas;
					return true;
				});
			}

			// Aplicar preset de período
			if (periodoPreset) {
				if (periodoPreset === 'trimestre_actual') {
					const ahora = new Date();
					const trimestre = Math.floor(ahora.getMonth() / 3) + 1;
					const mesInicio = (trimestre - 1) * 3 + 1;
					const mesFin = trimestre * 3;
					filtered = filtered.filter(f => {
						if (!f.periodo) return false;
						const [ano, mes] = f.periodo.split('-').map(Number);
						return ano === ahora.getFullYear() && mes >= mesInicio && mes <= mesFin;
					});
				} else if (periodoPreset === 'mes_actual') {
					const ahora = new Date();
					const mesActual = String(ahora.getMonth() + 1).padStart(2, '0');
					const periodoActual = `${ahora.getFullYear()}-${mesActual}`;
					filtered = filtered.filter(f => f.periodo === periodoActual);
				} else if (periodoPreset === 'ano_actual') {
					const anoActual = new Date().getFullYear();
					filtered = filtered.filter(f => f.periodo?.startsWith(String(anoActual)));
				}
			}

			// Aplicar filtros básicos (empresa también en cliente por si la API no filtra o devuelve objeto)
			if (filtros.empresa) {
				const empresaId = String(filtros.empresa);
				filtered = filtered.filter(f => {
					const fEmpresaId = f.empresa_id ?? f.empresa?.id ?? (typeof f.empresa === 'number' ? f.empresa : null);
					return fEmpresaId != null && String(fEmpresaId) === empresaId;
				});
			}
			if (filtros.usuario) {
				filtered = filtered.filter(f => f.usuario === parseInt(filtros.usuario, 10));
			}
			if (filtros.periodo) {
				filtered = filtered.filter(f => f.periodo === filtros.periodo);
			}
			if (filtros.tipo) {
				filtered = filtered.filter(f => f.tipo === filtros.tipo);
			}
			if (filtros.estado) {
				filtered = filtered.filter(f => f.estado === filtros.estado);
			}

			// Sin ordenar aquí; el orden se aplica en formulariosOrdenados (useMemo) para reaccionar a sortBy/sortOrder
			setFormularios(filtered);
			// Restaurar formulario y pestaña al volver de otra ruta; restaurar por id aunque no esté en la lista filtrada (t3)
			const storedId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY_FORMULARIO) : null;
			if (!restoredSelectionRef.current && storedId) {
				restoredSelectionRef.current = true;
				restoringFromStorageRef.current = true;
				const idNum = parseInt(storedId, 10);
				handleSelectFormulario(idNum)
					.then(() => {
						const savedTab = sessionStorage.getItem(STORAGE_KEY_TAB);
						if (savedTab && ['todos', 'duplicados', 'pendientes', 'errores'].includes(savedTab)) {
							setTabRegistros(savedTab);
						}
					})
					.catch(() => {
						restoredSelectionRef.current = false;
						restoringFromStorageRef.current = false;
					});
			}
		} catch (err) {
			// Error cargando formularios
		} finally {
			setLoading(false);
		}
	};

	// Ordenar formularios (más reciente primero por defecto: periodo/fecha desc)
	const formulariosOrdenados = useMemo(() => {
		const copy = [...formularios];
		copy.sort((a, b) => {
			let aVal = sortBy === 'fecha' ? (a.fecha_creacion || '') : (a.periodo || a.fecha_creacion || '');
			let bVal = sortBy === 'fecha' ? (b.fecha_creacion || '') : (b.periodo || b.fecha_creacion || '');
			if (sortBy === 'tipo') { aVal = a.tipo || ''; bVal = b.tipo || ''; }
			if (sortBy === 'estado') { aVal = a.estado || ''; bVal = b.estado || ''; }
			const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
			return sortOrder === 'desc' ? -cmp : cmp;
		});
		return copy;
	}, [formularios, sortBy, sortOrder]);

	// Filtrar y paginar formularios (usa lista ya ordenada)
	const getFilteredAndPaginatedFormularios = () => {
		const startIndex = (formulariosPage - 1) * formulariosPerPage;
		return formulariosOrdenados.slice(startIndex, startIndex + formulariosPerPage);
	};

	// Calcular total de páginas
	const totalFormulariosPages = Math.ceil(formulariosOrdenados.length / formulariosPerPage);

	const handleSortFormularios = (field) => {
		if (sortBy === field) {
			setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
		} else {
			setSortBy(field);
			setSortOrder('desc');
		}
		setFormulariosPage(1); // Reset a primera página al cambiar ordenamiento
	};

	// Resetear página cuando cambian los filtros o búsqueda
	useEffect(() => {
		setFormulariosPage(1);
	}, [searchTerm, filtros, quickFilters, advancedFilters, periodoPreset]);

	// Obtener filtros activos para mostrar chips
	const getActiveFilters = () => {
		const active = [];
		if (filtros.empresa) {
			const emp = empresas.find(e => String(e.id) === String(filtros.empresa));
			active.push({ type: 'empresa', label: emp ? `Empresa: ${emp.nombre}` : `Empresa ${filtros.empresa}`, value: filtros.empresa });
		}
		if (filtros.usuario) {
			active.push({ type: 'usuario', label: `Usuario ${filtros.usuario}`, value: filtros.usuario });
		}
		if (filtros.periodo) {
			active.push({ type: 'periodo', label: `Período: ${filtros.periodo}`, value: filtros.periodo });
		}
		if (filtros.tipo) {
			active.push({ type: 'tipo', label: `Tipo: ${filtros.tipo}`, value: filtros.tipo });
		}
		if (filtros.estado) {
			active.push({ type: 'estado', label: `Estado: ${filtros.estado}`, value: filtros.estado });
		}
		if (quickFilters.ultimoMes) active.push({ type: 'quick', label: 'Último mes', value: 'ultimoMes' });
		if (quickFilters.mesAnterior) active.push({ type: 'quick', label: 'Mes anterior', value: 'mesAnterior' });
		if (quickFilters.esteAno) active.push({ type: 'quick', label: 'Este año', value: 'esteAno' });
		if (quickFilters.abiertos) active.push({ type: 'quick', label: 'Abiertos', value: 'abiertos' });
		if (quickFilters.cerrados) active.push({ type: 'quick', label: 'Cerrados', value: 'cerrados' });
		return active;
	};

	const removeFilter = (type, value) => {
		if (type === 'quick') {
			setQuickFilters(prev => ({ ...prev, [value]: false }));
		} else {
			setFiltros(prev => ({ ...prev, [type]: '' }));
		}
	};

	const applySuggestion = (suggestion) => {
		if (suggestion.type === 'periodo') {
			setFiltros(prev => ({ ...prev, periodo: suggestion.value }));
		} else if (suggestion.type === 'tipo') {
			setFiltros(prev => ({ ...prev, tipo: suggestion.value }));
		} else if (suggestion.type === 'estado') {
			setFiltros(prev => ({ ...prev, estado: suggestion.value }));
		}
		setSearchTerm('');
		setShowSuggestions(false);
	};

	const aplicarPresetPeriodo = (preset) => {
		setPeriodoPreset(preset === periodoPreset ? '' : preset);
	};

	const limpiarTodosLosFiltros = () => {
		setFiltros({ empresa: '', usuario: '', periodo: '', tipo: '', estado: '' });
		setSearchTerm('');
		setQuickFilters({ ultimoMes: false, mesAnterior: false, esteAno: false, abiertos: false, cerrados: false });
		setAdvancedFilters({
			rangoFechas: { inicio: '', fin: '' },
			minFacturas: '',
			maxFacturas: '',
			minArchivos: '',
			maxArchivos: '',
			soloConProblemas: false,
			soloSinProblemas: false
		});
		setPeriodoPreset('');
	};

	// Función para manejar eliminación de formulario
	const handleDeleteFormulario = (formulario) => {
		setFormularioToDelete(formulario);
		setAdminKey('');
		setDeleteError('');
		setShowDeleteModal(true);
	};

	// Función para confirmar eliminación
	const confirmDeleteFormulario = async () => {
		if (!formularioToDelete) return;

		// Verificar permisos
		const tienePermisos = isAdmin() || isGestor();
		
		// Si no tiene permisos, requiere clave de administrador
		if (!tienePermisos && !adminKey.trim()) {
			setDeleteError('Debes proporcionar la clave de administrador para eliminar este formulario.');
			return;
		}

		try {
			// Preparar payload
			const payload = {};
			if (!tienePermisos && adminKey.trim()) {
				payload.clave_admin = adminKey;
			}

			// Llamar al API para eliminar
			await dgiiApi.deleteFormulario(formularioToDelete.id, payload);
			
			// Si el formulario eliminado estaba seleccionado, limpiar selección
			if (selectedFormulario?.id === formularioToDelete.id) {
			setSelectedFormulario(null);
			setEditingData(null);
			setFacturasEditadas([]); // Limpiar facturas editadas al cerrar formulario
			}

			// Recargar formularios
			await loadFormularios();
			
			// Cerrar modal
			setShowDeleteModal(false);
			setFormularioToDelete(null);
			setAdminKey('');
			setDeleteError('');
			
			window.alert('Formulario eliminado exitosamente.');
		} catch (err) {
			const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Error al eliminar el formulario.';
			setDeleteError(errorMessage);
		}
	};

	const loadUsuarios = async () => {
		// Cargar empresas para el filtro
		try {
			const data = await dgiiApi.getEmpresas();
			const empresasArray = Array.isArray(data) ? data : [];
			setEmpresas(empresasArray);
		} catch (err) {
			setEmpresas([]);
		}
	};

	const handleSelectFormulario = async (id) => {
		try {
			// Primero cargar el formulario inicial
			const formulario = await dgiiApi.getFormulario(id);
			setSelectedFormulario(formulario);
			setEditingData(null);
			// Resetear detalle de facturas físicas al cambiar de formulario
			setShowFacturasFisicas(false);
			
			// NUEVA ESTRUCTURA: Cargar FacturaEditada para obtener versiones editadas
			try {
				const editadas = await dgiiApi.getFacturasEditadas(id, true); // solo versiones actuales
				setFacturasEditadas(editadas);
			} catch (err) {
				setFacturasEditadas([]);
			}
			
			// Automáticamente refrescar el formulario para marcar duplicados y actualizar datos
			try {
				const refreshed = await dgiiApi.refreshFormulario(formulario.id);
				setSelectedFormulario(refreshed);
				
				// Recargar facturas editadas después del refresh
				try {
					const editadas = await dgiiApi.getFacturasEditadas(refreshed.id, true);
					setFacturasEditadas(editadas);
				} catch (err) {
					// Error recargando facturas editadas
				}
				
				// Cargar facturas físicas actualizadas después del refresh
				const fisicas = await dgiiApi.getFacturasFisicas(refreshed.id);
				setFacturasFisicas(Array.isArray(fisicas) ? fisicas : []);
			} catch (refreshErr) {
				// Si falla el refresh, continuar con los datos iniciales
				try {
					const fisicas = await dgiiApi.getFacturasFisicas(formulario.id);
					setFacturasFisicas(Array.isArray(fisicas) ? fisicas : []);
				} catch (err) {
					setFacturasFisicas([]);
				}
			}
		} catch (err) {
			// Error cargando formulario
		}
	};

	const handleCerrarFormulario = async () => {
		if (!selectedFormulario) return;
		
		if (!window.confirm(`¿Estás seguro de cerrar el formulario ${selectedFormulario.tipo} - ${selectedFormulario.periodo}? Una vez cerrado, no se podrán cargar más archivos.`)) {
			return;
		}

		try {
			await dgiiApi.cerrarFormulario(selectedFormulario.id);
			// Recargar el formulario para obtener el estado actualizado
			const formularioActualizado = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(formularioActualizado);
			// Recargar lista de formularios
			loadFormularios();
			window.alert('Formulario cerrado exitosamente');
		} catch (err) {
			window.alert('Error al cerrar el formulario: ' + (err.response?.data?.detail || err.message));
		}
	};

	const handleAbrirFormulario = async () => {
		if (!selectedFormulario) return;
		
		const claveAdmin = window.prompt('Ingresa la clave de administrador para abrir el formulario:');
		if (!claveAdmin) {
			return;
		}

		try {
			await dgiiApi.abrirFormulario(selectedFormulario.id, claveAdmin);
			// Recargar el formulario para obtener el estado actualizado
			const formularioActualizado = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(formularioActualizado);
			// Recargar lista de formularios
			loadFormularios();
			window.alert('Formulario abierto exitosamente');
		} catch (err) {
			window.alert('Error al abrir el formulario: ' + (err.response?.data?.detail || err.message));
		}
	};

	const handleAgregarRegistroManual = async () => {
		if (!selectedFormulario || selectedFormulario.estado !== 'abierto') {
			alert('Solo se pueden agregar registros manuales a formularios abiertos.');
			return;
		}
		
		// Obtener actividad económica del usuario desde la lista de usuarios
		let actividadEconomica = '';
		if (selectedFormulario.usuario) {
			const usuarioId = typeof selectedFormulario.usuario === 'object' 
				? selectedFormulario.usuario.id 
				: selectedFormulario.usuario;
			const usuarioCompleto = usuarios.find(u => {
				const uId = typeof u === 'object' ? u.id : u;
				return uId === usuarioId;
			});
			if (usuarioCompleto && typeof usuarioCompleto === 'object' && usuarioCompleto.profile) {
				actividadEconomica = usuarioCompleto.profile.actividad_economica || '';
			}
		}
		
		// Datos iniciales vacíos para el nuevo registro
		const datosIniciales = {
			rnc_emisor: '',
			tipo_id: '',
			tipo_bien_servicio: '',
			bien_servicio: '',
			ncf: '',
			ncf_modificado: '',
			fecha_comprobante: '',
			fecha_pago: '',
			monto_facturado_servicios: 0,
			monto_facturado_bienes: 0,
			monto_facturado: 0,
			itbis_facturado: 0,
			itbis_retenido: 0,
			itbis_proporcionalidad: 0,
			itbis_llevado_costo: 0,
			itbis_por_adelantar: 0,
			itbis_percibido_compras: 0,
			tipo_retencion_isr: '',
			monto_retencion_renta: 0,
			isr_percibido_compras: 0,
			impuesto_selectivo_consumo: 0,
			otros_impuestos_tasas: 0,
			monto_propina_legal: 0,
			forma_pago: '4',
			nombre_emisor: '',
			moneda: 'DOP',
			actividad_economica: actividadEconomica,
			auditado: false,
		};

		let registroId, rowIdUnico, nuevoRegistro;

		try {
			// Crear FacturaManual en BD para obtener PK real
			const manual = await dgiiApi.crearRegistroManual(selectedFormulario.id, datosIniciales);
			const pkReal = manual.id;
			registroId = pkReal;
			rowIdUnico = `row_${pkReal}`;
			nuevoRegistro = {
				...datosIniciales,
				_id: pkReal,
				_manual_id: pkReal,
				_rowId: rowIdUnico,
			};
		} catch (err) {
			// Fallback: generar ID local si el backend no está disponible
			console.warn('No se pudo crear FacturaManual en BD, usando ID local:', err);
			const localId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			registroId = localId;
			rowIdUnico = `row_${localId}`;
			nuevoRegistro = {
				...datosIniciales,
				_id: localId,
				_rowId: rowIdUnico,
			};
		}

		const tipo = selectedFormulario.tipo;
		const datosCompletadosActuales = editingData?.datos_completados 
			?? JSON.parse(JSON.stringify(selectedFormulario.datos_completados || {}));
		const datosExtraidosActuales = editingData?.datos_extraidos 
			?? JSON.parse(JSON.stringify(selectedFormulario.datos_extraidos || {}));
		const claveLista = tipo === '606' ? 'compras' : 'ventas';
		const registrosActuales = datosCompletadosActuales[claveLista] || [];
		
		const nuevosDatosCompletados = {
			...datosCompletadosActuales,
			[claveLista]: [nuevoRegistro, ...registrosActuales]
		};
		
		setEditingData({
			datos_completados: nuevosDatosCompletados,
			datos_extraidos: datosExtraidosActuales,
			editingRowId: rowIdUnico
		});
		
		// Mover a la primera página para ver el nuevo registro
		setCurrentPage(1);
		
		// Scroll a la tabla para que el usuario vea el nuevo registro
		setTimeout(() => {
			const tableElement = document.querySelector('[data-table="registros"]');
			if (tableElement) {
				tableElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}
		}, 100);
	};

	const handleEdit = (rowId = null) => {
		if (selectedFormulario) {
			// Inicializar datos_completados con los datos actuales si no existen
			const datosCompletados = JSON.parse(JSON.stringify(selectedFormulario.datos_completados || {}));
			const datosExtraidos = JSON.parse(JSON.stringify(selectedFormulario.datos_extraidos || {}));
			
			// NO copiar todos los registros de datos_extraidos a datos_completados
		// Solo inicializar arrays vacíos si no existen
		// Los registros se agregarán individualmente cuando se editen
			if (selectedFormulario.tipo === '606') {
				if (!datosCompletados.compras) {
					datosCompletados.compras = [];
				}
			} else if (selectedFormulario.tipo === '607') {
				if (!datosCompletados.ventas) {
					datosCompletados.ventas = [];
				}
			}
			
			// Asegurar que los datos_completados existentes tengan _rowId y estén limpios
			if (selectedFormulario.tipo === '606' && datosCompletados.compras && datosCompletados.compras.length > 0) {
				datosCompletados.compras = datosCompletados.compras.map((row) => {
					const clave = generarClaveRegistro(row);
					const rowLimpio = limpiarRegistroParaGuardar(row);
					return {
						...rowLimpio,
						_rowId: row._rowId || `row_${clave}`
					};
				});
			} else if (selectedFormulario.tipo === '607' && datosCompletados.ventas && datosCompletados.ventas.length > 0) {
				datosCompletados.ventas = datosCompletados.ventas.map((row) => {
					const clave = generarClaveRegistro(row);
					const rowLimpio = limpiarRegistroParaGuardar(row);
					return {
						...rowLimpio,
						_rowId: row._rowId || `row_${clave}`
					};
				});
			}
			
			// Si se especifica un rowId, solo editar ese registro específico
			if (rowId) {
				// Encontrar el registro específico
				// IMPORTANTE: Usar getDataRows con useEditingData=false para obtener los datos originales
				const allRows = dataRowsSelected;
				const rowToEdit = allRows.find(r => {
					// Buscar por _rowId primero
					if (r._rowId === rowId) return true;
					// Si no se encuentra, buscar por _id (puede que rowId sea el _id)
					if (r._id === rowId) return true;
					// Si no se encuentra, buscar por _originalIndex (fallback)
					if (r._originalIndex !== undefined && String(r._originalIndex) === String(rowId)) return true;
					return false;
				});
				
			if (rowToEdit) {
			// NO inicializar forma_pago con metodo_pago del archivo
			// El registro ya tiene forma_pago de n8n o '1' (Efectivo) por defecto
				
				// Generar clave única del registro para buscar en datos_completados
				const claveRegistro = generarClaveRegistro(rowToEdit);
					
					// Actualizar solo ese registro en datos_completados
					// Asegurar que datos_completados tenga la estructura correcta
					if (selectedFormulario.tipo === '606') {
						if (!datosCompletados.compras) {
							datosCompletados.compras = [];
						}
						
						// Limpiar campos internos del registro antes de guardarlo
						const registroLimpio = limpiarRegistroParaGuardar(rowToEdit);
						
						// IMPORTANTE: Preservar el _id del registro original si existe
						if (rowToEdit._id) {
							registroLimpio._id = rowToEdit._id;
						}
						
						// Verificar si ya existe usando PRIMERO _id (clave única real), luego clave generada
						let indexExistente = -1;
						
						// Método 1: Buscar por _id (más confiable)
						if (registroLimpio._id) {
							indexExistente = datosCompletados.compras.findIndex(r => r._id === registroLimpio._id);
						}
						
						// Método 2: Si no se encontró por _id, buscar por clave generada
						if (indexExistente === -1) {
							indexExistente = datosCompletados.compras.findIndex(r => {
								const clave = generarClaveRegistro(r);
								return clave === claveRegistro;
							});
						}
						
						// Método 3: Si aún no se encontró, buscar por NCF + RNC + Total + Fecha (búsqueda exhaustiva)
						if (indexExistente === -1) {
							const rncTarget = limpiarRNC(rowToEdit.rnc_emisor || rowToEdit.rnc_cedula || rowToEdit.rnc || '');
							const ncfTarget = normalizarNCF(rowToEdit.ncf || '');
							const totalTarget = rowToEdit.total_factura || rowToEdit.monto_facturado || 0;
							const fechaTarget = formatDateForComparison(rowToEdit.fecha_comprobante || rowToEdit.fecha || '');
							
							indexExistente = datosCompletados.compras.findIndex(r => {
								const rnc = limpiarRNC(r.rnc_emisor || r.rnc_cedula || r.rnc || '');
								const ncf = normalizarNCF(r.ncf || '');
								const total = r.total_factura || r.monto_facturado || 0;
								const fecha = formatDate(r.fecha_comprobante || r.fecha || '');
								return rnc === rncTarget && ncf === ncfTarget && 
									   Math.abs(total - totalTarget) < 0.01 && fecha === fechaTarget;
							});
						}
						
						if (indexExistente !== -1) {
							// Actualizar el registro existente, preservando su _id original
							const registroExistente = datosCompletados.compras[indexExistente];
							if (registroExistente._id && (!registroLimpio._id || String(registroLimpio._id).startsWith('legacy_'))) {
								registroLimpio._id = registroExistente._id;
							}
							// Preservar _rowId si existe
							if (registroExistente._rowId) {
								registroLimpio._rowId = registroExistente._rowId;
							}
							datosCompletados.compras[indexExistente] = registroLimpio;
						} else {
							// Si no existe, verificar una vez más por _id antes de agregar
							// Esto previene duplicados si el registro ya fue agregado previamente
							const yaExistePorId = registroLimpio._id && datosCompletados.compras.some(r => r._id === registroLimpio._id);
							
							if (!yaExistePorId) {
								// Si no existe, agregarlo (viene de datos_extraidos)
								if (!registroLimpio._id) {
									registroLimpio._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
								}
								// Preservar _rowId del registro original
								if (rowToEdit._rowId) {
									registroLimpio._rowId = rowToEdit._rowId;
								}
								datosCompletados.compras.push(registroLimpio);
							} else {
								// Si ya existe por _id, actualizarlo en lugar de agregarlo
								const indexExistentePorId = datosCompletados.compras.findIndex(r => r._id === registroLimpio._id);
								if (indexExistentePorId >= 0) {
									// Preservar _rowId del registro existente si es mejor
									const registroExistente = datosCompletados.compras[indexExistentePorId];
									if (registroExistente._rowId) {
										registroLimpio._rowId = registroExistente._rowId;
									}
									datosCompletados.compras[indexExistentePorId] = registroLimpio;
								}
							}
						}
						
						// Eliminar duplicados inmediatamente después de agregar/actualizar
						// IMPORTANTE: Usar _id como clave única, no generarClaveRegistro
						const registrosUnicos = new Map();
						let duplicadosEliminados = 0;
						
						datosCompletados.compras.forEach(r => {
							// Asegurar que tenga _id
							if (!r._id) {
								r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
							}
							
							// Usar _id como clave única
							if (!registrosUnicos.has(r._id)) {
								registrosUnicos.set(r._id, r);
							} else {
								// Si hay duplicado por _id, mantener el más completo
								duplicadosEliminados++;
								const existente = registrosUnicos.get(r._id);
								const camposExistentes = Object.keys(existente).filter(k => !k.startsWith('_')).length;
								const camposNuevos = Object.keys(r).filter(k => !k.startsWith('_')).length;
								
								// Priorizar: auditado > más campos > mantener existente
								if (r.auditado === true && existente.auditado !== true) {
									registrosUnicos.set(r._id, r);
								} else if (camposNuevos > camposExistentes && existente.auditado !== true) {
									registrosUnicos.set(r._id, r);
								} else {
								}
							}
						});
						
						datosCompletados.compras = Array.from(registrosUnicos.values());
						if (duplicadosEliminados > 0) {
						}
						
					} else if (selectedFormulario.tipo === '607') {
						if (!datosCompletados.ventas) {
							datosCompletados.ventas = [];
						}
						
						const registroLimpio = limpiarRegistroParaGuardar(rowToEdit);
						
						// IMPORTANTE: Preservar el _id del registro original si existe
						if (rowToEdit._id) {
							registroLimpio._id = rowToEdit._id;
						}
						
						// Verificar si ya existe usando PRIMERO _id (clave única real), luego clave generada
						let indexExistente = -1;
						
						// Método 1: Buscar por _id (más confiable)
						if (registroLimpio._id) {
							indexExistente = datosCompletados.ventas.findIndex(r => r._id === registroLimpio._id);
						}
						
						// Método 2: Si no se encontró por _id, buscar por clave generada
						if (indexExistente === -1) {
							indexExistente = datosCompletados.ventas.findIndex(r => {
								const clave = generarClaveRegistro(r);
								return clave === claveRegistro;
							});
						}
						
						// Método 3: Si aún no se encontró, buscar por NCF + RNC + Total + Fecha (búsqueda exhaustiva)
						if (indexExistente === -1) {
							const rncTarget = limpiarRNC(rowToEdit.rnc_emisor || rowToEdit.rnc_cedula || rowToEdit.rnc || '');
							const ncfTarget = normalizarNCF(rowToEdit.ncf || '');
							const totalTarget = rowToEdit.total_factura || rowToEdit.monto_facturado || 0;
							const fechaTarget = formatDateForComparison(rowToEdit.fecha_comprobante || rowToEdit.fecha || '');
							
							indexExistente = datosCompletados.ventas.findIndex(r => {
								const rnc = limpiarRNC(r.rnc_emisor || r.rnc_cedula || r.rnc || '');
								const ncf = normalizarNCF(r.ncf || '');
								const total = r.total_factura || r.monto_facturado || 0;
								const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
								return rnc === rncTarget && ncf === ncfTarget && 
									   Math.abs(total - totalTarget) < 0.01 && fecha === fechaTarget;
							});
						}
						
						if (indexExistente !== -1) {
							// Actualizar el registro existente, preservando su _id original
							const registroExistente = datosCompletados.ventas[indexExistente];
							if (registroExistente._id && (!registroLimpio._id || String(registroLimpio._id).startsWith('legacy_'))) {
								registroLimpio._id = registroExistente._id;
							}
							// Preservar _rowId si existe
							if (registroExistente._rowId) {
								registroLimpio._rowId = registroExistente._rowId;
							}
							datosCompletados.ventas[indexExistente] = registroLimpio;
						} else {
							// Si no existe, verificar una vez más por _id antes de agregar
							// Esto previene duplicados si el registro ya fue agregado previamente
							const yaExistePorId = registroLimpio._id && datosCompletados.ventas.some(r => r._id === registroLimpio._id);
							
							if (!yaExistePorId) {
								// Si no existe, agregarlo (viene de datos_extraidos)
								if (!registroLimpio._id) {
									registroLimpio._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
								}
								// Preservar _rowId del registro original
								if (rowToEdit._rowId) {
									registroLimpio._rowId = rowToEdit._rowId;
								}
								datosCompletados.ventas.push(registroLimpio);
							} else {
								// Si ya existe por _id, actualizarlo en lugar de agregarlo
								const indexExistentePorId = datosCompletados.ventas.findIndex(r => r._id === registroLimpio._id);
								if (indexExistentePorId >= 0) {
									// Preservar _rowId del registro existente si es mejor
									const registroExistente = datosCompletados.ventas[indexExistentePorId];
									if (registroExistente._rowId) {
										registroLimpio._rowId = registroExistente._rowId;
									}
									datosCompletados.ventas[indexExistentePorId] = registroLimpio;
								}
							}
						}
						
						// Eliminar duplicados inmediatamente después de agregar/actualizar
						// IMPORTANTE: Usar _id como clave única, no generarClaveRegistro
						const registrosUnicos = new Map();
						let duplicadosEliminados = 0;
						
						datosCompletados.ventas.forEach(r => {
							// Asegurar que tenga _id
							if (!r._id) {
								r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
							}
							
							// Usar _id como clave única
							if (!registrosUnicos.has(r._id)) {
								registrosUnicos.set(r._id, r);
							} else {
								// Si hay duplicado por _id, mantener el más completo
								duplicadosEliminados++;
								const existente = registrosUnicos.get(r._id);
								const camposExistentes = Object.keys(existente).filter(k => !k.startsWith('_')).length;
								const camposNuevos = Object.keys(r).filter(k => !k.startsWith('_')).length;
								
								// Priorizar: auditado > más campos > mantener existente
								if (r.auditado === true && existente.auditado !== true) {
									registrosUnicos.set(r._id, r);
								} else if (camposNuevos > camposExistentes && existente.auditado !== true) {
									registrosUnicos.set(r._id, r);
								} else {
								}
							}
						});
						
						datosCompletados.ventas = Array.from(registrosUnicos.values());
						if (duplicadosEliminados > 0) {
						}
					}
					
					// Guardar el rowId del registro que se está editando
					setEditingRowIndex(rowId);
					
					// IMPORTANTE: Usar el _rowId del registro encontrado, no el parámetro rowId
					// Esto asegura que coincida con el _rowId generado en getDataRows
					const finalRowId = rowToEdit._rowId || rowId;
					
					setEditingData({
						datos_extraidos: datosExtraidos,
						datos_completados: datosCompletados,
						notas: selectedFormulario.notas || '',
						editingRowId: finalRowId // Guardar el _rowId del registro encontrado
					});
					
				} else {
					// Si no se encuentra el registro, no establecer editingData
					return;
				}
			} else {
				// Si no se especifica rowId, editar todos (comportamiento anterior)
			// NO inicializar forma_pago con metodo_pago del archivo
			// Los registros ya tienen forma_pago de n8n o '1' (Efectivo) por defecto
			
			setEditingData({
				datos_extraidos: datosExtraidos,
				datos_completados: datosCompletados,
				notas: selectedFormulario.notas || '',
				editingRowId: null // No hay registro específico en edición
			});
			}
			
			// Si se está editando un registro específico, mover a la primera página para mostrarlo
			if (rowId) {
				setCurrentPage(1);
			}
		}
	};

	// Función helper para limpiar campos internos de un registro antes de guardar
	const limpiarRegistroParaGuardar = (registro) => {
		// Lista completa de campos permitidos para Form 606
		// Cualquier otro campo será filtrado (incluyendo campos internos y campos adicionales de n8n)
		const camposPermitidos = [
			'ncf', 'rnc_emisor', 'rnc_cedula', 'rnc', 'nombre_emisor', 'razon_social',
			'fecha_comprobante', 'fecha_pago', 'monto_facturado', 'monto_total', 'total_factura',
			'monto_servicios', 'monto_bienes', 'monto_facturado_servicios', 'monto_facturado_bienes', 'subtotal',
			'descuento',
			'itbis_facturado', 'itbis_retenido', 'itbis_proporcionalidad', 'itbis_costo', 'itbis_llevado_costo',
			'itbis_adelantar', 'itbis_por_adelantar', 'itbis_percibido', 'tipo_retencion_isr', 'monto_retencion_renta',
			// Campos de ISR / impuestos adicionales (soportar nombres antiguos y nuevos)
			'isr_percibido', 'isr_percibido_compras',
			'impuesto_selectivo', 'impuesto_selectivo_consumo',
			'otros_impuestos', 'otros_impuestos_tasas',
			'monto_propina', 'monto_propina_legal',
			'forma_pago', 'moneda', 'tipo_bien_servicio', 'tipo_bienes_servicios', 'bien_servicio',
			'actividad_economica', 'monto_retencion',
			'ncf_modificado', 'tipo_id', 'estatus', 'auditado',
			'_imagen_url' // URL de imagen de la factura (preservar al mover entre formularios)
		];
		
		const registroLimpio = {};
		camposPermitidos.forEach(campo => {
			if (registro.hasOwnProperty(campo)) {
				let valor = registro[campo];
				
				// Normalizar fechas: asegurar que estén en formato YYYY-MM-DD sin timestamp
				if (campo === 'fecha_comprobante' || campo === 'fecha_pago') {
					if (valor) {
						// Si es string, extraer solo la parte de fecha (YYYY-MM-DD)
						if (typeof valor === 'string') {
							if (valor.includes('T')) {
								valor = valor.split('T')[0];
							} else if (valor.includes(' ')) {
								valor = valor.split(' ')[0];
							}
							// Asegurar que tenga el formato correcto (YYYY-MM-DD)
							if (valor.match(/^\d{4}-\d{2}-\d{2}$/)) {
								registroLimpio[campo] = valor;
							} else {
								// Intentar convertir otros formatos
								const fecha = new Date(valor);
								if (!isNaN(fecha.getTime())) {
									const año = fecha.getFullYear();
									const mes = String(fecha.getMonth() + 1).padStart(2, '0');
									const dia = String(fecha.getDate()).padStart(2, '0');
									registroLimpio[campo] = `${año}-${mes}-${dia}`;
								} else {
									registroLimpio[campo] = valor; // Mantener valor original si no se puede convertir
								}
							}
						} else {
							registroLimpio[campo] = valor;
						}
					} else {
						registroLimpio[campo] = valor;
					}
				} else if (campo === 'forma_pago') {
					// Normalizar forma_pago para asegurar que siempre sea un código (no un label)
					const codigoNormalizado = normalizarCodigoFormaPago(valor);
					registroLimpio[campo] = codigoNormalizado || valor; // Si no se puede normalizar, mantener el valor original
				} else if (campo === 'tipo_bien_servicio' || campo === 'tipo_bienes_servicios') {
					// Normalizar tipo_bien_servicio para asegurar que siempre sea un código (no texto)
					const codigoNormalizado = normalizarCodigoTipoBienServicio(valor);
					registroLimpio[campo] = codigoNormalizado || valor; // Si no se puede normalizar, mantener el valor original
				} else {
					// Guardar el NCF tal cual viene (sin normalizar) para preservar el formato original de n8n
					registroLimpio[campo] = valor;
				}
			}
		});
		
		// IMPORTANTE: Preservar el _id único del registro si existe
		if (registro._id) {
			registroLimpio._id = registro._id;
		}
		// Preservar _extraida_id para que el botón "Marcar como pendiente" siga disponible tras guardar
		if (registro._extraida_id != null && registro._extraida_id !== '') {
			registroLimpio._extraida_id = registro._extraida_id;
		}
		
	// Asegurar que no se guarden campos que empiecen con _ (excepto los explícitamente preservados)
	// y campos adicionales que n8n pueda enviar
	Object.keys(registro).forEach(key => {
		const preservar = key === '_id' || key === '_extraida_id' || key === 'auditado' || key === '_pendiente';
		if (preservar) return;
		if (key.startsWith('_') || !camposPermitidos.includes(key)) {
			delete registroLimpio[key];
		}
	});
	// Preservar explícitamente _pendiente (puede ser borrado por la regla de '_' anterior)
	if (registro._pendiente === true) {
		registroLimpio._pendiente = true;
	}
		
		return registroLimpio;
	};

	// Función helper para normalizar NCF (eliminar guiones, espacios, y normalizar formato)
	// Esta función asegura que NCFs con el mismo valor pero diferentes formatos generen la misma clave
	const normalizarNCF = (ncf) => {
		if (!ncf) return '';
		let ncfNormalizado = ncf.toString().trim().toUpperCase().replace(/[-\s]/g, '');
		
		// Si el NCF tiene una letra al inicio (formato estándar: A01-0000000001)
		if (/^[A-Z]/.test(ncfNormalizado)) {
			// Mantener la letra y los primeros 2 dígitos, luego normalizar el resto
			const match = ncfNormalizado.match(/^([A-Z])(\d{2})(\d*)$/);
			if (match) {
				const [, letra, primerosDos, resto] = match;
				// Usar los últimos 10 dígitos del resto (NCF estándar tiene 10 dígitos después de la letra + 2)
				const ultimosDiez = resto.length >= 10 ? resto.slice(-10) : resto.padStart(10, '0');
				ncfNormalizado = `${letra}${primerosDos}${ultimosDiez}`;
			}
		} else {
			// No tiene letra, puede ser solo dígitos (NCF mal formateado desde n8n o parser)
			// Normalizar eliminando ceros a la izquierda y usando los últimos 13 dígitos significativos
			// Esto asegura que '000000001010000000' y '00001010000000' generen la misma clave
			// si representan el mismo NCF (ambos tienen '101000000' en algún lugar)
			
			// Eliminar todos los ceros a la izquierda para encontrar los dígitos significativos
			const sinCerosIzquierda = ncfNormalizado.replace(/^0+/, '');
			
			if (sinCerosIzquierda.length > 0) {
				// Si hay dígitos significativos, usar los últimos 13 del original (o todos si es más corto)
				// Esto preserva la estructura del NCF mientras normaliza
				if (ncfNormalizado.length > 13) {
					ncfNormalizado = ncfNormalizado.slice(-13);
				} else if (ncfNormalizado.length < 13) {
					// Si es más corto, puede ser que falten ceros a la izquierda
					// Usar los dígitos significativos y rellenar con ceros a la izquierda hasta 13
					ncfNormalizado = sinCerosIzquierda.padStart(13, '0');
				}
			} else {
				// Si solo hay ceros, mantener el string original normalizado (últimos 13 caracteres)
				ncfNormalizado = ncfNormalizado.length > 13 ? ncfNormalizado.slice(-13) : ncfNormalizado.padStart(13, '0');
			}
		}
		
		return ncfNormalizado;
	};

	// Función helper para generar una clave única de un registro
	// PRIORIDAD: Usar _id si existe (viene de n8n/backend), si no, generar uno basado en contenido
	const generarClaveRegistro = (registro) => {
		// Si el registro tiene _id (viene de n8n/backend), usarlo como clave única
		// Este ID es único y persistente, incluso si se edita el NCF
		if (registro._id) {
			return registro._id;
		}
		
		// Si no tiene _id, generar uno basado en timestamp + datos del registro
		// Esto es para registros antiguos que no tienen _id
		// Normalizar NCF para evitar duplicados por formato diferente
		const ncfNormalizado = normalizarNCF(registro.ncf || '');
		const rnc = (registro.rnc_emisor || registro.rnc_cedula || registro.rnc || '').toString().replace(/[-\s]/g, '').trim();
		const total = (registro.total_factura || registro.monto_total || registro.monto_facturado || 0).toString();
		const fecha = (registro.fecha_comprobante || registro.fecha || '').toString();
		
		// Generar ID único basado en contenido para registros antiguos
		return `legacy_${ncfNormalizado}_${rnc}_${total}_${fecha}`;
	};

	// Función para marcar/desmarcar un registro como auditado
	const handleToggleAuditado = async (rowId) => {
		if (!selectedFormulario) return;
		
		// Obtener todos los registros (ya combinados de datos_extraidos y datos_completados)
		const allRows = dataRowsSelected;
		const rowToToggle = allRows.find(r => r._rowId === rowId);
		
		if (!rowToToggle) {
			return;
		}
		
		// Determinar si está auditado actualmente
		const estaAuditado = rowToToggle.auditado === true;
		const nuevoEstado = !estaAuditado;
		
		// Generar clave única del registro usando NCF + RNC + Total (más estable que _rowId)
		const claveRegistro = generarClaveRegistro(rowToToggle);
		
		// Crear una copia limpia de datos_completados
		const datosCompletados = JSON.parse(JSON.stringify(selectedFormulario.datos_completados || {}));
		const datosExtraidos = selectedFormulario.datos_extraidos || {};
		
		// Inicializar datos_completados si no existe
		if (selectedFormulario.tipo === '606') {
			if (!datosCompletados.compras) {
				datosCompletados.compras = [];
			}
			
			// IMPORTANTE: Buscar primero por _id (más confiable), luego por clave generada
			// Esto evita duplicados cuando el registro ya está en datos_completados después de editar
			let index = -1;
			
			// Si el registro tiene _id, buscar por _id primero (más confiable)
			if (rowToToggle._id) {
				index = datosCompletados.compras.findIndex(r => r._id === rowToToggle._id);
			}
			
			// Si no se encontró por _id, buscar por clave generada
			if (index === -1) {
				index = datosCompletados.compras.findIndex(r => {
					const clave = generarClaveRegistro(r);
					return clave === claveRegistro;
				});
			}
			
			if (index !== -1) {
				// Si existe, actualizar solo el campo auditado y limpiar campos internos
				const registroLimpio = limpiarRegistroParaGuardar(datosCompletados.compras[index]);
				registroLimpio.auditado = nuevoEstado;
				if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua: auditado activo → pendiente inactivo
				// Preservar el _id si existe
				if (datosCompletados.compras[index]._id) {
					registroLimpio._id = datosCompletados.compras[index]._id;
				}
				datosCompletados.compras[index] = registroLimpio;
			} else {
				// Si no existe en datos_completados, buscar en datos_extraidos
				let registroEnExtraidos = null;
				
				// Buscar primero por _id si está disponible
				if (rowToToggle._id) {
					registroEnExtraidos = (datosExtraidos.compras || []).find(r => r._id === rowToToggle._id);
				}
				
				// Si no se encontró por _id, buscar por clave generada
				if (!registroEnExtraidos) {
					registroEnExtraidos = (datosExtraidos.compras || []).find(r => {
						const clave = generarClaveRegistro(r);
						return clave === claveRegistro;
					});
				}
				
				if (registroEnExtraidos) {
					// IMPORTANTE: Verificar PRIMERO si ya existe en datos_completados antes de copiar
					// Hacer una búsqueda exhaustiva por múltiples métodos
					let registroExistenteEnCompletados = null;
					let indexExistente = -1;
					
					// Método 1: Buscar por _id del registro en extraídos
					if (registroEnExtraidos._id) {
						indexExistente = datosCompletados.compras.findIndex(r => r._id === registroEnExtraidos._id);
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.compras[indexExistente];
						}
					}
					
					// Método 2: Buscar por _id del rowToToggle
					if (!registroExistenteEnCompletados && rowToToggle._id) {
						indexExistente = datosCompletados.compras.findIndex(r => r._id === rowToToggle._id);
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.compras[indexExistente];
						}
					}
					
					// Método 3: Buscar por clave generada (comparar todas las claves)
					if (!registroExistenteEnCompletados) {
						indexExistente = datosCompletados.compras.findIndex(r => {
							const clave = generarClaveRegistro(r);
							return clave === claveRegistro;
						});
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.compras[indexExistente];
						}
					}
					
					// Método 4: Buscar por NCF + RNC + Total (más específico)
					if (!registroExistenteEnCompletados) {
						const ncfTarget = normalizarNCF(rowToToggle.ncf || '');
						const rncTarget = (rowToToggle.rnc_emisor || rowToToggle.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
						const totalTarget = (rowToToggle.total_factura || rowToToggle.monto_facturado || 0);
						
						indexExistente = datosCompletados.compras.findIndex(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const rnc = (r.rnc_emisor || r.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
							const total = (r.total_factura || r.monto_facturado || 0);
							return ncf === ncfTarget && rnc === rncTarget && Math.abs(total - totalTarget) < 0.01;
						});
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.compras[indexExistente];
						}
					}
					
					if (registroExistenteEnCompletados && indexExistente !== -1) {
						// Si ya existe, solo actualizar el campo auditado
						const registroLimpio = limpiarRegistroParaGuardar(registroExistenteEnCompletados);
						registroLimpio.auditado = nuevoEstado;
						if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua
						// Preservar el _id
						if (registroExistenteEnCompletados._id) {
							registroLimpio._id = registroExistenteEnCompletados._id;
						}
						datosCompletados.compras[indexExistente] = registroLimpio;
					} else {
						// Si realmente no existe, copiar el registro desde datos_extraidos
						const registroLimpio = limpiarRegistroParaGuardar(registroEnExtraidos);
						registroLimpio.auditado = nuevoEstado;
						if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua
						// Preservar el _id si existe
						if (registroEnExtraidos._id) {
							registroLimpio._id = registroEnExtraidos._id;
						} else if (rowToToggle._id) {
							// Usar el _id del rowToToggle si el registro en extraídos no lo tiene
							registroLimpio._id = rowToToggle._id;
						}
						datosCompletados.compras.push(registroLimpio);
					}
				} else {
					return;
				}
			}
			
			// Limpiar todos los registros de campos internos antes de guardar
			datosCompletados.compras = datosCompletados.compras.map(r => limpiarRegistroParaGuardar(r));
			
			// Eliminar duplicados usando _id como clave principal (más confiable)
			const registrosUnicos = new Map();
			datosCompletados.compras.forEach(r => {
				// Usar _id como clave si existe, sino usar la clave generada
				const clave = r._id || generarClaveRegistro(r);
				if (!registrosUnicos.has(clave)) {
					registrosUnicos.set(clave, r);
				} else {
					// Si hay duplicado, mantener el que tiene auditado = true si alguno lo tiene
					const existente = registrosUnicos.get(clave);
					if (r.auditado === true && existente.auditado !== true) {
						registrosUnicos.set(clave, r);
					}
				}
			});
			datosCompletados.compras = Array.from(registrosUnicos.values());
			
		} else if (selectedFormulario.tipo === '607') {
			if (!datosCompletados.ventas) {
				datosCompletados.ventas = [];
			}
			
			// IMPORTANTE: Buscar primero por _id (más confiable), luego por clave generada
			// Esto evita duplicados cuando el registro ya está en datos_completados después de editar
			let index = -1;
			
			// Si el registro tiene _id, buscar por _id primero (más confiable)
			if (rowToToggle._id) {
				index = datosCompletados.ventas.findIndex(r => r._id === rowToToggle._id);
			}
			
			// Si no se encontró por _id, buscar por clave generada
			if (index === -1) {
				index = datosCompletados.ventas.findIndex(r => {
					const clave = generarClaveRegistro(r);
					return clave === claveRegistro;
				});
			}
			
			if (index !== -1) {
				const registroLimpio = limpiarRegistroParaGuardar(datosCompletados.ventas[index]);
				registroLimpio.auditado = nuevoEstado;
				if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua: auditado activo → pendiente inactivo
				// Preservar el _id si existe
				if (datosCompletados.ventas[index]._id) {
					registroLimpio._id = datosCompletados.ventas[index]._id;
				}
				datosCompletados.ventas[index] = registroLimpio;
			} else {
				// Si no existe en datos_completados, buscar en datos_extraidos
				let registroEnExtraidos = null;
				
				// Buscar primero por _id si está disponible
				if (rowToToggle._id) {
					registroEnExtraidos = (datosExtraidos.ventas || []).find(r => r._id === rowToToggle._id);
				}
				
				// Si no se encontró por _id, buscar por clave generada
				if (!registroEnExtraidos) {
					registroEnExtraidos = (datosExtraidos.ventas || []).find(r => {
						const clave = generarClaveRegistro(r);
						return clave === claveRegistro;
					});
				}
				
				if (registroEnExtraidos) {
					// IMPORTANTE: Verificar PRIMERO si ya existe en datos_completados antes de copiar
					// Hacer una búsqueda exhaustiva por múltiples métodos
					let registroExistenteEnCompletados = null;
					let indexExistente = -1;
					
					// Método 1: Buscar por _id del registro en extraídos
					if (registroEnExtraidos._id) {
						indexExistente = datosCompletados.ventas.findIndex(r => r._id === registroEnExtraidos._id);
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.ventas[indexExistente];
						}
					}
					
					// Método 2: Buscar por _id del rowToToggle
					if (!registroExistenteEnCompletados && rowToToggle._id) {
						indexExistente = datosCompletados.ventas.findIndex(r => r._id === rowToToggle._id);
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.ventas[indexExistente];
						}
					}
					
					// Método 3: Buscar por clave generada (comparar todas las claves)
					if (!registroExistenteEnCompletados) {
						indexExistente = datosCompletados.ventas.findIndex(r => {
							const clave = generarClaveRegistro(r);
							return clave === claveRegistro;
						});
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.ventas[indexExistente];
						}
					}
					
					// Método 4: Buscar por NCF + RNC + Total (más específico)
					if (!registroExistenteEnCompletados) {
						const ncfTarget = normalizarNCF(rowToToggle.ncf || '');
						const rncTarget = (rowToToggle.rnc_emisor || rowToToggle.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
						const totalTarget = (rowToToggle.total_factura || rowToToggle.monto_facturado || 0);
						
						indexExistente = datosCompletados.ventas.findIndex(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const rnc = (r.rnc_emisor || r.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
							const total = (r.total_factura || r.monto_facturado || 0);
							return ncf === ncfTarget && rnc === rncTarget && Math.abs(total - totalTarget) < 0.01;
						});
						if (indexExistente !== -1) {
							registroExistenteEnCompletados = datosCompletados.ventas[indexExistente];
						}
					}
					
					if (registroExistenteEnCompletados && indexExistente !== -1) {
						// Si ya existe, solo actualizar el campo auditado
						const registroLimpio = limpiarRegistroParaGuardar(registroExistenteEnCompletados);
						registroLimpio.auditado = nuevoEstado;
						if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua
						// Preservar el _id
						if (registroExistenteEnCompletados._id) {
							registroLimpio._id = registroExistenteEnCompletados._id;
						}
						datosCompletados.ventas[indexExistente] = registroLimpio;
					} else {
						// Si realmente no existe, copiar el registro desde datos_extraidos
						const registroLimpio = limpiarRegistroParaGuardar(registroEnExtraidos);
						registroLimpio.auditado = nuevoEstado;
						if (nuevoEstado) registroLimpio._pendiente = false; // Exclusión mutua
						// Preservar el _id si existe
						if (registroEnExtraidos._id) {
							registroLimpio._id = registroEnExtraidos._id;
						} else if (rowToToggle._id) {
							// Usar el _id del rowToToggle si el registro en extraídos no lo tiene
							registroLimpio._id = rowToToggle._id;
						}
						datosCompletados.ventas.push(registroLimpio);
					}
				} else {
					return;
				}
			}
			
			// Limpiar todos los registros de campos internos antes de guardar
			datosCompletados.ventas = datosCompletados.ventas.map(r => limpiarRegistroParaGuardar(r));
			
			// Eliminar duplicados usando _id como clave principal (más confiable)
			const registrosUnicos = new Map();
			datosCompletados.ventas.forEach(r => {
				// Usar _id como clave si existe, sino usar la clave generada
				const clave = r._id || generarClaveRegistro(r);
				if (!registrosUnicos.has(clave)) {
					registrosUnicos.set(clave, r);
				} else {
					// Si hay duplicado, mantener el que tiene auditado = true si alguno lo tiene
					const existente = registrosUnicos.get(clave);
					if (r.auditado === true && existente.auditado !== true) {
						registrosUnicos.set(clave, r);
					}
				}
			});
			datosCompletados.ventas = Array.from(registrosUnicos.values());
		}
		
		// Actualizar el formulario en el backend
		try {
			// IMPORTANTE: Solo enviar datos_completados, no tipo ni periodo
			// El backend ya tiene estos valores y puede rechazar si no coinciden exactamente
			const payload = {
				datos_completados: datosCompletados
			};
			
			
			const updated = await dgiiApi.updateFormulario(selectedFormulario.id, payload);
			
			
			// Exclusión mutua: si se marcó como auditado, quitar estado pendiente de la factura física
			if (nuevoEstado === true) {
				const facturaId = getFacturaIdFromRow(rowToToggle, selectedFormulario, facturasFisicas);
				if (facturaId) {
					try {
						await dgiiApi.actualizarEstadoFacturaExtraida(facturaId, 'incluida', 'Marcado como auditado');
					} catch (e) {
					}
				}
			}
			
			// Recargar el formulario completo para asegurar que los datos estén sincronizados
			const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
			
			// También recargar facturas físicas para actualizar tabs
			try {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
			} catch (err) {
			}
			
			// Filtrar duplicados y crear copia profunda para forzar actualización de React
			const refreshedConFiltros = filtrarFormularioSinDuplicados({
				...refreshed,
				datos_completados: refreshed.datos_completados ? {
					...refreshed.datos_completados,
					compras: refreshed.datos_completados.compras ? [...refreshed.datos_completados.compras] : [],
					ventas: refreshed.datos_completados.ventas ? [...refreshed.datos_completados.ventas] : []
				} : refreshed.datos_completados,
				datos_extraidos: refreshed.datos_extraidos ? {
					...refreshed.datos_extraidos,
					compras: refreshed.datos_extraidos.compras ? [...refreshed.datos_extraidos.compras] : [],
					ventas: refreshed.datos_extraidos.ventas ? [...refreshed.datos_extraidos.ventas] : []
				} : refreshed.datos_extraidos
			});
			
			setSelectedFormulario(refreshedConFiltros);
		} catch (err) {
			
			// Construir mensaje de error más detallado
			let mensajeError = 'Error al actualizar el estado de auditoría: ';
			if (err.response?.data) {
				if (err.response.data.detail) {
					mensajeError += err.response.data.detail;
					if (err.response.data.errores && Array.isArray(err.response.data.errores)) {
						mensajeError += '\n\nErrores:\n' + err.response.data.errores.join('\n');
					}
				} else if (typeof err.response.data === 'string') {
					mensajeError += err.response.data;
				} else {
					mensajeError += JSON.stringify(err.response.data, null, 2);
				}
			} else {
				mensajeError += err.message;
			}
			
			window.alert(mensajeError);
		}
	};

	// Validar NCF
	// Función helper para limpiar RNC (quitar guiones y espacios)
	const limpiarRNC = (rnc) => {
		if (!rnc) return '';
		return rnc.toString().replace(/[-\s]/g, '');
	};
	
	// Función para validar duplicados por RNC + NCF
	const validarDuplicadoRNC_NCF = (registro, excluirId = null) => {
		if (!selectedFormulario) return { esValido: true, mensaje: '', registroDuplicado: null };
		
		const rnc = registro.rnc_emisor || registro.rnc_cedula || registro.rnc || '';
		const ncf = registro.ncf || '';
		
		if (!rnc || !ncf) {
			return { esValido: true, mensaje: '', registroDuplicado: null };
		}
		
		// Normalizar RNC y NCF para comparación
		const rncLimpio = limpiarRNC(rnc).toUpperCase();
		const ncfLimpio = normalizarNCF(ncf).toUpperCase();
		
		// Buscar en datos_completados
		const datosCompletados = editingData?.datos_completados || selectedFormulario.datos_completados || {};
		const datosExtraidos = editingData?.datos_extraidos || selectedFormulario.datos_extraidos || {};
		
		const listaClave = selectedFormulario.tipo === '606' ? 'compras' : 'ventas';
		const registrosCompletados = datosCompletados[listaClave] || [];
		const registrosExtraidos = datosExtraidos[listaClave] || [];
		
		// Buscar en registros completados
		for (const reg of registrosCompletados) {
			if (excluirId && reg._id === excluirId) continue;
			
			const rncReg = limpiarRNC(reg.rnc_emisor || reg.rnc_cedula || reg.rnc || '').toUpperCase();
			const ncfReg = normalizarNCF(reg.ncf || '').toUpperCase();
			
			if (rncLimpio && ncfLimpio && rncLimpio === rncReg && ncfLimpio === ncfReg) {
				return {
					esValido: false,
					mensaje: `Ya existe un registro con RNC ${rnc} y NCF ${ncf} en este formulario`,
					registroDuplicado: reg
				};
			}
		}
		
		// Buscar en registros extraídos
		for (const reg of registrosExtraidos) {
			if (excluirId && reg._id === excluirId) continue;
			
			const rncReg = limpiarRNC(reg.rnc_emisor || reg.rnc_cedula || reg.rnc || '').toUpperCase();
			const ncfReg = normalizarNCF(reg.ncf || '').toUpperCase();
			
			if (rncLimpio && ncfLimpio && rncLimpio === rncReg && ncfLimpio === ncfReg) {
				return {
					esValido: false,
					mensaje: `Ya existe un registro con RNC ${rnc} y NCF ${ncf} en este formulario`,
					registroDuplicado: reg
				};
			}
		}
		
		return { esValido: true, mensaje: '', registroDuplicado: null };
	};

	// Función helper para formatear fecha a YYYY-MM-DD para comparaciones
	const formatDateForComparison = (dateStr) => {
		if (!dateStr) return '';
		// Si ya está en formato YYYY-MM-DD, devolverlo
		if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
			return dateStr;
		}
		// Intentar parsear diferentes formatos de fecha
		try {
			let date;
			if (dateStr.includes('/')) {
				// Formato DD/MM/YYYY o DD-MM-YYYY
				const parts = dateStr.split(/[\/\-]/);
				if (parts.length === 3) {
					date = new Date(parts[2], parts[1] - 1, parts[0]);
				}
			} else if (dateStr.includes('-')) {
				// Formato YYYY-MM-DD
				date = new Date(dateStr);
			} else if (dateStr.includes('T')) {
				// Formato ISO con timestamp
				date = new Date(dateStr);
			} else {
				date = new Date(dateStr);
			}
			
			if (date && !isNaN(date.getTime())) {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			}
		} catch (e) {
		}
		return '';
	};

	// Función helper para calcular Tipo ID basado en longitud del RNC
	const calcularTipoID = (rnc) => {
		const rncLimpio = limpiarRNC(rnc).toUpperCase();
		
		// Casos especiales: RNC que inician con letra
		if (rncLimpio.startsWith('B')) {
			// RNC con B tiene 11 caracteres totales (B + 10 dígitos) = Tipo 2 (Cédula)
			if (rncLimpio.length === 11) {
				return '2';
			}
		} else if (rncLimpio.startsWith('E')) {
			// RNC con E tiene 13 caracteres totales (E + 12 dígitos) = Tipo 2 (Cédula)
			if (rncLimpio.length === 13) {
				return '2';
			}
		} else {
			// RNC numérico tradicional
			if (rncLimpio.length === 11) {
				return '2'; // Cédula (11 dígitos)
			} else if (rncLimpio.length === 9) {
				return '1'; // RNC (9 dígitos)
			}
		}
		return ''; // Si no coincide, dejar vacío
	};

	const validarNCF = (ncf) => {
		if (!ncf || ncf === 'N/A' || ncf === '') {
			return { valido: false, motivo: 'NCF vacío o N/A' };
		}
		
		const ncfLimpio = ncf.toString().trim().toUpperCase();
		
		// Validación especial para NCF que inician con B (11 caracteres: B + 10 dígitos)
		if (ncfLimpio.startsWith('B')) {
			const sinGuion = ncfLimpio.replace('-', '');
			if (sinGuion.length === 11 && /^B\d{10}$/.test(sinGuion)) {
				return { valido: true, motivo: '' };
			} else if (sinGuion.length !== 11) {
				return { valido: false, motivo: `NCF con B debe tener 11 caracteres (tiene ${sinGuion.length})` };
			} else {
				return { valido: false, motivo: 'NCF con B debe tener formato B seguido de 10 dígitos' };
			}
		}
		
		// Validación especial para NCF que inician con E (13 caracteres: E + 12 dígitos)
		if (ncfLimpio.startsWith('E')) {
			const sinGuion = ncfLimpio.replace('-', '');
			if (sinGuion.length === 13 && /^E\d{12}$/.test(sinGuion)) {
				return { valido: true, motivo: '' };
			} else if (sinGuion.length !== 13) {
				return { valido: false, motivo: `NCF con E debe tener 13 caracteres (tiene ${sinGuion.length})` };
			} else {
				return { valido: false, motivo: 'NCF con E debe tener formato E seguido de 12 dígitos' };
			}
		}
		
		// Formato válido estándar: A01-0000000001 (letra + 2 dígitos + guion + 10 dígitos)
		// O sin guión: A010000000001 (letra + 2 dígitos + 10 dígitos = 13 caracteres)
		const patronConGuion = /^[A-Z]\d{2}-\d{10}$/;
		const patronSinGuion = /^[A-Z]\d{12}$/;
		
		if (patronConGuion.test(ncfLimpio)) {
			return { valido: true, motivo: '' };
		}
		
		if (patronSinGuion.test(ncfLimpio)) {
			return { valido: true, motivo: '' };
		}
		
		// Detectar problemas específicos
		if (!/^[A-Z]/.test(ncfLimpio)) {
			return { valido: false, motivo: 'No inicia con letra' };
		}
		
		const sinGuion = ncfLimpio.replace('-', '');
		if (sinGuion.length < 13) {
			return { valido: false, motivo: `Longitud incorrecta (${sinGuion.length} caracteres, debe ser 13 o 11 si inicia con B)` };
		}
		if (sinGuion.length > 13) {
			return { valido: false, motivo: `Longitud incorrecta (${sinGuion.length} caracteres, debe ser 13 o 11 si inicia con B)` };
		}
		
		if (!/^[A-Z]\d{12}$/.test(sinGuion)) {
			return { valido: false, motivo: 'Formato incorrecto (debe ser letra + 12 dígitos, o B + 10 dígitos)' };
		}
		
		return { valido: false, motivo: 'Formato inválido' };
	};

	// Detectar problemas en un registro
	const detectarProblemasRegistro = (row) => {
		const problemas = [];
		
		// Validar NCF
		const ncf = row.ncf || '';
		if (ncf) {
			const validacionNCF = validarNCF(ncf);
			if (!validacionNCF.valido) {
				problemas.push({ campo: 'NCF', motivo: validacionNCF.motivo, severidad: 'alta' });
			}
		} else {
			problemas.push({ campo: 'NCF', motivo: 'NCF faltante', severidad: 'media' });
		}
		
		// Validar RNC
		const rnc = row.rnc_emisor || row.rnc_cedula || row.rnc || '';
		if (!rnc || rnc === 'N/A') {
			problemas.push({ campo: 'RNC', motivo: 'RNC faltante', severidad: 'media' });
		} else {
			// Usar la función limpiarRNC para consistencia (solo quita guiones y espacios, NO quita letras)
			const rncLimpio = limpiarRNC(rnc).toUpperCase();
			
			// Validación especial para RNC que inician con letra
			if (rncLimpio.startsWith('B')) {
				// RNC que inicia con B debe tener 11 caracteres en total (incluyendo la B)
				if (rncLimpio.length !== 11) {
					problemas.push({ campo: 'RNC', motivo: `RNC con B debe tener 11 caracteres (tiene ${rncLimpio.length})`, severidad: 'alta' });
				} else if (!/^B\d{10}$/.test(rncLimpio)) {
					problemas.push({ campo: 'RNC', motivo: 'RNC con B debe tener formato B seguido de 10 dígitos', severidad: 'alta' });
				}
				// Si pasa todas las validaciones, no agregar problema (RNC con B válido)
			} else if (rncLimpio.startsWith('E')) {
				// RNC que inicia con E debe tener 13 caracteres en total (incluyendo la E)
				if (rncLimpio.length !== 13) {
					problemas.push({ campo: 'RNC', motivo: `RNC con E debe tener 13 caracteres (tiene ${rncLimpio.length})`, severidad: 'alta' });
				} else if (!/^E\d{12}$/.test(rncLimpio)) {
					problemas.push({ campo: 'RNC', motivo: 'RNC con E debe tener formato E seguido de 12 dígitos', severidad: 'alta' });
				}
				// Si pasa todas las validaciones, no agregar problema (RNC con E válido)
			} else {
				// RNC numérico tradicional (9 o 11 dígitos)
				// IMPORTANTE: Solo validar como numérico si NO empieza con letra
				if (!/^\d+$/.test(rncLimpio)) {
					problemas.push({ campo: 'RNC', motivo: 'RNC contiene caracteres no numéricos (debe ser solo números o empezar con B/E)', severidad: 'alta' });
				} else {
					// Solo validar longitud si es numérico puro
					if (rncLimpio.length === 9 || rncLimpio.length === 11) {
						// RNC válido: 9 dígitos (RNC) o 11 dígitos (Cédula) - NO agregar problema
					} else {
						problemas.push({ campo: 'RNC', motivo: `Longitud incorrecta (${rncLimpio.length} dígitos). Debe ser 9 o 11 dígitos`, severidad: 'alta' });
					}
				}
			}
		}
		
	// Validar fecha
	const fecha = row.fecha_comprobante || row.fecha || '';
	if (!fecha || fecha === 'N/A') {
		problemas.push({ campo: 'Fecha', motivo: 'Fecha faltante', severidad: 'media' });
	} else {
		const fechaComprobante = new Date(fecha);
		if (!isNaN(fechaComprobante.getTime())) {
			const hoy = new Date();
			hoy.setHours(0, 0, 0, 0);
			const fechaNormalizada = new Date(fechaComprobante);
			fechaNormalizada.setHours(0, 0, 0, 0);
			
			// Excepción: si la fecha está dentro del período del formulario, no marcar error.
			// Las fechas del período son válidas aunque sean "futuras" respecto a hoy.
			const periodoForm = selectedFormulario?.periodo;
			let dentroDelPeriodo = false;
			if (periodoForm && periodoForm.match(/^\d{4}-\d{2}$/)) {
				const [añoP, mesP] = periodoForm.split('-').map(Number);
				const inicio = new Date(añoP, mesP - 1, 1);
				const fin = new Date(añoP, mesP, 0);
				inicio.setHours(0, 0, 0, 0);
				fin.setHours(23, 59, 59, 999);
				dentroDelPeriodo = fechaNormalizada >= inicio && fechaNormalizada <= fin;
			}
			
			if (!dentroDelPeriodo) {
				// Validar futuro solo si la fecha está fuera del período del formulario
				if (fechaNormalizada > hoy) {
					problemas.push({ 
						campo: 'Fecha', 
						motivo: `Fecha del futuro: ${fecha}`, 
						severidad: 'alta' 
					});
				}
				
				// Validar más de 2 años en el pasado
				const dosAnosAtras = new Date(hoy);
				dosAnosAtras.setFullYear(hoy.getFullYear() - 2);
				
				if (fechaNormalizada < dosAnosAtras) {
					problemas.push({ 
						campo: 'Fecha', 
						motivo: `Fecha de más de 2 años en el pasado: ${fecha}`, 
						severidad: 'alta' 
					});
				}
			}
		}
	}
		
		// Validar montos
		const montoFacturado = row.monto_facturado || row.total_factura || row.monto_total || 0;
		if (!montoFacturado || montoFacturado === 0) {
			problemas.push({ campo: 'Monto', motivo: 'Monto facturado es cero o faltante', severidad: 'baja' });
		}
		
		return problemas;
	};

	// Obtener filas de datos para la tabla
	// Normalizar _id a string para evitar duplicados al combinar datos_completados + datos_extraidos (int vs string)
	const toIdKey = (id) => (id != null && id !== '') ? String(id) : null;

	// Quitar de datos_extraidos los registros que están en datos_completados
	// Evita que el registro modificado reaparezca en su forma original tras cálculos/actualizaciones
	// Match por: 1) _id, 2) NCF+fecha (cuando el monto cambió por conversión de moneda)
	const filtrarFormularioSinDuplicados = (formulario) => {
		if (!formulario?.datos_completados || !formulario?.datos_extraidos) return formulario;
		const clave = formulario.tipo === '606' ? 'compras' : 'ventas';
		const completados = formulario.datos_completados[clave] || [];
		const extraidos = formulario.datos_extraidos[clave] || [];
		const idsCompletados = new Set(completados.filter(r => r._id).map(r => String(r._id)));
		const ncfFechaCompletados = new Set(
			completados
				.filter(r => (r.ncf || r.ncf_comprobante) && (r.fecha_comprobante || r.fecha))
				.map(r => `${normalizarNCF(r.ncf || r.ncf_comprobante || '')}|${formatDateForComparison(r.fecha_comprobante || r.fecha || '')}`)
		);
		return {
			...formulario,
			datos_extraidos: {
				...formulario.datos_extraidos,
				[clave]: extraidos.filter(r => {
					if (r._id && idsCompletados.has(String(r._id))) return false;
					const ncfFecha = `${normalizarNCF(r.ncf || r.ncf_comprobante || '')}|${formatDateForComparison(r.fecha_comprobante || r.fecha || '')}`;
					if (ncfFechaCompletados.has(ncfFecha)) return false;
					return true;
				})
			}
		};
	};

	const getDataRows = (formulario, useEditingData = false) => {
		if (!formulario) return [];
		
		let rows = [];
		
		// Si estamos en modo edición, usar editingData
		if (useEditingData && editingData) {
			const datosCompletados = editingData.datos_completados || {};
			const datosExtraidos = editingData.datos_extraidos || {};
			
			if (formulario.tipo === '606') {
				const comprasCompletadas = (datosCompletados.compras || []).filter(r => r != null && typeof r === 'object');
				const comprasExtraidas = (datosExtraidos.compras || []).filter(r => r != null && typeof r === 'object');
				
			// Crear mapas de registros completados por _id (clave única real) y por clave generada (fallback)
			const mapaCompletadosPorId = new Map();
			const mapaCompletadosPorClave = new Map();
			
			comprasCompletadas.forEach(r => {
				// Priorizar _id como clave única (normalizar a string para evitar duplicados)
				const key = toIdKey(r._id);
				if (key) {
					if (!mapaCompletadosPorId.has(key)) {
						mapaCompletadosPorId.set(key, r);
					} else {
						// Si hay duplicado por _id, mantener el más completo
						const existente = mapaCompletadosPorId.get(key);
						if (r.auditado === true && existente.auditado !== true) {
							mapaCompletadosPorId.set(key, r);
						}
					}
				} else {
					// Fallback: usar clave generada para registros sin _id
					const clave = generarClaveRegistro(r);
					if (!mapaCompletadosPorClave.has(clave)) {
						mapaCompletadosPorClave.set(clave, r);
					}
				}
			});
			// Índice por _extraida_id (editing 606)
			const extraidaIdsEdit606 = new Set(
				comprasCompletadas
					.map(r => r._extraida_id ?? r.extraida_id)
					.filter(id => id != null && id !== '')
					.map(id => String(id))
			);
			
			// Empezar SOLO con datos_completados (tienen prioridad absoluta)
			rows = Array.from(mapaCompletadosPorId.values()).concat(Array.from(mapaCompletadosPorClave.values()));
			
			// Agregar registros de datos_extraidos que NO estén en datos_completados
			// Verificar por _id primero, luego _extraida_id, luego clave, luego NCF+monto+fecha
			comprasExtraidas.forEach(registroExtraido => {
				let yaExiste = false;
				
				// Verificar por _id primero
				const idExtraido = toIdKey(registroExtraido._id);
				if (idExtraido) {
					yaExiste = mapaCompletadosPorId.has(idExtraido);
				}
				
				// Verificar por _extraida_id (clave estable que reconsolidar siempre asigna)
				if (!yaExiste) {
					const eid = registroExtraido._extraida_id ?? registroExtraido.extraida_id;
					if (eid != null && eid !== '') yaExiste = extraidaIdsEdit606.has(String(eid));
				}
				
				// Si no existe por _id, verificar por clave generada
				if (!yaExiste) {
					const claveExtraido = generarClaveRegistro(registroExtraido);
					yaExiste = mapaCompletadosPorClave.has(claveExtraido) || 
					           Array.from(mapaCompletadosPorId.values()).some(r => {
						           const clave = generarClaveRegistro(r);
						           return clave === claveExtraido;
					           });
				}
				
				// Si aún no existe: NCF + monto + fecha (ignorar RNC - evita duplicado cuando se editó RNC)
				if (!yaExiste) {
					const ncfExtraido = normalizarNCF(registroExtraido.ncf || '');
					const totalExtraido = registroExtraido.total_factura || registroExtraido.monto_facturado || 0;
					const fechaExtraido = formatDateForComparison(registroExtraido.fecha_comprobante || registroExtraido.fecha || '');
					yaExiste = (ncfExtraido || totalExtraido || fechaExtraido) && (
						Array.from(mapaCompletadosPorId.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						}) || Array.from(mapaCompletadosPorClave.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						})
					);
				}
				
				if (!yaExiste) {
					rows.push(registroExtraido);
				}
			});
		} else if (formulario.tipo === '607') {
			const ventasCompletadas = (datosCompletados.ventas || []).filter(r => r != null && typeof r === 'object');
			const ventasExtraidas = (datosExtraidos.ventas || []).filter(r => r != null && typeof r === 'object');
			
		// Crear mapas de registros completados por _id (clave única real) y por clave generada (fallback)
		const mapaCompletadosPorId = new Map();
			const mapaCompletadosPorClave = new Map();
			
			ventasCompletadas.forEach(r => {
				// Priorizar _id como clave única (normalizar a string para evitar duplicados)
				const key = toIdKey(r._id);
				if (key) {
					if (!mapaCompletadosPorId.has(key)) {
						mapaCompletadosPorId.set(key, r);
					} else {
						// Si hay duplicado por _id, mantener el más completo
						const existente = mapaCompletadosPorId.get(key);
						if (r.auditado === true && existente.auditado !== true) {
							mapaCompletadosPorId.set(key, r);
						}
					}
				} else {
					// Fallback: usar clave generada para registros sin _id
					const clave = generarClaveRegistro(r);
					if (!mapaCompletadosPorClave.has(clave)) {
						mapaCompletadosPorClave.set(clave, r);
					}
				}
			});
			// Índice por _extraida_id (editing 607)
			const extraidaIdsEdit607 = new Set(
				ventasCompletadas
					.map(r => r._extraida_id ?? r.extraida_id)
					.filter(id => id != null && id !== '')
					.map(id => String(id))
			);
			
			// Empezar SOLO con datos_completados (tienen prioridad absoluta)
			rows = Array.from(mapaCompletadosPorId.values()).concat(Array.from(mapaCompletadosPorClave.values()));
			
			// Agregar registros de datos_extraidos que NO estén en datos_completados
			// Verificar por _id primero, luego _extraida_id, luego clave generada, luego NCF+monto+fecha
			ventasExtraidas.forEach(registroExtraido => {
				let yaExiste = false;
				
				// Verificar por _id primero
				const idExtraido = toIdKey(registroExtraido._id);
				if (idExtraido) {
					yaExiste = mapaCompletadosPorId.has(idExtraido);
				}
				
				// Verificar por _extraida_id (clave estable que reconsolidar siempre asigna)
				if (!yaExiste) {
					const eid = registroExtraido._extraida_id ?? registroExtraido.extraida_id;
					if (eid != null && eid !== '') yaExiste = extraidaIdsEdit607.has(String(eid));
				}
				
				// Si no existe por _id, verificar por clave generada
				if (!yaExiste) {
					const claveExtraido = generarClaveRegistro(registroExtraido);
					yaExiste = mapaCompletadosPorClave.has(claveExtraido) || 
					           Array.from(mapaCompletadosPorId.values()).some(r => {
						           const clave = generarClaveRegistro(r);
						           return clave === claveExtraido;
					           });
				}
				
				// Si aún no existe: NCF + monto + fecha (SIN RNC - evita duplicado cuando se editó RNC)
				if (!yaExiste) {
					const ncfExtraido = normalizarNCF(registroExtraido.ncf || '');
					const totalExtraido = registroExtraido.total_factura || registroExtraido.monto_facturado || 0;
					const fechaExtraido = formatDateForComparison(registroExtraido.fecha_comprobante || registroExtraido.fecha || '');
					yaExiste = (ncfExtraido || totalExtraido || fechaExtraido) && (
						Array.from(mapaCompletadosPorId.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						}) || Array.from(mapaCompletadosPorClave.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						})
					);
				}
				
				if (!yaExiste) {
					rows.push(registroExtraido);
				}
			});
		}
	} else {
			// NUEVA ESTRUCTURA: Si hay FacturaEditada, usar get_datos_completos() para obtener datos combinados
			// Esto preserva el histórico íntegro de n8n y muestra las ediciones
			let datosCompletados = formulario.datos_completados || {};
			
			// Si hay facturas editadas, construir datos_completados desde ellas
			if (facturasEditadas && facturasEditadas.length > 0) {
				const datosCompletadosDesdeEditadas = {
					compras: [],
					ventas: []
				};
				
				// Agrupar por tipo de formulario
				facturasEditadas.forEach(fe => {
					if (fe.datos_completos) {
						// datos_completos ya viene combinado (original + ediciones)
						const registroCompleto = fe.datos_completos;
						
						// Determinar si es compra o venta basado en el formulario
						if (formulario.tipo === '606') {
							datosCompletadosDesdeEditadas.compras.push(registroCompleto);
						} else if (formulario.tipo === '607') {
							datosCompletadosDesdeEditadas.ventas.push(registroCompleto);
						}
					}
				});
				
				// Combinar con datos_completados existentes (compatibilidad)
				if (datosCompletadosDesdeEditadas.compras.length > 0 || datosCompletadosDesdeEditadas.ventas.length > 0) {
					datosCompletados = {
						compras: [...(datosCompletados.compras || []), ...datosCompletadosDesdeEditadas.compras],
						ventas: [...(datosCompletados.ventas || []), ...datosCompletadosDesdeEditadas.ventas]
					};
				}
			}
			
			// Combinar datos_extraidos y datos_completados
			// Priorizar datos_completados sobre datos_extraidos, pero incluir todos los registros
			const datosExtraidos = formulario.datos_extraidos || {};
			
			// Para Form 606, obtener el array de compras (filtrar null/undefined para evitar crash al renderizar)
			if (formulario.tipo === '606') {
				const comprasCompletadas = (datosCompletados.compras || []).filter(r => r != null && typeof r === 'object');
				const comprasExtraidas = (datosExtraidos.compras || []).filter(r => r != null && typeof r === 'object');
				
				// IMPORTANTE: Asegurar que TODOS los registros tengan _id único
				// Primero generar _id para registros completados
				comprasCompletadas.forEach(r => {
					if (!r._id) {
						r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
					}
				});
				
				// Para registros extraídos, primero verificar si existe un registro equivalente en completados
				// Si existe, usar el mismo _id para evitar duplicados
				// Usar búsqueda exhaustiva similar a handleToggleAuditado
				comprasExtraidas.forEach(r => {
					if (!r._id) {
						// Búsqueda exhaustiva por múltiples métodos
						let registroEquivalente = null;
						
						// Método 1: Buscar por clave generada
						const claveExtraido = generarClaveRegistro(r);
						registroEquivalente = comprasCompletadas.find(rc => {
							const claveCompletado = generarClaveRegistro(rc);
							return claveCompletado === claveExtraido;
						});
						
						// Método 2: Buscar por NCF + RNC + Total (más específico)
						if (!registroEquivalente) {
							const ncfTarget = normalizarNCF(r.ncf || '');
							const rncTarget = (r.rnc_emisor || r.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
							const totalTarget = (r.total_factura || r.monto_facturado || 0);
							
							registroEquivalente = comprasCompletadas.find(rc => {
								const ncf = normalizarNCF(rc.ncf || '');
								const rnc = (rc.rnc_emisor || rc.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
								const total = (rc.total_factura || rc.monto_facturado || 0);
								return ncf === ncfTarget && rnc === rncTarget && Math.abs(total - totalTarget) < 0.01;
							});
						}
						
						if (registroEquivalente && registroEquivalente._id) {
							// Usar el mismo _id del registro completado
							r._id = registroEquivalente._id;
						} else {
							// Generar nuevo _id solo si no hay equivalente
							r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
						}
					}
				});
				
			// Crear un mapa de registros completados por _id (clave única real)
			// IMPORTANTE: Normalizar _id a string para evitar duplicados (int vs string)
			const mapaCompletadosPorId = new Map();
			const mapaCompletadosPorClave = new Map(); // Fallback para registros sin _id
			
			comprasCompletadas.forEach(r => {
				// Priorizar _id como clave única
				const key = toIdKey(r._id);
				if (key) {
					if (!mapaCompletadosPorId.has(key)) {
						mapaCompletadosPorId.set(key, r);
					} else {
						// Si hay duplicado por _id, mantener el más completo
						const existente = mapaCompletadosPorId.get(key);
						if (r.auditado === true && existente.auditado !== true) {
							mapaCompletadosPorId.set(key, r);
						}
					}
				} else {
					// Fallback: usar clave generada para registros sin _id
					const clave = generarClaveRegistro(r);
					if (!mapaCompletadosPorClave.has(clave)) {
						mapaCompletadosPorClave.set(clave, r);
					}
				}
			});
			// Índice por _extraida_id: permite deduplicar cuando el NCF fue editado
			// (completados tiene el nuevo NCF, extraidos tiene el original → NCF+fecha no coincide)
			const extraidaIdsCompletados606 = new Set(
				comprasCompletadas
					.map(r => r._extraida_id ?? r.extraida_id)
					.filter(id => id != null && id !== '')
					.map(id => String(id))
			);
			
			// Empezar con datos_completados (tienen prioridad, incluyen campo auditado)
			rows = Array.from(mapaCompletadosPorId.values()).concat(Array.from(mapaCompletadosPorClave.values()));
			
			// Agregar registros de datos_extraidos que no estén en datos_completados
			// Verificar por _id primero (normalizar a string), luego por clave generada, luego por NCF+monto+fecha (sin RNC)
			// También por _extraida_id (para cuando el NCF fue editado y los valores ya no coinciden)
			comprasExtraidas.forEach(registroExtraido => {
				let yaExiste = false;
				
				// Verificar por _id primero
				const idExtraido = toIdKey(registroExtraido._id);
				if (idExtraido) {
					yaExiste = mapaCompletadosPorId.has(idExtraido);
				}
				
				// Verificar por _extraida_id (clave estable que reconsolidar siempre asigna)
				if (!yaExiste) {
					const eid = registroExtraido._extraida_id ?? registroExtraido.extraida_id;
					if (eid != null && eid !== '') yaExiste = extraidaIdsCompletados606.has(String(eid));
				}
				
				// Si no existe por _id, verificar por clave generada
				if (!yaExiste) {
					const claveExtraido = generarClaveRegistro(registroExtraido);
					yaExiste = mapaCompletadosPorClave.has(claveExtraido) || 
					           Array.from(mapaCompletadosPorId.values()).some(r => {
						           const clave = generarClaveRegistro(r);
						           return clave === claveExtraido;
					           });
				}
				
				// Si aún no existe: NCF + monto + fecha (SIN RNC - evita duplicado cuando se editó RNC)
				if (!yaExiste) {
					const ncfExtraido = normalizarNCF(registroExtraido.ncf || '');
					const totalExtraido = registroExtraido.total_factura || registroExtraido.monto_facturado || 0;
					const fechaExtraido = formatDateForComparison(registroExtraido.fecha_comprobante || registroExtraido.fecha || '');
					yaExiste = (ncfExtraido || totalExtraido || fechaExtraido) && (
						Array.from(mapaCompletadosPorId.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						}) || Array.from(mapaCompletadosPorClave.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						})
					);
				}
				
				if (!yaExiste) {
					rows.push(registroExtraido);
				}
			});
			
			// IMPORTANTE: Asegurar que TODOS los registros tengan _rowId para que la edición funcione
			rows = rows.map((r, idx) => {
				if (!r._rowId) {
					// Generar _rowId basado en _id si existe, o en clave generada
					if (r._id) {
						r._rowId = `row_${r._id}`;
						} else {
							const clave = generarClaveRegistro(r);
							r._rowId = `row_${clave}`;
						}
					}
					// Asegurar que _originalIndex esté definido
					if (r._originalIndex === undefined) {
						r._originalIndex = idx;
					}
					return r;
				});
			}
			
			// Para Form 607, obtener el array de ventas (filtrar null/undefined para evitar crash)
			if (formulario.tipo === '607') {
				const ventasCompletadas = (datosCompletados.ventas || []).filter(r => r != null && typeof r === 'object');
				const ventasExtraidas = (datosExtraidos.ventas || []).filter(r => r != null && typeof r === 'object');
				
				// IMPORTANTE: Asegurar que TODOS los registros tengan _id único
				// Primero generar _id para registros completados
				ventasCompletadas.forEach(r => {
					if (!r._id) {
						r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
					}
				});
				
				// Para registros extraídos, primero verificar si existe un registro equivalente en completados
				// Si existe, usar el mismo _id para evitar duplicados
				// Usar búsqueda exhaustiva similar a handleToggleAuditado
				ventasExtraidas.forEach(r => {
					if (!r._id) {
						// Búsqueda exhaustiva por múltiples métodos
						let registroEquivalente = null;
						
						// Método 1: Buscar por clave generada
						const claveExtraido = generarClaveRegistro(r);
						registroEquivalente = ventasCompletadas.find(rc => {
							const claveCompletado = generarClaveRegistro(rc);
							return claveCompletado === claveExtraido;
						});
						
						// Método 2: Buscar por NCF + RNC + Total (más específico)
						if (!registroEquivalente) {
							const ncfTarget = normalizarNCF(r.ncf || '');
							const rncTarget = (r.rnc_emisor || r.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
							const totalTarget = (r.total_factura || r.monto_facturado || 0);
							
							registroEquivalente = ventasCompletadas.find(rc => {
								const ncf = normalizarNCF(rc.ncf || '');
								const rnc = (rc.rnc_emisor || rc.rnc_cedula || '').toString().replace(/[-\s]/g, '').trim();
								const total = (rc.total_factura || rc.monto_facturado || 0);
								return ncf === ncfTarget && rnc === rncTarget && Math.abs(total - totalTarget) < 0.01;
							});
						}
						
						if (registroEquivalente && registroEquivalente._id) {
							// Usar el mismo _id del registro completado
							r._id = registroEquivalente._id;
						} else {
							// Generar nuevo _id solo si no hay equivalente
							r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
						}
					}
				});
				
			// Crear mapas de registros completados por _id (clave única real) y por clave generada (fallback)
			// IMPORTANTE: Normalizar _id a string para evitar duplicados (int vs string)
			const mapaCompletadosPorId = new Map();
			const mapaCompletadosPorClave = new Map();
			
			ventasCompletadas.forEach(r => {
				// Priorizar _id como clave única
				const key = toIdKey(r._id);
				if (key) {
					if (!mapaCompletadosPorId.has(key)) {
						mapaCompletadosPorId.set(key, r);
					} else {
						// Si hay duplicado por _id, mantener el más completo
						const existente = mapaCompletadosPorId.get(key);
						if (r.auditado === true && existente.auditado !== true) {
							mapaCompletadosPorId.set(key, r);
						}
					}
				} else {
					// Fallback: usar clave generada para registros sin _id
					const clave = generarClaveRegistro(r);
					if (!mapaCompletadosPorClave.has(clave)) {
						mapaCompletadosPorClave.set(clave, r);
					}
				}
			});
			// Índice por _extraida_id: permite deduplicar cuando el NCF fue editado
			const extraidaIdsCompletados607 = new Set(
				ventasCompletadas
					.map(r => r._extraida_id ?? r.extraida_id)
					.filter(id => id != null && id !== '')
					.map(id => String(id))
			);
			
			// Empezar con datos_completados (tienen prioridad, incluyen campo auditado)
			rows = Array.from(mapaCompletadosPorId.values()).concat(Array.from(mapaCompletadosPorClave.values()));
			
			// Agregar registros de datos_extraidos que no estén en datos_completados
			// Verificar por _id primero (normalizar a string), luego por clave generada, luego por NCF+monto+fecha (sin RNC)
			// También por _extraida_id (para cuando el NCF fue editado y los valores ya no coinciden)
			ventasExtraidas.forEach(registroExtraido => {
				let yaExiste = false;
				
				// Verificar por _id primero
				const idExtraido = toIdKey(registroExtraido._id);
				if (idExtraido) {
					yaExiste = mapaCompletadosPorId.has(idExtraido);
				}
				
				// Verificar por _extraida_id (clave estable que reconsolidar siempre asigna)
				if (!yaExiste) {
					const eid = registroExtraido._extraida_id ?? registroExtraido.extraida_id;
					if (eid != null && eid !== '') yaExiste = extraidaIdsCompletados607.has(String(eid));
				}
				
				// Si no existe por _id, verificar por clave generada
				if (!yaExiste) {
					const claveExtraido = generarClaveRegistro(registroExtraido);
					yaExiste = mapaCompletadosPorClave.has(claveExtraido) || 
					           Array.from(mapaCompletadosPorId.values()).some(r => {
						           const clave = generarClaveRegistro(r);
						           return clave === claveExtraido;
					           });
				}
				
				// Si aún no existe: NCF + monto + fecha (SIN RNC - evita duplicado cuando se editó RNC)
				if (!yaExiste) {
					const ncfExtraido = normalizarNCF(registroExtraido.ncf || '');
					const totalExtraido = registroExtraido.total_factura || registroExtraido.monto_facturado || 0;
					const fechaExtraido = formatDateForComparison(registroExtraido.fecha_comprobante || registroExtraido.fecha || '');
					yaExiste = (ncfExtraido || totalExtraido || fechaExtraido) && (
						Array.from(mapaCompletadosPorId.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						}) || Array.from(mapaCompletadosPorClave.values()).some(r => {
							const ncf = normalizarNCF(r.ncf || '');
							const total = r.total_factura || r.monto_facturado || 0;
							const fecha = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
							return ncf === ncfExtraido && Math.abs((total || 0) - (totalExtraido || 0)) < 0.02 && fecha === fechaExtraido;
						})
					);
				}
				
				if (!yaExiste) {
					rows.push(registroExtraido);
				}
			});
				
				// IMPORTANTE: Asegurar que TODOS los registros tengan _rowId para que la edición funcione
				rows = rows.map((r, idx) => {
					if (!r._rowId) {
						// Generar _rowId basado en _id si existe, o en clave generada
						if (r._id) {
							r._rowId = `row_${r._id}`;
						} else {
							const clave = generarClaveRegistro(r);
							r._rowId = `row_${clave}`;
						}
					}
					// Asegurar que _originalIndex esté definido
					if (r._originalIndex === undefined) {
						r._originalIndex = idx;
					}
					return r;
				});
			}
		}
		
		// Fallback defensivo: si el merge local quedó vacío pero el backend ya envía
		// datos_completos (merge oficial + filtros excluidas/duplicadas), úsalo para
		// evitar mostrar "0 registros" cuando sí existen facturas físicas incluidas.
		if (!useEditingData && rows.length === 0) {
			const dc = formulario.datos_completos || {};
			const key = formulario.tipo === '607' ? 'ventas' : 'compras';
			const dcRows = Array.isArray(dc[key])
				? dc[key].filter(r => r != null && typeof r === 'object')
				: [];
			if (dcRows.length > 0) {
				rows = dcRows;
			}
		}

		// Agregar información de problemas y un ID único a cada fila
		// El ID único se basa en el índice original antes de ordenar
		rows = rows.map((row, originalIndex) => {
			// Calcular tipo_id automáticamente si no existe
			const rnc = row.rnc_emisor || row.rnc_cedula || row.rnc || '';
			const tipoIdCalculado = row.tipo_id || calcularTipoID(rnc);
			
			// Limpiar RNC (quitar guiones) para mostrar
			const rncLimpio = limpiarRNC(rnc);
			
		// Obtener forma_pago: PRIORIZAR row.forma_pago si existe (fue editado o viene de datos_completados)
		// Si row.forma_pago no existe o es null, usar '4' (Compra a Crédito) como default
		const formaPagoRow = normalizarCodigoFormaPago(row.forma_pago);
		const formaPago = formaPagoRow || '4';
			
			// Normalizar tipo_bien_servicio: convertir texto a código si es necesario
			const tipoBienServicioRow = normalizarCodigoTipoBienServicio(row.tipo_bien_servicio || row.tipo_bienes_servicios);
			// Si no se puede normalizar, dejar el valor original o usar '9' por defecto (Compras y Gastos)
			const tipoBienServicio = tipoBienServicioRow || row.tipo_bien_servicio || row.tipo_bienes_servicios || '9';
			
			// Auto-completar fecha_pago si forma_pago es Efectivo (1) y hay fecha_comprobante
			let fechaPago = row.fecha_pago;
			if (formaPago === '1' && !fechaPago && row.fecha_comprobante) {
				fechaPago = row.fecha_comprobante.includes('T') 
					? row.fecha_comprobante.split('T')[0] 
					: row.fecha_comprobante;
			}
			
			// Crear un objeto row procesado con RNC limpio para la validación
			const rowProcesado = {
				...row,
				// Asegurar que RNC se muestre sin guiones para la validación
				rnc_emisor: rncLimpio || row.rnc_emisor,
				rnc_cedula: rncLimpio || row.rnc_cedula,
				rnc: rncLimpio || row.rnc,
			};
			
			// Detectar problemas usando el row procesado (con RNC limpio)
			const problemas = detectarProblemasRegistro(rowProcesado);
			
		// Generar _rowId: priorizar _id original (backend), luego _rowId, luego clave
		let rowIdFinal;
		if (row._rowId) {
			// PRIORIDAD 1: Si ya tiene _rowId (registros manuales en edición), preservarlo
			rowIdFinal = row._rowId;
		} else if (row._id) {
			// PRIORIDAD 2: Si tiene _id del backend, usarlo
			rowIdFinal = row._id;
		} else {
			// PRIORIDAD 3: Generar uno basado en clave única
			const claveUnica = generarClaveRegistro(row);
			rowIdFinal = `row_${claveUnica}`;
		}
		
		const rowFinal = {
			...rowProcesado,
			// Asegurar que el campo auditado se propague correctamente (usar === true para evitar falsy values)
			auditado: row.auditado === true,
			// Calcular tipo_id automáticamente
			tipo_id: tipoIdCalculado,
			// Normalizar tipo_bien_servicio (texto → código)
			tipo_bien_servicio: tipoBienServicio,
			// Obtener forma_pago normalizada
			forma_pago: formaPago,
			// Auto-completar fecha_pago si es Efectivo
			fecha_pago: fechaPago || row.fecha_pago,
			_id: row._id || rowIdFinal, // Preservar _id original del backend
			_rowId: rowIdFinal, // ID para el frontend
			_originalIndex: originalIndex, // Guardar índice original
			_problemas: problemas,
			_tieneProblemas: problemas.length > 0,
			_tieneNCFIrregular: problemas.some(p => p.campo === 'NCF' && p.severidad === 'alta')
		};
			
			return rowFinal;
		});
		
		// No reordenar el registro al editar: mantener siempre la posición actual en la lista
		
		// Solo ordenar si NO estamos en modo edición
		// Durante la edición, mantener el orden original para evitar confusión
		if (!useEditingData) {
			// Ordenar: primero los que tienen NCF irregular, luego los que tienen otros problemas, luego los normales
			rows.sort((a, b) => {
				// Prioridad 1: NCF irregular
				if (a._tieneNCFIrregular && !b._tieneNCFIrregular) return -1;
				if (!a._tieneNCFIrregular && b._tieneNCFIrregular) return 1;
				
				// Prioridad 2: Otros problemas de alta severidad
				const problemasAAlta = a._problemas.filter(p => p.severidad === 'alta' && p.campo !== 'NCF').length;
				const problemasBAlta = b._problemas.filter(p => p.severidad === 'alta' && p.campo !== 'NCF').length;
				if (problemasAAlta > problemasBAlta) return -1;
				if (problemasAAlta < problemasBAlta) return 1;
				
				// Prioridad 3: Cualquier problema
				if (a._tieneProblemas && !b._tieneProblemas) return -1;
				if (!a._tieneProblemas && b._tieneProblemas) return 1;
				
				// Si son iguales, mantener orden original
				return a._originalIndex - b._originalIndex;
			});
		}
		
		// Aplicar filtros de la tabla si existen
		if (tablaFiltros.busqueda) {
			const busqueda = tablaFiltros.busqueda.toLowerCase();
			rows = rows.filter(row => {
				const ncf = (row.ncf || '').toLowerCase();
				const rnc = (row.rnc_emisor || row.rnc_cedula || '').toLowerCase();
				const nombre = (row.nombre_emisor || row.razon_social || '').toLowerCase();
				return ncf.includes(busqueda) || rnc.includes(busqueda) || nombre.includes(busqueda);
			});
		}
		
		if (tablaFiltros.forma_pago) {
			rows = rows.filter(row => {
				// Priorizar row.forma_pago sobre metodo_pago del archivo
				const formaPago = row.forma_pago || '1';
				return formaPago === tablaFiltros.forma_pago;
			});
		}
		
		if (tablaFiltros.tipo_bien_servicio) {
			rows = rows.filter(row => {
				const tipoBienServicio = row.tipo_bien_servicio || row.tipo_bienes_servicios || '';
				return tipoBienServicio === tablaFiltros.tipo_bien_servicio;
			});
		}

		if (tablaFiltros.estado === 'auditado') {
			rows = rows.filter(row => row.auditado === true);
		} else if (tablaFiltros.estado === 'pendiente') {
			rows = rows.filter(row => {
				if (row._pendiente === true) return true;
				const estado = getEstadoAnalisisFromRow(row, formulario, facturasFisicas);
				return estado === 'pendiente';
			});
		} else if (tablaFiltros.estado === 'sin_revisar') {
			rows = rows.filter(row => {
				if (row.auditado === true) return false;
				if (row._pendiente === true) return false;
				const estado = getEstadoAnalisisFromRow(row, formulario, facturasFisicas);
				return estado !== 'pendiente';
			});
		}

		if (tablaFiltros.monto_min !== '') {
			const min = parseFloat(tablaFiltros.monto_min);
			if (!isNaN(min)) {
				rows = rows.filter(row => {
					const monto = parseFloat(row.total_factura || row.monto_facturado || 0) || 0;
					return monto >= min;
				});
			}
		}

		if (tablaFiltros.monto_max !== '') {
			const max = parseFloat(tablaFiltros.monto_max);
			if (!isNaN(max)) {
				rows = rows.filter(row => {
					const monto = parseFloat(row.total_factura || row.monto_facturado || 0) || 0;
					return monto <= max;
				});
			}
		}

		return rows;
	};

	// Verificar si todos los registros están auditados O pendientes (cada uno debe tener uno u otro)
	const todosLosRegistrosAuditados = (formulario) => {
		if (!formulario) return false;
		
		const rows = getDataRows(formulario);
		if (rows.length === 0) return true; // Si no hay registros, se considera listo para exportar
		
		const todosAuditadosOPendientes = rows.every(row => {
			if (row.auditado === true) return true;
			if (row._pendiente === true) return true;
			const estado = getEstadoAnalisisFromRow(row, formulario, facturasFisicas);
			return estado === 'pendiente';
		});
		return todosAuditadosOPendientes;
	};

	// Obtener clase CSS para resaltar fila según problemas
	const getRowHighlightClass = (row) => {
		if (row._tieneNCFIrregular) {
			return 'bg-red-50 border-l-4 border-l-red-500';
		}
		if (row._tieneProblemas) {
			const problemasAlta = row._problemas.filter(p => p.severidad === 'alta').length;
			if (problemasAlta > 0) {
				return 'bg-orange-50 border-l-4 border-l-orange-500';
			}
			return 'bg-yellow-50 border-l-4 border-l-yellow-500';
		}
		return '';
	};

	// Registro manual: prefijo legacy `manual_*` en _id o FacturaManual en BD (_manual_id numérico).
	const esRegistroManualRow = (row) => {
		if (!row) return false;
		if (row._manual_id != null && row._manual_id !== '') return true;
		return row._id != null && String(row._id).startsWith('manual_');
	};

	// Obtener URL de imagen para una fila específica.
	// IMPORTANTE: no usar solo RNC (muchos comprobantes del mismo emisor) ni el primer adjunto (imagen incorrecta).
	const getImageUrlForRow = (row, formulario) => {
		if (row._imagen_url) {
			return row._imagen_url;
		}
		// Facturas físicas del formulario (p. ej. registro movido y adjunto aún no aparece en archivos_adjuntos del GET)
		if (facturasFisicas && facturasFisicas.length > 0) {
			const eid = row._extraida_id ?? row.extraida_id;
			if (eid != null && eid !== '') {
				const f = facturasFisicas.find(x => String(x.id) === String(eid));
				if (f) {
					if (typeof f.archivo_url === 'string' && f.archivo_url) return f.archivo_url;
					const adj = f.archivo_adjunto;
					if (adj && typeof adj === 'object' && adj.archivo_url) return adj.archivo_url;
				}
			}
		}
		const adjuntos = formulario?.archivos_adjuntos;
		if (!adjuntos?.length) {
			return null;
		}
		const rowExtraidaId = row._extraida_id ?? row.extraida_id;
		const rowNcf = getRowNcf(row);
		const rowRnc = getRowRnc(row);
		const rowMonto = getRowMonto(row);
		const rowFecha = formatDateForComparison(row.fecha_comprobante || row.fecha || '');

		const normFactNcf = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			return normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? d.comprobante_fiscal ?? fact.ncf ?? '');
		};
		const normFactRnc = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			return normalizeRncForMatch(d.rnc_emisor ?? d.rnc_cedula ?? d.rnc ?? fact.rnc_emisor ?? fact.rnc_cedula ?? '');
		};
		const getFactMonto = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			const v = d.monto_facturado ?? d.total_factura ?? d.monto_total ?? fact.monto_facturado ?? fact.total_factura;
			if (v == null || v === '') return null;
			const n = parseFloat(v);
			return isNaN(n) ? null : n;
		};
		const getFactFecha = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			return formatDateForComparison(d.fecha_comprobante ?? d.fecha ?? fact.fecha_comprobante ?? '');
		};

		const pairs = [];
		for (const archivo of adjuntos) {
			for (const fact of archivo.facturas_extraidas || []) {
				pairs.push({ archivo, fact });
			}
		}
		if (pairs.length === 0) {
			return adjuntos.length === 1 ? adjuntos[0].archivo_url : null;
		}

		// 1) FacturaExtraida.id enlazada al registro (sigue siendo válida si el usuario corrigió el NCF en la tabla)
		if (rowExtraidaId != null && rowExtraidaId !== '') {
			const hit = pairs.find(p =>
				p.fact.id === rowExtraidaId ||
				p.fact.id === Number(rowExtraidaId) ||
				String(p.fact.id) === String(rowExtraidaId)
			);
			if (hit?.archivo?.archivo_url) return hit.archivo.archivo_url;
		}

		// 2) NCF normalizado único
		if (rowNcf) {
			const ncfMatches = pairs.filter(p => {
				const fn = normFactNcf(p.fact);
				return fn && fn === rowNcf;
			});
			if (ncfMatches.length === 1 && ncfMatches[0].archivo?.archivo_url) {
				return ncfMatches[0].archivo.archivo_url;
			}
			if (ncfMatches.length > 1 && rowExtraidaId != null && rowExtraidaId !== '') {
				const byId = ncfMatches.find(p => String(p.fact.id) === String(rowExtraidaId));
				if (byId?.archivo?.archivo_url) return byId.archivo.archivo_url;
			}
		}

		// 3) RNC + monto + fecha (sin NCF o NCF editado distinto al OCR)
		if (rowRnc && rowMonto != null && rowFecha) {
			const tight = pairs.filter(p => {
				const fr = normFactRnc(p.fact);
				if (!fr || fr !== rowRnc) return false;
				const fm = getFactMonto(p.fact);
				if (fm == null || Math.abs(fm - rowMonto) >= 0.02) return false;
				const ff = getFactFecha(p.fact);
				return ff && ff === rowFecha;
			});
			if (tight.length === 1 && tight[0].archivo?.archivo_url) {
				return tight[0].archivo.archivo_url;
			}
		}

		// 4) _id del registro vs datos_estructurados de la factura (reconsolidación / n8n)
		// No cruzar FacturaManual (PK numérica) con _id de OCR (colisiones falsas).
		const rowId = row._id != null ? String(row._id).trim() : '';
		if (!esRegistroManualRow(row) && rowId && !rowId.startsWith('manual_')) {
			const byStructId = pairs.find(p => {
				const d = p.fact.datos_estructurados || {};
				const fid = d._id != null ? String(d._id).trim() : '';
				return fid && (fid === rowId || rowId.includes(fid) || fid.includes(rowId));
			});
			if (byStructId?.archivo?.archivo_url) return byStructId.archivo.archivo_url;
		}

		return null;
	};

	// Normalizar NCF para comparación (quitar espacios/guiones, mayúsculas)
	const normalizeNcfForMatch = (val) => {
		if (val == null || val === '') return '';
		return String(val).replace(/[-\s]/g, '').trim().toUpperCase();
	};
	// Normalizar RNC: quitar espacios/guiones y dejar solo dígitos (sin ceros a la izquierda) para comparar
	const normalizeRncForMatch = (val) => {
		if (val == null || val === '') return '';
		const digits = String(val).replace(/\D/g, '');
		return digits.replace(/^0+/, '') || '0';
	};

	// Extraer NCF del row (varios posibles nombres; fallback: buscar cualquier clave que contenga "ncf")
	const getRowNcf = (row) => {
		let v = row.ncf ?? row.ncf_comprobante ?? row.numero_comprobante ?? row.numero_ncf
			?? row.numero_comprobante_fiscal ?? row.comprobante_fiscal ?? row.ncf_comprobante_fiscal
			?? (row.datos_estructurados && typeof row.datos_estructurados === 'object' ? (row.datos_estructurados.ncf ?? row.datos_estructurados.numero_comprobante_fiscal) : '')
			?? '';
		if (!v && row && typeof row === 'object') {
			for (const [key, val] of Object.entries(row)) {
				if (key.toLowerCase().includes('ncf') && val != null && String(val).trim() !== '') {
					v = val;
					break;
				}
			}
			if (!v && row.datos_estructurados && typeof row.datos_estructurados === 'object') {
				for (const [key, val] of Object.entries(row.datos_estructurados)) {
					if (key.toLowerCase().includes('ncf') && val != null && String(val).trim() !== '') {
						v = val;
						break;
					}
				}
			}
		}
		return normalizeNcfForMatch(typeof v === 'string' ? v : (v ? String(v) : ''));
	};
	// Extraer RNC del row (varios posibles nombres; fallback: buscar cualquier clave que contenga "rnc" o "cedula"/"identificacion")
	const getRowRnc = (row) => {
		let v = row.rnc_emisor ?? row.rnc_cedula ?? row.rnc ?? row.rnc_proveedor ?? row.identificacion_fiscal ?? row.rnc_comprador
			?? (row.datos_estructurados && typeof row.datos_estructurados === 'object' ? (row.datos_estructurados.rnc_emisor ?? row.datos_estructurados.rnc_cedula ?? row.datos_estructurados.rnc) : '')
			?? '';
		if (!v && row && typeof row === 'object') {
			for (const [key, val] of Object.entries(row)) {
				const k = key.toLowerCase();
				if ((k.includes('rnc') || k.includes('cedula') || k === 'identificacion_fiscal') && val != null && String(val).trim() !== '') {
					v = val;
					break;
				}
			}
			if (!v && row.datos_estructurados && typeof row.datos_estructurados === 'object') {
				for (const [key, val] of Object.entries(row.datos_estructurados)) {
					const k = key.toLowerCase();
					if ((k.includes('rnc') || k.includes('cedula')) && val != null && String(val).trim() !== '') {
						v = val;
						break;
					}
				}
			}
		}
		return normalizeRncForMatch(typeof v === 'string' ? v : (v ? String(v) : ''));
	};
	// Monto del row para matching robusto (registros movidos/manuales)
	const getRowMonto = (row) => {
		const v = row.monto_facturado ?? row.total_facturado ?? row.total_factura ?? row.monto_total
			?? (row.datos_estructurados && typeof row.datos_estructurados === 'object' ? (row.datos_estructurados.monto_facturado ?? row.datos_estructurados.total_factura) : null);
		if (v == null || v === '') return null;
		const n = parseFloat(v);
		return isNaN(n) ? null : n;
	};

	// Obtener facturaId (FacturaExtraida.id) desde un row para marcar como pendiente.
	// listFacturas (ej. facturasFisicas) tiene prioridad.
	// allRows: lista completa de filas de la tabla; si se pasa, se usa índice "entre filas no manuales" para asignar factura (N facturas → N filas no manuales).
	// Prioridad 1: _extraida_id. 1.5: _id. 2: NCF/RNC. 3: índice. 3.5: índice en tabla. 4: índice entre no-manuales (si allRows).
	const getFacturaIdFromRow = (row, formulario, listFacturas = null, rowIndex = null, allRows = null) => {
		const esRegistroManual = esRegistroManualRow(row);
		// Registros manuales: no usar índice (evita marcar la factura equivocada). Sí permitir match por NCF/RNC más abajo.
		const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
		const todasLasFacturas = [];
		if (listFacturas && Array.isArray(listFacturas) && listFacturas.length > 0) {
			todasLasFacturas.push(...listFacturas);
		}
		if (todasLasFacturas.length === 0 && formulario?.archivos_adjuntos) {
			formulario.archivos_adjuntos.forEach(arch => {
				(arch.facturas_extraidas || []).forEach(f => { todasLasFacturas.push(f); });
			});
		}
		if (todasLasFacturas.length === 0) return null;

		const rowNcf = getRowNcf(row);
		const rowRnc = getRowRnc(row);
		const validarNcf = (facturaId) => {
			if (!facturaId || !rowNcf) return facturaId;
			const f = todasLasFacturas.find(x => x.id === facturaId || String(x.id) === String(facturaId));
			if (!f) return facturaId;
			const d = f.datos_estructurados || f || {};
			const fn = normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? '');
			return (fn && rowNcf !== fn) ? null : facturaId;
		};

		// Prioridad 1: _extraida_id (solo si NCF coincide, evita _extraida_id obsoleto)
		const extraidaId = row._extraida_id != null ? row._extraida_id : row.extraida_id;
		if (extraidaId !== undefined && extraidaId !== null && extraidaId !== '') {
			const encontrada = todasLasFacturas.find(f =>
				f.id === extraidaId || f.id === Number(extraidaId) || String(f.id) === String(extraidaId)
			);
			if (encontrada) {
				const rowNcf1 = getRowNcf(row);
				const factNcf1 = (() => { const d = encontrada.datos_estructurados || encontrada || {}; return normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? ''); })();
				if (!rowNcf1 || !factNcf1 || rowNcf1 === factNcf1) return encontrada.id;
			}
		}

		// Prioridad 1.5: registro movido de otro formulario (puede no tener _extraida_id pero sí _id)
		// CRÍTICO: si el row tiene NCF, verificar que la factura coincida por NCF (evita _id obsoleto/mezclado)
		const rowNcfEarly = getRowNcf(row);
		const rowId = row._id != null && String(row._id).trim() !== '' ? String(row._id).trim() : null;
		if (!esRegistroManual && rowId && !rowId.startsWith('manual_')) {
			const porId = todasLasFacturas.find(f => {
				const datosEst = f.datos_estructurados || f;
				const factId = datosEst._id != null ? String(datosEst._id).trim() : (f._id != null ? String(f._id).trim() : '');
				if (!factId) return false;
				return factId === rowId || rowId.includes(factId) || factId.includes(rowId);
			});
			if (porId?.id) {
				if (rowNcfEarly) {
					const d = porId.datos_estructurados || porId || {};
					const factNcfEarly = normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? '');
					if (factNcfEarly && rowNcfEarly !== factNcfEarly) {
						// _id apunta a factura con NCF distinto: ignorar
					} else {
						return porId.id;
					}
				} else {
					const v = validarNcf(porId.id);
					if (v) return v;
				}
			}
		}

		const getFactNcf = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			let raw = d.ncf ?? d.numero_comprobante_fiscal ?? d.comprobante_fiscal ?? d.numero_ncf ?? fact.ncf ?? '';
			if (!raw && d && typeof d === 'object') {
				for (const [key, val] of Object.entries(d)) {
					if (key.toLowerCase().includes('ncf') && val != null && String(val).trim() !== '') {
						raw = val;
						break;
					}
				}
			}
			return normalizeNcfForMatch(typeof raw === 'string' ? raw : (raw ? String(raw) : ''));
		};
		const getFactRnc = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			let raw = d.rnc_emisor ?? d.rnc_cedula ?? d.rnc ?? fact.rnc_emisor ?? fact.rnc_cedula ?? '';
			if (!raw && d && typeof d === 'object') {
				for (const [key, val] of Object.entries(d)) {
					const k = key.toLowerCase();
					if ((k.includes('rnc') || k.includes('cedula')) && val != null && String(val).trim() !== '') {
						raw = val;
						break;
					}
				}
			}
			return normalizeRncForMatch(typeof raw === 'string' ? raw : (raw ? String(raw) : ''));
		};
		const getFactMonto = (fact) => {
			const d = fact.datos_estructurados || fact || {};
			const v = d.monto_facturado ?? d.total_factura ?? d.monto_total ?? fact.monto_facturado ?? fact.total_factura;
			if (v == null || v === '') return null;
			const n = parseFloat(v);
			return isNaN(n) ? null : n;
		};
		// Prioridad 2: matching por NCF o RNC normalizado
		// Si hay varias facturas con mismo NCF, usar _extraida_id para desambiguar
		if (rowNcf || rowRnc) {
			const candidatas = todasLasFacturas.filter(fact => {
				const factNcf = getFactNcf(fact);
				const factRnc = getFactRnc(fact);
				if (rowNcf) {
					if (!factNcf) return false;
					return rowNcf === factNcf;
				}
				return rowRnc && factRnc && rowRnc === factRnc;
			});
			if (candidatas.length === 1) { const v = validarNcf(candidatas[0].id); if (v) return v; }
			if (candidatas.length > 1 && extraidaId != null && extraidaId !== '') {
				const byExtraida = candidatas.find(f => f.id === Number(extraidaId) || String(f.id) === String(extraidaId));
				if (byExtraida) { const v = validarNcf(byExtraida.id); if (v) return v; }
			}
			if (candidatas.length > 0) { const v = validarNcf(candidatas[0].id); if (v) return v; }
		}
		// Prioridad 2.5: NCF + monto (robusto para movidos/manuales)
		const rowMonto = getRowMonto(row);
		if ((rowNcf || rowRnc) && rowMonto != null) {
			const candidatasMonto = todasLasFacturas.filter(fact => {
				const factNcf = getFactNcf(fact);
				const factRnc = getFactRnc(fact);
				const factMonto = getFactMonto(fact);
				if (factMonto == null) return false;
				if (Math.abs(factMonto - rowMonto) > 0.02) return false;
				if (rowNcf) {
					if (!factNcf) return false;
					return rowNcf === factNcf;
				}
				return rowRnc && factRnc && rowRnc === factRnc;
			});
			if (candidatasMonto.length === 1) return candidatasMonto[0].id;
			if (candidatasMonto.length > 1 && extraidaId != null && extraidaId !== '') {
				const byEx = candidatasMonto.find(f => f.id === Number(extraidaId) || String(f.id) === String(extraidaId));
				if (byEx) return byEx.id;
			}
			if (candidatasMonto.length > 0) return candidatasMonto[0].id;
		}

		// Prioridad 3 (fallback): por índice solo si la factura en esa posición coincide con el row (NCF/RNC).
		// Nunca usar índice para registros manuales (evita marcar la factura equivocada).
		if (!esRegistroManual) {
			// Buscar por NCF/RNC (si row tiene NCF, solo match por NCF)
			if (rowNcf || rowRnc) {
				const facturaPorContenido = todasLasFacturas.find(fact => {
					const factNcf = getFactNcf(fact);
					const factRnc = getFactRnc(fact);
					const rncMatch = rowRnc && factRnc && rowRnc === factRnc;
					if (rowNcf) {
						if (!factNcf) return false;
						return rowNcf === factNcf;
					}
					return rncMatch;
				});
				if (facturaPorContenido?.id) {
					const v = validarNcf(facturaPorContenido.id);
					if (v) return v;
				}
			}
			// Último recurso: por índice cuando el row no tiene NCF/RNC
			if ((!rowNcf && !rowRnc) && typeof rowIndex === 'number' && rowIndex >= 0 && rowIndex < todasLasFacturas.length) {
				const facturaPorIndice = todasLasFacturas[rowIndex];
				if (facturaPorIndice?.id) return facturaPorIndice.id;
			}
			// Prioridad 3.5: por índice - NUNCA usar si row tiene NCF (evita marcar factura equivocada)
			if (rowNcf) {
				// Row tiene NCF: no usar índice, ya fallamos en Prioridad 2 (no hay factura con ese NCF)
				// Retornar null para que use handleMarcarPendienteRegistro
			} else if (typeof rowIndex === 'number' && rowIndex >= 0 && rowIndex < todasLasFacturas.length) {
				const facturaPorIndice = todasLasFacturas[rowIndex];
				if (facturaPorIndice?.id) return facturaPorIndice.id;
			}
		}

		// Prioridad 4: índice entre filas NO manuales - NUNCA si row tiene NCF (evita factura equivocada)
		if (rowNcf) {
			// Row tiene NCF pero no encontramos factura con ese NCF: retornar null
		} else if (allRows && Array.isArray(allRows) && !esRegistroManual && todasLasFacturas.length > 0) {
			const filasNoManuales = allRows.filter(r => !esRegistroManualRow(r));
			const rowId4 = row._id ?? row._rowId;
			const idxEnNoManuales = filasNoManuales.findIndex(r => (r._id != null && r._id === rowId4) || (r._rowId != null && row._rowId != null && r._rowId === row._rowId));
			if (idxEnNoManuales >= 0 && idxEnNoManuales < todasLasFacturas.length) {
				const facturaAsignada = todasLasFacturas[idxEnNoManuales];
				if (facturaAsignada?.id) return facturaAsignada.id;
			}
		}

		// Diagnóstico cuando no hay match (registros manuales, movidos o datos faltantes)
		const diag = {
			tipo: esRegistroManual ? 'manual' : (row._id != null && String(row._id).startsWith('legacy_') ? 'legacy/movido' : 'otro'),
			row_id: row._id != null ? String(row._id).substring(0, 50) : null,
			_extraida_id: row._extraida_id ?? row.extraida_id ?? null,
			rowNcf: rowNcf || '(vacío)',
			rowRnc: rowRnc || '(vacío)',
			rowMonto: rowMonto ?? '(vacío)',
			numFacturas: todasLasFacturas.length,
			muestraFacturas: todasLasFacturas.slice(0, 2).map(f => {
				const d = f.datos_estructurados || f;
				return { id: f.id, ncf: d.ncf ?? '(vacío)', rnc: (d.rnc_emisor ?? d.rnc_cedula ?? d.rnc) ?? '(vacío)', monto: getFactMonto(f) };
			})
		};
		// Siempre loguear en consola (también en producción) para diagnosticar por qué no aparece el botón pendiente
		return null;
	};

	// Obtener estado_analisis de una factura desde un row (usa listFacturas si se pasa, sino archivos_adjuntos).
	// Para registros manuales sin FacturaExtraida: usa row._pendiente (true → 'pendiente', false/undefined → 'incluida').
	const getEstadoAnalisisFromRow = (row, formulario, listFacturas = null) => {
		// Registros con FacturaManual en DB: leer estado desde facturas_manuales del formulario
		const manualId = row._manual_id;
		if (manualId != null) {
			const manuales = formulario?.facturas_manuales;
			if (manuales && Array.isArray(manuales)) {
				const fm = manuales.find(m => String(m.id) === String(manualId));
				if (fm) return fm.estado_analisis || 'incluida';
			}
			// Fallback al flag JSON mientras los datos no estén cargados
			if (row._pendiente === true) return 'pendiente';
			return 'incluida';
		}

	const facturaId = getFacturaIdFromRow(row, formulario, listFacturas);
	if (facturaId) {
		if (listFacturas && Array.isArray(listFacturas)) {
			const fact = listFacturas.find(f => f.id === facturaId);
			if (fact && fact.estado_analisis) return fact.estado_analisis;
		}
		if (formulario?.archivos_adjuntos) {
			for (const archivo of formulario.archivos_adjuntos) {
				if (!archivo.facturas_extraidas) continue;
				const fact = archivo.facturas_extraidas.find(f => f.id === facturaId);
				if (fact && fact.estado_analisis) return fact.estado_analisis;
			}
		}
		// FacturaExtraida existe pero no está en las listas o no tiene estado:
		// usar el flag JSON como respaldo antes de asumir 'incluida'.
		if (row._pendiente === true) return 'pendiente';
		return 'incluida';
	}
	// Registros legacy sin FacturaExtraida ni FacturaManual: usar _pendiente flag en JSON
	if (row._pendiente === true) return 'pendiente';
	return 'incluida';
	};

	// Función para contar facturas pendientes por formulario
	const contarFacturasPendientes = (formulario) => {
		if (!formulario || !formulario.archivos_adjuntos) {
			return 0;
		}
		
		let pendientes = 0;
		const extraidaPendingIds = new Set();

		formulario.archivos_adjuntos.forEach(archivo => {
			if (archivo.facturas_extraidas && Array.isArray(archivo.facturas_extraidas)) {
				const facturasDelFormulario = archivo.facturas_extraidas.filter(factura => {
					const facturaFormId = typeof factura.formulario === 'object' 
						? factura.formulario?.id 
						: factura.formulario;
					return facturaFormId === formulario.id;
				});
				
				facturasDelFormulario.filter(f => f.estado_analisis === 'pendiente').forEach(f => {
					pendientes++;
					extraidaPendingIds.add(String(f.id));
				});
			}
		});

		// Contar FacturaManual pendientes (PK real en DB)
		const manualPendingIds = new Set();
		if (formulario.facturas_manuales && Array.isArray(formulario.facturas_manuales)) {
			formulario.facturas_manuales.forEach(fm => {
				if (fm.estado_analisis === 'pendiente') {
					pendientes++;
					manualPendingIds.add(String(fm.id));
				}
			});
		}

		// También contar registros legacy con _pendiente:true en datos_completados
		// que no tengan un FacturaExtraida ni FacturaManual ya contado arriba
		const tipo = formulario.tipo || '606';
		const claveLista = tipo === '607' ? 'ventas' : 'compras';
		const datosCompletados = formulario.datos_completados || {};
		const registros = datosCompletados[claveLista] || [];
		registros.forEach(r => {
			if (r && r._pendiente === true) {
				const extraidaId = r._extraida_id ?? r.extraida_id;
				const manualId = r._manual_id;
				const yaContado = (extraidaId && extraidaPendingIds.has(String(extraidaId)))
					|| (manualId && manualPendingIds.has(String(manualId)));
				if (!yaContado) {
					pendientes++;
				}
			}
		});
		
		return pendientes;
	};

	// Estadísticas del panel Diagnóstico: calculadas en useMemo (después de getDataRows/contarFacturasPendientes para evitar TDZ).
	// Soporta 606 (compras) y 607 (ventas).
	const statsDiagnostico = useMemo(() => {
		if (!selectedFormulario) return null;
		try {
			const tipo = selectedFormulario.tipo || '606';
			const claveLista = tipo === '607' ? 'ventas' : 'compras';
			const numArchivosFisicos = selectedFormulario.archivos_adjuntos?.length || 0;
			let numFacturasExtraidas = 0;
			const facturasPorTipo = { individual: 0, masiva: 0 };
			if (selectedFormulario.archivos_adjuntos) {
				selectedFormulario.archivos_adjuntos.forEach(archivo => {
					const facturasDelArchivo = archivo.facturas_extraidas?.filter(factura => {
						const facturaFormId = typeof factura.formulario === 'object' ? factura.formulario?.id : factura.formulario;
						return facturaFormId === selectedFormulario.id;
					}) || [];
					numFacturasExtraidas += facturasDelArchivo.length;
					const tipoCarga = archivo.tipo_carga || 'individual';
					facturasPorTipo[tipoCarga] = (facturasPorTipo[tipoCarga] || 0) + facturasDelArchivo.length;
				});
			}
		const allGridRows = getDataRows(selectedFormulario);
		const numRegistros = allGridRows.length;
		const datosExtraidos = selectedFormulario.datos_extraidos || {};
		const datosCompletados = selectedFormulario.datos_completados || {};
		const listaExtraidos = (datosExtraidos[claveLista] || []).filter(r => r != null && typeof r === 'object');
		const listaCompletados = (datosCompletados[claveLista] || []).filter(r => r != null && typeof r === 'object');
		const numRegistrosExtraidos = listaExtraidos.length;
		const numRegistrosCompletados = listaCompletados.length;
		const baseComparacion = numFacturasExtraidas || numRegistrosExtraidos || 0;
		const faltantesCalculados = Math.max(baseComparacion - numRegistros, 0);
		const registrosAuditados = listaCompletados.filter(r => r && r.auditado === true).length;
		// Contar pendientes desde ambas fuentes sin duplicar:
		// - Filas del grid con _pendiente:true (handleMarcarPendienteRegistro → JSON flag)
		// - FacturaExtraida con estado_analisis:'pendiente' (handleMarcarPendiente → DB field)
		// Un registro que aparece en ambas se cuenta una sola vez.
		const gridPendingExtraidaIds = new Set(
			allGridRows
				.filter(r => r && r._pendiente === true)
				.map(r => r._extraida_id ?? r.extraida_id)
				.filter(id => id != null && id !== '')
				.map(String)
		);
		const numPendientesGrid = allGridRows.filter(r => r && r._pendiente === true).length;
		const fisicasPendientesList = facturasFisicas && facturasFisicas.length > 0
			? facturasFisicas.filter(f => f && f.estado_analisis === 'pendiente')
			: [];
		const numPendientesFisicasExtra = fisicasPendientesList.filter(f => !gridPendingExtraidaIds.has(String(f.id))).length;
		const numPendientes = numPendientesGrid + numPendientesFisicasExtra;
			const hayDiscrepancia = baseComparacion > 0 ? baseComparacion !== numRegistros : false;
			const rows = getDataRows(selectedFormulario);
			let totalFacturado = 0, totalItbisFacturado = 0, totalItbisProporcionalidad = 0, totalItbisLlevadoCosto = 0, totalItbisRetenido = 0, totalIsrRetenido = 0;
			let totalItbisPorServicio = 0, totalItbisPorBienes = 0;
			rows.forEach(r => {
				const serv = parseFloat(r.monto_servicios ?? r.monto_facturado_servicios ?? 0) || 0;
				const bienes = parseFloat(r.monto_bienes ?? r.monto_facturado_bienes ?? 0) || 0;
				const total = serv + bienes || parseFloat(r.monto_facturado ?? r.total_factura ?? 0) || 0;
				totalFacturado += total;
				totalItbisFacturado += parseFloat(r.itbis_facturado ?? r.itbis ?? 0) || 0;
				totalItbisProporcionalidad += parseFloat(r.itbis_proporcionalidad ?? 0) || 0;
				totalItbisLlevadoCosto += parseFloat(r.itbis_llevado_costo ?? r.itbis_costo ?? 0) || 0;
				totalItbisRetenido += parseFloat(r.itbis_retenido ?? 0) || 0;
				totalIsrRetenido += parseFloat(r.monto_retencion_renta ?? r.isr_percibido_compras ?? 0) || 0;
				const itbis = parseFloat(r.itbis_facturado ?? r.itbis ?? 0) || 0;
				if (serv > 0 && bienes <= 0) totalItbisPorServicio += itbis;
				else if (bienes > 0 && serv <= 0) totalItbisPorBienes += itbis;
				else if (serv > 0 && bienes > 0 && total > 0) {
					totalItbisPorServicio += itbis * (serv / total);
					totalItbisPorBienes += itbis * (bienes / total);
				}
			});
			return {
				formularioId: selectedFormulario.id,
				tipo,
				claveLista,
				numArchivosFisicos,
				numFacturasExtraidas,
				numRegistros,
				registrosAuditados,
				numPendientes,
				numRegistrosExtraidos,
				numRegistrosCompletados,
				faltantesCalculados,
				hayDiscrepancia,
				facturasPorTipo: { ...facturasPorTipo },
				totalFacturado,
				totalItbisFacturado,
				totalItbisProporcionalidad,
				totalItbisLlevadoCosto,
				totalItbisRetenido,
				totalIsrRetenido,
				totalItbisPorServicio,
				totalItbisPorBienes
			};
		} catch (e) {
			return {
				formularioId: selectedFormulario.id,
				tipo: selectedFormulario.tipo || '606',
				claveLista: (selectedFormulario.tipo === '607' ? 'ventas' : 'compras'),
				numArchivosFisicos: selectedFormulario.archivos_adjuntos?.length || 0,
				numFacturasExtraidas: 0,
				numRegistros: 0,
				registrosAuditados: 0,
				numPendientes: 0,
				numRegistrosExtraidos: 0,
				numRegistrosCompletados: 0,
				faltantesCalculados: 0,
				hayDiscrepancia: false,
				facturasPorTipo: { individual: 0, masiva: 0 },
				totalFacturado: 0,
				totalItbisFacturado: 0,
				totalItbisProporcionalidad: 0,
				totalItbisLlevadoCosto: 0,
				totalItbisRetenido: 0,
				totalIsrRetenido: 0,
				totalItbisPorServicio: 0,
				totalItbisPorBienes: 0
			};
		}
	}, [selectedFormulario, facturasFisicas]);

	useEffect(() => {
		if (!statsDiagnostico) {
			setDgiiAdminState({ formularioId: null, stats: null, periodoLabel: null });
			return;
		}
		try {
			sessionStorage.setItem(STORAGE_KEY_LAST_STATS, JSON.stringify(statsDiagnostico));
		} catch (e) { /* ignore */ }
		setDgiiAdminState({
			formularioId: statsDiagnostico.formularioId,
			stats: statsDiagnostico,
			periodoLabel: selectedFormulario ? `Formulario ${selectedFormulario.tipo} - ${selectedFormulario.periodo}` : null
		});
	// setDgiiAdminState omitido de deps: estable del contexto; solo sincronizar cuando cambian stats o formulario
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [statsDiagnostico, selectedFormulario]);

	// normalizarCodigoFormaPago ahora se importa desde dgiiConstants

	// Obtener método de pago desde el archivo adjunto para una fila específica
	const getMetodoPagoForRow = (row, formulario) => {
		if (!formulario || !formulario.archivos_adjuntos || formulario.archivos_adjuntos.length === 0) {
			return null;
		}
		
		// Limpiar NCF y RNC para comparación (sin guiones, mayúsculas)
		const ncfLimpio = row.ncf ? row.ncf.toString().replace(/[-\s]/g, '').toUpperCase() : '';
		const rncLimpio = (row.rnc_emisor || row.rnc_cedula || row.rnc || '').toString().replace(/[-\s]/g, '').toUpperCase();
		
		// Intentar encontrar el archivo por NCF o RNC
		let archivoEncontrado = null;
		
		for (const adj of formulario.archivos_adjuntos) {
			// Si el archivo tiene facturas extraídas, buscar por NCF/RNC
			if (adj.facturas_extraidas && adj.facturas_extraidas.length > 0) {
				const encontrado = adj.facturas_extraidas.some(fact => {
					const factNCF = fact.datos_estructurados?.ncf ? fact.datos_estructurados.ncf.toString().replace(/[-\s]/g, '').toUpperCase() : '';
					const factRNC = fact.datos_estructurados?.rnc_emisor ? fact.datos_estructurados.rnc_emisor.toString().replace(/[-\s]/g, '').toUpperCase() : '';
					
					// Comparar NCF si ambos existen
					if (ncfLimpio && factNCF && ncfLimpio === factNCF) {
						return true;
					}
					// Comparar RNC si ambos existen y no hay NCF
					if (rncLimpio && factRNC && rncLimpio === factRNC && !ncfLimpio) {
						return true;
					}
					return false;
				});
				
				if (encontrado && adj.metodo_pago) {
					archivoEncontrado = adj;
					break;
				}
			}
		}
		
		// Si no se encontró por NCF/RNC, y hay un solo archivo con metodo_pago, usarlo
		if (!archivoEncontrado && formulario.archivos_adjuntos.length === 1) {
			const unicoArchivo = formulario.archivos_adjuntos[0];
			if (unicoArchivo.metodo_pago) {
				archivoEncontrado = unicoArchivo;
			}
		}
		
		// Retornar el metodo_pago del archivo adjunto encontrado (normalizado a solo código)
		const metodoPago = archivoEncontrado?.metodo_pago || null;
		return normalizarCodigoFormaPago(metodoPago);
	};

	// Las funciones getFormaPagoLabel, getTipoBienServicioLabel, getTipoRetencionISRLabel
	// ahora se importan desde dgiiConstants.js

	// Manejar edición de celda
	const handleCellEdit = (rowId, field, value) => {
		if (!editingData || !selectedFormulario) return;
		
		// Normalizar fechas: asegurar formato YYYY-MM-DD sin timestamp
		let valorNormalizado = value;
		if ((field === 'fecha_comprobante' || field === 'fecha_pago') && value) {
			if (typeof value === 'string') {
				// Si ya está en formato YYYY-MM-DD, mantenerlo
				if (value.match(/^\d{4}-\d{2}-\d{2}$/)) {
					valorNormalizado = value;
				} else if (value.includes('T')) {
					// Extraer solo la parte de fecha si tiene timestamp
					valorNormalizado = value.split('T')[0];
				} else if (value.includes(' ')) {
					// Extraer solo la parte de fecha si tiene espacio
					valorNormalizado = value.split(' ')[0];
				} else {
					// Intentar convertir otros formatos
					const fecha = new Date(value);
					if (!isNaN(fecha.getTime())) {
						const año = fecha.getFullYear();
						const mes = String(fecha.getMonth() + 1).padStart(2, '0');
						const dia = String(fecha.getDate()).padStart(2, '0');
						valorNormalizado = `${año}-${mes}-${dia}`;
					} else {
						valorNormalizado = value; // Mantener valor original si no se puede convertir
					}
				}
			}
		}
		
		// VALIDACIÓN 1: Verificar que fecha_comprobante esté en el período del formulario
		// VALIDACIÓN 2: Validar que la fecha no sea del futuro ni más de 2 años en el pasado
		if (field === 'fecha_comprobante' && valorNormalizado) {
			const fechaComprobante = new Date(valorNormalizado);
			
			if (!isNaN(fechaComprobante.getTime())) {
				const hoy = new Date();
				hoy.setHours(0, 0, 0, 0);
				const fechaComprobanteNormalizada = new Date(fechaComprobante);
				fechaComprobanteNormalizada.setHours(0, 0, 0, 0);
				
				// Determinar si la fecha está dentro del período del formulario actual.
				// Si está dentro del período, NO aplicar advertencias de "futuro" ni "muy antiguo":
				// el usuario tiene derecho a usar cualquier fecha dentro del período del formulario.
				const periodoActual = selectedFormulario?.periodo; // Formato "2026-01"
				let dentroDelPeriodoActual = false;
				if (periodoActual && periodoActual.match(/^\d{4}-\d{2}$/)) {
					const [añoPer, mesPer] = periodoActual.split('-').map(Number);
					const inicioPeriodo = new Date(añoPer, mesPer - 1, 1); // Primer día del mes
					const finPeriodo = new Date(añoPer, mesPer, 0);        // Último día del mes
					inicioPeriodo.setHours(0, 0, 0, 0);
					finPeriodo.setHours(23, 59, 59, 999);
					dentroDelPeriodoActual = fechaComprobanteNormalizada >= inicioPeriodo && fechaComprobanteNormalizada <= finPeriodo;
				}
				
				if (!dentroDelPeriodoActual) {
					// Solo validar futuro/antiguo si la fecha está FUERA del período del formulario
					
					// Validar que no sea del futuro
					if (fechaComprobanteNormalizada > hoy) {
						const confirmar = window.confirm(
							`⚠️ ADVERTENCIA: La fecha de comprobante (${valorNormalizado}) es del futuro.\n\n` +
							`¿Desea continuar de todos modos? Se resaltará el error para validación manual.`
						);
						if (!confirmar) {
							return; // No aplicar el cambio
						}
					}
					
					// Validar que no sea de más de 2 años en el pasado
					const dosAnosAtras = new Date(hoy);
					dosAnosAtras.setFullYear(hoy.getFullYear() - 2);
					
					if (fechaComprobanteNormalizada < dosAnosAtras) {
						const confirmar = window.confirm(
							`⚠️ ADVERTENCIA: La fecha de comprobante (${valorNormalizado}) es de más de 2 años en el pasado.\n\n` +
							`¿Desea continuar de todos modos? Se resaltará el error para validación manual.`
						);
						if (!confirmar) {
							return; // No aplicar el cambio
						}
					}
				}
				
				// Validar período del formulario y mover al formulario correcto si es necesario
				const periodo = selectedFormulario.periodo; // Formato: "2025-10"
				if (periodo && periodo.match(/^\d{4}-\d{2}$/)) {
					const [añoPeriodo, mesPeriodo] = periodo.split('-');
					const añoFecha = fechaComprobante.getFullYear();
					const mesFecha = fechaComprobante.getMonth() + 1; // getMonth() devuelve 0-11
					const periodoNuevo = `${añoFecha}-${String(mesFecha).padStart(2, '0')}`;
					
					if (añoFecha !== parseInt(añoPeriodo) || mesFecha !== parseInt(mesPeriodo)) {
						// La fecha corresponde a un período diferente - mover al formulario correcto
						const confirmar = window.confirm(
							`La fecha de comprobante (${valorNormalizado}) corresponde al período ${periodoNuevo}, ` +
							`pero el registro está en el formulario del período ${periodo}.\n\n` +
							`¿Desea mover este registro al formulario del período ${periodoNuevo}?`
						);
						
						if (confirmar) {
							// Mover el registro al formulario correcto
							handleMoverRegistroAFormulario(rowId, periodoNuevo, valorNormalizado, field, value);
							return; // No aplicar el cambio aquí, se hará en el endpoint
						} else {
							return; // No aplicar el cambio
						}
					}
				}
			}
		}
		
		// Obtener las filas actuales desde editingData
		const tipo = selectedFormulario.tipo;
		const datosCompletados = JSON.parse(JSON.stringify(editingData.datos_completados || {}));
		const datosExtraidos = editingData.datos_extraidos || {};
		
		// Obtener las filas combinadas para encontrar el registro a editar
		// pero solo actualizar en datos_completados
		const allRows = dataRowsSelectedEditing;
		const targetRow = allRows.find(r => r._rowId === rowId);
		
		if (!targetRow) {
			return;
		}
		
		// Generar clave única del registro objetivo
		const claveTarget = generarClaveRegistro(targetRow);
		
		// Obtener las filas de datos_completados (solo estas se actualizan)
		let currentRows = [];
		if (tipo === '606') {
			if (!datosCompletados.compras) {
				datosCompletados.compras = [];
			}
			currentRows = datosCompletados.compras;
		} else if (tipo === '607') {
			if (!datosCompletados.ventas) {
				datosCompletados.ventas = [];
			}
			currentRows = datosCompletados.ventas;
		}
		
		// Buscar el índice en currentRows usando _rowId primero (más confiable), luego _id normalizado
		let rowIndex = -1;
		
		// Primero intentar buscar por _rowId directamente en currentRows
		rowIndex = currentRows.findIndex(r => {
			const rRowId = r._rowId;
			return rRowId === rowId;
		});
		
		// Si no se encontró por _rowId, buscar por _id normalizado (evita duplicados al editar RNC/NCF)
		if (rowIndex < 0 && targetRow._id != null) {
			const targetIdKey = toIdKey(targetRow._id);
			if (targetIdKey) {
				rowIndex = currentRows.findIndex(r => toIdKey(r._id) === targetIdKey);
			}
		}
		
		// Si no se encontró por _id, buscar por clave única
		if (rowIndex < 0) {
			rowIndex = currentRows.findIndex(r => {
				const clave = generarClaveRegistro(r);
				return clave === claveTarget;
			});
		}
		
		// Si aún no se encontró, buscar por NCF
		if (rowIndex < 0) {
			const ncf = targetRow.ncf || '';
			if (ncf) {
				rowIndex = currentRows.findIndex(r => {
					const rNcf = r.ncf || '';
					return rNcf === ncf;
				});
			}
		}
		
		// Si aún no se encontró, buscar por RNC + monto + fecha
		if (rowIndex < 0) {
			const rnc = limpiarRNC(targetRow.rnc_emisor || targetRow.rnc_cedula || targetRow.rnc || '');
			const monto = targetRow.total_factura || targetRow.monto_facturado || 0;
			const fecha = targetRow.fecha_comprobante || '';
			
			if (rnc) {
				rowIndex = currentRows.findIndex(r => {
					const rRnc = limpiarRNC(r.rnc_emisor || r.rnc_cedula || r.rnc || '');
					const rMonto = r.total_factura || r.monto_facturado || 0;
					const rFecha = r.fecha_comprobante || '';
					return rRnc === rnc && Math.abs(rMonto - monto) < 0.01 && rFecha === fecha;
				});
			}
		}
		
		// Si aún no se encontró, usar _originalIndex como último recurso
		if (rowIndex < 0 && targetRow._originalIndex !== undefined) {
			rowIndex = targetRow._originalIndex;
		}
		
		// Si el registro no está en datos_completados, agregarlo primero
		if (rowIndex < 0) {
		// El registro viene de datos_extraidos, agregarlo a datos_completados
		const registroLimpio = limpiarRegistroParaGuardar(targetRow);
		registroLimpio[field] = valorNormalizado; // Aplicar el cambio con valor normalizado
			
			// IMPORTANTE: Preservar el _id del registro original
			if (targetRow._id) {
				registroLimpio._id = targetRow._id;
			} else {
				registroLimpio._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			}
			
			// CRÍTICO: limpiarRegistroParaGuardar() elimina todos los campos "_*" excepto _id,
			// así que hay que restaurar _rowId manualmente para que el render localice
			// este registro en datos_completados y muestre los valores actualizados.
			registroLimpio._rowId = targetRow._rowId || rowId;
			
			// Auto-fill fecha_pago = fecha_comprobante cuando forma_pago cambia a no-crédito
			if (field === 'forma_pago' && valorNormalizado !== '4') {
				const fc = registroLimpio.fecha_comprobante;
				if (fc) {
					registroLimpio.fecha_pago = fc.includes('T') ? fc.split('T')[0] : fc;
				}
			}
			
			if (tipo === '606') {
				// Buscar primero por _id normalizado (evita fallos por int vs string)
				let yaExistePorId = false;
				if (registroLimpio._id) {
					const idKey = toIdKey(registroLimpio._id);
					yaExistePorId = idKey && currentRows.some(r => toIdKey(r._id) === idKey);
				}
				
				// Si no existe por _id, buscar por clave generada
				let yaExistePorClave = false;
				if (!yaExistePorId) {
					yaExistePorClave = currentRows.some(r => {
						const clave = generarClaveRegistro(r);
						return clave === claveTarget;
					});
				}
				
				if (!yaExistePorId && !yaExistePorClave) {
					currentRows.push(registroLimpio);
				} else {
					// Buscar el índice del registro existente
					if (yaExistePorId && registroLimpio._id) {
						const idKey = toIdKey(registroLimpio._id);
						rowIndex = currentRows.findIndex(r => toIdKey(r._id) === idKey);
					} else {
						rowIndex = currentRows.findIndex(r => {
							const clave = generarClaveRegistro(r);
							return clave === claveTarget;
						});
					}
					
					if (rowIndex >= 0) {
						const registroExistente = currentRows[rowIndex];
						// Preservar el _id del registro existente si es mejor
						if (registroExistente._id && (!registroLimpio._id || String(registroLimpio._id).startsWith('legacy_'))) {
							registroLimpio._id = registroExistente._id;
						}
						// Preservar _rowId si existe
						if (registroExistente._rowId) {
							registroLimpio._rowId = registroExistente._rowId;
						}
					}
					currentRows[rowIndex] = registroLimpio;
				}
			} else if (tipo === '607') {
				// Buscar primero por _id normalizado (evita fallos por int vs string)
				let yaExistePorId = false;
				if (registroLimpio._id) {
					const idKey = toIdKey(registroLimpio._id);
					yaExistePorId = idKey && currentRows.some(r => toIdKey(r._id) === idKey);
				}
				
				// Si no existe por _id, buscar por clave generada
				let yaExistePorClave = false;
				if (!yaExistePorId) {
					yaExistePorClave = currentRows.some(r => {
						const clave = generarClaveRegistro(r);
						return clave === claveTarget;
					});
				}
				
				if (!yaExistePorId && !yaExistePorClave) {
					currentRows.push(registroLimpio);
				} else {
					// Buscar el índice del registro existente
					if (yaExistePorId && registroLimpio._id) {
						const idKey = toIdKey(registroLimpio._id);
						rowIndex = currentRows.findIndex(r => toIdKey(r._id) === idKey);
					} else {
						rowIndex = currentRows.findIndex(r => {
							const clave = generarClaveRegistro(r);
							return clave === claveTarget;
						});
					}
					
					if (rowIndex >= 0) {
						const registroExistente = currentRows[rowIndex];
						// Preservar el _id del registro existente si es mejor
						if (registroExistente._id && (!registroLimpio._id || String(registroLimpio._id).startsWith('legacy_'))) {
							registroLimpio._id = registroExistente._id;
						}
						// Preservar _rowId si existe
						if (registroExistente._rowId) {
							registroLimpio._rowId = registroExistente._rowId;
						}
					}
					currentRows[rowIndex] = registroLimpio;
				}
			}
			
			// Actualizar datos_completados y quitar de extraidos para evitar duplicado
			const updatedDatos = { ...datosCompletados };
			if (tipo === '606') {
				updatedDatos.compras = currentRows;
			} else if (tipo === '607') {
				updatedDatos.ventas = currentRows;
			}
			const idAgregado = toIdKey(registroLimpio._id);
			const extraidosKey = tipo === '606' ? 'compras' : 'ventas';
			// Quitar de extraidos el registro que movimos a completados (evita duplicado)
			const extraidosList = (datosExtraidos[extraidosKey] || []).filter(r => {
				if (idAgregado) return toIdKey(r._id) !== idAgregado;
				// Sin _id: quitar por referencia o por NCF+monto+fecha (mismo registro lógico)
				if (r === targetRow) return false;
				const ncfR = normalizarNCF(r.ncf || '');
				const ncfT = normalizarNCF(targetRow.ncf || '');
				const montoR = r.total_factura || r.monto_facturado || 0;
				const montoT = targetRow.total_factura || targetRow.monto_facturado || 0;
				const fechaR = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
				const fechaT = formatDateForComparison(targetRow.fecha_comprobante || targetRow.fecha || '');
				const mismoRegistro = ncfR && ncfR === ncfT && Math.abs(montoR - montoT) < 0.02 && fechaR === fechaT;
				return !mismoRegistro; // mantener si NO es el mismo registro
			});
			const updatedExtraidos = { ...datosExtraidos, [extraidosKey]: extraidosList };

			setEditingData(prev => ({
				...prev,
				datos_completados: updatedDatos,
				datos_extraidos: updatedExtraidos
			}));
			return;
		}
		
		// Validar que el índice sea válido
		if (rowIndex < 0 || rowIndex >= currentRows.length) {
			return;
		}
		
		// Crear una copia profunda del array y actualizar el campo específico
		// Asegurar que _rowId se mantenga para que la búsqueda futura funcione
		const updatedRows = currentRows.map((row, idx) => {
			if (idx === rowIndex) {
			const updatedRow = {
				...row,
				[field]: valorNormalizado
			};
			// IMPORTANTE: Preservar el _id único del registro (viene de n8n/backend)
			if (row._id) {
				updatedRow._id = row._id;
			}
			// Asegurar que _rowId se mantenga si existe
			if (row._rowId) {
				updatedRow._rowId = row._rowId;
			} else {
				// Si no existe, generarlo basado en rowId
				updatedRow._rowId = rowId;
			}
			// Auto-fill fecha_pago = fecha_comprobante cuando forma_pago cambia a no-crédito
			if (field === 'forma_pago' && valorNormalizado !== '4') {
				const fc = updatedRow.fecha_comprobante;
				if (fc) {
					updatedRow.fecha_pago = fc.includes('T') ? fc.split('T')[0] : fc;
				}
			}
				
				// VALIDACIÓN: Si se está editando RNC o NCF, verificar duplicados
				if ((field === 'rnc_emisor' || field === 'rnc_cedula' || field === 'rnc' || field === 'ncf') && valorNormalizado) {
					const validacion = validarDuplicadoRNC_NCF(updatedRow, updatedRow._id);
					if (!validacion.esValido) {
						// Revertir el cambio y mostrar error
						window.alert(`No se puede modificar ${field}:\n\n${validacion.mensaje}\n\nEl cambio ha sido revertido.`);
						return row; // Devolver el registro original sin cambios
					}
				}
				
				// LÓGICA AUTOMÁTICA: Calcular monto_facturado_servicios y monto_facturado_bienes según bien_servicio
				// Si se edita bien_servicio o monto_facturado, recalcular los montos
				if (field === 'bien_servicio' || field === 'monto_facturado' || field === 'total_factura') {
					const bienServicio = field === 'bien_servicio' 
						? (valorNormalizado || '').toLowerCase().trim()
						: (updatedRow.bien_servicio || '').toLowerCase().trim();
					
					const montoTotal = field === 'monto_facturado' || field === 'total_factura'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.monto_facturado || updatedRow.total_factura || 0) || 0;
					
					if (bienServicio === 'servicio') {
						// Si es servicio, el total va en monto_facturado_servicios
						updatedRow.monto_facturado_servicios = montoTotal;
						updatedRow.monto_facturado_bienes = 0;
						// Actualizar monto_facturado como suma
						updatedRow.monto_facturado = montoTotal;
					} else if (bienServicio === 'bien') {
						// Si es bien, el total va en monto_facturado_bienes
						updatedRow.monto_facturado_servicios = 0;
						updatedRow.monto_facturado_bienes = montoTotal;
						// Actualizar monto_facturado como suma
						updatedRow.monto_facturado = montoTotal;
					} else {
						// Si no hay bien_servicio definido, mantener los valores existentes
						// pero asegurar que monto_facturado sea la suma
						const montoServicios = parseFloat(updatedRow.monto_facturado_servicios || 0) || 0;
						const montoBienes = parseFloat(updatedRow.monto_facturado_bienes || 0) || 0;
						updatedRow.monto_facturado = montoServicios + montoBienes || montoTotal;
					}
					
				}
				
				// MEJORA 3: Calcular automáticamente itbis_por_adelantar = itbis_facturado - itbis_llevado_costo
				if (field === 'itbis_facturado' || field === 'itbis_llevado_costo') {
					const itbisFacturado = field === 'itbis_facturado'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.itbis_facturado || 0) || 0;
					
					const itbisCosto = field === 'itbis_llevado_costo'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.itbis_llevado_costo || 0) || 0;
					
					// Calcular itbis_por_adelantar automáticamente
					updatedRow.itbis_por_adelantar = itbisFacturado - itbisCosto;
					
				}
				
				// Si se edita monto_facturado_servicios o monto_facturado_bienes, actualizar monto_facturado como suma
				if (field === 'monto_facturado_servicios' || field === 'monto_facturado_bienes') {
					const montoServicios = field === 'monto_facturado_servicios'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.monto_facturado_servicios || 0) || 0;
					
					const montoBienes = field === 'monto_facturado_bienes'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.monto_facturado_bienes || 0) || 0;
					
					// Actualizar monto_facturado como suma y sincronizar monto_servicios/monto_bienes
					updatedRow.monto_facturado = montoServicios + montoBienes;
					updatedRow.monto_servicios = montoServicios;
					updatedRow.monto_bienes = montoBienes;
					
				}
				
				// Si se edita monto_servicios o monto_bienes (vista tabla), actualizar monto_facturado y _servicios/_bienes
				if (field === 'monto_servicios' || field === 'monto_bienes') {
					const montoServicios = field === 'monto_servicios'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.monto_servicios ?? (updatedRow.monto_facturado_servicios || 0)) || 0;
					const montoBienes = field === 'monto_bienes'
						? parseFloat(valorNormalizado) || 0
						: parseFloat(updatedRow.monto_bienes ?? (updatedRow.monto_facturado_bienes || 0)) || 0;
					updatedRow.monto_servicios = montoServicios;
					updatedRow.monto_bienes = montoBienes;
					updatedRow.monto_facturado_servicios = montoServicios;
					updatedRow.monto_facturado_bienes = montoBienes;
					updatedRow.monto_facturado = montoServicios + montoBienes;
				}
				
				// t5: Al editar descuento, recalcular monto_facturado y total_factura (bruto - descuento)
				if (field === 'descuento') {
					const oldDescuento = parseFloat(row.descuento) || 0;
					const newDescuento = parseFloat(valorNormalizado) || 0;
					const montoNetoActual = parseFloat(row.monto_facturado || row.total_factura || row.monto_total || 0) || 0;
					const totalNetoActual = parseFloat(row.total_factura || row.monto_total || row.monto_facturado || 0) || 0;
					const montoBruto = montoNetoActual + oldDescuento;
					const totalBruto = totalNetoActual + oldDescuento;
					updatedRow.monto_facturado = Math.max(0, montoBruto - newDescuento);
					updatedRow.total_factura = Math.max(0, totalBruto - newDescuento);
					updatedRow.monto_total = updatedRow.total_factura;
					const bienServicio = (updatedRow.bien_servicio || '').toLowerCase().trim();
					if (bienServicio === 'servicio') {
						updatedRow.monto_servicios = updatedRow.monto_facturado;
						updatedRow.monto_bienes = 0;
						updatedRow.monto_facturado_servicios = updatedRow.monto_facturado;
						updatedRow.monto_facturado_bienes = 0;
					} else if (bienServicio === 'bien') {
						updatedRow.monto_servicios = 0;
						updatedRow.monto_bienes = updatedRow.monto_facturado;
						updatedRow.monto_facturado_servicios = 0;
						updatedRow.monto_facturado_bienes = updatedRow.monto_facturado;
					}
				}
				
				return updatedRow;
			}
			return row;
		});
		
		// Actualizar datos_completados
		const updatedDatos = { ...datosCompletados };
		
		if (tipo === '606') {
			updatedDatos.compras = updatedRows;
		} else if (tipo === '607') {
			updatedDatos.ventas = updatedRows;
		}
		
		// Actualizar el estado con una nueva referencia para forzar re-render
		setEditingData(prev => ({
			...prev,
			datos_completados: updatedDatos
		}));
	};

	// Función para mover un registro a otro formulario cuando cambia la fecha.
	// Funciona desde vista normal (edición por celdas) y desde modo edición por filas.
	const handleMoverRegistroAFormulario = async (rowId, nuevoPeriodo, nuevaFecha, field, value) => {
		if (!selectedFormulario) return;
		
		try {
			// Obtener el registro: en modo edición por filas usar dataRowsSelectedEditing, si no getDataRows (vista normal)
			const allRows = editingData ? dataRowsSelectedEditing : getDataRows(selectedFormulario, false);
			const targetRow = allRows.find(r => r._rowId === rowId || r._id === rowId);
			
			if (!targetRow) {
				window.alert('Error: No se encontró el registro a mover.');
				return;
			}
			
			// Determinar el ID a enviar: priorizar _id, luego _rowId
			// IMPORTANTE: El backend busca por _id o _rowId, pero _id es más confiable
			// Si _id existe (incluso si es legacy_xxx), usarlo directamente
			let registroIdParaEnviar = targetRow._id;
			
			// Si _id existe, usarlo directamente (incluso si es legacy_xxx)
			if (registroIdParaEnviar) {
				registroIdParaEnviar = String(registroIdParaEnviar);
			} else if (targetRow._rowId) {
				// Si no hay _id, intentar extraer de _rowId
				if (typeof targetRow._rowId === 'string' && targetRow._rowId.startsWith('row_')) {
					const idExtraido = targetRow._rowId.replace('row_', '');
					registroIdParaEnviar = idExtraido;
				} else {
					registroIdParaEnviar = String(targetRow._rowId);
				}
			}
			
			// Si aún no hay ID, intentar usar la clave generada como último recurso
			if (!registroIdParaEnviar) {
				const claveGenerada = generarClaveRegistro(targetRow);
				if (claveGenerada) {
					registroIdParaEnviar = claveGenerada;
				}
			}
			
			if (!registroIdParaEnviar) {
				window.alert('Error: No se pudo determinar el ID del registro a mover.');
				return;
			}
			
		// Construir campos de búsqueda por contenido (fallback cuando el ID no existe en la BD)
		// Esto es necesario porque el frontend puede generar IDs temporales (legacy_xxx) que no existen en la BD
		const camposBusqueda = {
			ncf: targetRow.ncf || '',
			rnc: targetRow.rnc_emisor || targetRow.rnc_cedula || targetRow.rnc || '',
			monto: targetRow.monto_facturado || targetRow.total_factura || 0,
			fecha: targetRow.fecha_comprobante || targetRow.fecha || ''
		};
		
		
		
		// Llamar al endpoint para mover el registro
		const resultado = await dgiiApi.moverRegistroAFormulario(
			selectedFormulario.id,
			registroIdParaEnviar,
			nuevoPeriodo,
			nuevaFecha,
			camposBusqueda
		);
			
		if (resultado.success) {
			const msg = resultado.formulario_origen_eliminado
				? `✅ Registro movido al formulario del período ${nuevoPeriodo}. El formulario de origen fue eliminado por quedar vacío.`
				: `✅ Registro movido exitosamente al formulario del período ${nuevoPeriodo}.`;
			window.alert(msg);
			
			// Salir del modo edición
			setEditingData(null);
			setEditingRowIndex(null);
			
			// Recargar la lista de formularios (incluye quitar el origen si fue eliminado)
			await loadFormularios();
			
			// Cargar el formulario destino directamente usando el ID devuelto por el backend
			const formularioDestinoId = resultado.formulario_destino_id;
			const origenEliminado = resultado.formulario_origen_eliminado === true;
			if (formularioDestinoId) {
				try {
					// Cargar datos frescos del formulario destino desde la BD
					const formularioDestinoDatos = await dgiiApi.getFormulario(formularioDestinoId);
					setSelectedFormulario(formularioDestinoDatos);
					// Recargar facturas físicas del destino (incluye la factura movida) para que el botón "pendiente" funcione
					try {
						const facturasDestino = await dgiiApi.getFacturasFisicas(formularioDestinoId);
						setFacturasFisicas(Array.isArray(facturasDestino) ? facturasDestino : []);
					} catch (errFisicas) {
					}
				} catch (err) {
					// Si el origen fue eliminado (quedó vacío), no intentar cargarlo (404)
					if (origenEliminado) {
						setSelectedFormulario(null);
					} else {
						try {
							const formularioOrigenFresco = await dgiiApi.getFormulario(selectedFormulario.id);
							setSelectedFormulario(formularioOrigenFresco);
						} catch (e2) {
							setSelectedFormulario(null);
						}
					}
				}
			} else {
				// Si no hay ID destino y el origen no fue eliminado, recargar el origen
				if (!origenEliminado) {
					try {
						const formularioOrigenFresco = await dgiiApi.getFormulario(selectedFormulario.id);
						setSelectedFormulario(formularioOrigenFresco);
					} catch (e2) {
						setSelectedFormulario(null);
					}
				} else {
					setSelectedFormulario(null);
				}
			}
		} else {
			window.alert(`Error al mover el registro: ${resultado.error || 'Error desconocido'}`);
		}
		} catch (error) {
			const errorMessage = error.response?.data?.error || error.response?.data?.detail || error.message || 'Error desconocido';
			window.alert(`Error al mover el registro: ${errorMessage}`);
		} finally {
			// Asegurar que se salga del modo edición
			setEditingData(null);
			setEditingRowIndex(null);
		}
	};

	// Guardar una sola celda editada (sin cambiar orden del registro).
	// Si el registro solo está en datos_extraidos, se agrega a datos_completados y se guarda.
	const handleSaveCell = async (rowId, field, newValue) => {
		if (!selectedFormulario) return;
		const allRows = getDataRows(selectedFormulario, false);
		const row = allRows.find(r => r._rowId === rowId || (rowId != null && toIdKey(r._id) === toIdKey(rowId)));
		if (!row) {
			setEditingCell(null);
			return;
		}
		// Movimiento por fecha: si se edita fecha_comprobante y corresponde a otro período, ofrecer mover registro
		if (field === 'fecha_comprobante' && newValue) {
			const periodo = selectedFormulario.periodo;
			if (periodo && periodo.match(/^\d{4}-\d{2}$/)) {
				const fechaNorm = getNormalizedDate(newValue);
				if (fechaNorm) {
					const d = new Date(fechaNorm);
					const añoFecha = d.getFullYear();
					const mesFecha = d.getMonth() + 1;
					const periodoNuevo = `${añoFecha}-${String(mesFecha).padStart(2, '0')}`;
					const [añoPeriodo, mesPeriodo] = periodo.split('-').map(Number);
					if (añoFecha !== añoPeriodo || mesFecha !== mesPeriodo) {
						const confirmar = window.confirm(
							`La fecha de comprobante (${newValue}) corresponde al período ${periodoNuevo}, ` +
							`pero el registro está en el formulario del período ${periodo}.\n\n` +
							`¿Desea mover este registro al formulario del período ${periodoNuevo}?`
						);
						if (confirmar) {
							setEditingCell(null);
							await handleMoverRegistroAFormulario(rowId, periodoNuevo, newValue, field, newValue);
							return;
						}
						return; // No guardar aquí si no confirmó mover
					}
				}
			}
		}
		const listaClave = selectedFormulario.tipo === '606' ? 'compras' : 'ventas';
		// Usar editingData si existe (cambios en sesión) para no perder ediciones previas
		const datosOrigen = editingData?.datos_completados || selectedFormulario.datos_completados || {};
		const lista = datosOrigen[listaClave] || [];
		const rowIdNorm = toIdKey(row._id);
		// Usar toIdKey para evitar duplicados por int vs string al editar cualquier campo
		const idx = rowIdNorm
			? lista.findIndex(r => toIdKey(r._id) === rowIdNorm)
			: lista.findIndex(r => r._rowId === rowId);
		const datosParaGuardar = JSON.parse(JSON.stringify(datosOrigen));
		if (!datosParaGuardar[listaClave]) datosParaGuardar[listaClave] = [];

		let registro;
		if (idx >= 0) {
			registro = datosParaGuardar[listaClave][idx];
			if (!registro) return;
		} else {
			// Registro solo existe en datos_extraidos: verificar que no exista ya (por _id normalizado) antes de añadir
			const yaExistePorId = rowIdNorm && datosParaGuardar[listaClave].some(r => toIdKey(r._id) === rowIdNorm);
			if (yaExistePorId) {
				const idxExist = datosParaGuardar[listaClave].findIndex(r => toIdKey(r._id) === rowIdNorm);
				registro = datosParaGuardar[listaClave][idxExist];
			} else {
				registro = limpiarRegistroParaGuardar(row);
				registro._id = row._id || registro._id;
				registro._rowId = row._rowId || rowId;
				datosParaGuardar[listaClave].push(registro);
			}
		}

		// Normalizar valor numérico si el campo es numérico (incl. monto_servicios/monto_bienes usados en la tabla)
		const numFields = ['monto_facturado', 'total_factura', 'monto_servicios', 'monto_bienes', 'monto_facturado_servicios', 'monto_facturado_bienes', 'descuento', 'itbis_facturado', 'itbis_retenido', 'itbis_proporcionalidad', 'itbis_llevado_costo', 'itbis_costo', 'itbis_por_adelantar', 'itbis_percibido_compras', 'monto_retencion_renta', 'isr_percibido_compras', 'impuesto_selectivo_consumo', 'otros_impuestos_tasas', 'monto_propina_legal'];
		if (numFields.includes(field)) {
			const n = parseFloat(String(newValue).replace(/,/g, '').trim());
			registro[field] = isNaN(n) ? 0 : n;
		} else {
			registro[field] = newValue == null ? '' : String(newValue).trim();
		}
		// Total facturado = suma de monto_servicios + monto_bienes (no editable, se actualiza automáticamente)
		if (field === 'monto_servicios' || field === 'monto_bienes') {
			const serv = parseFloat(registro.monto_servicios || 0) || 0;
			const bienes = parseFloat(registro.monto_bienes || 0) || 0;
			registro.monto_facturado = serv + bienes;
			registro.monto_facturado_servicios = serv;
			registro.monto_facturado_bienes = bienes;
		}
		try {
			const payload = {
				datos_completados: datosParaGuardar,
				notas: selectedFormulario.notas || '',
				tipo: selectedFormulario.tipo,
				periodo: selectedFormulario.periodo
			};
			const updated = await dgiiApi.updateFormulario(selectedFormulario.id, payload);
			const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
			// Preservar orden: mismo que en handleSave (orden actual de lista)
			// Usar toIdKey para evitar fallos por int vs string
			const ordenPrevio = (datosParaGuardar[listaClave] || []).map(r => r && toIdKey(r._id)).filter(Boolean);
			const llegados = refreshed.datos_completados?.[listaClave] || [];
			const mapaPorId = new Map();
			llegados.forEach(r => { if (r && r._id) mapaPorId.set(toIdKey(r._id), r); });
			const reordenados = [];
			ordenPrevio.forEach(idKey => {
				if (mapaPorId.has(idKey)) { reordenados.push(mapaPorId.get(idKey)); mapaPorId.delete(idKey); }
			});
			mapaPorId.forEach(r => reordenados.push(r));
			const datosActualizados = { ...refreshed.datos_completados, [listaClave]: reordenados };

			// Filtrar datos_extraidos: quitar duplicado del mismo comprobante (edición NCF por pasos).
			// Además de _id/_rowId/NCF+rnc: usar _extraida_id (estable aunque el NCF cambie en completados).
			const extraidosBase = refreshed.datos_extraidos?.[listaClave] || [];
			const idsCompletados = new Set(reordenados.filter(r => r._id).map(r => String(r._id)));
			const extraidaIdReg = registro._extraida_id ?? registro.extraida_id ?? row._extraida_id ?? row.extraida_id;
			const extraidosFiltrados = extraidosBase.filter(r => {
				if (r._id && idsCompletados.has(String(r._id))) return false;
				if (row._rowId && r._rowId && r._rowId === row._rowId) return false;
				// Misma factura física aunque el NCF en pantalla ya sea intermedio (B12… vs OCR original)
				if (extraidaIdReg != null && extraidaIdReg !== '') {
					const rid = r._extraida_id ?? r.extraida_id;
					if (rid != null && String(rid) === String(extraidaIdReg)) return false;
				}
				const ncfR = normalizarNCF(r.ncf || r.ncf_comprobante || '');
				const ncfOrig = normalizarNCF(row.ncf || row.ncf_comprobante || '');
				const ncfGuardado = normalizarNCF(registro.ncf || registro.ncf_comprobante || '');
				const rncR = limpiarRNC(r.rnc_emisor || r.rnc_cedula || r.rnc || '');
				const rncOrig = limpiarRNC(row.rnc_emisor || row.rnc_cedula || row.rnc || '');
				const montoR = r.total_factura || r.monto_facturado || 0;
				const montoOrig = row.total_factura || row.monto_facturado || 0;
				const fechaR = formatDateForComparison(r.fecha_comprobante || r.fecha || '');
				const fechaOrig = formatDateForComparison(row.fecha_comprobante || row.fecha || '');
				if (rncOrig && rncR === rncOrig && Math.abs(montoR - montoOrig) < 0.02 && fechaR === fechaOrig) {
					if (ncfOrig && ncfR === ncfOrig) return false;
					if (ncfGuardado && ncfR === ncfGuardado) return false;
				}
				return true;
			});
			const datosExtraidosFiltrados = refreshed.datos_extraidos
				? { ...refreshed.datos_extraidos, [listaClave]: extraidosFiltrados }
				: refreshed.datos_extraidos;

			setSelectedFormulario({
				...refreshed,
				datos_completados: datosActualizados,
				datos_extraidos: datosExtraidosFiltrados
			});
			// Sincronizar editingData si está activo para que la vista de edición muestre el valor guardado
			if (editingData) {
				setEditingData(prev => prev ? {
					...prev,
					datos_completados: { ...prev.datos_completados, [listaClave]: reordenados },
					datos_extraidos: datosExtraidosFiltrados
				} : null);
			}
			setEditingCell(null);
		} catch (err) {
			window.alert('Error al guardar la celda: ' + (err.response?.data?.detail || err.message || 'Error desconocido'));
		}
	};

	const handleSave = async () => {
		if (!selectedFormulario || !editingData) {
			window.alert('Error: No hay datos para guardar. Por favor, recarga la página e intenta de nuevo.');
			return;
		}

		try {
			
			// Obtener los datos actualizados desde editingData
			const datosParaGuardar = JSON.parse(JSON.stringify(editingData.datos_completados || {}));
			
			// VALIDACIÓN: Verificar duplicados por RNC + NCF antes de guardar
			// IMPORTANTE: Solo validar duplicados REALES, no el mismo registro comparado consigo mismo
			const listaClave = selectedFormulario.tipo === '606' ? 'compras' : 'ventas';
			const registros = datosParaGuardar[listaClave] || [];
			const erroresValidacion = [];
			
			// Crear un mapa de registros por _id para detectar duplicados dentro de la misma lista
			const registrosPorId = new Map();
			const registrosSinId = [];
			
			for (let idx = 0; idx < registros.length; idx++) {
				const registro = registros[idx];
				if (registro._id) {
					if (registrosPorId.has(registro._id)) {
						// Ya existe un registro con este _id en la lista (duplicado real)
						erroresValidacion.push(`Registro ${idx + 1}: Duplicado interno - mismo _id (${registro._id})`);
					} else {
						registrosPorId.set(registro._id, registro);
					}
				} else {
					registrosSinId.push({ registro, idx });
				}
			}
			
			// Validar duplicados por RNC + NCF solo para registros sin _id o comparando entre diferentes _ids
			for (let idx = 0; idx < registros.length; idx++) {
				const registro = registros[idx];
				// Solo validar si tiene RNC y NCF
				const rnc = registro.rnc_emisor || registro.rnc_cedula || registro.rnc || '';
				const ncf = registro.ncf || '';
				
				if (!rnc || !ncf) {
					continue; // Sin RNC o NCF, no se puede validar duplicados
				}
				
				// Buscar otros registros con el mismo RNC + NCF pero diferente _id
				for (let otroIdx = idx + 1; otroIdx < registros.length; otroIdx++) {
					const otroRegistro = registros[otroIdx];
					const otroRnc = otroRegistro.rnc_emisor || otroRegistro.rnc_cedula || otroRegistro.rnc || '';
					const otroNcf = otroRegistro.ncf || '';
					
					// Solo comparar si tienen RNC y NCF, y son diferentes registros (_id diferente)
					if (otroRnc && otroNcf && registro._id !== otroRegistro._id) {
						const rncLimpio = limpiarRNC(rnc).toUpperCase();
						const ncfLimpio = normalizarNCF(ncf).toUpperCase();
						const otroRncLimpio = limpiarRNC(otroRnc).toUpperCase();
						const otroNcfLimpio = normalizarNCF(otroNcf).toUpperCase();
						
						if (rncLimpio === otroRncLimpio && ncfLimpio === otroNcfLimpio) {
							erroresValidacion.push(`Registro ${idx + 1} y ${otroIdx + 1}: Mismo RNC (${rnc}) y NCF (${ncf})`);
						}
					}
				}
			}
			
			// Si hay errores de validación, mostrar mensaje y no guardar
			if (erroresValidacion.length > 0) {
				const mensajeError = `No se puede guardar. Se encontraron registros duplicados:\n\n${erroresValidacion.join('\n')}\n\nPor favor, corrija los duplicados antes de guardar.`;
				window.alert(mensajeError);
				return;
			}
			
			// Asegurar que tenga la estructura correcta con compras/ventas
			// IMPORTANTE: Solo usar datos_completados de editingData, NO combinar con datos_extraidos
			if (selectedFormulario.tipo === '606') {
				// Asegurar que existe el array de compras
				if (!datosParaGuardar.compras) {
					datosParaGuardar.compras = [];
				}
				
				// IMPORTANTE: Recalcular itbis_por_adelantar para todos los registros antes de guardar
				if (datosParaGuardar.compras && datosParaGuardar.compras.length > 0) {
					datosParaGuardar.compras = datosParaGuardar.compras.map(r => {
						// Calcular itbis_por_adelantar = itbis_facturado - itbis_llevado_costo
						const itbisFacturado = parseFloat(r.itbis_facturado || 0) || 0;
						const itbisCosto = parseFloat(r.itbis_llevado_costo || r.itbis_costo || 0) || 0;
						r.itbis_por_adelantar = itbisFacturado - itbisCosto;
						// IMPORTANTE: Asegurar que itbis_llevado_costo esté presente (si no existe, usar itbis_costo o el valor calculado)
						if (!r.hasOwnProperty('itbis_llevado_costo') && r.hasOwnProperty('itbis_costo')) {
							r.itbis_llevado_costo = r.itbis_costo;
						} else if (!r.hasOwnProperty('itbis_llevado_costo')) {
							r.itbis_llevado_costo = itbisCosto;
						}
						return r;
					});
					
					// Limpiar todos los registros de campos internos y no permitidos antes de guardar
					datosParaGuardar.compras = datosParaGuardar.compras.map(r => limpiarRegistroParaGuardar(r));
					
					// IMPORTANTE: Eliminar duplicados usando SOLO _id como clave única
					// No usar generarClaveRegistro porque puede generar claves diferentes si el NCF cambia
					const registrosUnicos = new Map();
					let duplicadosEncontrados = 0;
					
					datosParaGuardar.compras.forEach((r, idx) => {
						const _id = r._id;
						
						if (!_id) {
							// Si no tiene _id, generar uno (no debería pasar, pero por seguridad)
							r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
						}
						
						// Usar _id como clave única
						if (registrosUnicos.has(_id)) {
							duplicadosEncontrados++;
							const existente = registrosUnicos.get(_id);
							const camposExistentes = Object.keys(existente).filter(k => !k.startsWith('_')).length;
							const camposNuevos = Object.keys(r).filter(k => !k.startsWith('_')).length;
							
							// Priorizar: auditado > más campos > mantener existente
							if (r.auditado === true && existente.auditado !== true) {
								registrosUnicos.set(_id, r);
							} else if (camposNuevos > camposExistentes && existente.auditado !== true) {
								registrosUnicos.set(_id, r);
							} else {
							}
						} else {
							registrosUnicos.set(_id, r);
						}
					});
					
					datosParaGuardar.compras = Array.from(registrosUnicos.values());
					
				}
			} else if (selectedFormulario.tipo === '607') {
				// Asegurar que existe el array de ventas
				if (!datosParaGuardar.ventas) {
					datosParaGuardar.ventas = [];
				}
				
				// IMPORTANTE: Recalcular itbis_por_adelantar para todos los registros antes de guardar
				if (datosParaGuardar.ventas && datosParaGuardar.ventas.length > 0) {
					datosParaGuardar.ventas = datosParaGuardar.ventas.map(r => {
						// Calcular itbis_por_adelantar = itbis_facturado - itbis_llevado_costo
						const itbisFacturado = parseFloat(r.itbis_facturado || 0) || 0;
						const itbisCosto = parseFloat(r.itbis_llevado_costo || r.itbis_costo || 0) || 0;
						r.itbis_por_adelantar = itbisFacturado - itbisCosto;
						// IMPORTANTE: Asegurar que itbis_llevado_costo esté presente (si no existe, usar itbis_costo o el valor calculado)
						if (!r.hasOwnProperty('itbis_llevado_costo') && r.hasOwnProperty('itbis_costo')) {
							r.itbis_llevado_costo = r.itbis_costo;
						} else if (!r.hasOwnProperty('itbis_llevado_costo')) {
							r.itbis_llevado_costo = itbisCosto;
						}
						return r;
					});
					
					// Limpiar todos los registros de campos internos y no permitidos antes de guardar
					datosParaGuardar.ventas = datosParaGuardar.ventas.map(r => limpiarRegistroParaGuardar(r));
					
					// IMPORTANTE: Eliminar duplicados usando SOLO _id como clave única
					const registrosUnicos = new Map();
					let duplicadosEncontrados = 0;
					
					datosParaGuardar.ventas.forEach((r, idx) => {
						const _id = r._id;
						
						if (!_id) {
							r._id = `legacy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
						}
						
						if (registrosUnicos.has(_id)) {
							duplicadosEncontrados++;
							const existente = registrosUnicos.get(_id);
							const camposExistentes = Object.keys(existente).filter(k => !k.startsWith('_')).length;
							const camposNuevos = Object.keys(r).filter(k => !k.startsWith('_')).length;
							
							if (r.auditado === true && existente.auditado !== true) {
								registrosUnicos.set(_id, r);
							} else if (camposNuevos > camposExistentes && existente.auditado !== true) {
								registrosUnicos.set(_id, r);
							}
						} else {
							registrosUnicos.set(_id, r);
						}
					});
					
					datosParaGuardar.ventas = Array.from(registrosUnicos.values());
					
				}
			}
			
			// Preparar payload solo con los campos que se pueden actualizar
			// Incluir tipo y periodo para evitar errores de validación
			const payload = {
				datos_completados: datosParaGuardar,
				notas: editingData.notas || '',
				tipo: selectedFormulario.tipo,  // Incluir tipo del formulario existente
				periodo: selectedFormulario.periodo  // Incluir periodo del formulario existente
			};
			
			
			const updated = await dgiiApi.updateFormulario(selectedFormulario.id, payload);
			
			
			// Recargar el formulario completo para asegurar sincronización
			const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
			
			// NUEVA ESTRUCTURA: Recargar FacturaEditada después de guardar
			try {
				const editadas = await dgiiApi.getFacturasEditadas(refreshed.id, true);
				setFacturasEditadas(editadas);
			} catch (err) {
			}
			
			// IMPORTANTE: Eliminar de datos_extraidos los registros que están en datos_completados
			// Usar SOLO _id como identificador único (viene de BD y no cambia aunque se editen los campos)
			if (refreshed.datos_completados && refreshed.datos_extraidos) {
				if (refreshed.datos_completados.compras && refreshed.datos_extraidos.compras) {
					const comprasCompletadas = refreshed.datos_completados.compras || [];
					const comprasExtraidas = refreshed.datos_extraidos.compras || [];
					
					
					// Crear un Set de _ids de registros completados para búsqueda rápida
					const idsCompletados = new Set();
					comprasCompletadas.forEach(r => {
						if (r._id) {
							idsCompletados.add(r._id);
						}
					});
					
					
					// Filtrar datos_extraidos: eliminar registros que tienen el mismo _id en datos_completados
					// El _id es el identificador único de BD y no cambia aunque se editen los campos
					refreshed.datos_extraidos.compras = comprasExtraidas.filter(rExtraido => {
						if (rExtraido._id && idsCompletados.has(rExtraido._id)) {
							return false; // No incluir en extraídos (ya está en completados)
						}
						// Si no tiene _id o no está en completados, mantenerlo en extraídos
						return true;
					});
					
				}
				
				if (refreshed.datos_completados.ventas && refreshed.datos_extraidos.ventas) {
					const ventasCompletadas = refreshed.datos_completados.ventas || [];
					const ventasExtraidas = refreshed.datos_extraidos.ventas || [];
					
					
					// Crear un Set de _ids de registros completados para búsqueda rápida
					const idsCompletadosVentas = new Set();
					ventasCompletadas.forEach(r => {
						if (r._id) {
							idsCompletadosVentas.add(r._id);
						}
					});
					
					
					// Filtrar datos_extraidos: eliminar registros que tienen el mismo _id en datos_completados
					// El _id es el identificador único de BD y no cambia aunque se editen los campos
					refreshed.datos_extraidos.ventas = ventasExtraidas.filter(rExtraido => {
						if (rExtraido._id && idsCompletadosVentas.has(rExtraido._id)) {
							return false; // No incluir en extraídos (ya está en completados)
						}
						// Si no tiene _id o no está en completados, mantenerlo en extraídos
						return true;
					});
					
				}
			}
			
			// Preservar el orden de los registros al guardar: reordenar datos_completados según el orden previo (editingData)
			// para que el registro editado no cambie de posición en la lista
			if (editingData && editingData.datos_completados && refreshed.datos_completados) {
				const listaClave = selectedFormulario.tipo === '606' ? 'compras' : 'ventas';
				const ordenPrevio = (editingData.datos_completados[listaClave] || []).map(r => r && r._id).filter(Boolean);
				const llegados = refreshed.datos_completados[listaClave] || [];
				const mapaPorId = new Map();
				llegados.forEach(r => { if (r && r._id) mapaPorId.set(r._id, r); });
				const reordenados = [];
				ordenPrevio.forEach(id => {
					if (mapaPorId.has(id)) {
						reordenados.push(mapaPorId.get(id));
						mapaPorId.delete(id);
					}
				});
				mapaPorId.forEach(r => reordenados.push(r));
				refreshed.datos_completados = {
					...refreshed.datos_completados,
					[listaClave]: reordenados
				};
			}
			
			// IMPORTANTE: Crear una copia profunda del objeto refreshed para asegurar que los cambios persistan
			const refreshedConFiltros = {
				...refreshed,
				datos_extraidos: refreshed.datos_extraidos ? {
					...refreshed.datos_extraidos,
					compras: refreshed.datos_extraidos.compras ? [...refreshed.datos_extraidos.compras] : [],
					ventas: refreshed.datos_extraidos.ventas ? [...refreshed.datos_extraidos.ventas] : []
				} : refreshed.datos_extraidos,
				datos_completados: refreshed.datos_completados ? {
					...refreshed.datos_completados,
					compras: refreshed.datos_completados.compras ? [...refreshed.datos_completados.compras] : [],
					ventas: refreshed.datos_completados.ventas ? [...refreshed.datos_completados.ventas] : []
				} : refreshed.datos_completados
			};
			
			setSelectedFormulario(refreshedConFiltros);
			setEditingData(null);
			setEditingRowIndex(null);
			loadFormularios();
			
			window.alert('Datos guardados exitosamente');
		} catch (err) {
			
			// Mostrar error más detallado
			let errorMessage = 'Error al guardar: ';
			if (err.response?.data) {
				if (err.response.data.detail) {
					errorMessage += err.response.data.detail;
				} else if (err.response.data.message) {
					errorMessage += err.response.data.message;
				} else if (typeof err.response.data === 'object') {
					// Si hay errores de validación, mostrarlos
					const validationErrors = Object.entries(err.response.data)
						.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
						.join('\n');
					errorMessage += validationErrors || JSON.stringify(err.response.data);
				} else {
					errorMessage += err.response.data;
				}
			} else {
				errorMessage += err.message || 'Error desconocido';
			}
			
			window.alert(errorMessage);
		}
	};

	const handleCancel = () => {
		setEditingData(null);
		setEditingRowIndex(null);
	};

	// Calcular ITBIS por adelantar para un registro individual
	const handleCalcularItbisPorAdelantarIndividual = async (rowId) => {
		if (!selectedFormulario) {
			window.alert('Error: No hay formulario seleccionado.');
			return;
		}

		// Obtener el registro actual (puede estar en datos_completados o datos_extraidos)
		const allRows = dataRowsSelected;
		const targetRow = allRows.find(r => r._rowId === rowId || r._id === rowId);
		
		if (!targetRow) {
			window.alert('Error: No se encontró el registro.');
			return;
		}

		const itbisFacturado = parseFloat(targetRow.itbis_facturado || 0) || 0;
		const itbisCosto = parseFloat(targetRow.itbis_llevado_costo || targetRow.itbis_costo || 0) || 0;
		
		// Si no viene itbis_costo, usar itbis_facturado (resultado = 0)
		// IMPORTANTE: itbisCostoFinal siempre debe tener un valor (no puede ser 0 si itbisFacturado > 0)
		const itbisCostoFinal = itbisCosto > 0 ? itbisCosto : (itbisFacturado > 0 ? itbisFacturado : 0);
		const itbisPorAdelantar = itbisFacturado - itbisCostoFinal;
		
		// Pedir confirmación antes de aplicar
		const confirmar = window.confirm(
			`¿Desea calcular el ITBIS por adelantar para este registro?\n\n` +
			`ITBIS Facturado: ${itbisFacturado.toFixed(2)}\n` +
			`ITBIS Costo: ${itbisCostoFinal.toFixed(2)}\n` +
			`ITBIS por Adelantar: ${itbisPorAdelantar.toFixed(2)}\n\n` +
			`El cambio se guardará automáticamente.`
		);
		
		if (!confirmar) {
			return;
		}

		try {
			// Determinar el ID a enviar
			// IMPORTANTE: Priorizar _id completo (puede ser legacy_xxx) sobre extracción de _rowId
			let registroIdParaEnviar = targetRow._id;
			
			// Si _id existe, usarlo directamente (incluso si es legacy_xxx)
			if (registroIdParaEnviar) {
				// Usar el _id completo tal cual
				registroIdParaEnviar = String(registroIdParaEnviar);
			} else if (targetRow._rowId) {
				// Si no hay _id, intentar extraer de _rowId
				if (typeof targetRow._rowId === 'string' && targetRow._rowId.startsWith('row_')) {
					registroIdParaEnviar = targetRow._rowId.replace('row_', '');
				} else {
					registroIdParaEnviar = String(targetRow._rowId);
				}
			}
			
			// Como último recurso, usar clave generada
			if (!registroIdParaEnviar) {
				const claveGenerada = generarClaveRegistro(targetRow);
				registroIdParaEnviar = claveGenerada;
			}
			
			if (!registroIdParaEnviar) {
				window.alert('Error: No se pudo determinar el ID del registro.');
				return;
			}
			
		// Construir campos de búsqueda por contenido (fallback cuando el ID no existe en la BD)
		// Esto es necesario porque el frontend puede generar IDs temporales (legacy_xxx) que no existen en la BD
		const camposBusqueda = {
			ncf: targetRow.ncf || '',
			rnc: targetRow.rnc_emisor || targetRow.rnc_cedula || targetRow.rnc || '',
			monto: targetRow.monto_facturado || targetRow.total_factura || 0,
			fecha: targetRow.fecha_comprobante || targetRow.fecha || ''
		};
		
		
		// Actualizar directamente en el backend sin modo edición
		const resultado = await dgiiApi.actualizarRegistro(
			selectedFormulario.id,
			registroIdParaEnviar,
			{
				itbis_por_adelantar: itbisPorAdelantar,
				itbis_llevado_costo: itbisCostoFinal,
				itbis_costo: itbisCostoFinal
			},
			camposBusqueda
		);
			
		if (resultado.success) {
			window.alert(`✅ ITBIS por adelantar calculado y guardado: ${itbisPorAdelantar.toFixed(2)}`);
			// Cargar el formulario fresco y filtrar duplicados (evita que aparezca versión original)
			const formularioFresco = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(filtrarFormularioSinDuplicados(formularioFresco));
			// También refrescar la lista de formularios en segundo plano
			loadFormularios();
		} else {
			window.alert(`Error: ${resultado.error || 'Error desconocido'}`);
		}
	} catch (error) {
		window.alert(`Error al calcular ITBIS: ${error.response?.data?.error || error.message || 'Error desconocido'}`);
	}
};

	// Calcular ITBIS por adelantar para todos los registros
	const handleCalcularItbisPorAdelantarMasivo = async () => {
		if (!selectedFormulario) {
			window.alert('Error: No hay formulario seleccionado.');
			return;
		}

		// Usar todas las filas actuales (vista combinada)
		const allRowsVista = dataRowsSelected;
		if (!allRowsVista || allRowsVista.length === 0) {
			window.alert('No hay registros para calcular.');
			return;
		}

		// Contar registros con ITBIS facturado > 0
		const conItbis = allRowsVista.filter(r => parseFloat(r.itbis_facturado || 0) > 0).length;
		const totalRegistros = allRowsVista.length;

		const confirmar = window.confirm(
			`¿Desea calcular el ITBIS por adelantar para TODOS los registros del formulario actual?\n\n` +
			`Registros totales: ${totalRegistros}\n` +
			`Registros con ITBIS facturado > 0: ${conItbis}\n\n` +
			`La fórmula aplicada será: ITBIS Facturado - ITBIS al Costo.\n` +
			`Si ITBIS al Costo está vacío, se usará ITBIS Facturado (resultado = 0).\n\n` +
			`Los cambios se guardarán automáticamente.`
		);

		if (!confirmar) {
			return;
		}

		let calculados = 0;
		let errores = 0;
		
		// Procesar cada registro directamente en el backend (sin modo edición)
		for (const registro of allRowsVista) {
			const itbisFacturado = parseFloat(registro.itbis_facturado || 0) || 0;
			const itbisCosto = parseFloat(registro.itbis_llevado_costo || registro.itbis_costo || 0) || 0;
			
			const itbisCostoFinal = itbisCosto > 0 ? itbisCosto : (itbisFacturado > 0 ? itbisFacturado : 0);
			const itbisPorAdelantar = itbisFacturado - itbisCostoFinal;
			
			// Solo actualizar registros que tienen ITBIS facturado
			if (itbisFacturado === 0 && itbisCostoFinal === 0) continue;
			
			// Determinar ID a enviar (puede ser legacy_xxx generado en frontend)
			let registroIdParaEnviar = registro._id;
			if (registroIdParaEnviar) {
				registroIdParaEnviar = String(registroIdParaEnviar);
			} else if (registro._rowId) {
				registroIdParaEnviar = typeof registro._rowId === 'string' && registro._rowId.startsWith('row_')
					? registro._rowId.replace('row_', '')
					: String(registro._rowId);
			}
			
			// Campos de búsqueda por contenido (fallback cuando el ID no existe en la BD)
			const camposBusqueda = {
				ncf: registro.ncf || '',
				rnc: registro.rnc_emisor || registro.rnc_cedula || registro.rnc || '',
				monto: registro.monto_facturado || registro.total_factura || 0,
				fecha: registro.fecha_comprobante || registro.fecha || ''
			};
			
			try {
				await dgiiApi.actualizarRegistro(
					selectedFormulario.id,
					registroIdParaEnviar,
					{
						itbis_por_adelantar: itbisPorAdelantar,
						itbis_llevado_costo: itbisCostoFinal,
						itbis_costo: itbisCostoFinal
					},
					camposBusqueda
				);
				calculados++;
			} catch (err) {
				errores++;
			}
		}
		
		if (errores > 0) {
			window.alert(`ITBIS calculado para ${calculados} registro(s).\n${errores} registro(s) no pudieron actualizarse.`);
		} else {
			window.alert(`✅ ITBIS por adelantar calculado y guardado para ${calculados} registro(s).`);
		}
		
		// Cargar el formulario fresco y filtrar duplicados (evita que aparezca versión original)
		const formularioFresco = await dgiiApi.getFormulario(selectedFormulario.id);
		setSelectedFormulario(filtrarFormularioSinDuplicados(formularioFresco));
		// También refrescar la lista de formularios en segundo plano
		loadFormularios();
	};

	// Detectar si la URL es un PDF
	const isPdfUrl = (url) => {
		if (!url) return false;
		return url.toLowerCase().endsWith('.pdf') || url.toLowerCase().includes('.pdf?');
	};

	const viewImage = (url, event = null) => {
		if (!url) {
			window.alert('No hay imagen disponible para este registro');
			return;
		}
		// Si se presiona Ctrl/Cmd o botón derecho, abrir en nueva pestaña
		if (event && (event.ctrlKey || event.metaKey || event.button === 1 || event.button === 2)) {
			window.open(url, '_blank', 'noopener,noreferrer');
			return;
		}
		setImageUrl(url);
		setShowImageModal(true);
		setImageZoom(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleZoomIn = () => {
		setImageZoom(prev => Math.min(prev + 0.25, 5));
	};

	const handleZoomOut = () => {
		setImageZoom(prev => Math.max(prev - 0.25, 0.5));
	};

	const handleResetZoom = () => {
		setImageZoom(1);
		setImagePosition({ x: 0, y: 0 });
	};

	const handleMouseDown = (e) => {
		if (imageZoom > 1) {
			setIsDragging(true);
			setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
		}
	};

	const handleMouseMove = (e) => {
		if (isDragging && imageZoom > 1) {
			setImagePosition({
				x: e.clientX - dragStart.x,
				y: e.clientY - dragStart.y
			});
		}
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleWheel = (e) => {
		if (e.ctrlKey || e.metaKey) {
			e.preventDefault();
			const delta = e.deltaY > 0 ? -0.1 : 0.1;
			setImageZoom(prev => Math.max(0.5, Math.min(5, prev + delta)));
		}
	};

	const formatCurrency = (value) => {
		if (!value && value !== 0) return 'N/A';
		return new Intl.NumberFormat('es-DO', {
			style: 'currency',
			currency: 'DOP'
		}).format(value);
	};

	const formatDate = (dateString) => {
		if (!dateString) return 'N/A';
		try {
			// Normalizar fecha: extraer solo la parte de fecha si tiene timestamp
			let fechaNormalizada = dateString;
			if (typeof fechaNormalizada === 'string') {
				if (fechaNormalizada.includes('T')) {
					fechaNormalizada = fechaNormalizada.split('T')[0];
				} else if (fechaNormalizada.includes(' ')) {
					fechaNormalizada = fechaNormalizada.split(' ')[0];
				}
			}
			
			// Si ya está en formato YYYY-MM-DD, convertir a formato local
			if (/^\d{4}-\d{2}-\d{2}$/.test(fechaNormalizada)) {
				const [año, mes, dia] = fechaNormalizada.split('-');
				return `${dia}/${mes}/${año}`;
			}
			
			// Intentar parsear como fecha
			const date = new Date(fechaNormalizada);
			if (!isNaN(date.getTime())) {
				return date.toLocaleDateString('es-DO');
			}
			
			return fechaNormalizada;
		} catch {
			return dateString;
		}
	};
	
	// Función helper para obtener fecha normalizada (YYYY-MM-DD) para inputs de tipo date
	const getNormalizedDate = (dateString) => {
		if (!dateString) return '';
		if (typeof dateString === 'string') {
			if (dateString.includes('T')) {
				return dateString.split('T')[0];
			} else if (dateString.includes(' ')) {
				return dateString.split(' ')[0];
			} else if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
				return dateString;
			}
		}
		// Intentar convertir otros formatos
		try {
			const date = new Date(dateString);
			if (!isNaN(date.getTime())) {
				const año = date.getFullYear();
				const mes = String(date.getMonth() + 1).padStart(2, '0');
				const dia = String(date.getDate()).padStart(2, '0');
				return `${año}-${mes}-${dia}`;
			}
		} catch {
			// Ignorar errores
		}
		return '';
	};

	const getEstadoColor = (estado) => {
		const colors = {
			abierto: 'bg-green-100 text-green-800',
			cerrado: 'bg-red-100 text-red-800',
		};
		return colors[estado] || 'bg-gray-100 text-gray-800';
	};

	// Funciones de exportación
	const handleExportExcel = async () => {
		if (!selectedFormulario) return;
		try {
			// Refrescar formulario para que el backend exporte con datos al día
			try {
				const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
				setSelectedFormulario(refreshed);
			} catch (e) {
			}
			const response = await dgiiApi.exportarXLSX(selectedFormulario.id);
			
			// El backend ahora devuelve el archivo directamente como blob
			// Crear un enlace de descarga
			let blob;
			if (response instanceof Blob) {
				blob = response;
			} else if (response.data instanceof Blob) {
				blob = response.data;
			} else {
				// Si viene como array buffer o string, convertirlo a blob
				blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
			}
			
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `formulario_${selectedFormulario.tipo}_${selectedFormulario.periodo}.xlsx`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
			
			window.alert('Archivo Excel exportado exitosamente');
		} catch (err) {
			
			// Intentar leer el mensaje de error del response
			let errorMessage = 'Error al exportar a Excel: ';
			if (err.response?.data) {
				if (err.response.data instanceof Blob) {
					// Si el error viene como blob, intentar leerlo como texto
					err.response.data.text().then(text => {
						try {
							const errorJson = JSON.parse(text);
							window.alert(errorMessage + (errorJson.detail || text));
						} catch {
							window.alert(errorMessage + text);
						}
					});
					return;
				} else if (err.response.data.detail) {
					errorMessage += err.response.data.detail;
				} else if (typeof err.response.data === 'string') {
					errorMessage += err.response.data;
				} else {
					errorMessage += JSON.stringify(err.response.data);
				}
			} else {
				errorMessage += err.message || 'Error desconocido';
			}
			window.alert(errorMessage);
		}
	};

	const handleExportCSV = () => {
		if (!selectedFormulario) return;
		// Misma lista que XLSX/TXT: datos_completos del API (merge + excluidas/duplicadas/movidas).
		// Si aún no viene en el payload (caché antiguo), usar filas del grid.
		const apiDc = selectedFormulario.datos_completos;
		const rows =
			selectedFormulario.tipo === '606' && apiDc && Array.isArray(apiDc.compras)
				? apiDc.compras
				: dataRowsSelected;
		if (rows.length === 0) {
			window.alert('No hay datos para exportar');
			return;
		}

		// Encabezados CSV del formulario 606 según especificación (orden y nombres correctos)
		// Excluyendo: Subtotal, Moneda, Estatus
		const headers = [
			'Razón Social', 'RNC o Cédula', 'Tipo Id', 'Tipo Bienes y Servicios Comprados',
			'NCF', 'NCF ó Documento Modificado', 'Fecha Comprobante', 'Fecha Pago',
			'Monto Facturado en Servicios', 'Monto Facturado en Bienes', 'Monto Total', 'Descuento',
			'ITBIS Facturado', 'ITBIS Retenido', 'ITBIS sujeto a Proporcionalidad (Art. 349)',
			'ITBIS llevado al Costo', 'ITBIS por Adelantar', 'ITBIS percibido en compras',
			'Tipo de Retención en ISR', 'Monto Retención Renta', 'ISR Percibido en compras',
			'Impuesto Selectivo al Consumo', 'Otros Impuesto/Tasas', 'Monto Propina Legal', 'Forma de Pago'
		];
		
		// Función para escapar valores CSV
		const escapeCSV = (value) => {
			if (value === null || value === undefined) return '';
			const str = String(value);
			if (str.includes(',') || str.includes('"') || str.includes('\n')) {
				return `"${str.replace(/"/g, '""')}"`;
			}
			return str;
		};
		
		// Crear contenido CSV con orden y campos correctos (excluyendo Subtotal, Moneda, Estatus)
		const csvContent = [
			headers.join(','),
			...rows.map(row => [
				escapeCSV(row.nombre_emisor || row.razon_social || ''),  // 0: Razón Social
				escapeCSV(row.rnc_emisor || row.rnc_cedula || ''),       // 1: RNC o Cédula
				escapeCSV(row.tipo_id || ''),                            // 2: Tipo Id
				escapeCSV(row.tipo_bien_servicio || row.tipo_bienes_servicios || ''), // 3: Tipo Bienes y Servicios Comprados
				escapeCSV(row.ncf || ''),                                 // 4: NCF
				escapeCSV(row.ncf_modificado || ''),                     // 5: NCF ó Documento Modificado
				escapeCSV(row.fecha_comprobante || ''),                  // 6: Fecha Comprobante
				escapeCSV(row.fecha_pago || ''),                         // 7: Fecha Pago
				escapeCSV(row.monto_servicios || 0),                    // 8: Monto Facturado en Servicios
				escapeCSV(row.monto_bienes || 0),                        // 9: Monto Facturado en Bienes
				escapeCSV(row.monto_total || row.monto_facturado || row.total_factura || 0), // 10: Monto Total
				escapeCSV(row.descuento != null && row.descuento !== '' ? parseFloat(row.descuento) || 0 : 0), // 11: Descuento
				escapeCSV(row.itbis_facturado || row.itbis || 0),        // 12: ITBIS Facturado
				escapeCSV(row.itbis_retenido || 0),                      // 13: ITBIS Retenido
				escapeCSV(row.itbis_proporcionalidad || 0),              // 14: ITBIS sujeto a Proporcionalidad (Art. 349)
				escapeCSV(row.itbis_costo || row.itbis_llevado_costo || 0), // 15: ITBIS llevado al Costo
				escapeCSV((() => {
					const v = row.itbis_por_adelantar ?? row.itbis_adelantar;
					if (v != null && v !== '') return parseFloat(v) || 0;
					const f = parseFloat(row.itbis_facturado || row.itbis || 0) || 0;
					const c = parseFloat(row.itbis_llevado_costo || row.itbis_costo || 0) || 0;
					return Math.max(0, f - c);
				})()),                                                     // 16: ITBIS por Adelantar
				escapeCSV(row.itbis_percibido || 0),                     // 17: ITBIS percibido en compras
			escapeCSV(row.tipo_retencion_isr || row.retencion_isr || ''),          // 18: Tipo de Retención en ISR
			escapeCSV(row.monto_retencion_renta || 0),               // 19: Monto Retención Renta
			escapeCSV(row.isr_percibido || 0),                       // 20: ISR Percibido en compras
			escapeCSV(row.impuesto_selectivo || row.impuesto_selectivo_consumo || row.imp_isc || 0), // 21: Impuesto Selectivo al Consumo
			escapeCSV(row.otros_impuestos || row.otros_impuestos_tasas || row.otros_ipm || 0), // 22: Otros Impuesto/Tasas
				escapeCSV(row.monto_propina || 0),                        // 23: Monto Propina Legal
				escapeCSV(row.forma_pago || '')                           // 24: Forma de Pago
			].join(','))
		].join('\n');

		// Descargar archivo
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `formulario_${selectedFormulario.tipo}_${selectedFormulario.periodo}.csv`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	const handleExportTXT = async () => {
		if (!selectedFormulario) return;

		// Formulario 606: TXT generado en backend (excluye pendientes por defecto)
		if (selectedFormulario.tipo === '606') {
			try {
				const response = await dgiiApi.exportarTXT(selectedFormulario.id, true);
				const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: 'text/plain;charset=utf-8' });
				const cd = response.headers['content-disposition'];
				const match = cd && cd.match(/filename="?([^";\n]+)"?/);
				const filename = match ? match[1].trim() : `DGII_F_606_${(selectedFormulario.empresa_rnc || '').replace(/[-\s]/g, '') || '00000000000'}_${(selectedFormulario.periodo || '').replace('-', '')}.txt`;
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(url);
				const excluded = response.headers['x-excluded-pendientes'];
				if (excluded && parseInt(excluded, 10) > 0) {
					window.alert(`Exportación completada.\n\nSe excluyeron ${excluded} registro(s) marcados como pendientes del archivo TXT.`);
				}
			} catch (e) {
				window.alert(e.response?.data?.detail || e.message || 'Error al exportar TXT');
			}
			return;
		}

		// 607 u otro: generar TXT en el cliente
		let formularioFresco = selectedFormulario;
		try {
			formularioFresco = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(formularioFresco);
		} catch (e) {
		}

		const rows = getDataRows(formularioFresco);
		if (rows.length === 0) {
			window.alert('No hay datos para exportar');
			return;
		}

		// Función auxiliar para formatear valores según especificación DGII
		// Formato deseado: sin padding innecesario, campos vacíos sin espacios
		const formatValue = (value, type = 'A', length = null, allowEmpty = true) => {
			if (value === null || value === undefined || value === '') {
				return allowEmpty ? '' : (type === 'N' ? '0' : '');
			}
			
			let str = String(value);
			
			// Para campos numéricos, remover separadores y formatear
			if (type === 'N') {
				// Remover separadores de miles y decimales, mantener solo el punto decimal
				str = str.toString().replace(/[^\d.]/g, '');
				
				// Si es 0 o vacío, retornar vacío (según formato deseado)
				if (str === '' || str === '0' || parseFloat(str) === 0) {
					return allowEmpty ? '' : '0';
				}
				
				// Si tiene punto decimal, mantenerlo con hasta 2 decimales
				if (str.includes('.')) {
					const parts = str.split('.');
					const decimals = parts[1].substring(0, 2);
					// Si los decimales son .00, removerlos
					if (decimals === '00' || decimals === '0') {
						str = parts[0];
					} else {
						// Remover ceros finales innecesarios
						str = parts[0] + '.' + decimals.replace(/0+$/, '');
					}
				}
				
				// NO agregar padding para montos (según formato deseado)
				// Solo para campos de código numérico (tipo ID, forma pago, etc.)
				if (length && length <= 2) {
					str = str.padStart(length, '0');
				}
			} else {
				// Para campos alfanuméricos, truncar si excede longitud
				if (length && str.length > length) {
					str = str.substring(0, length);
				}
				// NO rellenar con espacios (según formato deseado)
			}
			
			return str;
		};

		// Función para formatear fecha AAAAMMDD
		const formatDate = (dateStr) => {
			if (!dateStr) return '';
			// Intentar parsear diferentes formatos de fecha
			let date;
			if (dateStr.includes('/')) {
				// Formato DD/MM/YYYY o DD-MM-YYYY
				const parts = dateStr.split(/[\/\-]/);
				if (parts.length === 3) {
					date = new Date(parts[2], parts[1] - 1, parts[0]);
				}
			} else if (dateStr.includes('-')) {
				// Formato YYYY-MM-DD
				date = new Date(dateStr);
			} else {
				return '';
			}
			
			if (date && !isNaN(date.getTime())) {
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}${month}${day}`;
			}
			return '';
		};

		// Función para obtener RNC del usuario/empresa (del formulario)
		const getRNCContribuyente = () => {
			// Prioridad 1: Obtener desde empresa_rnc del serializer
			if (selectedFormulario.empresa_rnc) {
				return selectedFormulario.empresa_rnc.replace(/[-\s]/g, '');
			}
			// Prioridad 2: Intentar obtener del objeto empresa si está disponible
			if (selectedFormulario.empresa && selectedFormulario.empresa.rnc) {
				return selectedFormulario.empresa.rnc.replace(/[-\s]/g, '');
			}
			// Prioridad 3: Intentar obtener del usuario del formulario
			if (selectedFormulario.usuario && selectedFormulario.usuario.profile) {
				const rnc = selectedFormulario.usuario.profile.rnc_empresa;
				if (rnc) {
					// Remover guiones y espacios (sin padding)
					return rnc.replace(/[-\s]/g, '');
				}
			}
			// Si no hay, retornar vacío
			return '';
		};

		// Función para obtener período en formato AAAAMM
		const getPeriodoFormato = () => {
			const periodo = selectedFormulario.periodo || '';
			// Formato esperado: YYYY-MM, convertir a YYYYMM (sin padding)
			return periodo.replace('-', '');
		};

		// Construir encabezado según formato DGII
		// Formato: 606|RNC|PERIODO|CANTIDAD_REGISTROS (sin padding)
		const rncContribuyente = getRNCContribuyente();
		const periodoFormato = getPeriodoFormato();
		const cantidadRegistros = String(rows.length); // Sin padding
		
		const header = `606|${rncContribuyente}|${periodoFormato}|${cantidadRegistros}`;
		
		// Construir líneas de detalle según formato 606 (23 campos)
		const detailLines = rows.map(row => {
			// Normalizar RNC (remover guiones y espacios, sin padding)
			const rnc = (row.rnc_emisor || row.rnc_cedula || '').replace(/[-\s]/g, '');
			
			// Tipo ID: 1=RNC, 2=Cédula
			// Usar el valor del campo tipo_id si existe, sino calcularlo automáticamente
			const rncSinGuiones = (row.rnc_emisor || row.rnc_cedula || row.rnc || '').replace(/[-\s]/g, '');
			let tipoId = row.tipo_id || '';
			// Si no hay tipo_id, calcularlo usando la función calcularTipoID
			if (!tipoId && rncSinGuiones) {
				tipoId = calcularTipoID(rncSinGuiones);
			}
			// Si aún no hay tipo_id, usar cálculo por defecto basado en longitud
			if (!tipoId) {
				tipoId = rncSinGuiones.length === 11 ? '1' : (rncSinGuiones.length === 9 ? '2' : '1');
			}
			// Asegurar que sea un string de 1 dígito
			tipoId = String(tipoId).trim();
			
			// Tipo Bien/Servicio (código numérico)
			const tipoBienServicio = formatValue(row.tipo_bien_servicio || row.tipo_bienes_servicios || '', 'N', 2);
			
			// NCF (sin padding, mantener formato original)
			const ncf = (row.ncf || '').replace(/[-\s]/g, '').toUpperCase();
			
			// NCF Modificado (sin padding)
			const ncfModificado = (row.ncf_modificado || '').replace(/[-\s]/g, '').toUpperCase();
			
			// Fechas en formato AAAAMMDD
			const fechaComprobante = formatDate(row.fecha_comprobante || row.fecha || '');
			const fechaPago = formatDate(row.fecha_pago || '');
			
		// Obtener el monto total de la factura (priorizar monto_total/total_factura sobre monto_facturado que es subtotal sin ITBIS)
		const totalFactura = parseFloat(row.monto_total || row.total_factura || row.monto_facturado || 0) || 0;
			
			// Clasificar montos según el campo bien_servicio
			// Si existe bien_servicio, clasificar el total_factura en bienes o servicios
			const bienServicio = (row.bien_servicio || '').toLowerCase().trim();
			let montoServicios = 0;
			let montoBienes = 0;
			
			if (bienServicio === 'servicio') {
				// Si es servicio, el total va en montoServicios
				montoServicios = totalFactura;
				montoBienes = 0;
			} else if (bienServicio === 'bien') {
				// Si es bien, el total va en montoBienes
				montoServicios = 0;
				montoBienes = totalFactura;
			} else {
				// Si no hay bien_servicio, usar los valores existentes o 0
				montoServicios = parseFloat(row.monto_facturado_servicios || row.monto_servicios || 0) || 0;
				montoBienes = parseFloat(row.monto_facturado_bienes || row.monto_bienes || 0) || 0;
			}
			
			// Monto Total siempre es la suma de bienes + servicios
			// Si hay clasificación (bien_servicio), usar la suma; si no, usar total_factura
			let montoTotal = 0;
			if (bienServicio === 'servicio' || bienServicio === 'bien') {
				// Si hay clasificación, usar la suma calculada
				montoTotal = montoServicios + montoBienes;
			} else {
				// Si no hay clasificación, usar total_factura o la suma de los montos existentes
				if (montoServicios > 0 || montoBienes > 0) {
					montoTotal = montoServicios + montoBienes;
				} else {
					montoTotal = totalFactura;
				}
			}
			
			// Formatear montos (sin padding, mantener formato natural con decimales si aplica)
			const montoServiciosFormateado = formatValue(montoServicios, 'N', null, true);
			const montoBienesFormateado = formatValue(montoBienes, 'N', null, true);
			const montoTotalFormateado = formatValue(montoTotal, 'N', null, true);
			const itbisFacturado = formatValue(row.itbis_facturado || row.itbis || 0, 'N', null, true);
			const itbisRetenido = formatValue(row.itbis_retenido || 0, 'N', null, true);
			const itbisProporcionalidad = formatValue(row.itbis_proporcionalidad || row.itbis_sujeto_proporcionalidad || 0, 'N', null, true);
			const itbisLlevadoCosto = formatValue(row.itbis_llevado_costo || 0, 'N', null, true);
			// ITBIS por adelantar: valor guardado o itbis_facturado - itbis_llevado_costo. Escribir siempre como número (nunca vacío).
			const itbisF = parseFloat(row.itbis_facturado || row.itbis || 0) || 0;
			const itbisC = parseFloat(row.itbis_llevado_costo || row.itbis_costo || 0) || 0;
			let itbisPorAdelantarVal = row.itbis_por_adelantar != null && String(row.itbis_por_adelantar).trim() !== ''
				? parseFloat(row.itbis_por_adelantar)
				: Math.max(0, itbisF - itbisC);
			if (Number.isNaN(itbisPorAdelantarVal)) itbisPorAdelantarVal = 0;
			const itbisPorAdelantar = itbisPorAdelantarVal === 0 ? '0' : String(Number(Math.round(itbisPorAdelantarVal * 100) / 100));
			const itbisPercibido = formatValue(row.itbis_percibido_compras || row.itbis_percibido || 0, 'N', null, true);
		const tipoRetencionISR = formatValue(row.tipo_retencion_isr || row.retencion_isr || '', 'N', 2, true);
		const montoRetencionRenta = formatValue(row.monto_retencion_renta || row.monto_retencion || 0, 'N', null, true);
		const isrPercibido = formatValue(row.isr_percibido_compras || row.isr_percibido || 0, 'N', null, true);
		const impuestoSelectivo = formatValue(row.impuesto_selectivo_consumo || row.impuesto_selectivo || row.imp_isc || 0, 'N', null, true);
		const otrosImpuestos = formatValue(row.otros_impuestos_tasas || row.otros_impuestos || row.otros_ipm || 0, 'N', null, true);
			const montoPropina = formatValue(row.monto_propina_legal || row.monto_propina || 0, 'N', null, true);
			
			// Forma de Pago (código numérico de 2 dígitos, 1-7 según Norma 07-18). Default: 4 = Compra a Crédito
		const formaPago = formatValue(row.forma_pago || '4', 'N', 2, false);

			// Construir línea con 23 campos delimitados por pipe (formato DGII, sin Descuento)
			return [
				rnc,                    // 1. RNC o Cédula
				tipoId,                 // 2. Tipo Id
				tipoBienServicio,       // 3. Tipo Bienes y Servicios Comprados
				ncf,                    // 4. NCF
				ncfModificado,          // 5. NCF o Documento Modificado
				fechaComprobante,       // 6. Fecha Comprobante
				fechaPago,              // 7. Fecha Pago
				montoServiciosFormateado, // 8. Monto Facturado en Servicios
				montoBienesFormateado,   // 9. Monto Facturado en Bienes
				montoTotalFormateado,    // 10. Total Monto Facturado
				itbisFacturado,         // 11. ITBIS Facturado
				itbisRetenido,          // 12. ITBIS Retenido
				itbisProporcionalidad,  // 13. ITBIS sujeto a Proporcionalidad
				itbisLlevadoCosto,      // 14. ITBIS llevado al Costo
				itbisPorAdelantar,      // 15. ITBIS por Adelantar
				itbisPercibido,         // 16. ITBIS percibido en compras
				tipoRetencionISR,       // 17. Tipo de Retención en ISR
				montoRetencionRenta,    // 18. Monto Retención Renta
				isrPercibido,           // 19. ISR Percibido en compras
				impuestoSelectivo,      // 20. Impuesto Selectivo al Consumo
				otrosImpuestos,         // 21. Otros Impuestos/Tasas
				montoPropina,           // 22. Monto Propina Legal
				formaPago               // 23. Forma de Pago
			].join('|');
		});

		// Combinar encabezado y detalles
		const txtContent = [header, ...detailLines].join('\n');

		// Descargar archivo con nombre según formato DGII: DGII_F_606_RNC_PERIODO.TXT
		const rncArchivo = rncContribuyente || '00000000000';
		const periodoArchivo = periodoFormato || '000000';
		const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8;' });
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `DGII_F_${selectedFormulario.tipo}_${rncArchivo}_${periodoArchivo}.TXT`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	};

	// ── Conversión de moneda ────────────────────────────────────────────────
	// Campos monetarios que se convierten al cambiar la moneda
	const CAMPOS_MONETARIOS = [
		'monto_servicios', 'monto_bienes', 'monto_facturado', 'total_factura', 'monto_total',
		'itbis_facturado', 'itbis_retenido', 'itbis_proporcionalidad',
		'itbis_costo', 'itbis_llevado_costo',
		'itbis_adelantar', 'itbis_por_adelantar',
		'itbis_percibido', 'itbis_percibido_compras',
		'monto_retencion_renta',
		'isr_percibido', 'isr_percibido_compras',
		'impuesto_selectivo', 'impuesto_selectivo_consumo',
		'otros_impuestos', 'otros_impuestos_tasas',
		'monto_propina', 'monto_propina_legal',
	];

	const handleOpenCurrencyModal = (rowId, fromCurrency, toCurrency = 'DOP', mode = 'editing') => {
		// Ref para evitar stale state: al hacer clic en Aplicar, usar estos valores (React puede no haber actualizado pendingCurrencyChange aún)
		currencyConversionRef.current = { rowId, fromCurrency: fromCurrency || 'DOP', mode };
		setPendingCurrencyChange({ rowId, fromCurrency: fromCurrency || 'DOP', toCurrency: 'DOP', mode });
		setExchangeRateInput('');
		setShowCurrencyModal(true);
	};

	const handleApplyExchangeRate = async () => {
		const rate = parseFloat(exchangeRateInput);
		if (!rate || rate <= 0) {
			window.alert('Ingresa una tasa de cambio válida (número mayor a 0).');
			return;
		}
		if (!selectedFormulario) {
			window.alert('Espera a que termine de cargar el formulario antes de convertir.');
			setShowCurrencyModal(false);
			return;
		}

		// Usar ref para evitar stale state (primer clic puede leer estado anterior)
		const ctx = currencyConversionRef.current || pendingCurrencyChange;
		const rowId = ctx.rowId;
		const mode = ctx.mode || pendingCurrencyChange.mode;
		if (!rowId && rowId !== 0) {
			window.alert('No se pudo identificar el registro. Intenta de nuevo.');
			setShowCurrencyModal(false);
			setExchangeRateInput('');
			return;
		}

		// Usar actualizar_registro: actualiza in-place (sin duplicar) en datos_completados o datos_extraidos
		const useEditingData = mode === 'editing' && !!editingData;
		const allRows = getDataRows(selectedFormulario, useEditingData);
		const foundRow = allRows.find(r => r._rowId === rowId || r._id === rowId || String(r._rowId) === String(rowId) || String(r._id) === String(rowId));
		if (!foundRow) {
			window.alert('No se encontró el registro. Recarga la página e intenta de nuevo.');
			setShowCurrencyModal(false);
			setExchangeRateInput('');
			return;
		}
		const getValMonetario = (r, c) => {
			if (c === 'itbis_facturado') return parseFloat(r.itbis_facturado ?? r.itbis ?? 0) || 0;
			if (c === 'itbis_costo' || c === 'itbis_llevado_costo') return parseFloat(r.itbis_llevado_costo ?? r.itbis_costo ?? 0) || 0;
			// monto_servicios/monto_bienes: la tabla usa ambos nombres según el origen del dato
			if (c === 'monto_servicios') return parseFloat(r.monto_servicios ?? r.monto_facturado_servicios ?? 0) || 0;
			if (c === 'monto_bienes') return parseFloat(r.monto_bienes ?? r.monto_facturado_bienes ?? 0) || 0;
			// total_factura/monto_facturado/monto_total: distintos orígenes usan distintos nombres
			if (c === 'total_factura') return parseFloat(r.total_factura ?? r.monto_total ?? r.monto_facturado ?? 0) || 0;
			if (c === 'monto_facturado' || c === 'monto_total') return parseFloat(r[c] ?? r.total_factura ?? r.monto_total ?? r.monto_facturado ?? 0) || 0;
			// Variantes 606 vs 607
			if (c === 'impuesto_selectivo') return parseFloat(r.impuesto_selectivo ?? r.impuesto_selectivo_consumo ?? 0) || 0;
			if (c === 'impuesto_selectivo_consumo') return parseFloat(r.impuesto_selectivo_consumo ?? r.impuesto_selectivo ?? 0) || 0;
			if (c === 'itbis_percibido_compras') return parseFloat(r.itbis_percibido_compras ?? r.itbis_percibido ?? 0) || 0;
			return parseFloat(r[c] ?? 0) || 0;
		};
		try {
				const camposActualizar = { moneda: 'DOP' };
				CAMPOS_MONETARIOS.forEach(campo => {
					const val = getValMonetario(foundRow, campo);
					if (val !== 0) {
						const converted = Math.round(val * rate * 100) / 100;
						camposActualizar[campo] = converted;
						// Enviar también variantes de nombre para que backend y tabla muestren el valor
						if (campo === 'monto_servicios') camposActualizar.monto_facturado_servicios = converted;
						if (campo === 'monto_bienes') camposActualizar.monto_facturado_bienes = converted;
					}
				});
				// Incluir RNC editado para que el backend persista el cambio (evita duplicado original)
				const rncEditado = foundRow.rnc_emisor ?? foundRow.rnc_cedula ?? foundRow.rnc ?? '';
				if (rncEditado) {
					camposActualizar.rnc_emisor = rncEditado;
					camposActualizar.rnc_cedula = rncEditado;
				}
				let registroId = foundRow._id ?? foundRow._rowId;
				if (registroId != null) registroId = String(registroId).replace(/^row_/, '') || null;
				if (!registroId) registroId = foundRow.ncf || `ncf_${foundRow.rnc_emisor || foundRow.rnc_cedula || ''}_${foundRow.fecha_comprobante || ''}`;
				const camposBusqueda = {
					ncf: foundRow.ncf || '',
					rnc: foundRow.rnc_emisor || foundRow.rnc_cedula || foundRow.rnc || '',
					monto: parseFloat(foundRow.monto_facturado ?? foundRow.total_factura ?? foundRow.monto_total ?? 0) || 0,
					fecha: (foundRow.fecha_comprobante || foundRow.fecha || '').toString().split('T')[0].split(' ')[0] || ''
				};
				const resultado = await dgiiApi.actualizarRegistro(selectedFormulario.id, registroId || '', camposActualizar, camposBusqueda);
				if (resultado?.success) {
					let refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
					setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
					if (editingData) {
						// En modo edición: aplicar montos convertidos y evitar duplicado
						const clave = selectedFormulario.tipo === '606' ? 'compras' : 'ventas';
						const matchRow = (r) => r._rowId === rowId || r._id === rowId || String(r._rowId) === String(rowId) || String(r._id) === String(rowId);
						setEditingData(prev => {
							const applyTo = (list) => {
								if (!list) return list;
								return list.map(r => matchRow(r) ? { ...r, ...camposActualizar } : r);
							};
							// Aplicar montos en completados
							const newCompletados = { ...prev.datos_completados, [clave]: applyTo(prev.datos_completados?.[clave]) };
							// Quitar de extraidos los que ya están en completados (por _id O por NCF+fecha)
							// NCF+fecha es crítico cuando el registro editado (RNC) está en completados con _id distinto
							const idsCompletados = new Set((newCompletados[clave] || []).filter(r => r._id).map(r => String(r._id)));
							const ncfFechaCompletados = new Set(
								(newCompletados[clave] || [])
									.filter(r => (r.ncf || r.ncf_comprobante) && (r.fecha_comprobante || r.fecha))
									.map(r => `${normalizarNCF(r.ncf || r.ncf_comprobante || '')}|${formatDateForComparison(r.fecha_comprobante || r.fecha || '')}`)
							);
							const newExtraidos = {
								...prev.datos_extraidos,
								[clave]: (prev.datos_extraidos?.[clave] || [])
									.map(r => matchRow(r) ? { ...r, ...camposActualizar } : r)
									.filter(r => {
										if (r._id && idsCompletados.has(String(r._id))) return false;
										const ncfFecha = `${normalizarNCF(r.ncf || r.ncf_comprobante || '')}|${formatDateForComparison(r.fecha_comprobante || r.fecha || '')}`;
										if (ncfFechaCompletados.has(ncfFecha)) return false;
										return true;
									})
							};
							return { ...prev, datos_completados: newCompletados, datos_extraidos: newExtraidos };
						});
					}
				} else {
					window.alert(resultado?.error || 'Error al convertir moneda.');
				}
		} catch (e) {
			const msg = e.response?.data?.error || e.response?.data?.detail || e.message || 'Error';
			window.alert('Error al convertir moneda: ' + (typeof msg === 'string' ? msg : JSON.stringify(msg)));
		}
		setShowCurrencyModal(false);
		setPendingCurrencyChange({ rowId: null, fromCurrency: 'DOP', toCurrency: 'DOP', mode: 'editing' });
		setExchangeRateInput('');
		currencyConversionRef.current = null;
	};
	// ────────────────────────────────────────────────────────────────────────

	// Eliminar registro (guarda cambios en backend inmediatamente)
	// Acepta el row completo para poder hacer matching robusto por _id y NCF+fecha
	const handleDeleteRow = async (row) => {
		if (!selectedFormulario) return;
		if (!window.confirm('¿Estás seguro de que deseas eliminar este registro?')) return;

		// Normalizar: si se pasa un ID legacy en lugar de un row, envolver
		if (typeof row !== 'object' || row === null) {
			row = { _id: row, _rowId: row };
		}

		try {
			const tipo = selectedFormulario.tipo;
			const clave = tipo === '606' ? 'compras' : 'ventas';

			// Obtener datos actuales
			const datosCompletados = { ...(selectedFormulario.datos_completados || {}) };
			const datosExtraidos = { ...(selectedFormulario.datos_extraidos || {}) };

			// Normalizar el _id del row para comparación
			const rowIdNorm = row._id != null ? String(row._id).replace(/^row_/, '').trim() : null;
			const rowRowIdNorm = row._rowId != null ? String(row._rowId).replace(/^row_/, '').trim() : null;
			const rowNcf = (row.ncf || row.ncf_comprobante || '').replace(/-/g, '').replace(/\s/g, '').toUpperCase();
			const rowFecha = (row.fecha_comprobante || row.fecha || '').toString().slice(0, 10);

			// Matching robusto: _id primero, luego _rowId, luego NCF+fecha como último recurso
			const matchRow = (r) => {
				if (!r) return false;
				const rIdNorm = r._id != null ? String(r._id).replace(/^row_/, '').trim() : null;
				const rRowIdNorm = r._rowId != null ? String(r._rowId).replace(/^row_/, '').trim() : null;
				// 1) _id exacto (no undefined/null vs undefined/null)
				if (rowIdNorm && rIdNorm && rowIdNorm === rIdNorm) return true;
				// 2) _rowId
				if (rowRowIdNorm && rRowIdNorm && rowRowIdNorm === rRowIdNorm) return true;
				// 3) NCF + fecha (fallback)
				if (rowNcf) {
					const rNcf = (r.ncf || r.ncf_comprobante || '').replace(/-/g, '').replace(/\s/g, '').toUpperCase();
					const rFecha = (r.fecha_comprobante || r.fecha || '').toString().slice(0, 10);
					if (rNcf && rNcf === rowNcf && rFecha === rowFecha) return true;
				}
				return false;
			};

			// Usar el _extraida_id del row directamente (ya conocido)
			const extraidaId = row._extraida_id ?? row.extraida_id
				?? (datosCompletados[clave] || []).concat(datosExtraidos[clave] || []).find(r => matchRow(r))?._extraida_id;

			// Misma FacturaExtraida (duplicados OCR con NCF distinto / sin _extraida_id en una fila)
			const sameExtraidaId = (r) => {
				if (extraidaId == null || extraidaId === '') return false;
				const re = r._extraida_id ?? r.extraida_id;
				return re != null && re !== '' && String(re) === String(extraidaId);
			};
			const shouldRemoveRow = (r) => matchRow(r) || sameExtraidaId(r);
			
			// Eliminar de datos_completados
			if (datosCompletados[clave]) {
				datosCompletados[clave] = datosCompletados[clave].filter(r => !shouldRemoveRow(r));
			}

			// Eliminar de datos_extraidos
			if (datosExtraidos[clave]) {
				datosExtraidos[clave] = datosExtraidos[clave].filter(r => !shouldRemoveRow(r));
			}

			// Marcar FacturaExtraida como excluida para que no reaparezca en reconsolidaciones
			if (extraidaId) {
				try {
					await dgiiApi.actualizarEstadoFacturaExtraida(
						extraidaId,
						'excluida',
						'Eliminado manualmente por el usuario'
					);
				} catch (exclErr) {
				}
			}

			// FacturaManual en BD: soft-delete para que get_datos_completos / export no la reinyecten
			let manualIdParaEliminar = null;
			if (row._manual_id != null && row._manual_id !== '') {
				manualIdParaEliminar = row._manual_id;
			} else if (esRegistroManualRow(row) && row._id != null && /^\d+$/.test(String(row._id).trim())) {
				manualIdParaEliminar = row._id;
			}
			if (manualIdParaEliminar) {
				try {
					await dgiiApi.eliminarFacturaManual(manualIdParaEliminar);
				} catch (manErr) {
				}
			}
			
			// Guardar en backend inmediatamente
			await dgiiApi.updateFormulario(selectedFormulario.id, {
				datos_completados: datosCompletados,
				datos_extraidos: datosExtraidos,
				tipo: selectedFormulario.tipo,
				periodo: selectedFormulario.periodo
			});
			
			// Refrescar formulario completo desde backend y filtrar duplicados
			const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
			
			// Recargar facturas físicas para tabs de duplicados/faltantes
			const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
			setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
			
			// Limpiar edición
			setEditingData(null);
			setEditingRowIndex(null);
			
		} catch (err) {
			window.alert('Error al eliminar el registro: ' + (err.response?.data?.detail || err.message));
		}
	};

	// Funciones para procesar registros duplicados
	const handleConvertirAPrincipal = async (facturaId) => {
		if (!window.confirm('¿Está seguro de convertir este registro duplicado en el registro principal? El registro principal actual será marcado como duplicado.')) {
			return;
		}
		
		try {
			await dgiiApi.convertirDuplicadoAPrincipal(facturaId);
			
			// Recargar facturas físicas
			if (selectedFormulario) {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
				
				// Recargar formulario para actualizar datos_extraidos
				const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
				setSelectedFormulario(refreshed);
			}
			
			window.alert('✅ Registro convertido a principal exitosamente');
		} catch (err) {
			window.alert('Error al convertir a principal: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	const handleEliminarDuplicado = async (facturaId) => {
		if (!window.confirm('¿Está seguro de eliminar permanentemente este registro duplicado? Esta acción no se puede deshacer.')) {
			return;
		}
		
		try {
			await dgiiApi.eliminarDuplicado(facturaId);
			
			// Recargar facturas físicas
			if (selectedFormulario) {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
			}
			
			window.alert('✅ Registro duplicado eliminado exitosamente');
		} catch (err) {
			window.alert('Error al eliminar duplicado: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	const handleCambiarEstadoDuplicado = async (facturaId, nuevoEstado, motivo = '') => {
		try {
			await dgiiApi.actualizarEstadoFacturaExtraida(facturaId, nuevoEstado, motivo);
			
			// Recargar facturas físicas
			if (selectedFormulario) {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
			}
			
			window.alert('✅ Estado actualizado exitosamente');
		} catch (err) {
			window.alert('Error al actualizar estado: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Función para marcar factura como pendiente manualmente (exclusión mutua: también quita auditado del registro)
	const handleMarcarPendiente = async (facturaId) => {
		if (!window.confirm('¿Estás seguro de que deseas marcar esta factura como pendiente?')) {
			return;
		}
		try {
			await dgiiApi.actualizarEstadoFacturaExtraida(facturaId, 'pendiente', 'Marcado como pendiente manualmente');
			
			// Exclusión mutua: quitar auditado del registro asociado a esta factura
			if (selectedFormulario) {
				const rows = getDataRows(selectedFormulario, false);
				const row = rows.find(r => getFacturaIdFromRow(r, selectedFormulario, facturasFisicas) === facturaId);
				if (row) {
					const registroId = row._id ?? (typeof row._rowId === 'string' && row._rowId.startsWith('row_') ? row._rowId.replace('row_', '') : row._rowId);
					if (registroId) {
						try {
							const camposBusqueda = {
								ncf: row.ncf || '',
								rnc: row.rnc_emisor || row.rnc_cedula || row.rnc || '',
								monto: row.monto_facturado || row.total_factura || 0,
								fecha: row.fecha_comprobante || row.fecha || ''
							};
							await dgiiApi.actualizarRegistro(selectedFormulario.id, String(registroId), { auditado: false, _pendiente: true }, camposBusqueda);
						} catch (e) {
						}
					}
				}
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
				const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
				setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
			}
			loadFormularios(); // Recargar formularios para actualizar contadores
			
			window.alert('✅ Factura marcada como pendiente exitosamente');
		} catch (err) {
			window.alert('Error al marcar factura como pendiente: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Función para desmarcar factura como pendiente (marcar como incluida)
	const handleDesmarcarPendiente = async (facturaId) => {
		if (!window.confirm('¿Estás seguro de que deseas desmarcar esta factura como pendiente? Se marcará como incluida.')) {
			return;
		}
		try {
			await dgiiApi.actualizarEstadoFacturaExtraida(facturaId, 'incluida', 'Desmarcado como pendiente manualmente');
			
			// Limpiar el flag _pendiente en el JSON del registro asociado
			if (selectedFormulario) {
				const rows = getDataRows(selectedFormulario, false);
				const row = rows.find(r => getFacturaIdFromRow(r, selectedFormulario, facturasFisicas) === facturaId);
				if (row) {
					const registroId = row._id ?? row._rowId;
					if (registroId) {
						try {
							const camposBusqueda = {
								ncf: row.ncf || '',
								rnc: row.rnc_emisor || row.rnc_cedula || row.rnc || '',
								monto: row.monto_facturado || row.total_factura || 0,
								fecha: row.fecha_comprobante || row.fecha || ''
							};
							await dgiiApi.actualizarRegistro(selectedFormulario.id, String(registroId), { _pendiente: false }, camposBusqueda);
						} catch (e) {}
					}
				}
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
				const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
				setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
			}
			loadFormularios(); // Recargar formularios para actualizar contadores
			
			window.alert('✅ Factura desmarcada como pendiente exitosamente');
		} catch (err) {
			window.alert('Error al desmarcar factura como pendiente: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};

	// Marcar/desmarcar pendiente en registros manuales vía actualizar_registro o FacturaManual API
	const handleMarcarPendienteRegistro = async (row, desmarcar) => {
		const registroId = row._id ?? row._rowId;
		if (!registroId || !selectedFormulario) return;
		if (!window.confirm(desmarcar
			? '¿Desmarcar este registro como pendiente?'
			: '¿Marcar este registro como pendiente?')) {
			return;
		}
		try {
			const manualId = row._manual_id;
			if (manualId) {
				// Registro con FacturaManual en DB: actualizar estado_analisis directamente
				await dgiiApi.actualizarFacturaManual(manualId, {
					estado_analisis: desmarcar ? 'incluida' : 'pendiente',
					motivo_analisis: desmarcar ? 'Desmarcado como pendiente' : 'Marcado como pendiente manualmente',
					...(!desmarcar ? { auditado: false } : {}),
				});
			} else {
				// Registro legacy (sin _manual_id): usar JSON flag _pendiente
				const camposBusqueda = {
					ncf: row.ncf || row.ncf_comprobante || '',
					rnc: row.rnc_emisor || row.rnc_cedula || row.rnc || '',
					monto: row.monto_facturado || row.total_factura || 0,
					fecha: row.fecha_comprobante || row.fecha || ''
				};
				// Exclusión mutua: pendiente activo → auditado inactivo
				await dgiiApi.actualizarRegistro(
					selectedFormulario.id,
					String(registroId),
					{ _pendiente: !desmarcar, ...(!desmarcar ? { auditado: false } : {}) },
					camposBusqueda
				);
			}
			const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
			setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
			loadFormularios();
			window.alert(desmarcar ? '✅ Registro desmarcado como pendiente' : '✅ Registro marcado como pendiente');
		} catch (err) {
			window.alert('Error: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Comparar duplicado con principal
	const handleCompararConPrincipal = async (facturaId) => {
		try {
			const datos = await dgiiApi.compararDuplicadoConPrincipal(facturaId);
			setDatosComparacion(datos);
			setMostrarModalComparacion(true);
		} catch (err) {
			window.alert('Error al comparar: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Abrir modal de merge
	const handleAbrirModalMerge = async (facturaId) => {
		try {
			const datos = await dgiiApi.compararDuplicadoConPrincipal(facturaId);
			setDatosMerge({ ...datos, facturaId });
			setMostrarModalMerge(true);
		} catch (err) {
			window.alert('Error al obtener datos: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Ejecutar merge
	const handleEjecutarMerge = async (facturaId, camposSeleccionados, accionDuplicado) => {
		try {
			await dgiiApi.mergeDuplicadoConPrincipal(facturaId, camposSeleccionados, accionDuplicado);
			
			// Recargar facturas físicas
			if (selectedFormulario) {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
				
				// Recargar formulario y filtrar duplicados
				const refreshed = await dgiiApi.getFormulario(selectedFormulario.id);
				setSelectedFormulario(filtrarFormularioSinDuplicados(refreshed));
			}
			
			setMostrarModalMerge(false);
			setDatosMerge(null);
			window.alert('✅ Merge realizado exitosamente');
		} catch (err) {
			window.alert('Error al ejecutar merge: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Procesamiento en lote
	const handleProcesarLote = async (accion, nuevoEstado = null, motivo = '') => {
		if (duplicadosSeleccionados.size === 0) {
			window.alert('Por favor seleccione al menos un registro duplicado');
			return;
		}
		
		const confirmacion = window.confirm(
			`¿Está seguro de ${accion === 'eliminar' ? 'eliminar' : accion === 'convertir_a_principal' ? 'convertir a principal' : 'cambiar el estado de'} ${duplicadosSeleccionados.size} registro(s) duplicado(s)?`
		);
		
		if (!confirmacion) return;
		
		try {
			const facturaIds = Array.from(duplicadosSeleccionados);
			const resultado = await dgiiApi.procesarDuplicadosLote(facturaIds, accion, nuevoEstado, motivo);
			
			// Recargar facturas físicas
			if (selectedFormulario) {
				const facturasData = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
				setFacturasFisicas(Array.isArray(facturasData) ? facturasData : []);
			}
			
			setDuplicadosSeleccionados(new Set());
			window.alert(`✅ ${resultado.detail}`);
		} catch (err) {
			window.alert('Error al procesar lote: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Exportar duplicados
	const handleExportarDuplicados = async () => {
		if (!selectedFormulario) {
			window.alert('Por favor seleccione un formulario');
			return;
		}
		
		try {
			const blob = await dgiiApi.exportarDuplicados(selectedFormulario.id);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `duplicados_formulario_${selectedFormulario.id}.csv`;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
			window.alert('✅ Duplicados exportados exitosamente');
		} catch (err) {
			window.alert('Error al exportar: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Ver historial
	const handleVerHistorial = async (facturaId) => {
		try {
			const factura = await dgiiApi.getFacturaExtraida(facturaId);
			setHistorialFactura(factura);
			setMostrarModalHistorial(true);
		} catch (err) {
			window.alert('Error al obtener historial: ' + (err.response?.data?.error || err.response?.data?.detail || err.message));
		}
	};
	
	// Nueva función: Toggle selección para lote
	const handleToggleSeleccionDuplicado = (facturaId) => {
		const nuevosSeleccionados = new Set(duplicadosSeleccionados);
		if (nuevosSeleccionados.has(facturaId)) {
			nuevosSeleccionados.delete(facturaId);
		} else {
			nuevosSeleccionados.add(facturaId);
		}
		setDuplicadosSeleccionados(nuevosSeleccionados);
	};
	
	// Nueva función: Seleccionar todos los duplicados visibles
	const handleSeleccionarTodosDuplicados = () => {
		const duplicadosVisibles = facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'duplicada');
		const todosIds = new Set(duplicadosVisibles.map(f => f.id));
		setDuplicadosSeleccionados(todosIds);
	};
	
	// Nueva función: Limpiar selección
	const handleLimpiarSeleccionDuplicados = () => {
		setDuplicadosSeleccionados(new Set());
	};
	
	// Filtrar facturas duplicadas según filtros avanzados
	const getFacturasDuplicadasFiltradas = () => {
		let filtradas = facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'duplicada');
		
		if (filtroArchivo) {
			const archivoLower = filtroArchivo.toLowerCase();
			filtradas = filtradas.filter(f => {
				const archivoNombre = f.archivo_adjunto?.nombre_original || (typeof f.archivo_adjunto === 'string' ? f.archivo_adjunto : '');
				return archivoNombre.toLowerCase().includes(archivoLower);
			});
		}
		
		if (filtroMotivo) {
			const motivoLower = filtroMotivo.toLowerCase();
			filtradas = filtradas.filter(f => (f.motivo_analisis || '').toLowerCase().includes(motivoLower));
		}
		
		if (filtroFechaDesde) {
			filtradas = filtradas.filter(f => {
				if (!f.fecha_extraccion) return false;
				const fechaFactura = new Date(f.fecha_extraccion);
				const fechaDesde = new Date(filtroFechaDesde);
				return fechaFactura >= fechaDesde;
			});
		}
		
		if (filtroFechaHasta) {
			filtradas = filtradas.filter(f => {
				if (!f.fecha_extraccion) return false;
				const fechaFactura = new Date(f.fecha_extraccion);
				const fechaHasta = new Date(filtroFechaHasta);
				fechaHasta.setHours(23, 59, 59, 999); // Incluir todo el día
				return fechaFactura <= fechaHasta;
			});
		}
		
		return filtradas;
	};

	// Paginación
	const getPaginatedRows = (rows) => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return rows.slice(startIndex, endIndex);
	};

	// Evitar recalcular filas en cada render (era una causa de lentitud/cuelgues). Try/catch evita pantalla en blanco si getDataRows falla.
	const dataRowsSelected = useMemo(() => {
		try {
			const rows = getDataRows(selectedFormulario, false);
			// Enriquecer con datos_estructurados de FacturaExtraida para campos alias que
			// pudieron haberse mapeado como 0 (ej. otros_imp → otros_impuestos_tasas).
			if (!facturasFisicas || facturasFisicas.length === 0) return rows;
			return rows.map(row => {
				const extraidaId = row._extraida_id ?? row.extraida_id;
				if (!extraidaId) return row;
				const factura = facturasFisicas.find(f => String(f.id) === String(extraidaId));
				const d = factura?.datos_estructurados;
				if (!d) return row;
				const enrichments = {};
			// otros_impuestos_tasas (campo 21): solo enriquecer si ausente (no sobrescribir 0 explícito)
			if (row.otros_impuestos_tasas == null || row.otros_impuestos_tasas === '') {
				const v = d.otros_impuestos_tasas || d.otros_impuestos || d.otros_ipm || d.otros_imp;
				if (v != null && v !== '') enrichments.otros_impuestos_tasas = v;
			}
				// impuesto_selectivo_consumo (campo 20): solo enriquecer si ausente (no sobrescribir 0 explícito)
				if (row.impuesto_selectivo_consumo == null || row.impuesto_selectivo_consumo === '') {
					const v = d.impuesto_selectivo_consumo || d.impuesto_selectivo || d.imp_isc;
					if (v != null && v !== '') enrichments.impuesto_selectivo_consumo = v;
				}
				if (Object.keys(enrichments).length === 0) return row;
				return { ...row, ...enrichments };
			});
		} catch (e) {
			return [];
		}
	}, [selectedFormulario, facturasFisicas, tablaFiltros]);
	const dataRowsSelectedEditing = useMemo(() => {
		try {
			const rows = getDataRows(selectedFormulario, true);
			if (!facturasFisicas || facturasFisicas.length === 0) return rows;
			return rows.map(row => {
				const extraidaId = row._extraida_id ?? row.extraida_id;
				if (!extraidaId) return row;
				const factura = facturasFisicas.find(f => String(f.id) === String(extraidaId));
				const d = factura?.datos_estructurados;
				if (!d) return row;
				const enrichments = {};
				if (row.otros_impuestos_tasas == null || row.otros_impuestos_tasas === '') {
					const v = d.otros_impuestos_tasas || d.otros_impuestos || d.otros_ipm || d.otros_imp;
					if (v != null && v !== '') enrichments.otros_impuestos_tasas = v;
				}
				if (row.impuesto_selectivo_consumo == null || row.impuesto_selectivo_consumo === '') {
					const v = d.impuesto_selectivo_consumo || d.impuesto_selectivo || d.imp_isc;
					if (v != null && v !== '') enrichments.impuesto_selectivo_consumo = v;
				}
				if (Object.keys(enrichments).length === 0) return row;
				return { ...row, ...enrichments };
			});
		} catch (e) {
			return [];
		}
	}, [selectedFormulario, editingData, facturasFisicas, tablaFiltros]);

	const getSortValue = (row, key) => {
		let v = row[key];
		if (key === 'monto_facturado' && (v == null || v === '') && row.total_factura != null) v = row.total_factura;
		if (key === 'monto_servicios' && (v == null || v === '') && row.monto_facturado_servicios != null) v = row.monto_facturado_servicios;
		if (key === 'monto_bienes' && (v == null || v === '') && row.monto_facturado_bienes != null) v = row.monto_facturado_bienes;
		if (key === 'fecha_comprobante' || key === 'fecha_pago') {
			const d = getNormalizedDate(v);
			return d ? new Date(d).getTime() : 0;
		}
		if (typeof v === 'number' && !Number.isNaN(v)) return v;
		const n = parseFloat(v);
		if (!Number.isNaN(n)) return n;
		return (v != null && v !== '') ? String(v) : '';
	};

	const dataRowsSorted = useMemo(() => {
		if (!tableSortKey) return dataRowsSelected;
		const arr = [...dataRowsSelected];
		const dir = tableSortDir === 'asc' ? 1 : -1;
		arr.sort((a, b) => {
			const va = getSortValue(a, tableSortKey);
			const vb = getSortValue(b, tableSortKey);
			if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
			return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
		});
		return arr;
	}, [dataRowsSelected, tableSortKey, tableSortDir]);

	// Al filtrar la tabla de registros, volver a página 1 para no mostrar filas vacías si la página actual queda fuera de rango
	useEffect(() => {
		setCurrentPage(1);
	}, [
		tablaFiltros.busqueda,
		tablaFiltros.forma_pago,
		tablaFiltros.tipo_bien_servicio,
		tablaFiltros.estado,
		tablaFiltros.monto_min,
		tablaFiltros.monto_max,
	]);

	// Mapa rowKey → facturaId: usa getFacturaIdFromRow para consistencia (incluye _id, NCF+monto para registros movidos)
	const facturaIdByRowId = useMemo(() => {
		const map = new Map();
		if (!dataRowsSelected || dataRowsSelected.length === 0) return map;
		// Si hay facturas, usar getFacturaIdFromRow (misma lógica que el botón) para incluir registros movidos
		if (facturasFisicas && facturasFisicas.length > 0) {
			dataRowsSelected.forEach((row, idx) => {
				const rowKey = row._id ?? row._rowId;
				if (!rowKey) return;
				const facturaId = getFacturaIdFromRow(row, selectedFormulario, facturasFisicas, idx, dataRowsSelected);
				if (facturaId) map.set(rowKey, facturaId);
			});
		}
		if (facturasFisicas && facturasFisicas.length > 0) {
			if (map.size === 0) {
			}
		}
		return map;
	}, [dataRowsSelected, facturasFisicas, selectedFormulario]);

	const dataRowsSortedEditing = useMemo(() => {
		if (!tableSortKey) return dataRowsSelectedEditing;
		const arr = [...dataRowsSelectedEditing];
		const dir = tableSortDir === 'asc' ? 1 : -1;
		arr.sort((a, b) => {
			const va = getSortValue(a, tableSortKey);
			const vb = getSortValue(b, tableSortKey);
			if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
			return String(va).localeCompare(String(vb), undefined, { numeric: true }) * dir;
		});
		return arr;
	}, [dataRowsSelectedEditing, tableSortKey, tableSortDir]);

	const handleTableSort = (key) => {
		setTableSortKey(prev => {
			if (prev === key) {
				setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
				return key;
			}
			setTableSortDir('asc');
			return key;
		});
	};

	const totalPages = Math.ceil(dataRowsSelected.length / itemsPerPage);

	// t3: usar caché de restauración (actualizada en useEffect), no leer sessionStorage en cada render
	const storedFormularioIdForRestore = cachedRestoreData.formularioId;
	const cachedStatsForRestore = cachedRestoreData.stats;
	const hasCachedStats = !!(dgiiAdminStats || cachedStatsForRestore);
	const showMainArea = !!selectedFormulario || !!storedFormularioIdForRestore || !!dgiiAdminFormularioId || hasCachedStats;
	const isRestoring = !selectedFormulario && (!!storedFormularioIdForRestore || !!dgiiAdminFormularioId || hasCachedStats);
	const statsToShow = dgiiAdminStats || cachedStatsForRestore;

	return (
		<>
		<div className="min-h-screen bg-gray-50 flex flex-col">
			<div className="w-full max-w-[1920px] mx-auto px-3 sm:px-4 lg:px-6 flex-1 flex flex-col">
				{/* Header: título + botón Filtros (abre modal) */}
				<div className="mb-3 pt-2 sm:pt-4 flex flex-wrap items-center justify-between gap-2">
					<div>
						<h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
							Gestión 606
						</h1>
						<p className="text-sm text-gray-600">
							Administración y gestión de formularios 606. Visualiza, edita y exporta los registros de tus formularios.
						</p>
					</div>
					<div className="flex items-center gap-2 flex-wrap">
						<button
							onClick={() => setShowFormulariosSection(prev => !prev)}
							className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
								showFormulariosSection
									? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
									: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-primary'
							}`}
							title={showFormulariosSection ? 'Ocultar listado de formularios' : 'Mostrar listado de formularios'}
						>
							{showFormulariosSection ? (
								<>
									<ChevronDownIcon className="h-4 w-4" />
									<span>Ocultar formularios</span>
								</>
							) : (
								<>
									<ChevronRightIcon className="h-4 w-4" />
									<span>Formularios</span>
								</>
							)}
						</button>
						<button
							onClick={() => setShowFiltersModal(true)}
							className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-primary transition-colors"
							title="Abrir filtros"
						>
							<FunnelIcon className="h-4 w-4" />
							<span>Filtros</span>
							{getActiveFilters().length > 0 && (
								<span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-primary/20 text-primary">
									{getActiveFilters().length}
								</span>
							)}
						</button>
					</div>
				</div>
				
				{/* Alertas API */}
				<div className="mb-3">
					<AlertasAPI />
				</div>

				{/* Modal de Filtros */}
				{showFiltersModal && (
					<div
				className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4"
					onClick={() => setShowFiltersModal(false)}
					>
						<div
							className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
								<h2 className="text-sm font-bold text-gray-800">Filtros</h2>
								<div className="flex items-center gap-2">
									<button
										onClick={limpiarTodosLosFiltros}
										className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-primary hover:bg-gray-100 rounded transition-colors"
									>
										Limpiar todo
									</button>
									<button
										onClick={() => setShowFiltersModal(false)}
										className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
										title="Cerrar"
									>
										<XMarkIcon className="h-5 w-5" />
									</button>
								</div>
							</div>
							<div className="p-4 space-y-4">
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">Búsqueda</label>
									<input
										type="text"
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										placeholder="Buscar por tipo, período, estado, empresa..."
										className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
									/>
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">Tiempo</label>
									<div className="flex flex-wrap gap-1.5">
										<button
											onClick={() => setQuickFilters(prev => ({ ...prev, ultimoMes: !prev.ultimoMes }))}
											className={`px-2 py-1 text-xs whitespace-nowrap rounded-lg border transition-all ${
												quickFilters.ultimoMes ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
											}`}
										>Último mes</button>
										<button
											onClick={() => setQuickFilters(prev => ({ ...prev, mesAnterior: !prev.mesAnterior }))}
											className={`px-2 py-1 text-xs whitespace-nowrap rounded-lg border transition-all ${
												quickFilters.mesAnterior ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
											}`}
										>Mes anterior</button>
										<button
											onClick={() => setQuickFilters(prev => ({ ...prev, esteAno: !prev.esteAno }))}
											className={`px-2 py-1 text-xs whitespace-nowrap rounded-lg border transition-all ${
												quickFilters.esteAno ? 'bg-primary text-white border-primary shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-primary'
											}`}
										>Este año</button>
									</div>
								</div>
								<div>
									<label className="block text-xs font-medium text-gray-700 mb-1">Estado (rápido)</label>
									<div className="flex gap-1.5">
										<button
											onClick={() => {
												const nuevoEstado = quickFilters.abiertos ? false : true;
												setQuickFilters(prev => ({ ...prev, abiertos: nuevoEstado, cerrados: false }));
											}}
											className={`flex-1 px-2 py-1 text-xs whitespace-nowrap rounded-lg border transition-all ${
												quickFilters.abiertos ? 'bg-green-500 text-white border-green-500 shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-green-500'
											}`}
										>Abiertos</button>
										<button
											onClick={() => {
												const nuevoEstado = quickFilters.cerrados ? false : true;
												setQuickFilters(prev => ({ ...prev, cerrados: nuevoEstado, abiertos: false }));
											}}
											className={`flex-1 px-2 py-1 text-xs whitespace-nowrap rounded-lg border transition-all ${
												quickFilters.cerrados ? 'bg-gray-600 text-white border-gray-600 shadow-sm' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-600'
											}`}
										>Cerrados</button>
									</div>
								</div>
								<div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-1">Empresa</label>
										<select
											value={filtros.empresa}
											onChange={(e) => setFiltros({ ...filtros, empresa: e.target.value })}
											className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										>
											<option value="">Todas las empresas</option>
											{empresas.map((empresa) => (
												<option key={empresa.id} value={empresa.id}>{empresa.nombre} ({empresa.rnc})</option>
											))}
										</select>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-1">Período</label>
										<input
											type="text"
											value={filtros.periodo}
											onChange={(e) => setFiltros({ ...filtros, periodo: e.target.value })}
											placeholder="Ej: 2025-10"
											className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										/>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-1">Tipo</label>
										<select
											value={filtros.tipo}
											onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value })}
											className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										>
											<option value="">Todos</option>
											<option value="606">606</option>
											<option value="607">607</option>
										</select>
									</div>
									<div>
										<label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
										<select
											value={filtros.estado}
											onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
											className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										>
											<option value="">Todos</option>
											<option value="abierto">Abierto</option>
											<option value="cerrado">Cerrado</option>
										</select>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Sección de Formularios - Visible solo si showFormulariosSection */}
				{showFormulariosSection && (
				<div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
					{/* Barra superior: título + chevron (expandir/colapsar grid); botón ocultar */}
					<div className="flex items-center gap-2 w-full px-4 sm:px-6 lg:px-8 py-3 bg-white border-b border-gray-100">
						<button
							type="button"
							onClick={() => setFormulariosGridCollapsed(prev => !prev)}
							className="flex flex-wrap items-center justify-between gap-2 text-left hover:bg-gray-50/80 transition-colors rounded-lg py-1 pr-2 -ml-1 flex-1 min-w-0"
						>
							<div className="flex items-center gap-2">
								{formulariosGridCollapsed ? (
									<ChevronRightIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
								) : (
									<ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
								)}
								<span className="text-sm font-bold text-gray-800 uppercase tracking-wide">
									Formularios ({formularios.length} {formularios.length === 1 ? 'formulario' : 'formularios'})
								</span>
							</div>
						{!formulariosGridCollapsed && (
							<div className="flex items-center gap-2 text-xs text-gray-600" onClick={(e) => e.stopPropagation()}>
								<span>Ordenar por:</span>
								<button
									onClick={(e) => {
										e.stopPropagation();
										const nextOrder = sortBy === 'periodo' ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'desc';
										setSortBy('periodo'); setSortOrder(nextOrder); setFormulariosPage(1);
									}}
									className={`px-2 py-1 rounded ${sortBy === 'periodo' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
								>
									Período {sortBy === 'periodo' && (sortOrder === 'desc' ? '↓' : '↑')}
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation();
										const nextOrder = sortBy === 'fecha' ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'desc';
										setSortBy('fecha'); setSortOrder(nextOrder); setFormulariosPage(1);
									}}
									className={`px-2 py-1 rounded ${sortBy === 'fecha' ? 'bg-primary text-white' : 'bg-gray-100 hover:bg-gray-200'}`}
								>
									Fecha {sortBy === 'fecha' && (sortOrder === 'desc' ? '↓' : '↑')}
								</button>
							</div>
						)}
					</button>
						<button
							type="button"
							onClick={() => setShowFormulariosSection(false)}
							className="flex-shrink-0 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
							title="Ocultar listado de formularios"
						>
							Ocultar
						</button>
					</div>
					{!formulariosGridCollapsed && (
					<div className="px-4 sm:px-6 lg:px-8 pb-3 pt-0">
							{loading ? (
								<div className="text-sm text-gray-500 py-4 text-center">Cargando...</div>
							) : formularios.length === 0 ? (
								<div className="text-sm text-gray-500 py-4 text-center">
									{searchTerm || filtros.empresa || filtros.usuario || filtros.periodo || filtros.tipo || filtros.estado
										? 'No se encontraron formularios'
										: 'No hay formularios'}
								</div>
							) : (
								<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
									<table className="min-w-full divide-y divide-gray-200">
										<thead className="bg-gray-50">
											<tr>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Empresa</th>
												<th
													className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
													onClick={() => handleSortFormularios('periodo')}
													title="Ordenar por período (clic para alternar)"
												>
													<span className="inline-flex items-center gap-1">Período {sortBy === 'periodo' && (sortOrder === 'desc' ? <ArrowDownIcon className="h-3.5 w-3.5" /> : <ArrowUpIcon className="h-3.5 w-3.5" />)}</span>
												</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Estado</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Archivos</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Facturas</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Registros</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Pendientes</th>
												<th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Después Cierre</th>
												<th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Acciones</th>
											</tr>
										</thead>
										<tbody className="bg-white divide-y divide-gray-200">
											{getFilteredAndPaginatedFormularios().map((form) => {
												const numArchivos = form.archivos_adjuntos?.length || 0;
												let numFacturas = 0;
												if (form.archivos_adjuntos) {
													for (const archivo of form.archivos_adjuntos) {
														if (archivo.facturas_extraidas && Array.isArray(archivo.facturas_extraidas)) {
															const facturasDelFormulario = archivo.facturas_extraidas.filter(factura => {
																const facturaFormId = typeof factura.formulario === 'object' 
																	? factura.formulario?.id 
																	: factura.formulario;
																return facturaFormId === form.id;
															});
															numFacturas += facturasDelFormulario.length;
														}
													}
												}
												const numRegistros = getDataRows(form).length;
												const numPendientes = contarFacturasPendientes(form);
												const numDespuesCierre = form.facturas_despues_cierre || 0;
												
												return (
													<tr
														key={form.id}
														onClick={() => handleSelectFormulario(form.id)}
														className={`cursor-pointer transition-colors ${
															selectedFormulario?.id === form.id
																? 'bg-primary/10 hover:bg-primary/15'
																: 'hover:bg-gray-50'
														}`}
													>
														<td className="px-4 py-3">
															<span className="text-sm font-semibold text-gray-900">
																{form.empresa_nombre || form.usuario_nombre || 'N/A'}
															</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className="text-sm text-gray-700">{form.periodo}</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className={`px-2 py-1 rounded text-xs font-semibold ${
																form.estado === 'abierto' 
																	? 'bg-green-100 text-green-700' 
																	: 'bg-gray-100 text-gray-700'
															}`}>
																{form.estado === 'abierto' ? '✓ Abierto' : '🔒 Cerrado'}
															</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className="text-sm text-gray-600">{numArchivos}</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className="text-sm text-gray-600">{numFacturas}</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className="text-sm text-gray-600">{numRegistros}</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className={`text-sm font-semibold ${
																numPendientes > 0 ? 'text-yellow-600' : 'text-gray-600'
															}`}>
																{numPendientes > 0 ? `⚠️ ${numPendientes}` : numPendientes}
															</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap">
															<span className={`text-sm font-semibold ${
																numDespuesCierre > 0 ? 'text-red-600' : 'text-gray-600'
															}`}>
																{numDespuesCierre > 0 ? `🔴 ${numDespuesCierre}` : numDespuesCierre}
															</span>
														</td>
														<td className="px-4 py-3 whitespace-nowrap text-right">
															{isAdmin && (
																<button
																	onClick={(e) => {
																		e.stopPropagation();
																		setFormularioToDelete(form);
																		setShowDeleteModal(true);
																	}}
																	className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
																	title="Eliminar formulario"
																>
																	<TrashIcon className="h-4 w-4" />
																</button>
															)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
									{/* Paginación de formularios */}
									{formularios.length > 0 && (
										<div className="flex flex-col gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
											{/* Primera fila: Información y selector */}
											<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
												<div className="text-xs sm:text-sm text-gray-600">
													Mostrando {((formulariosPage - 1) * formulariosPerPage) + 1} - {Math.min(formulariosPage * formulariosPerPage, formularios.length)} de {formularios.length} formularios
												</div>
												<div className="flex items-center gap-2">
													<label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
														Formularios por página:
													</label>
													<select
														value={formulariosPerPage}
														onChange={(e) => {
															setFormulariosPerPage(Number(e.target.value));
															setFormulariosPage(1); // Resetear a página 1
														}}
														className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
													>
														<option value={5}>5</option>
														<option value={10}>10</option>
														<option value={20}>20</option>
														<option value={50}>50</option>
														<option value={100}>100</option>
													</select>
												</div>
											</div>
											{/* Segunda fila: Navegación */}
											{totalFormulariosPages > 1 && (
												<div className="flex items-center justify-center gap-2">
													<button
														onClick={() => setFormulariosPage(prev => Math.max(1, prev - 1))}
														disabled={formulariosPage === 1}
														className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
														title="Página anterior"
													>
														<ArrowLeftIcon className="h-4 w-4" />
													</button>
													<span className="text-xs sm:text-sm text-gray-700 font-medium px-2 sm:px-3">
														Página {formulariosPage} de {totalFormulariosPages}
													</span>
													<button
														onClick={() => setFormulariosPage(prev => Math.min(totalFormulariosPages, prev + 1))}
														disabled={formulariosPage === totalFormulariosPages}
														className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
														title="Página siguiente"
													>
														<ArrowRightIcon className="h-4 w-4" />
													</button>
												</div>
											)}
										</div>
									)}
								</div>
							)}
					</div>
					)}
				</div>
				)}

				{/* Área de contenido principal: mostrar también panel con stats en caché mientras se restaura (t3) */}
				{showMainArea ? (
						<>
							{isRestoring ? (
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
									{dgiiAdminPeriodoLabel && (
										<h2 className="text-lg font-bold text-gray-900 mb-1">{dgiiAdminPeriodoLabel}</h2>
									)}
									<div className="flex items-center gap-2 text-gray-500 mb-3">
										<ArrowPathIcon className="h-5 w-5 animate-spin" />
										<span>Restaurando formulario...</span>
									</div>
									{statsToShow && (
										<div className={`mt-3 p-3 rounded-lg border text-xs ${statsToShow.hayDiscrepancia ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
											<div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
												<InformationCircleIcon className="h-4 w-4" />
												Diagnóstico de Datos
											</div>
											<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-gray-600">
												<div>
													<div className="font-medium text-gray-700">Archivos Físicos</div>
													<div className="text-lg font-bold text-primary">{statsToShow.numArchivosFisicos ?? 0}</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Facturas Extraídas</div>
													<div className="text-lg font-bold text-blue-600">{statsToShow.numFacturasExtraidas ?? 0}</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Registros en Tabla</div>
													<div className={`text-lg font-bold ${statsToShow.hayDiscrepancia ? 'text-red-600' : 'text-green-600'}`}>
														{statsToShow.numRegistros ?? 0}
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Registros Auditados</div>
													<div className="text-lg font-bold text-gray-700">{statsToShow.registrosAuditados ?? 0}</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Desglose</div>
													<div className="text-xs text-gray-600">
														Extraídos: {statsToShow.numRegistrosExtraidos ?? 0} · Completados: {statsToShow.numRegistrosCompletados ?? 0}
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Facturas Pendientes</div>
													<div className="text-lg font-bold text-gray-700">{statsToShow.numPendientes ?? 0}</div>
												</div>
											</div>
										</div>
									)}
								</div>
							) : (
						<>
							{/* Header del formulario */}
							<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
								<div className="flex items-start justify-between mb-3">
									<div>
										<h2 className="text-xl font-bold text-gray-900 mb-1">
											Formulario {selectedFormulario?.tipo ?? '—'} - {selectedFormulario?.periodo ?? '—'}
										</h2>
										{(selectedFormulario?.empresa_nombre || selectedFormulario?.usuario_nombre) && (
											<p className="text-sm font-medium text-gray-700 mb-2">
												{selectedFormulario.empresa_nombre || selectedFormulario.usuario_nombre}
											</p>
										)}
										<div className="flex items-center gap-3 text-sm text-gray-600">
											<span className="flex items-center gap-1">
												Estado: <span className={`font-medium ${selectedFormulario.estado === 'abierto' ? 'text-green-600' : 'text-red-600'}`}>{selectedFormulario.estado}</span>
											</span>
											{selectedFormulario.usuario_nombre && (
												<span className="text-gray-400">•</span>
											)}
										{selectedFormulario.usuario_nombre && (
											<span>Propietario: {selectedFormulario.usuario_nombre}</span>
										)}
										</div>
									</div>
									{/* Botones de acción */}
									{!editingData && (
										<div className="flex items-center gap-2 flex-wrap">
											<button
												onClick={() => setShowStatsPanel(prev => !prev)}
												className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
													showStatsPanel
														? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20'
														: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-primary'
												}`}
												title={showStatsPanel ? 'Ocultar estadísticas' : 'Mostrar estadísticas'}
											>
												<ChartBarIcon className="h-4 w-4" />
												<span className="hidden sm:inline">{showStatsPanel ? 'Ocultar estadísticas' : 'Estadísticas'}</span>
											</button>
											{/* Botones de exportación */}
											<div className="relative group">
												<button
													className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
													title="Exportar"
												>
													<ArrowDownTrayIcon className="h-4 w-4" />
													<span className="hidden sm:inline">Exportar</span>
												</button>
												<div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
													<button
														onClick={handleExportExcel}
														className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center gap-2"
													>
														<DocumentIcon className="h-4 w-4" />
														Exportar Excel
													</button>
													<button
														onClick={handleExportCSV}
														className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
													>
														<DocumentTextIcon className="h-4 w-4" />
														Exportar CSV
													</button>
													<button
														onClick={handleExportTXT}
														disabled={!todosLosRegistrosAuditados(selectedFormulario)}
														title={todosLosRegistrosAuditados(selectedFormulario) ? 'Exportar TXT (no se incluyen registros pendientes)' : 'Cada registro debe estar auditado o marcado como pendiente para exportar TXT'}
														className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 rounded-b-lg ${todosLosRegistrosAuditados(selectedFormulario) ? 'text-gray-700 hover:bg-gray-50' : 'text-gray-400 cursor-not-allowed bg-gray-50'}`}
													>
														<DocumentTextIcon className="h-4 w-4" />
														Exportar TXT
													</button>
												</div>
											</div>
											<button
												onClick={async () => {
													if (selectedFormulario) {
														try {
															// 1. Reconsolidar primero: reconstruye datos_extraidos desde FacturaExtraida
															//    (recupera registros de periodos cruzados, registros mal-asignados, etc.)
															await dgiiApi.reconsolidarFormulario(selectedFormulario.id);
															// 2. Luego refrescar para obtener los datos actualizados
															const refreshed = await dgiiApi.refreshFormulario(selectedFormulario.id);
															setSelectedFormulario(refreshed);
															// Recargar facturas físicas para que tabs de duplicados/faltantes/errores se actualicen
															const dataFisicas = await dgiiApi.getFacturasFisicas(refreshed.id);
															setFacturasFisicas(Array.isArray(dataFisicas) ? dataFisicas : []);
															setCurrentPage(1);
															window.alert('Datos actualizados correctamente');
														} catch (err) {
															window.alert('Error al actualizar los datos.');
														}
													}
												}}
												className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
											>
												<ArrowPathIcon className="h-4 w-4" />
												<span className="hidden sm:inline">Actualizar</span>
											</button>
											{selectedFormulario.estado === 'abierto' ? (
												<button
													onClick={handleCerrarFormulario}
													disabled={!todosLosRegistrosAuditados(selectedFormulario)}
													className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
														todosLosRegistrosAuditados(selectedFormulario)
															? 'text-white bg-red-600 hover:bg-red-700'
															: 'text-gray-400 bg-gray-300 cursor-not-allowed'
													}`}
													title={
														todosLosRegistrosAuditados(selectedFormulario)
															? "Cerrar formulario"
															: "Cada registro debe estar auditado o marcado como pendiente para cerrar"
													}
												>
													<LockClosedIcon className="h-4 w-4" />
													<span className="hidden sm:inline">Cerrar</span>
												</button>
											) : (
												<button
													onClick={handleAbrirFormulario}
													className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
													title="Abrir formulario"
												>
													<LockOpenIcon className="h-4 w-4" />
													<span className="hidden sm:inline">Abrir</span>
												</button>
											)}
										</div>
									)}
								</div>
								
								{/* Panel de diagnóstico de números (ocultable con showStatsPanel) */}
								{showStatsPanel && statsDiagnostico && (
										<div className={`mt-3 p-3 rounded-lg border text-xs ${statsDiagnostico.hayDiscrepancia ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'}`}>
											<div className="font-semibold text-gray-700 mb-2 flex items-center gap-2 flex-wrap">
												<InformationCircleIcon className="h-4 w-4 flex-shrink-0" />
												<span>Diagnóstico de Datos</span>
												{selectedFormulario && (
													<button
														type="button"
														onClick={async () => {
															if (!selectedFormulario) return;
															try {
																setLoadingFacturasFisicas(true);
																const data = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
																setFacturasFisicas(Array.isArray(data) ? data : []);
																setShowFacturasFisicas(true);
															} catch (err) {
																window.alert('No se pudieron cargar las facturas físicas. Revisa la consola para más detalles.');
															} finally {
																setLoadingFacturasFisicas(false);
															}
														}}
														className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border border-primary/30 text-primary bg-white hover:bg-primary/5 transition-colors"
													>
														<PaperClipIcon className="h-3 w-3" />
														Ver facturas físicas
													</button>
												)}
												<button
													type="button"
													onClick={() => setShowStatsPanel(false)}
													className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-200 transition-colors"
													title="Ocultar estadísticas"
												>
													Ocultar
												</button>
											</div>
											<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-gray-600">
												<div>
													<div className="font-medium text-gray-700">Archivos Físicos</div>
													<div className="text-lg font-bold text-primary">{statsDiagnostico.numArchivosFisicos}</div>
													{statsDiagnostico.numFacturasExtraidas > 0 && (
														<div className="mt-1 text-xs text-gray-500 space-y-0.5">
															{statsDiagnostico.facturasPorTipo.individual > 0 && (
																<div>• {statsDiagnostico.facturasPorTipo.individual} facturas (individual)</div>
															)}
															{statsDiagnostico.facturasPorTipo.masiva > 0 && (
																<div>• {statsDiagnostico.facturasPorTipo.masiva} facturas (masiva)</div>
															)}
														</div>
													)}
												</div>
												<div>
													<div className="font-medium text-gray-700">Facturas Extraídas</div>
													<div className="text-lg font-bold text-blue-600">{statsDiagnostico.numFacturasExtraidas}</div>
													<div className="text-xs text-gray-500 mt-1">
														(En FacturaExtraida)
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Registros en Tabla</div>
													<div className={`text-lg font-bold ${statsDiagnostico.hayDiscrepancia ? 'text-red-600' : 'text-green-600'}`}>
														{statsDiagnostico.numRegistros}
													</div>
													<div className="text-xs text-gray-500 mt-1">
														(datos_extraidos/completados)
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Registros Auditados</div>
													<div className={`text-lg font-bold ${statsDiagnostico.registrosAuditados === statsDiagnostico.numRegistros && statsDiagnostico.numRegistros > 0 ? 'text-green-600' : statsDiagnostico.registrosAuditados > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
														{statsDiagnostico.registrosAuditados}
													</div>
													<div className="text-xs text-gray-500 mt-1">
														{statsDiagnostico.numRegistros > 0 ? `${Math.round((statsDiagnostico.registrosAuditados / statsDiagnostico.numRegistros) * 100)}% completado` : 'Sin registros'}
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Desglose</div>
													<div className="text-xs text-gray-600 space-y-0.5">
														<div>Extraídos: {statsDiagnostico.numRegistrosExtraidos}</div>
														<div>Completados: {statsDiagnostico.numRegistrosCompletados}</div>
														{statsDiagnostico.hayDiscrepancia && (
															<div className="text-red-600 font-medium mt-1">
																⚠️ Faltan {statsDiagnostico.faltantesCalculados} registro(s)
															</div>
														)}
													</div>
												</div>
												<div>
													<div className="font-medium text-gray-700">Facturas Pendientes</div>
													<div className={`text-lg font-bold ${statsDiagnostico.numPendientes > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
														{statsDiagnostico.numPendientes}
													</div>
													<div className="text-xs text-gray-500 mt-1">
														Requieren atención
													</div>
												</div>
											</div>
											{/* Totales del formulario */}
											{(statsDiagnostico.totalFacturado != null || statsDiagnostico.totalItbisFacturado != null) && (
												<div className="mt-3 pt-3 border-t border-gray-200">
													<div className="font-medium text-gray-700 mb-2">Totales del formulario</div>
													<div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-gray-600">
														<div>
															<div className="text-xs text-gray-500">Total facturado</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalFacturado ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ITBIS facturado</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisFacturado ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ITBIS proporcionalidad</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisProporcionalidad ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ITBIS llevado costo</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisLlevadoCosto ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ITBIS retenido</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisRetenido ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ISR retenido</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalIsrRetenido ?? 0)}</div>
														</div>
													</div>
													<div className="mt-2 grid grid-cols-2 gap-3 text-gray-600">
														<div>
															<div className="text-xs text-gray-500">ITBIS (por monto servicio)</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisPorServicio ?? 0)}</div>
														</div>
														<div>
															<div className="text-xs text-gray-500">ITBIS (por monto bienes)</div>
															<div className="font-semibold text-gray-900">{formatCurrency(statsDiagnostico.totalItbisPorBienes ?? 0)}</div>
														</div>
													</div>
												</div>
											)}
										</div>
								)}
							</div>

						{/* Alerta visual para facturas pendientes */}
						{selectedFormulario && (() => {
							const facturasPendientesCount = statsDiagnostico.numPendientes ?? 0;
							
							if (facturasPendientesCount > 0) {
									return (
										<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded-r-lg shadow-sm">
											<div className="flex items-start">
												<div className="flex-shrink-0">
													<svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
														<path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
													</svg>
												</div>
												<div className="ml-3 flex-1">
													<p className="text-sm font-medium text-yellow-800">
														⚠️ {facturasPendientesCount} factura(s) pendiente(s) de análisis
													</p>
													<p className="mt-1 text-sm text-yellow-700">
														Hay facturas marcadas como pendientes que requieren atención. Revisa la pestaña "Registros Faltantes" para más detalles.
													</p>
												</div>
											</div>
										</div>
									);
								}
								return null;
							})()}

					{/* Filtros horizontales para la tabla de registros */}
					{(() => {
						const hayFiltrosActivos = !!(tablaFiltros.busqueda || tablaFiltros.forma_pago || tablaFiltros.tipo_bien_servicio || tablaFiltros.estado || tablaFiltros.monto_min || tablaFiltros.monto_max);
						const numFiltrosActivos = [tablaFiltros.busqueda, tablaFiltros.forma_pago, tablaFiltros.tipo_bien_servicio, tablaFiltros.estado, tablaFiltros.monto_min, tablaFiltros.monto_max].filter(Boolean).length;
						return (
						<div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
							{/* Fila de encabezado con toggle y limpiar */}
							<div className="flex items-center justify-between px-4 py-2">
								<button
									onClick={() => setShowTablaFiltros(prev => !prev)}
									className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-primary transition-colors"
								>
									<FunnelIcon className="h-4 w-4" />
									Filtros
									{hayFiltrosActivos && (
										<span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs font-bold bg-primary text-white rounded-full">
											{numFiltrosActivos}
										</span>
									)}
									{showTablaFiltros ? <ChevronUpIcon className="h-4 w-4 ml-1" /> : <ChevronDownIcon className="h-4 w-4 ml-1" />}
								</button>
								{hayFiltrosActivos && (
									<button
										onClick={() => setTablaFiltros({ busqueda: '', forma_pago: '', tipo_bien_servicio: '', estado: '', monto_min: '', monto_max: '' })}
										className="flex items-center gap-1 px-2.5 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
									>
										<XMarkIcon className="h-3.5 w-3.5" />
										Limpiar
									</button>
								)}
							</div>
							{/* Campos de filtro (colapsables) */}
							{showTablaFiltros && (
								<div className="px-4 pb-4 border-t border-gray-100 pt-3">
									<div className="flex flex-wrap items-end gap-3">
										{/* Búsqueda general: NCF, RNC, nombre */}
										<div className="flex-1 min-w-[220px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Búsqueda</label>
											<input
												type="text"
												value={tablaFiltros.busqueda}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, busqueda: e.target.value }))}
												placeholder="Buscar por NCF, RNC, nombre..."
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											/>
										</div>
										{/* Filtro Estado */}
										<div className="min-w-[160px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
											<select
												value={tablaFiltros.estado}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, estado: e.target.value }))}
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											>
												<option value="">Todos</option>
												<option value="auditado">Auditado</option>
												<option value="pendiente">Pendiente</option>
												<option value="sin_revisar">Sin revisar</option>
											</select>
										</div>
										{/* Filtro Forma de Pago */}
										<div className="min-w-[170px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Forma de Pago</label>
											<select
												value={tablaFiltros.forma_pago}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, forma_pago: e.target.value }))}
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											>
												<option value="">Todas</option>
												{FORMA_PAGO_OPTIONS.map(option => (
													<option key={option.value} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
										{/* Filtro Tipo Bien/Servicio */}
										<div className="min-w-[180px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Tipo Bien/Servicio</label>
											<select
												value={tablaFiltros.tipo_bien_servicio}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, tipo_bien_servicio: e.target.value }))}
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											>
												<option value="">Todos</option>
												{TIPO_BIEN_SERVICIO_OPTIONS.map(option => (
													<option key={option.value} value={option.value}>{option.label}</option>
												))}
											</select>
										</div>
										{/* Filtro Monto */}
										<div className="min-w-[120px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Monto mínimo</label>
											<input
												type="number"
												step="0.01"
												min="0"
												value={tablaFiltros.monto_min}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, monto_min: e.target.value }))}
												placeholder="0.00"
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											/>
										</div>
										<div className="min-w-[120px]">
											<label className="block text-xs font-medium text-gray-700 mb-1">Monto máximo</label>
											<input
												type="number"
												step="0.01"
												min="0"
												value={tablaFiltros.monto_max}
												onChange={(e) => setTablaFiltros(prev => ({ ...prev, monto_max: e.target.value }))}
												placeholder="Sin límite"
												className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											/>
										</div>
									</div>
								</div>
							)}
						</div>
						);
					})()}

							{/* Tabs para Todos los Registros, Duplicados, Faltantes y Errores */}
							<div className="mb-4 border-b border-gray-200">
								{(() => {
									// Clasificación por estado_analisis para conteos consistentes
									const registrosErroresEstado = facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'error');
									const registrosDuplicadosEstado = facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'duplicada');
									const registrosPendientesEstado = facturasFisicas.filter(f => {
										const estado = (f.estado_analisis || '').toLowerCase();
										return estado === 'pendiente';
									});
									// Reusar en labels
									const countErrores = registrosErroresEstado.length;
									const countDuplicados = registrosDuplicadosEstado.length;
							const _gpEids = new Set(
								dataRowsSelected
									.filter(r => r && r._pendiente === true)
									.map(r => r._extraida_id ?? r.extraida_id)
									.filter(id => id != null && id !== '')
									.map(String)
							);
							const countPendientesGrid = dataRowsSelected.filter(r => r && r._pendiente === true).length;
							const countPendientesFisicasBase = registrosFaltantes.filter(f => f.estado_analisis === 'pendiente').length || registrosPendientesEstado.length;
							const countPendientesFisicasExtra = registrosPendientesEstado.filter(f => !_gpEids.has(String(f.id))).length;
							const countPendientes = countPendientesGrid + countPendientesFisicasExtra;
									return (
								<nav className="-mb-px flex space-x-4">
									<button
										onClick={() => setTabRegistros('todos')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											tabRegistros === 'todos'
												? 'border-primary text-primary'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Todos los Registros ({dataRowsSelected.length})
									</button>
									<button
										onClick={async () => {
											setTabRegistros('duplicados');
											// Cargar facturas físicas duplicadas cuando se selecciona el tab
											if (selectedFormulario && facturasFisicas.length === 0) {
												try {
													setLoadingFacturasFisicas(true);
													const data = await dgiiApi.getFacturasFisicas(selectedFormulario.id);
													setFacturasFisicas(Array.isArray(data) ? data : []);
												} catch (err) {
												} finally {
													setLoadingFacturasFisicas(false);
												}
											}
										}}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											tabRegistros === 'duplicados'
												? 'border-red-500 text-red-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Registros Duplicados ({countDuplicados})
									</button>
									<button
										onClick={() => setTabRegistros('pendientes')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											tabRegistros === 'pendientes'
												? 'border-orange-500 text-orange-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Registros Pendientes ({countPendientes})
									</button>
									<button
										onClick={() => setTabRegistros('errores')}
										className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
											tabRegistros === 'errores'
												? 'border-amber-500 text-amber-600'
												: 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
										}`}
									>
										Errores de Validación ({countErrores})
									</button>
								</nav>
									);
								})()}
							</div>

							{/* Tabla de datos */}
							<div className="bg-white rounded-lg shadow-sm border border-gray-200">
								{/* Header de la tabla */}
								<div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
									<div className="flex items-center justify-between">
										<div>
											<h3 className="text-base font-semibold text-gray-900">
												{tabRegistros === 'todos' 
													? `Registros (${dataRowsSelected.length})`
													: tabRegistros === 'duplicados'
													? `Registros Duplicados (${facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'duplicada').length})`
													: tabRegistros === 'errores'
													? `Errores de Validación (${facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'error').length})`
													: (() => {
														const _gp = new Set(dataRowsSelected.filter(r=>r&&r._pendiente===true).map(r=>r._extraida_id??r.extraida_id).filter(Boolean).map(String));
														const _gr = dataRowsSelected.filter(r=>r&&r._pendiente===true).length;
														const _fx = facturasFisicas.filter(f=>f&&(f.estado_analisis||'').toLowerCase()==='pendiente'&&!_gp.has(String(f.id))).length;
														return `Registros Pendientes (${_gr + _fx})`;
													})()
												}
											</h3>
											{dataRowsSelected.some(r => r && r._tieneNCFIrregular) && (
												<div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
													<ExclamationTriangleIcon className="h-3.5 w-3.5" />
													<span>
														{dataRowsSelected.filter(r => r && r._tieneNCFIrregular).length} con NCF irregular
													</span>
												</div>
											)}
										</div>
									<div className="flex items-center gap-2">
										{editingData ? (
											<>
												<button
													onClick={handleSave}
													className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium shadow-sm"
												>
													<CheckIcon className="h-4 w-4" />
													Guardar
												</button>
												<button
													onClick={handleCancel}
													className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium shadow-sm"
												>
													<XMarkIcon className="h-4 w-4" />
													Cancelar
												</button>
											</>
										) : (
											selectedFormulario && selectedFormulario.estado === 'abierto' && (
												<button
													onClick={handleAgregarRegistroManual}
													className="flex items-center justify-center p-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
													title="Agregar registro manual: crea una nueva fila en la tabla para ingresar datos a mano (solo en formularios abiertos)."
													aria-label="Agregar registro manual"
												>
													<DocumentTextIcon className="h-5 w-5" />
												</button>
											)
										)}
										{/* Botón masivo de ITBIS por adelantar: siempre visible cuando hay formulario abierto */}
										{selectedFormulario && selectedFormulario.estado === 'abierto' && (
											<button
												onClick={handleCalcularItbisPorAdelantarMasivo}
												className="flex items-center justify-center p-2.5 rounded-lg shadow-sm transition-colors bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800"
												title="Calcular ITBIS por adelantar (todos): aplica la fórmula ITBIS Facturado − ITBIS Costo a todos los registros del formulario actual."
												aria-label="Calcular ITBIS por adelantar para todos los registros"
											>
												<SparklesIcon className="h-5 w-5" />
											</button>
										)}
									</div>
									</div>
								</div>
								{/* Contenido de la tabla */}
								<div className="p-4">
									{tabRegistros === 'pendientes' ? (
										// Grid de registros pendientes - Mostrar TODAS las facturas con estado pendiente
										<div>
											{(() => {
												// FacturaExtraida records con estado_analisis='pendiente'
												const facturasPendientesFisicas = facturasFisicas.filter(f => 
													f && (f.estado_analisis || '').toLowerCase() === 'pendiente'
												);
												// IDs de FacturaExtraida ya cubiertos para no duplicar
												const fisicasPendingIds = new Set(facturasPendientesFisicas.map(f => String(f.id)));
												// FacturaManual pendientes (PK real en DB)
												const facturaManualPendientes = (selectedFormulario?.facturas_manuales || [])
													.filter(fm => fm.estado_analisis === 'pendiente');
												const manualPendingIds = new Set(facturaManualPendientes.map(fm => String(fm.id)));
												// Registros del grid con _pendiente:true que no tienen FacturaExtraida ni FacturaManual pendiente
												const gridRowsPendientes = dataRowsSelected.filter(r => {
													if (!r || r._pendiente !== true) return false;
													const eid = r._extraida_id ?? r.extraida_id;
													if (eid && fisicasPendingIds.has(String(eid))) return false;
													const mid = r._manual_id;
													if (mid && manualPendingIds.has(String(mid))) return false;
													return true;
												});
												
												if (facturasPendientesFisicas.length === 0 && facturaManualPendientes.length === 0 && gridRowsPendientes.length === 0) {
													return (
														<div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
															<p className="text-green-800 font-medium">No hay registros pendientes.</p>
															<p className="text-sm text-green-600 mt-1">Todos los registros están correctamente procesados.</p>
														</div>
													);
												}
												
												return (
													<div className="overflow-x-auto">
														<table className="min-w-full divide-y divide-gray-200">
															<thead className="bg-orange-50">
																<tr>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">ID</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Archivo</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Posición</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">NCF</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">RNC</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Total</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Fecha</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Estado</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Motivo</th>
																	<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Acciones</th>
																</tr>
															</thead>
															<tbody className="bg-white divide-y divide-gray-200">
																{facturasPendientesFisicas.map((factura) => {
																	const datos = factura.datos_estructurados || {};
																	const facturaId = factura.id;
																	const archivo = factura.archivo || 'N/A';
																	const posicion = factura.posicion || 'N/A';
																	const ncf = datos.ncf || factura.ncf || '—';
																	const rnc = datos.rnc_emisor || factura.rnc || '—';
																	const total = datos.total_factura || datos.monto_facturado || factura.total || 0;
																	const fecha = datos.fecha_pago || datos.fecha_comprobante || factura.fecha || '—';
																	const estado = factura.estado_analisis || 'pendiente';
																	const motivo = factura.motivo_analisis || 'Marcado como pendiente';
																	
																	return (
																		<tr key={`fisica-${facturaId}`} className="hover:bg-orange-50">
																			<td className="px-3 py-2 text-sm text-gray-700">{facturaId}</td>
																			<td className="px-3 py-2 text-sm text-gray-700 truncate max-w-[200px]" title={archivo}>
																				{archivo}
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{posicion}</td>
																			<td className="px-3 py-2 text-sm text-gray-700 font-semibold">{ncf}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{rnc}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				{typeof total === 'number' ? formatCurrency(total) : (total || '—')}
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{fecha !== '—' ? formatDate(fecha) : '—'}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
																					estado === 'excluida' ? 'bg-red-100 text-red-800' :
																					estado === 'pendiente' ? 'bg-yellow-100 text-yellow-800' :
																					'bg-gray-100 text-gray-800'
																				}`}>
																					{estado}
																				</span>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700 max-w-[300px]">
																				<div className="line-clamp-2" title={motivo}>
																					{motivo}
																				</div>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<div className="flex items-center gap-2">
																					<button
																						onClick={() => handleDesmarcarPendiente(facturaId)}
																						className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
																						title="Desmarcar como pendiente"
																					>
																						Desmarcar Pendiente
																					</button>
																				</div>
																			</td>
																		</tr>
																	);
																})}
																{facturaManualPendientes.map((fm) => {
																	const datos = fm.datos_estructurados || {};
																	const ncf = datos.ncf || datos.ncf_comprobante || '—';
																	const rnc = datos.rnc_emisor || datos.rnc_cedula || datos.rnc || '—';
																	const total = datos.monto_facturado || datos.total_factura || 0;
																	const fecha = datos.fecha_comprobante || datos.fecha || '—';
																	
																	return (
																		<tr key={`manual-${fm.id}`} className="hover:bg-orange-50">
																			<td className="px-3 py-2 text-sm font-mono text-blue-700">#{fm.id}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">Manual</td>
																			<td className="px-3 py-2 text-sm text-gray-700">—</td>
																			<td className="px-3 py-2 text-sm text-gray-700 font-semibold">{ncf}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{rnc}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				{typeof total === 'number' ? formatCurrency(total) : (total || '—')}
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{fecha !== '—' ? formatDate(fecha) : '—'}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
																					pendiente
																				</span>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700 max-w-[300px]">
																				<div className="line-clamp-2">
																					{fm.motivo_analisis || 'Marcado como pendiente manualmente'}
																				</div>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<div className="flex items-center gap-2">
																					<button
																						onClick={() => handleMarcarPendienteRegistro({ _id: fm.id, _manual_id: fm.id }, true)}
																						className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
																						title="Desmarcar como pendiente"
																					>
																						Desmarcar Pendiente
																					</button>
																				</div>
																			</td>
																		</tr>
																	);
																})}
																{gridRowsPendientes.map((row) => {
																	const rowKey = row._id ?? row._rowId ?? Math.random();
																	const ncf = row.ncf || row.ncf_comprobante || '—';
																	const rnc = row.rnc_emisor || row.rnc_cedula || row.rnc || '—';
																	const total = row.monto_facturado || row.total_factura || 0;
																	const fecha = row.fecha_comprobante || row.fecha || '—';
																	
																	return (
																		<tr key={`grid-${rowKey}`} className="hover:bg-orange-50">
																			<td className="px-3 py-2 text-sm text-gray-500 italic">—</td>
																			<td className="px-3 py-2 text-sm text-gray-700">N/A</td>
																			<td className="px-3 py-2 text-sm text-gray-700">N/A</td>
																			<td className="px-3 py-2 text-sm text-gray-700 font-semibold">{ncf}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{rnc}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				{typeof total === 'number' ? formatCurrency(total) : (total || '—')}
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">{fecha !== '—' ? formatDate(fecha) : '—'}</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
																					pendiente
																				</span>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700 max-w-[300px]">
																				<div className="line-clamp-2">
																					Marcado como pendiente manualmente
																				</div>
																			</td>
																			<td className="px-3 py-2 text-sm text-gray-700">
																				<div className="flex items-center gap-2">
																					<button
																						onClick={() => handleMarcarPendienteRegistro(row, true)}
																						className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200 transition-colors"
																						title="Desmarcar como pendiente"
																					>
																						Desmarcar Pendiente
																					</button>
																				</div>
																			</td>
																		</tr>
																	);
																})}
															</tbody>
														</table>
													</div>
												);
											})()}
										</div>
									) : tabRegistros === 'duplicados' ? (
										// Grid de registros duplicados con funcionalidades avanzadas
										<div className="space-y-4">
											{/* Filtros avanzados y controles */}
											<div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
												<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
													{/* Filtro Archivo */}
													<div>
														<label className="block text-xs font-medium text-gray-700 mb-1">Archivo</label>
														<input
															type="text"
															value={filtroArchivo}
															onChange={(e) => setFiltroArchivo(e.target.value)}
															placeholder="Filtrar por archivo..."
															className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
														/>
													</div>
													
													{/* Filtro Motivo */}
													<div>
														<label className="block text-xs font-medium text-gray-700 mb-1">Motivo</label>
														<input
															type="text"
															value={filtroMotivo}
															onChange={(e) => setFiltroMotivo(e.target.value)}
															placeholder="Filtrar por motivo..."
															className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
														/>
													</div>
													
													{/* Filtro Fecha Desde */}
													<div>
														<label className="block text-xs font-medium text-gray-700 mb-1">Fecha Desde</label>
														<input
															type="date"
															value={filtroFechaDesde}
															onChange={(e) => setFiltroFechaDesde(e.target.value)}
															className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
														/>
													</div>
													
													{/* Filtro Fecha Hasta */}
													<div>
														<label className="block text-xs font-medium text-gray-700 mb-1">Fecha Hasta</label>
														<input
															type="date"
															value={filtroFechaHasta}
															onChange={(e) => setFiltroFechaHasta(e.target.value)}
															className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
														/>
													</div>
												</div>
												
												{/* Botones de acción en lote y exportar - Alineados a la derecha */}
												<div className="flex flex-wrap items-center justify-end gap-2">
													{duplicadosSeleccionados.size > 0 && (
														<>
															<span className="text-sm text-gray-700 font-medium">
																{duplicadosSeleccionados.size} seleccionado(s)
															</span>
															<button
																onClick={() => handleProcesarLote('eliminar')}
																className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors"
																style={{ backgroundColor: '#dc2626' }}
																onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
																onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
															>
																Eliminar Seleccionados
															</button>
															<button
																onClick={() => handleProcesarLote('cambiar_estado', 'incluida', 'Marcado como incluida en lote')}
																className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors"
																style={{ backgroundColor: '#44797B' }}
																onMouseEnter={(e) => e.target.style.backgroundColor = '#3a6a6c'}
																onMouseLeave={(e) => e.target.style.backgroundColor = '#44797B'}
															>
																Marcar como Incluida
															</button>
															<button
																onClick={handleLimpiarSeleccionDuplicados}
																className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
															>
																Limpiar Selección
															</button>
														</>
													)}
													<button
														onClick={handleSeleccionarTodosDuplicados}
														className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors"
														style={{ backgroundColor: '#12284C' }}
														onMouseEnter={(e) => e.target.style.backgroundColor = '#0d1e38'}
														onMouseLeave={(e) => e.target.style.backgroundColor = '#12284C'}
													>
														Seleccionar Todos
													</button>
													<button
														onClick={handleExportarDuplicados}
														className="px-3 py-1.5 text-sm text-white rounded-lg transition-colors flex items-center gap-1"
														style={{ backgroundColor: '#44797B' }}
														onMouseEnter={(e) => e.target.style.backgroundColor = '#3a6a6c'}
														onMouseLeave={(e) => e.target.style.backgroundColor = '#44797B'}
													>
														<ArrowDownTrayIcon className="h-4 w-4" />
														Exportar CSV
													</button>
													<button
														onClick={() => {
															setFiltroArchivo('');
															setFiltroMotivo('');
															setFiltroFechaDesde('');
															setFiltroFechaHasta('');
														}}
														className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
													>
														Limpiar Filtros
													</button>
												</div>
											</div>
											
											{loadingFacturasFisicas ? (
												<div className="py-8 flex items-center justify-center text-sm text-gray-500">
													Cargando registros duplicados...
												</div>
											) : getFacturasDuplicadasFiltradas().length === 0 ? (
												<div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
													<p className="text-green-800 font-medium">No hay registros duplicados.</p>
													<p className="text-sm text-green-600 mt-1">Todos los registros están correctamente procesados.</p>
												</div>
											) : (
												<div className="overflow-x-auto">
													<table className="min-w-full divide-y divide-gray-200">
														<thead className="bg-red-50">
															<tr>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">
																	<input
																		type="checkbox"
																		checked={duplicadosSeleccionados.size === getFacturasDuplicadasFiltradas().length && getFacturasDuplicadasFiltradas().length > 0}
																		onChange={(e) => {
																			if (e.target.checked) {
																				handleSeleccionarTodosDuplicados();
																			} else {
																				handleLimpiarSeleccionDuplicados();
																			}
																		}}
																		className="rounded border-gray-300 text-primary focus:ring-primary"
																	/>
																</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">ID</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Archivo</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">NCF</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">RNC</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Total</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Fecha</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Motivo</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase sticky right-0 bg-red-50 z-50">Acciones</th>
															</tr>
														</thead>
														<tbody className="bg-white divide-y divide-gray-200">
															{getFacturasDuplicadasFiltradas().map((f) => {
																// Los datos pueden estar en datos_estructurados o directamente en el objeto
																const datos = f.datos_estructurados || f || {};
																const ncf = datos.ncf || f.ncf || '';
																const rnc = datos.rnc_emisor || datos.rnc_cedula || datos.rnc || f.rnc_emisor || f.rnc_cedula || f.rnc || '';
																const total = datos.total_factura || datos.monto_total || datos.monto_facturado || f.total_factura || f.monto_total || f.monto_facturado || 0;
																const fecha = datos.fecha_comprobante || datos.fecha || f.fecha_comprobante || f.fecha || '';
																const archivoNombre = f.archivo_adjunto?.nombre_original || (typeof f.archivo_adjunto === 'string' ? f.archivo_adjunto : 'Sin nombre');
																const estaSeleccionado = duplicadosSeleccionados.has(f.id);
																return (
																	<tr key={f.id} className={`hover:bg-red-50 ${estaSeleccionado ? 'bg-blue-50' : ''}`}>
																		<td className="px-3 py-2">
																			<input
																				type="checkbox"
																				checked={estaSeleccionado}
																				onChange={() => handleToggleSeleccionDuplicado(f.id)}
																				className="rounded border-gray-300 text-primary focus:ring-primary"
																			/>
																		</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{f.id}</td>
																		<td className="px-3 py-2 text-sm text-gray-700 truncate max-w-[200px]" title={archivoNombre}>
																			{archivoNombre}
																		</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{ncf || '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{rnc || '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">
																			{typeof total === 'number' ? formatCurrency(total) : (total || '—')}
																		</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{fecha ? formatDate(fecha) : '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700 max-w-[300px]">
																			<div className="line-clamp-2" title={f.motivo_analisis || ''}>
																				{f.motivo_analisis || '—'}
																			</div>
																		</td>
																		<td className="px-3 py-2 text-sm whitespace-nowrap sticky right-0 bg-white z-40 shadow-sm border-l border-gray-300">
																			<div className="flex items-center gap-1 sm:gap-1.5 flex-wrap">
																				{/* Comparar con Principal */}
																				<button
																					onClick={() => handleCompararConPrincipal(f.id)}
																					className="p-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 active:bg-indigo-800 transition-colors touch-manipulation"
																					title="Comparar con registro principal"
																					aria-label="Comparar"
																				>
																					<EyeIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Merge */}
																				<button
																					onClick={() => handleAbrirModalMerge(f.id)}
																					className="p-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 active:bg-purple-800 transition-colors touch-manipulation"
																					title="Merge con registro principal"
																					aria-label="Merge"
																				>
																					<DocumentDuplicateIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Historial */}
																				<button
																					onClick={() => handleVerHistorial(f.id)}
																					className="p-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 active:bg-gray-800 transition-colors touch-manipulation"
																					title="Ver historial de acciones"
																					aria-label="Historial"
																				>
																					<DocumentTextIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Convertir a Principal */}
																				<button
																					onClick={() => handleConvertirAPrincipal(f.id)}
																					className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors touch-manipulation"
																					title="Convertir a registro principal"
																					aria-label="Convertir a principal"
																				>
																					<ArrowPathIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Eliminar */}
																				<button
																					onClick={() => handleEliminarDuplicado(f.id)}
																					className="p-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors touch-manipulation"
																					title="Eliminar registro duplicado"
																					aria-label="Eliminar duplicado"
																				>
																					<TrashIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Cambiar a Incluida */}
																				<button
																					onClick={() => handleCambiarEstadoDuplicado(f.id, 'incluida', 'Marcado como incluida manualmente')}
																					className="p-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors touch-manipulation"
																					title="Marcar como incluida"
																					aria-label="Marcar como incluida"
																				>
																					<CheckIcon className="h-4 w-4" />
																				</button>
																			</div>
																		</td>
																	</tr>
																);
															})}
														</tbody>
													</table>
												</div>
											)}
										</div>
									) : dataRowsSelected.length === 0 ? (
										<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
											<p className="text-yellow-800 font-medium">No hay datos registrados en este formulario.</p>
											<p className="text-sm text-yellow-600 mt-1">Procesa facturas para ver los datos aquí.</p>
										</div>
									) : editingData ? (
										<div className="space-y-4">
											{/* Tabla editable completa con todos los campos del 606 */}
											<div className="border border-gray-300 rounded-lg" style={{ maxHeight: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
												<div className="overflow-x-auto overflow-y-auto flex-1" style={{ maxHeight: '100%' }}>
												<table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '2800px' }}>
												<thead className="bg-white sticky top-0 z-[100] shadow-md">
													<tr>
														{/* Imagen - Contraíble */}
														<th className={`text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap sticky left-0 top-0 bg-white z-[110] shadow-sm ${imagenColumnExpanded ? 'px-2 py-1.5 min-w-[80px]' : 'p-1 w-10'}`}>
															{imagenColumnExpanded ? (
																<span className="flex items-center gap-1">
																	Imagen
																	<button type="button" onClick={toggleImagenColumn} className="p-0.5 rounded hover:bg-gray-200" title="Contraer columna">
																		<ChevronRightIcon className="h-3.5 w-3.5" />
																	</button>
																</span>
															) : (
																<button type="button" onClick={toggleImagenColumn} className="p-1 rounded hover:bg-gray-200" title="Expandir columna Imagen">
																	<ChevronLeftIcon className="h-4 w-4" />
																</button>
															)}
														</th>
														{/* Formato 606 oficial - Orden de columnas según especificación DGII */}
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															1. RNC/Cédula
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[80px]">
															2. Tipo ID
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															3. Tipo Bien/Serv.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[110px]">
															4. NCF
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[110px]">
															5. NCF Modificado
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															Bien/Servicio
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															6. Fecha Comp.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															7. Fecha Pago
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															8. Monto Serv.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															9. Monto Bienes
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															10. Total Facturado
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															Descuento
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															11. ITBIS Fact.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															12. ITBIS Ret.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															13. ITBIS Prop.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															14. ITBIS Costo
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															15. ITBIS Adel.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															16. ITBIS Perc.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															17. Tipo Ret. ISR
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															18. Monto Ret. Renta
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															19. ISR Perc.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															20. Imp. Selectivo
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															21. Otros Imp.
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															22. Propina
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															23. Forma Pago
														</th>
														{/* Campos adicionales útiles para visualización administrativa */}
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															Razón Social
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[80px]">
															Moneda
														</th>
														<th className="px-2 py-1.5 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															Actividad Econ.
														</th>
														<th className={`text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap sticky right-0 top-0 bg-white z-[110] shadow-sm border-l border-gray-300 ${accionesColumnExpanded ? 'px-2 py-1.5 min-w-[80px]' : 'p-1 w-10'}`}>
															{accionesColumnExpanded ? (
																<span className="flex items-center gap-1">
																	Acciones
																	<button type="button" onClick={toggleAccionesColumn} className="p-0.5 rounded hover:bg-gray-200" title="Contraer columna">
																		<ChevronRightIcon className="h-3.5 w-3.5" />
																	</button>
																</span>
															) : (
																<button type="button" onClick={toggleAccionesColumn} className="p-1 rounded hover:bg-gray-200" title="Expandir columna Acciones">
																	<ChevronLeftIcon className="h-4 w-4" />
																</button>
															)}
														</th>
													</tr>
												</thead>
													<tbody className="bg-white divide-y divide-gray-200">
														{getPaginatedRows(dataRowsSortedEditing).map((row, index) => {
															// Usar el ID único de la fila en lugar del índice de posición
															const rowId = row._rowId || row._originalIndex;
															const highlightClass = getRowHighlightClass(row);
															const problemasNCF = row._problemas?.filter(p => p.campo === 'NCF') || [];
															const problemasRNC = row._problemas?.filter(p => p.campo === 'RNC') || [];
															
															// Determinar si este registro específico está en modo edición
															const isEditingThisRow = editingData && editingData.editingRowId === rowId;
															
															// Si estamos editando, obtener los valores actualizados de editingData
															// Usar _rowId para buscar el registro, ya que es más confiable que NCF/RNC (que pueden cambiar)
															let editingRowData = row;
															if (isEditingThisRow && editingData && editingData.datos_completados) {
																const tipo = selectedFormulario.tipo;
																const datosCompletados = editingData.datos_completados || {};
																let currentRows = [];
																if (tipo === '606') {
																	currentRows = datosCompletados.compras || [];
																} else if (tipo === '607') {
																	currentRows = datosCompletados.ventas || [];
																}
																
																// Buscar el registro usando _rowId (más confiable que NCF/RNC)
																// Primero intentar buscar por _rowId
																let editingRow = currentRows.find(r => {
																	const rRowId = r._rowId || `row_${currentRows.indexOf(r)}_${r.ncf || r.rnc_emisor || ''}`;
																	return rRowId === rowId;
																});
																
																// Si no se encontró por _rowId, buscar por NCF (más confiable que RNC)
																if (!editingRow) {
																	const ncf = row.ncf || '';
																	if (ncf) {
																		editingRow = currentRows.find(r => {
																			const rNcf = r.ncf || '';
																			return rNcf === ncf;
																		});
																	}
																}
																
																// Si aún no se encontró, buscar por RNC + monto + fecha
																if (!editingRow) {
																	const rnc = limpiarRNC(row.rnc_emisor || row.rnc_cedula || row.rnc || '');
																	const monto = row.total_factura || row.monto_facturado || 0;
																	const fecha = row.fecha_comprobante || '';
																	
																	if (rnc) {
																		editingRow = currentRows.find(r => {
																			const rRnc = limpiarRNC(r.rnc_emisor || r.rnc_cedula || r.rnc || '');
																			const rMonto = r.total_factura || r.monto_facturado || 0;
																			const rFecha = r.fecha_comprobante || '';
																			return rRnc === rnc && Math.abs(rMonto - monto) < 0.01 && rFecha === fecha;
																		});
																	}
																}
																
																// Si encontramos el registro, combinarlo con row para mantener campos calculados y _rowId
																if (editingRow) {
																	// Asegurar que _rowId y _originalIndex se mantengan de row
																	editingRowData = { 
																		...row, 
																		...editingRow,
																		_rowId: row._rowId, // Mantener el _rowId original
																		_originalIndex: row._originalIndex, // Mantener el índice original
																		_problemas: row._problemas, // Mantener los problemas detectados
																		_tieneProblemas: row._tieneProblemas,
																		_tieneNCFIrregular: row._tieneNCFIrregular
																	};
																} else {
																	// Si no se encuentra, usar row directamente
																	editingRowData = row;
																}
															}
															
															// Helper: celda con icono editar (derecha, fijo); contenido puede truncarse
															const isCellEdit = (f) => editingCell?.rowId === rowId && editingCell?.field === f;
															const wrapCellWithPencil = (field, displayContent, currentValue) => (
																<div className="relative flex items-center w-full min-h-[26px]">
																	<div className="min-w-0 flex-1 truncate pr-9">
																		{displayContent}
																	</div>
																	<button
																		type="button"
																		onClick={(e) => { e.stopPropagation(); setEditingCell({ rowId, field }); setCellEditValue(currentValue != null && currentValue !== '' ? currentValue : ''); }}
																		className="absolute right-0 top-1/2 -translate-y-1/2 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 transition-colors shadow-sm"
																		title="Modificar esta celda"
																		aria-label="Modificar esta celda"
																	>
																		<PencilSquareIcon className="h-4 w-4" />
																	</button>
																</div>
															);
															// Helper para crear inputs numéricos
															const createNumberInput = (field, minWidth = '90px') => {
																const value = field === 'otros_impuestos_tasas'
																	? (editingRowData.otros_impuestos_tasas ?? editingRowData.otros_impuestos ?? editingRowData.otros_ipm ?? editingRowData.otros_imp ?? editingRowData[field])
																	: editingRowData[field];
																const inCellEdit = isCellEdit(field);
																if (!isEditingThisRow && !inCellEdit) {
																	return wrapCellWithPencil(field, <span className="text-xs text-gray-900">{value !== null && value !== undefined ? formatCurrency(value) : 'N/A'}</span>, value);
																}
																const inputVal = inCellEdit ? cellEditValue : (value ?? '');
																return (
																	<input
																		type="number"
																		step="0.01"
																		value={inputVal}
																		onChange={(e) => {
																			const v = e.target.value;
																			if (inCellEdit) setCellEditValue(v);
																			else {
																				const val = v === '' ? '' : parseFloat(v);
																				if (!isNaN(val) || v === '') handleCellEdit(rowId, field, val);
																			}
																		}}
																		onBlur={inCellEdit ? (e) => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, field, e.target.value); setEditingCell(null); } : undefined}
																		onFocus={(e) => e.target.select()}
																		onKeyDown={(e) => { e.stopPropagation(); if (inCellEdit && e.key === 'Enter') e.target.blur(); if (inCellEdit && e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }}
																		className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																		style={{ minWidth }}
																	/>
																);
															};
															
															// Helper para crear inputs de texto
															const createTextInput = (field, minWidth = '100px', transformValue = null) => {
																let value = editingRowData[field];
																if (transformValue && typeof transformValue === 'function') {
																	value = transformValue(value);
																}
																const inCellEdit = isCellEdit(field);
																if (!isEditingThisRow && !inCellEdit) {
																	const displayVal = value || 'N/A';
																	return wrapCellWithPencil(field, <span className="text-xs text-gray-900">{displayVal}</span>, value ?? '');
																}
																const inputVal = inCellEdit ? cellEditValue : (value ?? '');
																return (
																	<input
																		type="text"
																		value={inputVal}
																		onChange={(e) => inCellEdit ? setCellEditValue(e.target.value) : handleCellEdit(rowId, field, e.target.value)}
																		onBlur={inCellEdit ? (e) => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, field, e.target.value); setEditingCell(null); } : undefined}
																		onFocus={(e) => e.target.select()}
																		onKeyDown={(e) => { e.stopPropagation(); if (inCellEdit && e.key === 'Enter') e.target.blur(); if (inCellEdit && e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }}
																		className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																		style={{ minWidth }}
																		autoComplete="off"
																	/>
																);
															};
															
															return (
															<tr key={index} className={`hover:bg-gray-50 ${highlightClass}`}>
																{/* Imagen - Sticky (contraíble) */}
																<td className={`whitespace-nowrap border-r border-gray-300 sticky left-0 bg-white z-40 shadow-sm ${imagenColumnExpanded ? 'px-2 py-1.5' : 'p-1 w-10'}`}>
																	{imagenColumnExpanded ? (
																		getImageUrlForRow(row, selectedFormulario) ? (
																			<button
																				onClick={(e) => viewImage(getImageUrlForRow(row, selectedFormulario), e)}
																				onContextMenu={(e) => {
																					e.preventDefault();
																					viewImage(getImageUrlForRow(row, selectedFormulario), { button: 2 });
																				}}
																				className="flex items-center justify-center w-7 h-7 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
																				title="Click para ver imagen | Ctrl+Click o clic derecho para abrir en nueva pestaña"
																			>
																				<PhotoIcon className="h-4 w-4" />
																			</button>
																		) : (
																			<div className="w-7 h-7 flex items-center justify-center text-gray-400">
																				<PhotoIcon className="h-4 w-4 opacity-30" />
																			</div>
																		)
																	) : (
																		<button type="button" onClick={toggleImagenColumn} className="p-1 rounded hover:bg-gray-100" title="Expandir Imagen">
																			<ChevronLeftIcon className="h-4 w-4 text-gray-500" />
																		</button>
																	)}
																</td>
																
																{/* RNC/Cédula */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	<div className="flex items-center gap-1">
																		{problemasRNC.length > 0 && (
																			<ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" title={problemasRNC.map(p => p.motivo).join(', ')} />
																		)}
																		{createTextInput('rnc_emisor', '100px', (val) => limpiarRNC(val || editingRowData.rnc_cedula || ''))}
																	</div>
																</td>
																
																{/* Tipo ID - Generado automáticamente según RNC */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createTextInput('tipo_id', '80px', (val) => val || calcularTipoID(limpiarRNC(editingRowData.rnc_emisor || editingRowData.rnc_cedula || '')))}
																</td>
																
																{/* Tipo Bien/Servicio - Select con códigos (Formato 606 campo 3) */}
																<td className="px-2 py-1.5 border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('tipo_bien_servicio')) ? (
																		<select
																			value={(isCellEdit('tipo_bien_servicio') ? cellEditValue : (editingRowData.tipo_bien_servicio || editingRowData.tipo_bienes_servicios || ''))}
																			onChange={(e) => isCellEdit('tipo_bien_servicio') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'tipo_bien_servicio', e.target.value)}
																			onBlur={isCellEdit('tipo_bien_servicio') ? (e) => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'tipo_bien_servicio', e.target.value); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '120px' }}
																		>
																			<option value="">Seleccionar...</option>
																			{TIPO_BIEN_SERVICIO_OPTIONS.map(option => (
																				<option key={option.value} value={option.value}>{option.label}</option>
																			))}
																		</select>
																	) : (
																		wrapCellWithPencil('tipo_bien_servicio', <span className="text-xs text-gray-900">{row.tipo_bien_servicio || row.tipo_bienes_servicios || 'N/A'}</span>, editingRowData.tipo_bien_servicio || editingRowData.tipo_bienes_servicios || '')
																	)}
																</td>
																
																{/* NCF */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	<div className="flex items-center gap-1">
																		{problemasNCF.length > 0 && (
																			<ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" title={problemasNCF.map(p => p.motivo).join(', ')} />
																		)}
																		{(isEditingThisRow || isCellEdit('ncf')) ? (
																			<input
																				type="text"
																				value={isCellEdit('ncf') ? cellEditValue : (editingRowData.ncf ?? '')}
																				onChange={(e) => isCellEdit('ncf') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'ncf', e.target.value)}
																			onBlur={(e) => {
																				if (isCellEdit('ncf')) { handleSaveCell(rowId, 'ncf', e.target.value); setEditingCell(null); }
																				else if (e.target.value !== (editingRowData.ncf ?? '')) handleCellEdit(rowId, 'ncf', e.target.value);
																			}}
																				onFocus={(e) => e.target.select()}
																				onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }}
																				className={`w-full px-1.5 py-1 text-xs border rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow ${
																					problemasNCF.length > 0 ? 'border-red-500 bg-red-50 focus:ring-red-500 focus:bg-red-100 font-semibold' : 'border-gray-300 focus:ring-primary'
																				}`}
																				style={{ minWidth: '110px' }}
																				autoComplete="off"
																			/>
																		) : (
																			wrapCellWithPencil('ncf', <span className={`text-xs ${problemasNCF.length > 0 ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>{row.ncf || 'N/A'}</span>, editingRowData.ncf ?? '')
																		)}
																	</div>
																</td>
																
																{/* NCF Modificado */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createTextInput('ncf_modificado', '110px')}
																</td>
																
																{/* Bien/Servicio - Clasificación para campos 8 y 9 (columna adicional, no numerada) */}
																<td className="px-2 py-1.5 border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('bien_servicio')) ? (
																		<select
																			value={isCellEdit('bien_servicio') ? cellEditValue : (editingRowData.bien_servicio || '')}
																			onChange={(e) => isCellEdit('bien_servicio') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'bien_servicio', e.target.value)}
																			onBlur={isCellEdit('bien_servicio') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'bien_servicio', cellEditValue); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																		>
																			<option value="">Seleccionar...</option>
																			<option value="bien">Bien</option>
																			<option value="servicio">Servicio</option>
																		</select>
																	) : (
																		wrapCellWithPencil('bien_servicio', <span className="text-xs text-gray-900">{row.bien_servicio || 'N/A'}</span>, editingRowData.bien_servicio || '')
																	)}
																</td>
																
																{/* Fecha Comprobante */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('fecha_comprobante')) ? (
																		<input
																			type="date"
																			value={isCellEdit('fecha_comprobante') ? cellEditValue : getNormalizedDate(editingRowData.fecha_comprobante)}
																			onChange={(e) => isCellEdit('fecha_comprobante') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'fecha_comprobante', e.target.value)}
																			onBlur={isCellEdit('fecha_comprobante') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'fecha_comprobante', cellEditValue); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																		/>
																	) : (
																		wrapCellWithPencil('fecha_comprobante', <span className="text-xs text-gray-900">{formatDate(row.fecha_comprobante)}</span>, getNormalizedDate(editingRowData.fecha_comprobante) || '')
																	)}
																</td>
																
																{/* Fecha Pago - Auto-completar si forma_pago es Efectivo (1) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('fecha_pago')) ? (
																		<input
																			type="date"
																			value={isCellEdit('fecha_pago') ? cellEditValue : (() => {
																				const formaPago = editingRowData.forma_pago || '4';
																				if (formaPago !== '4' && !editingRowData.fecha_pago && editingRowData.fecha_comprobante) return getNormalizedDate(editingRowData.fecha_comprobante);
																				return getNormalizedDate(editingRowData.fecha_pago);
																			})()}
																			onFocus={(e) => {
																				if (!isCellEdit('fecha_pago')) {
																					const formaPago = editingRowData.forma_pago || '4';
																					if (formaPago !== '4' && !editingRowData.fecha_pago && editingRowData.fecha_comprobante) {
																						const fc = getNormalizedDate(editingRowData.fecha_comprobante);
																						if (fc) handleCellEdit(rowId, 'fecha_pago', fc);
																					}
																				}
																			}}
																			onChange={(e) => isCellEdit('fecha_pago') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'fecha_pago', e.target.value)}
																			onBlur={isCellEdit('fecha_pago') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'fecha_pago', cellEditValue); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																		/>
																	) : (
																		wrapCellWithPencil('fecha_pago', <span className="text-xs text-gray-900">{(() => {
																			const formaPago = row.forma_pago || '4';
																			if (formaPago !== '4' && !row.fecha_pago && row.fecha_comprobante) return formatDate(row.fecha_comprobante);
																			return row.fecha_pago ? formatDate(row.fecha_pago) : 'N/A';
																		})()}</span>, getNormalizedDate(editingRowData.fecha_pago) || getNormalizedDate(editingRowData.fecha_comprobante) || '')
																	)}
																</td>
																
																{/* Monto Servicios (Monto Facturado en Servicios - Formato 606 campo 8) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('monto_servicios')) ? (
																		<input
																			type="number"
																			step="0.01"
																			min="0"
																			value={isCellEdit('monto_servicios') ? cellEditValue : (editingRowData.monto_servicios !== null && editingRowData.monto_servicios !== undefined ? editingRowData.monto_servicios : (editingRowData.monto_facturado_servicios || ''))}
																			onChange={(e) => {
																				const v = e.target.value;
																				if (isCellEdit('monto_servicios')) setCellEditValue(v);
																				else if (v === '') handleCellEdit(rowId, 'monto_servicios', 0);
																				else { const val = parseFloat(v); if (!isNaN(val)) handleCellEdit(rowId, 'monto_servicios', val); }
																			}}
																			onBlur={isCellEdit('monto_servicios') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'monto_servicios', cellEditValue); setEditingCell(null); } : undefined}
																			onFocus={(e) => e.target.select()}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																			placeholder="0.00"
																		/>
																	) : (
																		wrapCellWithPencil('monto_servicios', <span className="text-xs text-gray-900">{(() => {
																			const montoServicios = parseFloat(row.monto_servicios || row.monto_facturado_servicios || 0) || 0;
																			return montoServicios > 0 ? formatCurrency(montoServicios) : 'N/A';
																		})()}</span>, editingRowData.monto_servicios !== null && editingRowData.monto_servicios !== undefined ? editingRowData.monto_servicios : (editingRowData.monto_facturado_servicios || ''))
																	)}
																</td>
																
																{/* Monto Bienes (Monto Facturado en Bienes - Formato 606 campo 9) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('monto_bienes')) ? (
																		<input
																			type="number"
																			step="0.01"
																			min="0"
																			value={isCellEdit('monto_bienes') ? cellEditValue : (editingRowData.monto_bienes !== null && editingRowData.monto_bienes !== undefined ? editingRowData.monto_bienes : (editingRowData.monto_facturado_bienes || ''))}
																			onChange={(e) => {
																				const v = e.target.value;
																				if (isCellEdit('monto_bienes')) setCellEditValue(v);
																				else if (v === '') handleCellEdit(rowId, 'monto_bienes', 0);
																				else { const val = parseFloat(v); if (!isNaN(val)) handleCellEdit(rowId, 'monto_bienes', val); }
																			}}
																			onBlur={isCellEdit('monto_bienes') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'monto_bienes', cellEditValue); setEditingCell(null); } : undefined}
																			onFocus={(e) => e.target.select()}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																			placeholder="0.00"
																		/>
																	) : (
																		wrapCellWithPencil('monto_bienes', <span className="text-xs text-gray-900">{(() => {
																			const montoBienes = parseFloat(row.monto_facturado_bienes || 0) || 0;
																			return montoBienes !== null && montoBienes !== undefined && montoBienes !== 0 ? formatCurrency(montoBienes) : 'N/A';
																		})()}</span>, editingRowData.monto_bienes !== null && editingRowData.monto_bienes !== undefined ? editingRowData.monto_bienes : (editingRowData.monto_facturado_bienes || ''))
																	)}
																</td>
																
																{/* 10. Total Facturado (calculado: monto_servicios + monto_bienes, no editable) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	<span className="text-xs text-gray-900">
																		{formatCurrency(
																			(parseFloat(editingRowData.monto_servicios ?? editingRowData.monto_facturado_servicios ?? 0) || 0) +
																			(parseFloat(editingRowData.monto_bienes ?? editingRowData.monto_facturado_bienes ?? 0) || 0) ||
																			parseFloat(editingRowData.monto_facturado ?? editingRowData.total_factura ?? 0) || 0
																		)}
																	</span>
																</td>
																
																{/* Descuento (desde n8n o manual) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('descuento', '90px')}
																</td>
																
																{/* ITBIS Facturado */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('itbis_facturado', '100px')}
																</td>
																
																{/* ITBIS Retenido */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('itbis_retenido', '100px')}
																</td>
																
																{/* ITBIS Proporcionalidad (ITBIS sujeto a Proporcionalidad Art. 349 - Formato 606 campo 13) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('itbis_proporcionalidad', '100px')}
																</td>
																
																{/* ITBIS Costo (ITBIS llevado al Costo - Formato 606 campo 14) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('itbis_llevado_costo', '90px')}
																</td>
																
																{/* ITBIS Adelantar (ITBIS por Adelantar - Formato 606 campo 15) - Calculado automáticamente */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{isEditingThisRow ? (
																		<input
																			type="number"
																			step="0.01"
																			value={(() => {
																				// Calcular automáticamente: itbis_por_adelantar = itbis_facturado - itbis_llevado_costo
																				const itbisFacturado = parseFloat(editingRowData.itbis_facturado || 0) || 0;
																				const itbisCosto = parseFloat(editingRowData.itbis_llevado_costo || 0) || 0;
																				return itbisFacturado - itbisCosto;
																			})()}
																			readOnly
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded bg-gray-50 text-gray-600 cursor-not-allowed"
																			style={{ minWidth: '100px' }}
																			title="Calculado automáticamente: ITBIS Facturado - ITBIS Costo"
																		/>
																	) : (
																		<span className="text-xs text-gray-900">
																			{(() => {
																				const itbisFacturado = parseFloat(row.itbis_facturado || 0) || 0;
																				const itbisCosto = parseFloat(row.itbis_llevado_costo || 0) || 0;
																				const total = itbisFacturado - itbisCosto;
																				return total > 0 ? formatCurrency(total) : 'N/A';
																			})()}
																		</span>
																	)}
																</td>
																
																{/* ITBIS Percibido (ITBIS percibido en compras - Formato 606 campo 16) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('itbis_percibido_compras', '100px')}
																</td>
																
																{/* Tipo Retención ISR - Select con códigos */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('tipo_retencion_isr')) ? (
																		<select
																			value={isCellEdit('tipo_retencion_isr') ? cellEditValue : (editingRowData.tipo_retencion_isr || '')}
																			onChange={(e) => isCellEdit('tipo_retencion_isr') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'tipo_retencion_isr', e.target.value)}
																			onBlur={isCellEdit('tipo_retencion_isr') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'tipo_retencion_isr', cellEditValue); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '100px' }}
																		>
																			<option value="">Seleccionar...</option>
																			{TIPO_RETENCION_ISR_OPTIONS.map(option => (
																				<option key={option.value} value={option.value}>{option.label}</option>
																			))}
																		</select>
																	) : (
																		wrapCellWithPencil('tipo_retencion_isr', <span className="text-xs text-gray-900">{getTipoRetencionISRLabel(row.tipo_retencion_isr)}</span>, editingRowData.tipo_retencion_isr || '')
																	)}
																</td>
																
																{/* Monto Retención Renta */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('monto_retencion_renta', '100px')}
																</td>
																
																{/* ISR Percibido (ISR percibido en compras - Formato 606 campo 19) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('isr_percibido_compras', '90px')}
																</td>
																
																{/* Impuesto Selectivo (Impuesto Selectivo al Consumo - Formato 606 campo 20) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('impuesto_selectivo_consumo', '100px')}
																</td>
																
																{/* Otros Impuestos (Otros Impuestos/Tasas - Formato 606 campo 21) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('otros_impuestos_tasas', '100px')}
																</td>
																
																{/* Monto Propina (Monto Propina Legal - Formato 606 campo 22) */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{createNumberInput('monto_propina_legal', '90px')}
																</td>
																
																{/* Forma Pago (Formato 606 campo 23) — t8: normalizar valor para que "1-Efectivo" sea seleccionable */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('forma_pago')) ? (
																		<select
																			value={isCellEdit('forma_pago') ? cellEditValue : (normalizarCodigoFormaPago(editingRowData.forma_pago) || '4')}
																			onChange={(e) => isCellEdit('forma_pago') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'forma_pago', e.target.value)}
																			onBlur={isCellEdit('forma_pago') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'forma_pago', cellEditValue); setEditingCell(null); } : undefined}
																			onKeyDown={(e) => e.stopPropagation()}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '90px' }}
																		>
																			{FORMA_PAGO_OPTIONS.map(option => (
																				<option key={option.value} value={option.value}>{option.label}</option>
																			))}
																		</select>
																	) : (
																		wrapCellWithPencil('forma_pago', <span className="text-xs text-gray-900">{getFormaPagoLabel(row.forma_pago || '4')}</span>, normalizarCodigoFormaPago(editingRowData.forma_pago) || '4')
																	)}
																</td>
																
																{/* Campos adicionales útiles para visualización administrativa */}
																{/* Razón Social */}
																<td className="px-2 py-1.5 border-r border-gray-300">
																	{createTextInput('nombre_emisor', '120px', (val) => val || editingRowData.razon_social || '')}
																</td>
																
																{/* Moneda - abre modal de conversión de tasa */}
																<td className="px-2 py-1.5 whitespace-nowrap border-r border-gray-300">
																	<button
																		type="button"
																		onClick={() => handleOpenCurrencyModal(rowId, editingRowData.moneda || 'DOP', 'DOP', 'editing')}
																		className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
																		title="Clic para convertir montos usando tasa de cambio"
																	>
																		{editingRowData.moneda || 'DOP'}
																		<span className="text-gray-400 text-xs">↔</span>
																	</button>
																</td>
																
																{/* Actividad Económica */}
																<td className="px-2 py-1.5 border-r border-gray-300">
																	{(isEditingThisRow || isCellEdit('actividad_economica')) ? (
																		<input
																			type="text"
																			value={isCellEdit('actividad_economica') ? cellEditValue : (() => {
																				const actividad = editingRowData.actividad_economica || '';
																				return actividad.includes(' - ') ? actividad.split(' - ')[0].trim() : actividad;
																			})()}
																			onChange={(e) => isCellEdit('actividad_economica') ? setCellEditValue(e.target.value) : handleCellEdit(rowId, 'actividad_economica', e.target.value)}
																			onBlur={isCellEdit('actividad_economica') ? () => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'actividad_economica', cellEditValue); setEditingCell(null); } : undefined}
																			onFocus={(e) => e.target.select()}
																			onKeyDown={(e) => { e.stopPropagation(); if (isCellEdit('actividad_economica') && e.key === 'Enter') e.target.blur(); if (isCellEdit('actividad_economica') && e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }}
																			className="w-full px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary focus:shadow-md transition-shadow"
																			style={{ minWidth: '120px' }}
																			autoComplete="off"
																		/>
																	) : (
																		wrapCellWithPencil('actividad_economica', <span className="text-xs text-gray-900">{(() => {
																			const actividad = row.actividad_economica || '';
																			return actividad.includes(' - ') ? actividad.split(' - ')[0].trim() : (actividad || 'N/A');
																		})()}</span>, (() => {
																			const actividad = editingRowData.actividad_economica || '';
																			return actividad.includes(' - ') ? actividad.split(' - ')[0].trim() : actividad;
																		})())
																	)}
																</td>
																
																{/* Acciones - Sticky (contraíble) */}
																<td className={`whitespace-nowrap sticky right-0 bg-white z-40 shadow-sm border-l border-gray-300 ${accionesColumnExpanded ? 'px-2 py-1.5' : 'p-1 w-10'}`}>
																	{accionesColumnExpanded ? (
																	<div className="flex items-center gap-1.5">
																		{isEditingThisRow ? (
																			<>
																				<button
																					onClick={() => {
																						handleToggleAuditado(rowId);
																					}}
																					className={`p-1.5 rounded transition-colors ${
																						editingRowData.auditado
																							? 'bg-green-600 text-white hover:bg-green-700'
																							: 'bg-gray-200 text-gray-600 hover:bg-gray-300'
																					}`}
																					title={editingRowData.auditado ? 'Marcado como auditado' : 'Marcar como auditado'}
																				>
																					<CheckIcon className="h-4 w-4" />
																				</button>
																				
																				{/* Botón de Marcar/Desmarcar como Pendiente - Para facturas y registros manuales */}
																				{(() => {
																					const rowKey = row._id ?? row._rowId;
																					const idx = dataRowsSelectedEditing.findIndex(r => (r._id ?? r._rowId) === rowKey);
																					const facturaId = getFacturaIdFromRow(editingRowData, selectedFormulario, facturasFisicas, idx >= 0 ? idx : null, dataRowsSelectedEditing);
																					const estadoAnalisis = getEstadoAnalisisFromRow(editingRowData, selectedFormulario, facturasFisicas);
																					const esPendiente = estadoAnalisis === 'pendiente';
																					if (!rowKey) return null;
																					return (
																						<button
																							onClick={() => {
																								const fid = getFacturaIdFromRow(editingRowData, selectedFormulario, facturasFisicas, idx >= 0 ? idx : null, dataRowsSelectedEditing);
																								const rowNcf = getRowNcf(editingRowData);
																								if (fid && facturasFisicas?.length) {
																									const fact = facturasFisicas.find(f => f.id === fid);
																									const d = fact?.datos_estructurados || fact || {};
																									const factNcf = normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? '');
																									console.warn('[Pendiente] click rowNcf=', rowNcf, 'facturaId=', fid, 'factNcf=', factNcf, 'match=', rowNcf === factNcf);
																								}
																								if (fid) {
																									if (esPendiente) handleDesmarcarPendiente(fid);
																									else handleMarcarPendiente(fid);
																								} else {
																									handleMarcarPendienteRegistro(editingRowData, esPendiente);
																								}
																							}}
																							className={`p-1.5 rounded transition-colors ${
																								esPendiente
																									? 'bg-green-100 text-green-800 hover:bg-green-200'
																									: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
																							}`}
																							title={esPendiente ? 'Desmarcar como pendiente' : 'Marcar como pendiente'}
																						>
																							<ClockIcon className="h-4 w-4" />
																						</button>
																					);
																				})()}
																				<button
																					onClick={() => handleDeleteRow(row)}
																					className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
																					title="Eliminar"
																				>
																					<TrashIcon className="h-4 w-4" />
																				</button>
																			</>
																		) : (
																			<span className="text-xs text-gray-400">-</span>
																		)}
																	</div>
																	) : (
																		<button type="button" onClick={toggleAccionesColumn} className="p-1 rounded hover:bg-gray-100" title="Expandir Acciones">
																			<ChevronLeftIcon className="h-4 w-4 text-gray-500" />
																		</button>
																	)}
																</td>
															</tr>
															);
														})}
													</tbody>
												</table>
												</div>
											</div>
											
											{/* Notas */}
											<div>
												<label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
													Notas
												</label>
												<textarea
													value={editingData.notas}
													onChange={(e) => setEditingData({ ...editingData, notas: e.target.value })}
													className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
													rows="3"
												/>
											</div>
											
											{/* Paginación en modo edición */}
											{dataRowsSelectedEditing.length > itemsPerPage && (
												<div className="flex flex-col gap-3 pt-4 border-t border-gray-200">
													{/* Primera fila: Información de registros y selector */}
													<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
														<div className="text-xs sm:text-sm text-gray-600">
															Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, dataRowsSelectedEditing.length)} de {dataRowsSelectedEditing.length} registros
														</div>
														<div className="flex items-center gap-2">
															<label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
																Registros por página:
															</label>
															<select
																value={itemsPerPage}
																onChange={(e) => {
																	setItemsPerPage(Number(e.target.value));
																	setCurrentPage(1);
																}}
																className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
															>
																<option value={5}>5</option>
																<option value={10}>10</option>
																<option value={20}>20</option>
																<option value={50}>50</option>
																<option value={100}>100</option>
															</select>
														</div>
													</div>
													{/* Segunda fila: Navegación de páginas */}
													<div className="flex items-center justify-center gap-2">
														<button
															onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
															disabled={currentPage === 1}
															className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity touch-manipulation"
															title="Página anterior"
															aria-label="Página anterior"
														>
															<ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
														</button>
														<span className="text-xs sm:text-sm text-gray-700 font-medium px-2 sm:px-3">
															Página {currentPage} de {Math.ceil(dataRowsSelectedEditing.length / itemsPerPage)}
														</span>
														<button
															onClick={() => setCurrentPage(prev => Math.min(Math.ceil(dataRowsSelectedEditing.length / itemsPerPage), prev + 1))}
															disabled={currentPage === Math.ceil(dataRowsSelectedEditing.length / itemsPerPage)}
															className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity touch-manipulation"
															title="Página siguiente"
															aria-label="Página siguiente"
														>
															<ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
														</button>
													</div>
												</div>
											)}
										</div>
									) : tabRegistros === 'errores' ? (
										// Grid de registros con error de validación
										<div>
											{facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'error').length === 0 ? (
												<div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
													<p className="text-green-800 font-medium">No hay registros con errores de validación.</p>
													<p className="text-sm text-green-600 mt-1">Todos los registros están correctamente procesados.</p>
												</div>
											) : (
												<div className="overflow-x-auto">
													<table className="min-w-full divide-y divide-gray-200">
														<thead className="bg-amber-50">
															<tr>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">ID</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Archivo</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Posición</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">NCF</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">RNC</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Total</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Fecha</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Estado</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Motivo</th>
																<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase">Acciones</th>
															</tr>
														</thead>
														<tbody className="bg-white divide-y divide-gray-200">
															{facturasFisicas.filter(f => (f.estado_analisis || '').toLowerCase() === 'error').map((f) => {
																const datos = f.datos_estructurados || f || {};
																const ncf = datos.ncf || f.ncf || '';
																const rnc = datos.rnc_emisor || datos.rnc_cedula || datos.rnc || f.rnc_emisor || f.rnc_cedula || f.rnc || '';
																const total = datos.total_factura || datos.monto_total || datos.monto_facturado || f.total_factura || f.monto_total || f.monto_facturado || 0;
																const fecha = datos.fecha_comprobante || datos.fecha || f.fecha_comprobante || f.fecha || '';
																const archivoNombre = f.archivo_adjunto?.nombre_original || (typeof f.archivo_adjunto === 'string' ? f.archivo_adjunto : 'Sin nombre');
																return (
																	<tr key={f.id} className="hover:bg-amber-50">
																		<td className="px-3 py-2 text-sm text-gray-700">{f.id}</td>
																		<td className="px-3 py-2 text-sm text-gray-700 truncate max-w-[200px]" title={archivoNombre}>{archivoNombre}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{f.posicion_en_archivo || f.posicion || 'N/A'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700 font-semibold">{ncf || '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{rnc || '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{typeof total === 'number' ? formatCurrency(total) : (total || '—')}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">{fecha ? formatDate(fecha) : '—'}</td>
																		<td className="px-3 py-2 text-sm text-gray-700">
																			<span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
																				{f.estado_analisis || 'error'}
																			</span>
																		</td>
																		<td className="px-3 py-2 text-sm text-gray-700 max-w-[300px]">
																			<div className="line-clamp-2" title={f.motivo_analisis}>
																				{f.motivo_analisis || 'Error de validación'}
																			</div>
																		</td>
																	</tr>
																);
															})}
														</tbody>
													</table>
												</div>
											)}
										</div>
									) : (
										<div className="border border-gray-300 rounded-lg" style={{ maxHeight: 'calc(100vh - 400px)', display: 'flex', flexDirection: 'column' }}>
											<div className="overflow-x-auto overflow-y-auto flex-1" style={{ maxHeight: '100%' }}>
											<table className="min-w-full divide-y divide-gray-200" style={{ minWidth: '2800px' }}>
												<thead className="bg-white sticky top-0 z-[100] shadow-md">
													<tr>
														{/* Imagen - Contraíble */}
														<th className={`text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap sticky left-0 top-0 bg-white z-[110] shadow-sm ${imagenColumnExpanded ? 'px-3 py-2 min-w-[80px]' : 'p-1 w-10'}`}>
															{imagenColumnExpanded ? (
																<span className="flex items-center gap-1">
																	Imagen
																	<button type="button" onClick={toggleImagenColumn} className="p-0.5 rounded hover:bg-gray-200" title="Contraer columna">
																		<ChevronRightIcon className="h-3.5 w-3.5" />
																	</button>
																</span>
															) : (
																<button type="button" onClick={toggleImagenColumn} className="p-1 rounded hover:bg-gray-200" title="Expandir columna Imagen">
																	<ChevronLeftIcon className="h-4 w-4" />
																</button>
															)}
														</th>
														{/* Formato 606 oficial - Orden de columnas según especificación DGII */}
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															1. RNC/Cédula
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[80px]">
															2. Tipo ID
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															3. Tipo Bien/Serv.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[110px]">
															4. NCF
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[110px]">
															5. NCF Modificado
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															<button type="button" onClick={() => handleTableSort('fecha_comprobante')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																6. Fecha Comp.
																{tableSortKey === 'fecha_comprobante' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															<button type="button" onClick={() => handleTableSort('fecha_pago')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																7. Fecha Pago
																{tableSortKey === 'fecha_pago' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															<button type="button" onClick={() => handleTableSort('monto_servicios')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																8. Monto Serv.
																{tableSortKey === 'monto_servicios' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															<button type="button" onClick={() => handleTableSort('monto_bienes')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																9. Monto Bienes
																{tableSortKey === 'monto_bienes' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
															<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
																<button type="button" onClick={() => handleTableSort('monto_facturado')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																	10. Total Facturado
																	{tableSortKey === 'monto_facturado' && (tableSortDir === 'asc' ? '↑' : '↓')}
																</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															<button type="button" onClick={() => handleTableSort('descuento')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																Descuento
																{tableSortKey === 'descuento' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															<button type="button" onClick={() => handleTableSort('itbis_facturado')} className="flex items-center gap-0.5 hover:text-primary focus:outline-none focus:ring-1 focus:ring-primary rounded">
																11. ITBIS Fact.
																{tableSortKey === 'itbis_facturado' && (tableSortDir === 'asc' ? '↑' : '↓')}
															</button>
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															12. ITBIS Ret.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															13. ITBIS Prop.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															14. ITBIS Costo
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															15. ITBIS Adel.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															16. ITBIS Perc.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															17. Tipo Ret. ISR
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															18. Monto Ret. Renta
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															19. ISR Perc.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															20. Imp. Selectivo
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[100px]">
															21. Otros Imp.
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															22. Propina
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[90px]">
															23. Forma Pago
														</th>
														{/* Campos adicionales útiles para visualización administrativa */}
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															Razón Social
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[80px]">
															Moneda
														</th>
														<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-r border-gray-300 whitespace-nowrap min-w-[120px]">
															Actividad Econ.
														</th>
														<th className={`text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap sticky right-0 top-0 bg-white z-[110] shadow-sm border-l border-gray-300 ${accionesColumnExpanded ? 'px-3 py-2 min-w-[80px]' : 'p-1 w-10'}`}>
															{accionesColumnExpanded ? (
																<span className="flex items-center gap-1">
																	Acciones
																	<button type="button" onClick={toggleAccionesColumn} className="p-0.5 rounded hover:bg-gray-200" title="Contraer columna">
																		<ChevronRightIcon className="h-3.5 w-3.5" />
																	</button>
																</span>
															) : (
																<button type="button" onClick={toggleAccionesColumn} className="p-1 rounded hover:bg-gray-200" title="Expandir columna Acciones">
																	<ChevronLeftIcon className="h-4 w-4" />
																</button>
															)}
														</th>
													</tr>
												</thead>
													<tbody className="bg-white divide-y divide-gray-200">
													{getPaginatedRows(dataRowsSorted).map((row, index) => {
														const actualIndex = (currentPage - 1) * itemsPerPage + index;
														const rowId = row._rowId || row._id || actualIndex;
														const highlightClass = getRowHighlightClass(row);
														const problemasNCF = row._problemas?.filter(p => p.campo === 'NCF') || [];
														const problemasRNC = row._problemas?.filter(p => p.campo === 'RNC') || [];
														const problemasFecha = row._problemas?.filter(p => p.campo === 'Fecha') || [];
														const isCellEditNormal = (f) => editingCell?.rowId === rowId && editingCell?.field === f;
														const wrapCellPencilHover = (field, displayContent, currentValue) => (
															<div className="group relative flex items-center w-full min-h-[26px]">
																<div className="min-w-0 flex-1 truncate pr-9">
																	{displayContent}
																</div>
																<button
																	type="button"
																	onClick={(e) => { e.stopPropagation(); setEditingCell({ rowId, field }); setCellEditValue(currentValue != null && currentValue !== '' ? currentValue : ''); }}
																	className="absolute right-0 top-1/2 -translate-y-1/2 flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-md bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
																	title="Modificar esta celda"
																	aria-label="Modificar esta celda"
																>
																	<PencilSquareIcon className="h-4 w-4" />
																</button>
															</div>
														);
														return (
														<tr key={actualIndex} className={`hover:bg-gray-50 ${highlightClass}`}>
															{/* Imagen (contraíble) */}
															<td className={`whitespace-nowrap border-r border-gray-300 sticky left-0 bg-white z-40 shadow-sm ${imagenColumnExpanded ? 'px-3 py-2' : 'p-1 w-10'}`}>
																{imagenColumnExpanded ? (
																getImageUrlForRow(row, selectedFormulario) ? (
																	<button
																		onClick={(e) => viewImage(getImageUrlForRow(row, selectedFormulario), e)}
																		onContextMenu={(e) => {
																			e.preventDefault();
																			viewImage(getImageUrlForRow(row, selectedFormulario), { button: 2 });
																		}}
																		className="flex items-center justify-center w-7 h-7 bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
																		title="Click para ver imagen | Ctrl+Click o clic derecho para abrir en nueva pestaña"
																	>
																		<PhotoIcon className="h-4 w-4" />
																	</button>
																) : (
																	<div className="w-7 h-7 flex items-center justify-center text-gray-400">
																		<PhotoIcon className="h-4 w-4 opacity-30" />
																	</div>
																)
																) : (
																	<button type="button" onClick={toggleImagenColumn} className="p-1 rounded hover:bg-gray-100" title="Expandir Imagen">
																		<ChevronLeftIcon className="h-4 w-4 text-gray-500" />
																	</button>
																)}
															</td>
															{/* 1. RNC/Cédula */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<div className="flex items-center gap-1">
																	{problemasRNC.length > 0 && (
																		<ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" title={problemasRNC.map(p => p.motivo).join(', ')} />
																	)}
																	{isCellEditNormal('rnc_emisor') ? (
																		<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'rnc_emisor', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[90px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																	) : wrapCellPencilHover('rnc_emisor', <span className={`text-xs ${problemasRNC.length > 0 ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>{limpiarRNC(row.rnc_emisor || row.rnc_cedula) || 'N/A'}</span>, row.rnc_emisor || row.rnc_cedula || '')}
																</div>
															</td>
															{/* 2. Tipo ID */}
															<td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-300">
																{isCellEditNormal('tipo_id') ? (
																	<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'tipo_id', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[70px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('tipo_id', <span className="text-xs">{row.tipo_id || calcularTipoID(row.rnc_emisor || row.rnc_cedula) || 'N/A'}</span>, row.tipo_id || '')}
															</td>
															{/* 3. Tipo Bien/Servicio */}
															<td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-300">
																{isCellEditNormal('tipo_bien_servicio') ? (
																	<select value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'tipo_bien_servicio', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[100px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary">
																		<option value="">Seleccionar...</option>
																		{TIPO_BIEN_SERVICIO_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
																	</select>
																) : wrapCellPencilHover('tipo_bien_servicio', <span className="text-xs">{row.tipo_bien_servicio || row.tipo_bienes_servicios || 'N/A'}</span>, row.tipo_bien_servicio || row.tipo_bienes_servicios || '')}
															</td>
															{/* 4. NCF */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<div className="flex items-center gap-1">
																	{problemasNCF.length > 0 && (
																		<ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" title={problemasNCF.map(p => p.motivo).join(', ')} />
																	)}
																	{isCellEditNormal('ncf') ? (
																		<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={(e) => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'ncf', e.target.value); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className={`w-full min-w-[100px] px-1.5 py-1 text-xs border rounded focus:ring-2 focus:ring-primary ${problemasNCF.length > 0 ? 'border-red-500 bg-red-50' : 'border-gray-300'}`} />
																	) : wrapCellPencilHover('ncf', <span className={`text-xs ${problemasNCF.length > 0 ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>{row.ncf || 'N/A'}</span>, row.ncf || '')}
																</div>
															</td>
															{/* 5. NCF Modificado */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																{isCellEditNormal('ncf_modificado') ? (
																	<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'ncf_modificado', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[90px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('ncf_modificado', <span className="text-xs">{row.ncf_modificado || 'N/A'}</span>, row.ncf_modificado || '')}
															</td>
															{/* 6. Fecha Comprobante */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<div className="flex items-center gap-1">
																	{problemasFecha.length > 0 && (
																		<ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" title={problemasFecha.map(p => p.motivo).join(', ')} />
																	)}
																	{isCellEditNormal('fecha_comprobante') ? (
																		<input type="date" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'fecha_comprobante', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[100px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																	) : wrapCellPencilHover('fecha_comprobante', <span className={`text-xs ${problemasFecha.length > 0 ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>{formatDate(row.fecha_comprobante)}</span>, getNormalizedDate(row.fecha_comprobante) || '')}
																</div>
															</td>
															{/* 7. Fecha Pago */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																{isCellEditNormal('fecha_pago') ? (
																	<input type="date" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'fecha_pago', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[100px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('fecha_pago', <span className="text-xs">{row.fecha_pago ? formatDate(row.fecha_pago) : 'N/A'}</span>, getNormalizedDate(row.fecha_pago) || '')}
															</td>
															{/* 8. Monto Servicios */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300" {...(isCellEditNormal('monto_servicios') ? { 'data-cell-edit': 'true' } : {})}>
																{isCellEditNormal('monto_servicios') ? (
																	<input type="number" step="0.01" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'monto_servicios', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[80px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('monto_servicios', <span className="text-xs">{row.monto_servicios != null && row.monto_servicios !== '' ? formatCurrency(row.monto_servicios) : (row.monto_facturado_servicios != null ? formatCurrency(row.monto_facturado_servicios) : 'N/A')}</span>, row.monto_servicios ?? row.monto_facturado_servicios ?? '')}
															</td>
															{/* 9. Monto Bienes */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300" {...(isCellEditNormal('monto_bienes') ? { 'data-cell-edit': 'true' } : {})}>
																{isCellEditNormal('monto_bienes') ? (
																	<input type="number" step="0.01" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'monto_bienes', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[80px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('monto_bienes', <span className="text-xs">{row.monto_bienes != null && row.monto_bienes !== '' ? formatCurrency(row.monto_bienes) : (row.monto_facturado_bienes != null ? formatCurrency(row.monto_facturado_bienes) : 'N/A')}</span>, row.monto_bienes ?? row.monto_facturado_bienes ?? '')}
															</td>
															{/* 10. Total Facturado (calculado: monto_servicios + monto_bienes, no editable) */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<span className="text-xs">{formatCurrency((row.monto_servicios ?? row.monto_facturado_servicios ?? 0) + (row.monto_bienes ?? row.monto_facturado_bienes ?? 0) || row.monto_facturado || row.total_factura || 0)}</span>
															</td>
															{/* Descuento */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																{isCellEditNormal('descuento') ? (
																	<input type="number" step="0.01" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'descuento', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[70px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('descuento', <span className="text-xs">{(row.descuento != null && row.descuento !== '') ? formatCurrency(row.descuento) : 'N/A'}</span>, row.descuento ?? '')}
															</td>
															{/* 11. ITBIS Facturado - 14. ITBIS Costo (editables) */}
															{['itbis_facturado','itbis_retenido','itbis_proporcionalidad','itbis_llevado_costo'].map(field => (
																<td key={field} className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																	{isCellEditNormal(field) ? (
																		<input type="number" step="0.01" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, field, cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[70px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																	) : wrapCellPencilHover(field, <span className="text-xs">{row[field] != null && row[field] !== '' ? formatCurrency(row[field]) : 'N/A'}</span>, row[field] ?? '')}
																</td>
															))}
															{/* 15. ITBIS Adelantar - solo lectura (calculado) */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<span className="text-xs">{(() => { const f = parseFloat(row.itbis_facturado||0); const c = parseFloat(row.itbis_llevado_costo||0); const t = f - c; return t > 0 ? formatCurrency(t) : 'N/A'; })()}</span>
															</td>
															{/* 16. ITBIS Percibido y 17-22 */}
															{['itbis_percibido_compras','tipo_retencion_isr','monto_retencion_renta','isr_percibido_compras','impuesto_selectivo_consumo','otros_impuestos_tasas','monto_propina_legal'].map(field => (
																<td key={field} className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																	{isCellEditNormal(field) ? (
																		field === 'tipo_retencion_isr' ? (
																			<select value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, field, cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[90px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary">
																				<option value="">Seleccionar...</option>
																				{TIPO_RETENCION_ISR_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
																			</select>
																		) : (
																			<input type="number" step="0.01" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, field, cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[70px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																		)
																	) : wrapCellPencilHover(field, <span className="text-xs">{field === 'tipo_retencion_isr' ? getTipoRetencionISRLabel(row.tipo_retencion_isr) : (() => { const v = field === 'otros_impuestos_tasas' ? (row.otros_impuestos_tasas ?? row.otros_impuestos ?? row.otros_ipm ?? row.otros_imp) : row[field]; return v != null && v !== '' && v !== 0 ? formatCurrency(v) : (v === 0 ? formatCurrency(0) : 'N/A'); })()}</span>, field === 'otros_impuestos_tasas' ? (row.otros_impuestos_tasas ?? row.otros_impuestos ?? row.otros_ipm ?? row.otros_imp ?? '') : (row[field] ?? ''))}
																</td>
															))}
														{/* 23. Forma Pago */}
														<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
															{isCellEditNormal('forma_pago') ? (
																<select value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'forma_pago', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[90px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary">
																	{FORMA_PAGO_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
																</select>
															) : wrapCellPencilHover('forma_pago', <span className="text-xs">{getFormaPagoLabel(row.forma_pago || '1')}</span>, normalizarCodigoFormaPago(row.forma_pago) || '4')}
														</td>
															{/* Razón Social */}
															<td className="px-3 py-2 text-sm border-r border-gray-300">
																{isCellEditNormal('nombre_emisor') ? (
																	<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'nombre_emisor', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[100px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('nombre_emisor', <span className="text-xs">{row.nombre_emisor || row.razon_social || 'N/A'}</span>, row.nombre_emisor || row.razon_social || '')}
															</td>
															{/* Moneda - abre modal de conversión de tasa */}
															<td className="px-3 py-2 whitespace-nowrap text-sm border-r border-gray-300">
																<button
																	type="button"
																	onClick={() => handleOpenCurrencyModal(rowId, row.moneda || 'DOP', 'DOP', 'normal')}
																	className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline transition-colors"
																	title="Clic para convertir montos usando tasa de cambio"
																>
																	{row.moneda || 'DOP'}
																	<span className="text-gray-400 text-xs">↔</span>
																</button>
															</td>
															{/* Actividad Económica */}
															<td className="px-3 py-2 text-sm border-r border-gray-300">
																{isCellEditNormal('actividad_economica') ? (
																	<input type="text" value={cellEditValue} onChange={(e) => setCellEditValue(e.target.value)} onBlur={() => { if (escapePressedRef.current) { escapePressedRef.current = false; setEditingCell(null); return; } handleSaveCell(rowId, 'actividad_economica', cellEditValue); setEditingCell(null); }} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { e.preventDefault(); escapePressedRef.current = true; e.target.blur(); } }} className="w-full min-w-[100px] px-1.5 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-primary" />
																) : wrapCellPencilHover('actividad_economica', <span className="text-xs">{row.actividad_economica && row.actividad_economica.includes(' - ') ? row.actividad_economica.split(' - ')[0].trim() : (row.actividad_economica || 'N/A')}</span>, row.actividad_economica && row.actividad_economica.includes(' - ') ? row.actividad_economica.split(' - ')[0].trim() : (row.actividad_economica || ''))}
															</td>
															{/* Acciones (contraíble) */}
															<td className={`whitespace-nowrap text-sm sticky right-0 bg-white z-40 shadow-sm border-l border-gray-300 ${accionesColumnExpanded ? 'px-3 py-2' : 'p-1 w-10'}`}>
																{accionesColumnExpanded ? (
																<div className="flex items-center gap-1.5 sm:gap-2">
																	{/* Botón de Auditar - Siempre disponible */}
																	<button
																		onClick={() => {
																			handleToggleAuditado(row._rowId);
																		}}
																		className={`p-1.5 sm:p-2 rounded-lg transition-colors touch-manipulation ${
																			row.auditado
																				? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
																				: 'bg-gray-200 text-gray-600 hover:bg-gray-300 active:bg-gray-400'
																		}`}
																		title={row.auditado ? 'Marcado como auditado' : 'Marcar como auditado'}
																		aria-label={row.auditado ? 'Desmarcar como auditado' : 'Marcar como auditado'}
																	>
																		<CheckIcon className="h-4 w-4 sm:h-5 sm:w-5" />
																	</button>
																	
																	{/* Botón de Calcular ITBIS por Adelantar - Individual (siempre activo) */}
																	<button
																		onClick={(e) => {
																			e.stopPropagation();
																			handleCalcularItbisPorAdelantarIndividual(row._rowId);
																		}}
																		className="p-1.5 sm:p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 active:bg-blue-300 transition-colors touch-manipulation"
																		title="Calcular ITBIS por adelantar (ITBIS Facturado - ITBIS Costo)"
																		aria-label="Calcular ITBIS por adelantar"
																	>
																		<SparklesIcon className="h-4 w-4 sm:h-5 sm:w-5" />
																	</button>
																	
																{/* Botón de Marcar/Desmarcar como Pendiente - Para facturas (FacturaExtraida) y registros manuales (_pendiente) */}
																{(() => {
																	const rowKey = row._id ?? row._rowId;
																	const idx = dataRowsSelected.findIndex(r => (r._id ?? r._rowId) === rowKey);
																	// Calcular facturaId al vuelo (no usar mapa) para asegurar que corresponde a ESTA fila
																	const facturaId = getFacturaIdFromRow(row, selectedFormulario, facturasFisicas, idx >= 0 ? idx : null, dataRowsSelected);
																	const estadoAnalisis = getEstadoAnalisisFromRow(row, selectedFormulario, facturasFisicas);
																	const esPendiente = estadoAnalisis === 'pendiente';
																	if (!rowKey) return null;
																	return (
																		<button
																			onClick={() => {
																				const fid = getFacturaIdFromRow(row, selectedFormulario, facturasFisicas, idx >= 0 ? idx : null, dataRowsSelected);
																				const rowNcf = getRowNcf(row);
																				if (fid && facturasFisicas?.length) {
																					const fact = facturasFisicas.find(f => f.id === fid);
																					const d = fact?.datos_estructurados || fact || {};
																					const factNcf = normalizeNcfForMatch(d.ncf ?? d.numero_comprobante_fiscal ?? '');
																					console.warn('[Pendiente] click rowNcf=', rowNcf, 'facturaId=', fid, 'factNcf=', factNcf, 'match=', rowNcf === factNcf);
																				}
																				if (fid) {
																					if (esPendiente) handleDesmarcarPendiente(fid);
																					else handleMarcarPendiente(fid);
																				} else {
																					handleMarcarPendienteRegistro(row, esPendiente);
																				}
																			}}
																			className={`p-1.5 sm:p-2 rounded-lg transition-colors touch-manipulation ${
																				esPendiente 
																					? 'bg-green-100 text-green-800 hover:bg-green-200 active:bg-green-300' 
																					: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 active:bg-yellow-300'
																			}`}
																			title={esPendiente ? 'Desmarcar como pendiente' : 'Marcar como pendiente'}
																			aria-label={esPendiente ? 'Desmarcar como pendiente' : 'Marcar como pendiente'}
																		>
																			<ClockIcon className="h-4 w-4 sm:h-5 sm:w-5" />
																		</button>
																	);
																})()}
																	{/* Botón Eliminar registro */}
																	<button
																		onClick={() => handleDeleteRow(row)}
																		className="p-1.5 sm:p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 active:bg-red-300 transition-colors touch-manipulation"
																		title="Eliminar registro"
																		aria-label="Eliminar registro"
																	>
																		<TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
																	</button>
																</div>
																) : (
																	<button type="button" onClick={toggleAccionesColumn} className="p-1 rounded hover:bg-gray-100" title="Expandir Acciones">
																		<ChevronLeftIcon className="h-4 w-4 text-gray-500" />
																	</button>
																)}
															</td>
														</tr>
														);
													})}
												</tbody>
											</table>
											</div>
										</div>
									)}
									
									{/* Paginación */}
									{dataRowsSelected.length > 0 && (
										<div className="flex flex-col gap-3 mt-4 pt-4 border-t border-gray-200">
											{/* Primera fila: Información de registros y selector */}
											<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
												<div className="text-xs sm:text-sm text-gray-600">
													Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, dataRowsSelected.length)} de {dataRowsSelected.length} registros
												</div>
												<div className="flex items-center gap-2">
													<label className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
														Registros por página:
													</label>
													<select
														value={itemsPerPage}
														onChange={(e) => {
															setItemsPerPage(Number(e.target.value));
															setCurrentPage(1); // Resetear a página 1 al cambiar
														}}
														className="px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
													>
														<option value={5}>5</option>
														<option value={10}>10</option>
														<option value={20}>20</option>
														<option value={50}>50</option>
														<option value={100}>100</option>
													</select>
												</div>
											</div>
											{/* Segunda fila: Navegación de páginas */}
											<div className="flex items-center justify-center gap-2">
												<button
													onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
													disabled={currentPage === 1}
													className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity touch-manipulation"
													title="Página anterior"
													aria-label="Página anterior"
												>
													<ArrowLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
												</button>
												<span className="text-xs sm:text-sm text-gray-700 font-medium px-2 sm:px-3">
													Página {currentPage} de {totalPages}
												</span>
												<button
													onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
													disabled={currentPage === totalPages}
													className="p-2 bg-primary text-white rounded-lg hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity touch-manipulation"
													title="Página siguiente"
													aria-label="Página siguiente"
												>
													<ArrowRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
												</button>
											</div>
										</div>
									)}
								</div>
							</div>
						</>
					)}
						</>
					) : (
						<div className="flex items-center justify-center py-16">
							<div className="text-center text-gray-500 max-w-md">
								<DocumentTextIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
								<p className="text-lg font-medium text-gray-700 mb-2">Selecciona un formulario</p>
								<p className="text-sm text-gray-500">Elige un formulario de la lista para ver y editar sus datos</p>
							</div>
						</div>
					)}
				</div>

				{/* Modal de imagen con zoom y pan - z-[200] para quedar por encima del encabezado sticky (z-[110]) */}
			{showImageModal && imageUrl && (
				<div 
					className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[200] p-4"
					onClick={() => {
						setShowImageModal(false);
						setImageZoom(1);
						setImagePosition({ x: 0, y: 0 });
					}}
					onMouseMove={!isPdfUrl(imageUrl) ? handleMouseMove : undefined}
					onMouseUp={!isPdfUrl(imageUrl) ? handleMouseUp : undefined}
					onMouseLeave={!isPdfUrl(imageUrl) ? handleMouseUp : undefined}
					onWheel={!isPdfUrl(imageUrl) ? handleWheel : undefined}
				>
					<div className="relative w-full h-full flex items-center justify-center">
						{/* Controles de zoom - Solo para imágenes, no para PDFs */}
						{!isPdfUrl(imageUrl) && (
							<div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleZoomIn();
									}}
									className="bg-black bg-opacity-50 text-white rounded-lg p-2 hover:bg-opacity-75 transition-opacity"
									title="Acercar (Ctrl + Scroll)"
								>
									<ArrowUpIcon className="h-5 w-5" />
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleZoomOut();
									}}
									className="bg-black bg-opacity-50 text-white rounded-lg p-2 hover:bg-opacity-75 transition-opacity"
									title="Alejar (Ctrl + Scroll)"
								>
									<ArrowDownIcon className="h-5 w-5" />
								</button>
								<button
									onClick={(e) => {
										e.stopPropagation();
										handleResetZoom();
									}}
									className="bg-black bg-opacity-50 text-white rounded-lg p-2 hover:bg-opacity-75 transition-opacity text-xs"
									title="Resetear zoom"
								>
									100%
								</button>
							</div>
						)}

						{/* Botón cerrar */}
						<button
							onClick={(e) => {
								e.stopPropagation();
								setShowImageModal(false);
								setImageZoom(1);
								setImagePosition({ x: 0, y: 0 });
							}}
							className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75 z-20"
							title="Cerrar"
						>
							<XMarkIcon className="h-6 w-6" />
						</button>

						{/* Indicador de zoom - Solo para imágenes */}
						{!isPdfUrl(imageUrl) && (
							<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 bg-black bg-opacity-50 text-white rounded-lg px-3 py-1 text-sm">
								{Math.round(imageZoom * 100)}%
							</div>
						)}

						{/* Imagen o PDF con zoom y pan */}
						{isPdfUrl(imageUrl) ? (
							// Mostrar PDF en iframe
							<div className="relative w-full h-full flex items-center justify-center">
								<iframe
									src={imageUrl}
									className="w-full h-full border-0 rounded"
									style={{
										maxWidth: '100%',
										maxHeight: '100%',
										transform: `scale(${imageZoom})`,
										transformOrigin: 'center center',
										transition: 'transform 0.1s ease-out'
									}}
									title="Vista previa del PDF"
								/>
								{/* Botón para abrir PDF en nueva pestaña */}
								<a
									href={imageUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="absolute bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors z-20"
									onClick={(e) => e.stopPropagation()}
								>
									Abrir PDF en nueva pestaña
								</a>
							</div>
						) : (
							// Mostrar imagen normal
							<div 
								className="relative overflow-hidden"
								style={{
									width: '100%',
									height: '100%',
									cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
								}}
								onMouseDown={handleMouseDown}
								onClick={(e) => {
									if (imageZoom === 1) {
										e.stopPropagation();
									}
								}}
							>
								<img 
									src={imageUrl} 
									alt="Factura" 
									className="object-contain rounded select-none"
									style={{
										transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageZoom})`,
										transformOrigin: 'center center',
										maxWidth: '100%',
										maxHeight: '100%',
										transition: isDragging ? 'none' : 'transform 0.1s ease-out',
										userSelect: 'none',
										pointerEvents: 'none'
									}}
									draggable={false}
									onError={(e) => {
										e.target.style.display = 'none';
										// Mostrar mensaje de error
										const errorDiv = document.createElement('div');
										errorDiv.className = 'text-white text-center p-4';
										errorDiv.innerHTML = `
											<p class="text-lg font-semibold mb-2">Error al cargar la imagen</p>
											<p class="text-sm opacity-75">URL: ${imageUrl}</p>
										`;
										e.target.parentElement.appendChild(errorDiv);
									}}
									onLoad={() => {
									}}
								/>
							</div>
						)}

						{/* Instrucciones */}
						{!isPdfUrl(imageUrl) && (
							<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 bg-black bg-opacity-50 text-white rounded-lg px-4 py-2 text-xs text-center">
								<p>Usa Ctrl + Scroll para zoom | Arrastra cuando esté ampliada</p>
							</div>
						)}
					</div>
				</div>
			)}

			{/* Modal de detalle de facturas físicas (FacturaExtraida) */}
			{showFacturasFisicas && (
				<div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40">
					<div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[80vh] flex flex-col">
						<div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
							<div>
								<h3 className="text-sm font-semibold text-gray-900">Detalle de facturas físicas</h3>
								<p className="text-xs text-gray-500">
									Cada fila es un registro de <code>FacturaExtraida</code> con su estado de análisis.
								</p>
							</div>
							<button
								type="button"
								onClick={() => setShowFacturasFisicas(false)}
								className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500"
							>
								<XMarkIcon className="h-4 w-4" />
							</button>
						</div>
						<div className="flex-1 overflow-auto">
							{loadingFacturasFisicas ? (
								<div className="py-8 flex items-center justify-center text-sm text-gray-500">
									Cargando facturas físicas...
								</div>
							) : facturasFisicas.length === 0 ? (
								<div className="py-8 flex items-center justify-center text-sm text-gray-500">
									No se encontraron facturas físicas para este formulario.
								</div>
							) : (
								<table className="min-w-full divide-y divide-gray-200 text-xs">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">ID</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">Archivo</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">Posición</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">NCF</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">RNC</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">Total</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700 border-r">Estado análisis</th>
											<th className="px-2 py-2 text-left font-medium text-gray-700">Motivo</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{facturasFisicas.map((f) => {
											const datos = f.datos_estructurados || {};
											const ncf = datos.ncf || '';
											const rnc = datos.rnc_emisor || datos.rnc_cedula || datos.rnc || '';
											const total = datos.total_factura || datos.monto_total || datos.monto_facturado || 0;
											return (
												<tr key={f.id}>
													<td className="px-2 py-1.5 border-r text-gray-700">{f.id}</td>
													<td
														className="px-2 py-1.5 border-r text-gray-700 truncate max-w-[180px]"
														title={f.archivo_adjunto?.nombre_original}
													>
														{f.archivo_adjunto?.nombre_original || 'Sin nombre'}
													</td>
													<td className="px-2 py-1.5 border-r text-gray-700">
														Pág. {f.pagina_inicio}
														{f.pagina_fin && f.pagina_fin !== f.pagina_inicio ? `-${f.pagina_fin}` : ''}, pos.{' '}
														{f.posicion_en_archivo}
													</td>
													<td className="px-2 py-1.5 border-r text-gray-700">{ncf || '—'}</td>
													<td className="px-2 py-1.5 border-r text-gray-700">{rnc || '—'}</td>
													<td className="px-2 py-1.5 border-r text-gray-700">
														{typeof total === 'number'
															? total.toLocaleString('es-DO', { style: 'currency', currency: 'DOP' })
															: total}
													</td>
													<td className="px-2 py-1.5 border-r">
														<span
															className={
																f.estado_analisis === 'incluida'
																	? 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-green-50 text-green-800 border border-green-300'
																	: f.estado_analisis === 'duplicada'
																	? 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-red-50 text-red-800 border border-red-300'
																	: f.estado_analisis === 'pendiente'
																	? 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-yellow-50 text-yellow-800 border border-yellow-300'
																	: 'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-gray-50 text-gray-700 border border-gray-300'
															}
														>
															{f.estado_analisis || 'pendiente'}
														</span>
													</td>
													<td className="px-2 py-1.5 text-gray-700 max-w-[260px]">
														<div className="line-clamp-3" title={f.motivo_analisis}>
															{f.motivo_analisis || '—'}
														</div>
													</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							)}
						</div>
					</div>
				</div>
			)}
			
			{/* Modal de Confirmación de Eliminación */}
			{showDeleteModal && formularioToDelete && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
						<div className="p-6">
							{/* Header */}
							<div className="flex items-center gap-3 mb-4">
								<div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
									<ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
								</div>
								<div>
									<h3 className="text-lg font-bold text-gray-900">
										Confirmar Eliminación
									</h3>
									<p className="text-sm text-gray-600">
										Esta acción no se puede deshacer
									</p>
								</div>
							</div>

							{/* Información del formulario */}
							<div className="mb-4 p-3 bg-gray-50 rounded-lg">
								<p className="text-sm text-gray-700 mb-1">
									<strong>Formulario:</strong> {formularioToDelete.tipo} - {formularioToDelete.periodo}
								</p>
								{formularioToDelete.usuario_nombre && (
									<p className="text-sm text-gray-700">
										<strong>Usuario:</strong> {formularioToDelete.usuario_nombre}
									</p>
								)}
							</div>

							{/* Mensaje de confirmación */}
							<p className="text-sm text-gray-700 mb-4">
								¿Estás seguro de que deseas eliminar este formulario? Todos los datos asociados serán eliminados permanentemente.
							</p>

							{/* Campo de clave de administrador si no tiene permisos */}
							{(!isAdmin() && !isGestor()) && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Clave de Administrador *
									</label>
									<input
										type="password"
										value={adminKey}
										onChange={(e) => {
											setAdminKey(e.target.value);
											setDeleteError('');
										}}
										placeholder="Ingresa la clave de administrador"
										className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
										autoFocus
									/>
									{deleteError && (
										<p className="mt-2 text-sm text-red-600">{deleteError}</p>
									)}
								</div>
							)}

							{/* Botones */}
							<div className="flex gap-3 justify-end">
								<button
									onClick={() => {
										setShowDeleteModal(false);
										setFormularioToDelete(null);
										setAdminKey('');
										setDeleteError('');
									}}
									className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
								>
									Cancelar
								</button>
								<button
									onClick={confirmDeleteFormulario}
									disabled={(!isAdmin() && !isGestor()) && !adminKey.trim()}
									className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
								>
									Eliminar
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			
			{/* Modal de Comparación Duplicado vs Principal */}
			{mostrarModalComparacion && datosComparacion && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4" onClick={() => setMostrarModalComparacion(false)}>
					<div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
						<div className="p-6 border-b border-gray-200">
							<div className="flex items-center justify-between">
								<h2 className="text-xl font-bold text-gray-900">Comparación: Duplicado vs Principal</h2>
								<button onClick={() => setMostrarModalComparacion(false)} className="text-gray-400 hover:text-gray-600">
									<XMarkIcon className="h-6 w-6" />
								</button>
							</div>
							<p className="text-sm text-gray-600 mt-2">
								Total de diferencias: <span className="font-semibold text-red-600">{datosComparacion.total_diferencias}</span>
							</p>
						</div>
						<div className="p-6">
							<div className="grid grid-cols-2 gap-4">
								<div>
									<h3 className="text-lg font-semibold text-red-600 mb-3">Registro Duplicado (ID: {datosComparacion.duplicado.id})</h3>
									<div className="space-y-2 max-h-[60vh] overflow-y-auto">
										{Object.entries(datosComparacion.diferencias || {}).map(([campo, valores]) => (
											<div key={campo} className={`p-2 rounded ${valores.diferente ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
												<div className="text-xs font-semibold text-gray-700 mb-1">{campo}</div>
												<div className="text-sm text-gray-900">{String(valores.duplicado || '—')}</div>
											</div>
										))}
									</div>
								</div>
								<div>
									<h3 className="text-lg font-semibold text-green-600 mb-3">Registro Principal (ID: {datosComparacion.principal.id})</h3>
									<div className="space-y-2 max-h-[60vh] overflow-y-auto">
										{Object.entries(datosComparacion.diferencias || {}).map(([campo, valores]) => (
											<div key={campo} className={`p-2 rounded ${valores.diferente ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
												<div className="text-xs font-semibold text-gray-700 mb-1">{campo}</div>
												<div className="text-sm text-gray-900">{String(valores.principal || '—')}</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			
			{/* Modal de Merge - Necesita useState interno */}
			{mostrarModalMerge && datosMerge && <ModalMerge datosMerge={datosMerge} onClose={() => setMostrarModalMerge(false)} onEjecutar={handleEjecutarMerge} />}
			
			{/* Modal de Historial */}
			{mostrarModalHistorial && historialFactura && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4" onClick={() => setMostrarModalHistorial(false)}>
					<div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
						<div className="p-6 border-b border-gray-200">
							<div className="flex items-center justify-between">
								<h2 className="text-xl font-bold text-gray-900">Historial de Acciones</h2>
								<button onClick={() => setMostrarModalHistorial(false)} className="text-gray-400 hover:text-gray-600">
									<XMarkIcon className="h-6 w-6" />
								</button>
							</div>
							<p className="text-sm text-gray-600 mt-2">Factura ID: {historialFactura.id}</p>
						</div>
						<div className="p-6">
							{historialFactura.historial_acciones && historialFactura.historial_acciones.length > 0 ? (
								<div className="space-y-3">
									{historialFactura.historial_acciones.slice().reverse().map((accion, index) => (
										<div key={index} className="p-4 border border-gray-200 rounded-lg">
											<div className="flex items-start justify-between mb-2">
												<div>
													<div className="text-sm font-semibold text-gray-900">{accion.accion}</div>
													<div className="text-xs text-gray-600 mt-1">
														Por: {accion.usuario} • {new Date(accion.fecha).toLocaleString('es-DO')}
													</div>
												</div>
											</div>
											{accion.detalles && Object.keys(accion.detalles).length > 0 && (
												<div className="mt-2 text-xs text-gray-700 bg-gray-50 p-2 rounded">
													<pre className="whitespace-pre-wrap">{JSON.stringify(accion.detalles, null, 2)}</pre>
												</div>
											)}
										</div>
									))}
								</div>
							) : (
								<p className="text-gray-500 text-center py-8">No hay historial de acciones registrado</p>
							)}
						</div>
					</div>
				</div>
			)}
		</div>

		{/* ── Modal de Tasa de Cambio - z-[200] para quedar por encima del encabezado sticky (z-[110]) ── */}
		{showCurrencyModal && (
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4" onClick={() => setShowCurrencyModal(false)}>
				<div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
					{/* Header */}
					<div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-200">
						<div className="flex items-center gap-3">
							<div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-lg">
								💱
							</div>
						<div>
							<h3 className="text-sm font-semibold text-gray-900">Convertir montos a DOP</h3>
							<p className="text-xs text-gray-500 mt-0.5">
								{pendingCurrencyChange.fromCurrency} → DOP (pesos dominicanos)
							</p>
						</div>
						</div>
						<button onClick={() => setShowCurrencyModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</div>

					{/* Body */}
					<div className="px-6 py-5">
					<p className="text-xs text-gray-600 mb-4">
						Ingresa la tasa de cambio. Todos los montos se multiplicarán por este valor y la moneda quedará en <strong>DOP</strong>.
					</p>

					<div className="mb-4">
						<label className="block text-xs font-medium text-gray-700 mb-1.5">
							Tasa: 1 {pendingCurrencyChange.fromCurrency} = ? DOP
						</label>
							<div className="relative">
								<input
									type="number"
									step="0.0001"
									min="0.0001"
									placeholder="Ej: 60.50"
									value={exchangeRateInput}
									onChange={e => setExchangeRateInput(e.target.value)}
									onKeyDown={e => { if (e.key === 'Enter') handleApplyExchangeRate(); if (e.key === 'Escape') setShowCurrencyModal(false); }}
									autoFocus
									className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
								/>
								<span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 font-medium">
									{pendingCurrencyChange.toCurrency}
								</span>
							</div>
						</div>

						{/* Preview */}
						{parseFloat(exchangeRateInput) > 0 && (
							<div className="bg-blue-50 rounded-lg px-4 py-3 text-xs text-blue-700 border border-blue-100">
								<span className="font-medium">Ejemplo:</span> 1,000 {pendingCurrencyChange.fromCurrency} → RD$ {(1000 * parseFloat(exchangeRateInput)).toLocaleString('es-DO', { minimumFractionDigits: 2 })} DOP
							</div>
						)}
					</div>

					{/* Footer */}
					<div className="px-6 pb-5 flex gap-3">
						<button
							onClick={() => setShowCurrencyModal(false)}
							className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
						>
							Cancelar
						</button>
						<button
							onClick={handleApplyExchangeRate}
							disabled={!parseFloat(exchangeRateInput) || parseFloat(exchangeRateInput) <= 0}
							className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
						>
							Aplicar Conversión
						</button>
					</div>
				</div>
			</div>
		)}
		{/* ─────────────────────────────────────────────────────────────────── */}
		</>
	);
}

// Componente Modal de Merge (necesita su propio estado)
const ModalMerge = ({ datosMerge, onClose, onEjecutar }) => {
	const [camposSeleccionados, setCamposSeleccionados] = useState(new Set());
	const [accionDuplicado, setAccionDuplicado] = useState('cambiar_estado');
	
	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-4" onClick={onClose}>
			<div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
				<div className="p-6 border-b border-gray-200">
					<div className="flex items-center justify-between">
						<h2 className="text-xl font-bold text-gray-900">Merge de Datos</h2>
						<button onClick={onClose} className="text-gray-400 hover:text-gray-600">
							<XMarkIcon className="h-6 w-6" />
						</button>
					</div>
					<p className="text-sm text-gray-600 mt-2">Seleccione los campos del duplicado que desea copiar al registro principal</p>
				</div>
				<div className="p-6">
					<div className="space-y-3 max-h-[50vh] overflow-y-auto mb-4">
						{Object.entries(datosMerge.diferencias || {}).map(([campo, valores]) => (
							<div key={campo} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
								<input
									type="checkbox"
									checked={camposSeleccionados.has(campo)}
									onChange={(e) => {
										const nuevos = new Set(camposSeleccionados);
										if (e.target.checked) {
											nuevos.add(campo);
										} else {
											nuevos.delete(campo);
										}
										setCamposSeleccionados(nuevos);
									}}
									className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
								/>
								<div className="flex-1">
									<div className="text-sm font-semibold text-gray-900 mb-1">{campo}</div>
									<div className="grid grid-cols-2 gap-2 text-xs">
										<div className="text-red-600">
											<span className="font-medium">Duplicado:</span> {String(valores.duplicado || '—')}
										</div>
										<div className="text-green-600">
											<span className="font-medium">Principal:</span> {String(valores.principal || '—')}
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-2">Acción para el duplicado después del merge:</label>
						<select
							value={accionDuplicado}
							onChange={(e) => setAccionDuplicado(e.target.value)}
							className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
						>
							<option value="cambiar_estado">Cambiar estado a "Excluida"</option>
							<option value="eliminar">Eliminar permanentemente</option>
						</select>
					</div>
					<div className="flex justify-end gap-3">
						<button
							onClick={onClose}
							className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
						>
							Cancelar
						</button>
						<button
							onClick={() => {
								if (camposSeleccionados.size === 0) {
									window.alert('Por favor seleccione al menos un campo para mergear');
									return;
								}
								onEjecutar(datosMerge.facturaId, Array.from(camposSeleccionados), accionDuplicado);
							}}
							className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
						>
							Ejecutar Merge
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
